import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./moodboardFonts.css";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { alpha } from "@mui/material/styles";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  Select,
  Slider,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CropFreeOutlinedIcon from "@mui/icons-material/CropFreeOutlined";
import CropOutlinedIcon from "@mui/icons-material/CropOutlined";
import FlipToFrontOutlinedIcon from "@mui/icons-material/FlipToFrontOutlined";
import FlipToBackOutlinedIcon from "@mui/icons-material/FlipToBackOutlined";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ZoomInOutlinedIcon from "@mui/icons-material/ZoomInOutlined";
import ZoomOutOutlinedIcon from "@mui/icons-material/ZoomOutOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import { readSourceAiDesignId } from "../AI/aiDesignsStudioState";
import { normalizeBoardAiMode, resolveBoardAiPrompt } from "./moodboardBoardAi";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ViewQuiltOutlinedIcon from "@mui/icons-material/ViewQuiltOutlined";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import CheckBoxOutlineBlankOutlinedIcon from "@mui/icons-material/CheckBoxOutlineBlankOutlined";
import SvgIcon from "@mui/material/SvgIcon";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import RedoOutlinedIcon from "@mui/icons-material/RedoOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import SkillAgentInlineTitle from "./SkillAgentInlineTitle";
import {
  addSkillAgentMoodboardAssets,
  autoSkillAgentMoodboardPages,
  contourStrokeSkillAgentMoodboardAsset,
  cropSkillAgentMoodboardAsset,
  deleteSkillAgentMoodboard,
  deleteSkillAgentMoodboardAsset,
  exportSkillAgentMoodboardPdf,
  fetchSkillAgentMoodboardAssetBlob,
  fetchSkillAgentMoodboardAssetBlobUrl,
  getSkillAgentMoodboardById,
  getSkillAgentMoodboardPool,
  getSkillAgentMoodboardMeetingPool,
  getSkillAgentMoodboardProjectPool,
  removeBgSkillAgentMoodboardAsset,
  generateSkillAgentMoodboardImage,
  editSkillAgentMoodboardAsset,
  saveSkillAgentMoodboardPages,
  updateSkillAgentMoodboard,
} from "../../api/Skills/skillAgentMoodboardsApi";
import {
  fetchSkillAgentSearchItemBlobUrl,
  getSkillAgentSearchById,
  listSkillAgentSearches,
} from "../../api/Skills/skillAgentSearchesApi";
import { pageFieldSx } from "../../styles/pageModernStyles";
import { socket } from "../../socket";
import { meetingMediaUrl } from "../../api/Meetings/meetingSessionsApi";
import { isPropuestaBoard } from "./skillAgentTaskTree";
import {
  applyAviatorPresetToPage,
  clampSlot,
  clampSlotAllowOutside,
  fitSlotInPage,
  slotIsOffPage,
  markStrokeSvg,
  clampMark,
  clampMarkAllowOutside,
  markToPasteboardItem,
  rotatedImageLayout,
  panAfterDrag,
  emptyFreePage,
  blankPageFromBoardTemplate,
  MIN_SLOT,
  newSlotId,
  newMarkId,
  normalizeChromeStyle,
  normalizeFreePage,
  normalizePages,
  loadPageHeaderFooterMarks,
  seedPageHeaderFooterMarks,
  scrubChromeMarkDupes,
  snapSlot,
  sortSlotsByLayer,
  DEFAULT_MARK_COLOR,
  markFontCss,
  markFontGroups,
  markFontSizeOptions,
  clampMarkFontSize,
  clampGridCols,
  clampGridRows,
  clampPolarRings,
  clampPolarRays,
  parseGridDivisions,
  parsePolarDivisions,
  textMarkFillColor,
  textMarkOutlineCss,
  applyLivePaintFillToMark,
  createGradientMeshOnMark,
  gradientAnnotatorAxis,
  lerpHexColor,
  normalizeMarkMesh,
  normalizeMarkFontFamily,
  normalizeMarkFontWeight,
  normalizeMarkTextAlign,
  normalizeMarkWritingMode,
  translateMark,
  resizeTextMarkFontSize,
  resizeTextMarkWidth,
  textMarkBoxWidth,
  assetTeamNote,
  notesByAssetIdFromAssets,
  textMarksForMeetingNotes,
  mergeNoteMarksForSlots,
  stampMeetingNotesOnPages,
  normalizeRotation,
  pointerAngleDeg,
  rotationFromPointerDrag,
  worldDeltaToLocal,
  regularPolygonPoints,
  pointInPolygon,
  starPolygonPoints,
  smoothPathPoints,
  splitPathAtPoint,
  insertPathAnchor,
  deletePathAnchor,
  movePathAnchor,
  nearestAnchorIndex,
  markIdsInsidePolygon,
  markIdsWithColor,
  ellipticalArcPathD,
  sampleEllipticalArcPoints,
  spiralPathPoints,
  clampSpiralTurns,
  dimensionMetrics,
  flareGeometry,
  arcAnglesFromPointer,
  erasePathNearPoint,
  knifeCutMarks,
  scissorsCutMark,
  penAnchorsToPolyline,
  PEN_HANDLE_DRAG_MIN,
  blobStrokeToOutline,
  normalizeWidthPoints,
  samplePolylineAtT,
  variableWidthOutline,
  markIdsHitByStroke,
  offsetMark,
  outlineStrokeMark,
  simplifyPathPoints,
  createOutlinesMark,
  averagePathAnchors,
  markPaintColors,
  markSupportsFill,
  linearGradientAxis,
  normalizeMarkGradient,
  markHasBox,
  markBounds,
  markOutlinePoints,
  boxMarkAnchorPoints,
  expandBoxMarkToPath,
  CLIP_MASK_MARK_TYPES,
  resizeMarkBox,
  recognizeShaperStroke,
  shaperResultToMarkDraft,
  marksHitByShaperScribble,
  TEXT_MARK_MAX_LEN,
} from "./skillAgentMoodboardLayout";
import {
  buildMoodboardClipboard,
  pageAfterMoodboardCut,
  pasteboardAfterMoodboardCut,
  pasteMoodboardClipboard,
  pasteMoodboardClipboardOnAllPages,
} from "./moodboardClipboard";
import {
  PAGE_VIEW_ZOOM_MAX,
  PAGE_VIEW_ZOOM_MIN,
  PAGE_VIEW_ZOOM_STEP,
  clampPageViewZoom,
  pageViewZoomFromScrollBox,
} from "./skillAgentMoodboardViewZoom";
import {
  ARTBOARD_GAP,
  BOARD_CAM_ZOOM_MAX,
  BOARD_CAM_ZOOM_MIN,
  BOARD_CAM_ZOOM_STEP,
  BOARD_PX_PER_UNIT,
  PASTEBOARD_ITEM_H,
  PASTEBOARD_ITEM_W,
  WORKSPACE_MODE_BOARD,
  WORKSPACE_MODE_PAGE,
  artboardIndexAtPoint,
  assignDefaultBoards,
  boardWorldBounds,
  clampBoardCamZoom,
  clientToBoardPoint,
  fitBoardCamera,
  focusBoardCameraOnPage,
  newPasteboardId,
  normalizePageBoard,
  normalizePasteboard,
  pasteboardSizeFromSlot,
  pointToArtboardNorm,
  movePageBoard,
  rearrangeArtboards,
  remapIndexAfterReorder,
  reorderPages,
  readStoredWorkspaceMode,
  writeStoredWorkspaceMode,
  readStoredPasteboardBg,
  writeStoredPasteboardBg,
  DEFAULT_PASTEBOARD_BG,
  slotToPasteboardItem,
  pasteboardItemToSlotDraft,
  reparentSlotsToPage,
} from "./skillAgentMoodboardBoard";
import MoodboardArtboardPreview from "./MoodboardArtboardPreview";
import {
  clearMoodboardAssetBlobCache,
  createMoodboardAssetBlobFetcher,
  peekMoodboardAssetBlobUrl,
  invalidateMoodboardAssetBlob,
} from "./moodboardAssetBlobCache";
import {
  isOfflineEligibleBoardKind,
  isBrowserOnline,
  shouldQueueOfflineSave,
  buildPendingPagesPayload,
  buildPendingMetaPayload,
} from "./moodboardOfflinePure";
import {
  getOfflineBoardSnapshot,
  hasOfflinePending,
  putOfflineBoardSnapshot,
  queueOfflinePages,
  queueOfflineMeta,
  clearOfflinePending,
  prefetchBoardForOffline,
  flushOfflineBoard,
} from "./moodboardOfflineStore";
import MoodboardPasteboardItem from "./MoodboardPasteboardItem";
import MoodboardGeneratePanel from "./MoodboardGeneratePanel";
import MoodboardLayersDrawer from "./MoodboardLayersDrawer";
import MoodboardInpaintDialog from "./MoodboardInpaintDialog";
import MoodboardContextMenu from "./MoodboardContextMenu";
import MoodboardVcToolbar from "./MoodboardVcToolbar";
import MoodboardVcRightDock from "./MoodboardVcRightDock";
import { activeVcToolId } from "./moodboardVcTools";
import {
  defaultPerspectiveGrid,
  normalizePerspectiveGrid,
  perspectiveGridSegments,
  perspectivePlaneColor,
  projectDeltaOnPerspectivePlane,
} from "./skillAgentMoodboardPerspective";
import {
  alignSlots,
  alignMarks,
  bringSlotForward,
  bringMarksToFront,
  distributeMarks,
  distributeSlots,
  duplicatePage,
  nextBoardNearPages,
  knifeCutSlots,
  reflectMarksH,
  reflectMarksV,
  reflectSlotsH,
  reflectSlotsV,
  rotateMarksBy,
  rotateSlotsBy,
  scaleSlotsBy,
  shearMarksBy,
  shearSlotsBy,
  joinTwoPaths,
  joinPathChain,
  pathToTextPath,
  textCharIndexAt,
  applyTextCharOffset,
  sendSlotBackward,
  sendMarksToBack,
  distortMarksNear,
  widthMarksAt,
  blendTwoMarks,
  ensurePuppetState,
  movePuppetPin,
  addPuppetPin,
  envelopeWarpMarks,
  envelopeMeshMarks,
  attachMarksToPath,
  expandObjectsOnPath,
  resolveObjectsOnPathTargets,
  markAsPath,
  defineSymbolFromMark,
  spraySymbolAt,
  shiftSymbolsNear,
  scrunchSymbolsNear,
  sizeSymbolsNear,
  spinSymbolsNear,
  stainSymbolsNear,
  screenSymbolsNear,
  rectCutMarks,
  mirrorCutMarks,
  pathfinderMarks,
  marksBBox,
  scaleMarksWithBBox,
  rotateMarksAroundBBox,
  shearMarksInBBox,
  makeClippingMask,
  releaseClippingMask,
  resolveClippingMaskTargets,
  syncSlotClipWithFrame,
  slotClipLocalGeometry,
  transformSlotClipLocal,
  expandSelectionByGroup,
  groupMarks,
  ungroupMarks,
} from "./skillAgentMoodboardTools";
import {
  clearPagesHistory as clearPagesHistoryState,
  canRedoPages,
  canUndoPages,
  createPagesHistory,
  pushPagesHistory,
  redoPagesHistory,
  undoPagesHistory,
} from "./skillAgentMoodboardHistory";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import CropLandscapeOutlinedIcon from "@mui/icons-material/CropLandscapeOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import AlignHorizontalLeftOutlinedIcon from "@mui/icons-material/AlignHorizontalLeftOutlined";
import AlignHorizontalCenterOutlinedIcon from "@mui/icons-material/AlignHorizontalCenterOutlined";
import AlignHorizontalRightOutlinedIcon from "@mui/icons-material/AlignHorizontalRightOutlined";
import AlignVerticalTopOutlinedIcon from "@mui/icons-material/AlignVerticalTopOutlined";
import AlignVerticalCenterOutlinedIcon from "@mui/icons-material/AlignVerticalCenterOutlined";
import AlignVerticalBottomOutlinedIcon from "@mui/icons-material/AlignVerticalBottomOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import SwapVertOutlinedIcon from "@mui/icons-material/SwapVertOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import BrushOutlinedIcon from "@mui/icons-material/BrushOutlined";

/** Por defecto las fotos salen del moodboard. Product Tree sustituye el lector. */
export const MoodboardAssetBlobContext = createContext(
  fetchSkillAgentMoodboardAssetBlobUrl,
);

const HANDLES = ["nw", "n", "ne", "e", "se", "s", "sw", "w"];

/** Backdrop desenfocado para dialogs del moodboard. */
const moodboardDialogBackdropSx = {
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  backgroundColor: "rgba(15, 23, 42, 0.45)",
};

const TEXT_RESIZE_HANDLE_SX = {
  position: "absolute",
  width: 12,
  height: 12,
  bgcolor: "primary.main",
  border: "1px solid #fff",
  borderRadius: 0.25,
  zIndex: 22,
  boxShadow: "0 0 0 1px rgba(0,0,0,0.15)",
  touchAction: "none",
  WebkitUserSelect: "none",
  userSelect: "none",
  WebkitTouchCallout: "none",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: -10,
  },
};

/** Lados W/E: hit más estrecho en vertical para no tapar N/S ni la esquina de letra. */
const TEXT_SIDE_HANDLE_SX = {
  ...TEXT_RESIZE_HANDLE_SX,
  zIndex: 22,
  "&::before": {
    content: '""',
    position: "absolute",
    top: -8,
    bottom: -8,
    left: -12,
    right: -12,
  },
};

/** N/S + esquina: prioridad sobre los laterales (tamaño de letra). */
const TEXT_FONT_HANDLE_SX = {
  ...TEXT_RESIZE_HANDLE_SX,
  zIndex: 24,
  "&::before": {
    content: '""',
    position: "absolute",
    inset: -12,
  },
};

/** Cuadrado punteado + círculo sólido → pasar a circular (estilo Illustrator). */
function ShapeToCircleIcon(props) {
  return (
    <SvgIcon viewBox="0 0 24 24" fontSize="small" {...props}>
      <rect
        x="3.25"
        y="3.25"
        width="17.5"
        height="17.5"
        rx="1.25"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeDasharray="2.4 1.8"
      />
      <circle
        cx="12"
        cy="12"
        r="5.4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.85"
      />
    </SvgIcon>
  );
}

/** Cuadrado sólido + círculo punteado → pasar a cuadrada. */
function ShapeToSquareIcon(props) {
  return (
    <SvgIcon viewBox="0 0 24 24" fontSize="small" {...props}>
      <rect
        x="3.25"
        y="3.25"
        width="17.5"
        height="17.5"
        rx="1.25"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.85"
      />
      <circle
        cx="12"
        cy="12"
        r="5.4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeDasharray="2.4 1.8"
      />
    </SvgIcon>
  );
}

/** Franja CRM: sin cajas ni labels (las tools van en la barra VectorCraft). */
function ToolGroup({ grow = false, children }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.15,
        flex: grow ? 1 : "0 0 auto",
        minWidth: grow ? 0 : undefined,
        maxWidth: grow ? "100%" : undefined,
      }}
    >
      {children}
    </Box>
  );
}

function pointsToSvgPath(points) {
  if (!points?.length) return "";
  return points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x} ${p.y}`).join(" ");
}

function arrowHeadPoints(x1, y1, x2, y2, size = 0.018) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  const px = -uy;
  const py = ux;
  return [
    [x2, y2],
    [x2 - ux * size + px * size * 0.45, y2 - uy * size + py * size * 0.45],
    [x2 - ux * size - px * size * 0.45, y2 - uy * size - py * size * 0.45],
  ]
    .map(([x, y]) => `${x},${y}`)
    .join(" ");
}

function distToSegment(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len2 = dx * dx + dy * dy || 1;
  let t = ((px - x1) * dx + (py - y1) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const qx = x1 + t * dx;
  const qy = y1 + t * dy;
  return Math.hypot(px - qx, py - qy);
}

function markHitTest(mark, x, y, tol = 0.02, { fill = false } = {}) {
  if (!mark) return false;
  if (mark.type === "path" || mark.type === "textpath") {
    const pts = mark.points || [];
    // fill:true → interior de path cerrado (Live Paint / varita), aunque fill sea none
    if (
      fill &&
      mark.type === "path" &&
      mark.closed &&
      pts.length >= 3 &&
      pointInPolygon(x, y, pts)
    ) {
      return true;
    }
    for (let i = 1; i < pts.length; i += 1) {
      if (
        distToSegment(x, y, pts[i - 1].x, pts[i - 1].y, pts[i].x, pts[i].y) <=
        tol
      ) {
        return true;
      }
    }
    // Cierre del path cerrado
    if (mark.closed && pts.length >= 3) {
      const a = pts[pts.length - 1];
      const b = pts[0];
      if (distToSegment(x, y, a.x, a.y, b.x, b.y) <= tol) return true;
    }
    return pts.some((p) => Math.hypot(p.x - x, p.y - y) <= tol);
  }
  if (
    mark.type === "line" ||
    mark.type === "arrow" ||
    mark.type === "dimension"
  ) {
    return distToSegment(x, y, mark.x1, mark.y1, mark.x2, mark.y2) <= tol;
  }
  if (mark.type === "grid") {
    const inside =
      x >= mark.x - tol &&
      x <= mark.x + mark.w + tol &&
      y >= mark.y - tol &&
      y <= mark.y + mark.h + tol;
    if (!inside) return false;
    const cols = Math.max(2, Number(mark.cols) || 4);
    const rows = Math.max(2, Number(mark.rows) || 4);
    for (let c = 0; c <= cols; c += 1) {
      const gx = mark.x + (mark.w * c) / cols;
      if (Math.abs(x - gx) <= tol && y >= mark.y && y <= mark.y + mark.h)
        return true;
    }
    for (let r = 0; r <= rows; r += 1) {
      const gy = mark.y + (mark.h * r) / rows;
      if (Math.abs(y - gy) <= tol && x >= mark.x && x <= mark.x + mark.w)
        return true;
    }
    return false;
  }
  if (mark.type === "polarGrid" || mark.type === "flare") {
    const cx = mark.x + mark.w / 2;
    const cy = mark.y + mark.h / 2;
    const rx = Math.max(mark.w / 2, 0.001);
    const ry = Math.max(mark.h / 2, 0.001);
    const nx = (x - cx) / rx;
    const ny = (y - cy) / ry;
    const d = Math.hypot(nx, ny);
    if (fill && d <= 1 + tol / Math.min(rx, ry)) return true;
    return Math.abs(d - 1) <= tol / Math.min(rx, ry) || d <= 1;
  }
  if (
    mark.type === "rect" ||
    mark.type === "roundrect" ||
    mark.type === "polygon" ||
    mark.type === "star"
  ) {
    const inside =
      x >= mark.x - tol &&
      x <= mark.x + mark.w + tol &&
      y >= mark.y - tol &&
      y <= mark.y + mark.h + tol;
    if (fill && inside) return true;
    return (
      Math.abs(x - mark.x) <= tol ||
      Math.abs(x - (mark.x + mark.w)) <= tol ||
      Math.abs(y - mark.y) <= tol ||
      Math.abs(y - (mark.y + mark.h)) <= tol ||
      (x >= mark.x &&
        x <= mark.x + mark.w &&
        y >= mark.y &&
        y <= mark.y + mark.h &&
        Math.min(
          x - mark.x,
          mark.x + mark.w - x,
          y - mark.y,
          mark.y + mark.h - y,
        ) <=
          tol * 1.5)
    );
  }
  if (mark.type === "ellipse") {
    const cx = mark.x + mark.w / 2;
    const cy = mark.y + mark.h / 2;
    const rx = Math.max(mark.w / 2, 0.001);
    const ry = Math.max(mark.h / 2, 0.001);
    const nx = (x - cx) / rx;
    const ny = (y - cy) / ry;
    const d = Math.hypot(nx, ny);
    if (fill && d <= 1 + tol / Math.min(rx, ry)) return true;
    return (
      Math.abs(d - 1) <= tol / Math.min(rx, ry) ||
      (d <= 1 && d >= 1 - tol / Math.min(rx, ry))
    );
  }
  if (mark.type === "arc") {
    const pts = sampleEllipticalArcPoints(mark, 32);
    for (let i = 1; i < pts.length; i += 1) {
      if (
        distToSegment(x, y, pts[i - 1].x, pts[i - 1].y, pts[i].x, pts[i].y) <=
        tol
      ) {
        return true;
      }
    }
    return false;
  }
  if (mark.type === "text") {
    const tw = textMarkBoxWidth(mark);
    const lines = String(mark.text || "").split(/\r?\n/);
    const lineCount = Math.max(1, lines.length);
    // Altura aproximada: wrap ~ chars por línea según ancho
    const charsPerLine = Math.max(4, Math.floor(tw / 0.012));
    const wrappedApprox = Math.max(
      lineCount,
      Math.ceil(
        lines.reduce(
          (sum, line) =>
            sum + Math.max(1, Math.ceil(line.length / charsPerLine)),
          0,
        ),
      ),
    );
    const th = Math.max(0.03, (mark.fontSize || 14) * 0.0028 * wrappedApprox);
    const rot = normalizeRotation(mark.rotation);
    const cx = mark.x + tw / 2;
    const cy = mark.y + th / 2;
    const local = rot
      ? worldDeltaToLocal(x - cx, y - cy, rot)
      : { dx: x - cx, dy: y - cy };
    return (
      Math.abs(local.dx) <= tw / 2 + tol && Math.abs(local.dy) <= th / 2 + tol
    );
  }
  return false;
}

function findMarkAt(marks, x, y, tol = 0.025) {
  const list = Array.isArray(marks) ? marks : [];
  for (let i = list.length - 1; i >= 0; i -= 1) {
    if (markHitTest(list[i], x, y, tol, { fill: true })) return list[i];
  }
  return null;
}

/** Marca casi/totalmente fuera de la mesa (0–1) — basura del offset o dibujo lejano. */
function markIsMostlyOutsideArtboard(mark, margin = 0.05) {
  const b = markBounds(mark);
  if (!b) return false;
  const right = b.x + b.w;
  const bottom = b.y + b.h;
  return (
    right < -margin || bottom < -margin || b.x > 1 + margin || b.y > 1 + margin
  );
}

/**
 * Hit “apretado” del texto (ancho ~glifos). El w de cabecera es casi toda la
 * barra; si usamos ese AABB, el bote nunca pinta la caja.
 */
function livePaintTextGlyphHit(mark, x, y, tol = 0.012) {
  if (!mark || mark.type !== "text") return false;
  const raw = String(mark.text || "");
  if (!raw.trim()) return false;
  const fs = Number(mark.fontSize) || 14;
  const lines = raw.split(/\r?\n/);
  const charW = Math.max(0.0035, fs * 0.00115);
  const lineH = Math.max(0.018, fs * 0.0028);
  let maxChars = 1;
  lines.forEach((line) => {
    maxChars = Math.max(maxChars, String(line || "").length);
  });
  const boxW = textMarkBoxWidth(mark);
  const tw = Math.min(boxW, Math.max(0.035, maxChars * charW + tol));
  const th = Math.max(0.018, lineH * Math.max(1, lines.length) + tol);
  const align = normalizeMarkTextAlign(mark.textAlign);
  let left = Number(mark.x) || 0;
  if (align === "center") left += (boxW - tw) / 2;
  else if (align === "right") left += boxW - tw;
  const top = Number(mark.y) || 0;
  const rot = normalizeRotation(mark.rotation);
  const cx = left + tw / 2;
  const cy = top + th / 2;
  const local = rot
    ? worldDeltaToLocal(x - cx, y - cy, rot)
    : { dx: x - cx, dy: y - cy };
  return Math.abs(local.dx) <= tw / 2 && Math.abs(local.dy) <= th / 2;
}

/**
 * Live Paint: clic en letras → texto; clic en zona vacía de la barra → caja.
 * (El texto chrome tiene w≈barra y si gana siempre, la caja nunca se pinta.)
 */
function findLivePaintTarget(marks, x, y, tol = 0.03) {
  const list = Array.isArray(marks) ? marks : [];
  let tightText = null;
  let shapeHit = null;
  let looseText = null;
  for (let i = list.length - 1; i >= 0; i -= 1) {
    const m = list[i];
    if (!m) continue;
    const t = String(m.type || "");
    if (t === "text" || t === "textpath") {
      if (
        !tightText &&
        t === "text" &&
        livePaintTextGlyphHit(m, x, y, tol * 0.4)
      ) {
        tightText = m;
      } else if (!looseText && markHitTest(m, x, y, tol, { fill: true })) {
        looseText = m;
      }
      continue;
    }
    if (shapeHit) continue;
    const isChromeBar = m.role === "pageTitleBar" || m.role === "pageStudioBar";
    let hit = markHitTest(m, x, y, tol, { fill: true });
    if (!hit && isChromeBar && t === "path") {
      const b = markBounds(m);
      if (
        b &&
        x >= b.x - tol &&
        x <= b.x + b.w + tol &&
        y >= b.y - tol &&
        y <= b.y + b.h + tol
      ) {
        hit = true;
      }
    }
    if (hit) shapeHit = m;
  }
  return tightText || shapeHit || looseText;
}

function MarkSvgShape({
  mark,
  draft = false,
  selected = false,
  hit = false,
  onPointerDown,
}) {
  if (!mark) return null;
  const sw = markStrokeSvg(mark.stroke) * (selected ? 1.35 : 1);
  const markOp = Number(mark.opacity);
  const opacity = draft
    ? 0.75
    : Number.isFinite(markOp)
      ? Math.max(0.05, Math.min(1, markOp))
      : 1;
  // En select: hit-test solo la marca; el vacío del SVG deja pasar a las casillas
  const pe = hit ? "all" : "none";
  const selGlow = selected ? "drop-shadow(0 0 2px #2563eb)" : undefined;
  const hitProps = {
    style: {
      pointerEvents: pe,
      cursor: hit ? "move" : undefined,
      filter: selGlow,
    },
    onPointerDown: hit && onPointerDown ? onPointerDown : undefined,
  };
  const paint = markPaintColors(mark);
  const ink = paint.strokeColor;
  const fillPaint = paint.fill;
  const mesh = normalizeMarkMesh(mark.mesh);
  const closedShape =
    markSupportsFill(mark.type) ||
    (mark.type === "path" && Boolean(mark.closed));
  const hasFill = closedShape && fillPaint && fillPaint !== "none";
  const grad = !mesh
    ? normalizeMarkGradient(mark.gradient, paint.fill || ink)
    : null;
  const gradId = grad
    ? `mbGrad_${String(mark.id || "d").replace(/[^a-zA-Z0-9_-]/g, "")}`
    : null;
  const strokeProps = {
    fill: mesh
      ? "none"
      : gradId
        ? `url(#${gradId})`
        : hasFill
          ? fillPaint
          : "none",
    fillOpacity: mesh ? undefined : gradId || hasFill ? 1 : undefined,
    stroke: ink,
    strokeWidth: sw,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    opacity,
    ...hitProps,
  };
  const meshQuads =
    mesh &&
    mesh.points.flatMap((row, i) => {
      if (i >= mesh.rows - 1) return [];
      const next = mesh.points[i + 1];
      if (!next) return [];
      const cells = [];
      for (let j = 0; j < mesh.cols - 1; j += 1) {
        const a = row[j];
        const b = row[j + 1];
        const c = next[j + 1];
        const d = next[j];
        if (!a || !b || !c || !d) continue;
        const fill = lerpHexColor(
          lerpHexColor(a.color, b.color, 0.5),
          lerpHexColor(d.color, c.color, 0.5),
          0.5,
        );
        cells.push(
          <polygon
            key={`mesh_${mark.id}_${i}_${j}`}
            points={`${a.x},${a.y} ${b.x},${b.y} ${c.x},${c.y} ${d.x},${d.y}`}
            fill={fill}
            stroke="none"
            opacity={opacity}
            style={{ pointerEvents: "none" }}
          />,
        );
      }
      return cells;
    });
  // Mesh: quads de color + contorno de la forma (sin fill sólido)
  if (meshQuads && meshQuads.length) {
    const outline = markOutlinePoints(mark, 48);
    const od =
      outline && outline.length >= 2
        ? `${outline.map((p) => `${p.x},${p.y}`).join(" ")}`
        : null;
    return (
      <g {...hitProps}>
        {meshQuads}
        {od ? (
          <polygon
            points={od}
            fill="none"
            stroke={ink}
            strokeWidth={sw}
            opacity={opacity}
          />
        ) : null}
      </g>
    );
  }
  const axis = grad ? linearGradientAxis(grad.angle) : null;
  const gradDef = gradId ? (
    <defs>
      {grad.kind === "radial" ? (
        <radialGradient
          id={gradId}
          gradientUnits="objectBoundingBox"
          cx="0.5"
          cy="0.5"
          r="0.7"
        >
          <stop offset="0" stopColor={grad.from} />
          <stop offset="1" stopColor={grad.to} />
        </radialGradient>
      ) : (
        <linearGradient
          id={gradId}
          gradientUnits="objectBoundingBox"
          x1={axis.x1}
          y1={axis.y1}
          x2={axis.x2}
          y2={axis.y2}
        >
          <stop offset="0" stopColor={grad.from} />
          <stop offset="1" stopColor={grad.to} />
        </linearGradient>
      )}
    </defs>
  ) : null;

  if (mark.type === "path") {
    const pts = mark.points || [];
    const hover =
      mark._pen && mark._hover
        ? Array.isArray(mark._anchors) && mark._anchors.length
          ? penAnchorsToPolyline(
              [
                ...mark._anchors,
                {
                  x: mark._hover.x,
                  y: mark._hover.y,
                },
              ],
              8,
            )
          : [...pts, mark._hover]
        : pts;
    const d = pointsToSvgPath(hover);
    const isClosed =
      Boolean(mark.closed) ||
      (mark._lasso && pts.length >= 3) ||
      (pts.length >= 3 &&
        Math.hypot(
          (Number(pts[0]?.x) || 0) - (Number(pts[pts.length - 1]?.x) || 0),
          (Number(pts[0]?.y) || 0) - (Number(pts[pts.length - 1]?.y) || 0),
        ) < 0.001);
    const holes = Array.isArray(mark.holes) ? mark.holes : [];
    const holeD = holes
      .map((ring) => {
        const hd = pointsToSvgPath(ring);
        return hd ? `${hd} Z` : "";
      })
      .filter(Boolean)
      .join(" ");
    const closedPath =
      isClosed && !mark._knife ? `${d} Z${holeD ? ` ${holeD}` : ""}` : d;
    const pathFill = mark._lasso
      ? "rgba(37,99,235,0.12)"
      : mark._shapeBuilder
        ? "rgba(124,58,237,0.12)"
        : mark._knife
          ? "none"
          : gradId
            ? `url(#${gradId})`
            : fillPaint && fillPaint !== "none"
              ? fillPaint
              : "none";
    const widthPts = normalizeWidthPoints(mark.widthPoints);
    if (
      widthPts.length &&
      !mark._lasso &&
      !mark._knife &&
      !mark._shapeBuilder
    ) {
      const outline = variableWidthOutline(pts, mark.stroke, widthPts, {
        closed: isClosed,
      });
      const od =
        outline && outline.length >= 3
          ? outline.map((p) => `${p.x},${p.y}`).join(" ")
          : null;
      const centerFill =
        isClosed && pathFill && pathFill !== "none" ? pathFill : "none";
      return (
        <g {...hitProps}>
          {gradDef}
          {centerFill !== "none" ? (
            <path
              d={closedPath}
              fill={centerFill}
              stroke="none"
              fillRule={
                holes.length || mark.fillRule === "evenodd"
                  ? "evenodd"
                  : undefined
              }
              opacity={opacity}
            />
          ) : null}
          {od ? (
            <polygon points={od} fill={ink} stroke="none" opacity={opacity} />
          ) : (
            <path
              d={closedPath}
              fill="none"
              stroke={ink}
              strokeWidth={sw}
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity={opacity}
            />
          )}
        </g>
      );
    }
    const penHandles =
      mark._pen && Array.isArray(mark._anchors)
        ? mark._anchors.flatMap((a, i) => {
            const ax = Number(a.x) || 0;
            const ay = Number(a.y) || 0;
            const nodes = [
              <circle
                key={`pa_${i}`}
                cx={ax}
                cy={ay}
                r={0.006}
                fill="#fff"
                stroke="#2563eb"
                strokeWidth={0.002}
              />,
            ];
            if (
              Number.isFinite(Number(a.ox)) &&
              Number.isFinite(Number(a.oy))
            ) {
              const hx = ax + Number(a.ox);
              const hy = ay + Number(a.oy);
              nodes.push(
                <line
                  key={`po_${i}`}
                  x1={ax}
                  y1={ay}
                  x2={hx}
                  y2={hy}
                  stroke="#2563eb"
                  strokeWidth={0.002}
                />,
                <circle
                  key={`poc_${i}`}
                  cx={hx}
                  cy={hy}
                  r={0.004}
                  fill="#2563eb"
                />,
              );
            }
            if (
              Number.isFinite(Number(a.ix)) &&
              Number.isFinite(Number(a.iy))
            ) {
              const hx = ax + Number(a.ix);
              const hy = ay + Number(a.iy);
              nodes.push(
                <line
                  key={`pi_${i}`}
                  x1={ax}
                  y1={ay}
                  x2={hx}
                  y2={hy}
                  stroke="#2563eb"
                  strokeWidth={0.002}
                />,
                <circle
                  key={`pic_${i}`}
                  cx={hx}
                  cy={hy}
                  r={0.004}
                  fill="#2563eb"
                />,
              );
            }
            return nodes;
          })
        : null;
    return (
      <g>
        {gradDef}
        <path
          d={closedPath}
          {...strokeProps}
          strokeDasharray={
            mark._lasso || mark._knife || mark._shapeBuilder
              ? "0.02 0.015"
              : undefined
          }
          fill={pathFill}
          fillRule={
            holes.length || mark.fillRule === "evenodd" ? "evenodd" : undefined
          }
          fillOpacity={
            mark._lasso || gradId || (fillPaint && fillPaint !== "none")
              ? 1
              : undefined
          }
        />
        {penHandles}
      </g>
    );
  }
  if (mark.type === "textpath") {
    const pts = mark.points || [];
    const d = pointsToSvgPath(pts);
    const pathId = `mbTp_${String(mark.id || "d").replace(/[^a-zA-Z0-9_-]/g, "")}`;
    const fs = Math.max(0.018, ((Number(mark.fontSize) || 14) / 14) * 0.028);
    return (
      <g {...hitProps}>
        <defs>
          <path id={pathId} d={d} fill="none" />
        </defs>
        <path
          d={d}
          fill="none"
          stroke={selected ? "#2563eb" : "transparent"}
          strokeWidth={selected ? 0.006 : 0.01}
          style={{ pointerEvents: hit ? "stroke" : "none" }}
        />
        <text
          fill={textMarkFillColor(mark)}
          stroke={
            Number(mark.stroke) > 0
              ? mark.strokeColor || mark.color || ink
              : "none"
          }
          strokeWidth={
            Number(mark.stroke) > 0
              ? markStrokeSvg(mark.stroke) * 0.45
              : undefined
          }
          paintOrder={Number(mark.stroke) > 0 ? "stroke fill" : undefined}
          fontSize={fs}
          fontFamily={markFontCss(mark.fontFamily || "sans")}
          fontWeight={normalizeMarkFontWeight(mark.fontWeight)}
          opacity={opacity}
          style={{
            pointerEvents: "none",
            ...(mark.orientation === "vertical"
              ? {
                  writingMode: "vertical-rl",
                  glyphOrientationVertical: "auto",
                }
              : null),
          }}
        >
          <textPath href={`#${pathId}`} startOffset="0%">
            {String(mark.text || "").trim() || "Text"}
          </textPath>
        </text>
      </g>
    );
  }
  if (mark.type === "line") {
    return (
      <line
        x1={mark.x1}
        y1={mark.y1}
        x2={mark.x2}
        y2={mark.y2}
        {...strokeProps}
      />
    );
  }
  if (mark.type === "arrow") {
    return (
      <g {...hitProps}>
        <line
          x1={mark.x1}
          y1={mark.y1}
          x2={mark.x2}
          y2={mark.y2}
          fill="none"
          stroke={ink}
          strokeWidth={sw}
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={opacity}
          style={{
            pointerEvents: hit ? "stroke" : "none",
            cursor: hit ? "move" : undefined,
            filter: selGlow,
          }}
        />
        <polygon
          points={arrowHeadPoints(mark.x1, mark.y1, mark.x2, mark.y2)}
          fill={ink}
          stroke="none"
          opacity={opacity}
          style={{
            pointerEvents: hit ? "all" : "none",
            cursor: hit ? "move" : undefined,
            filter: selGlow,
          }}
        />
      </g>
    );
  }
  if (mark.type === "rect" || mark.type === "roundrect") {
    const rx =
      mark.type === "roundrect"
        ? Math.min(
            Number(mark.rx) || 0.04,
            Math.min(mark.w || 0, mark.h || 0) / 2,
          )
        : 0;
    return (
      <g>
        {gradDef}
        <rect
          x={mark.x}
          y={mark.y}
          width={Math.max(mark.w, 0.001)}
          height={Math.max(mark.h, 0.001)}
          rx={rx || undefined}
          ry={rx || undefined}
          {...strokeProps}
        />
      </g>
    );
  }
  if (mark.type === "polygon" || mark.type === "star") {
    const verts =
      mark.type === "star"
        ? starPolygonPoints({
            x: mark.x,
            y: mark.y,
            w: mark.w,
            h: mark.h,
            tips: mark.tips,
            innerRatio: mark.innerRatio,
          })
        : regularPolygonPoints({
            x: mark.x,
            y: mark.y,
            w: mark.w,
            h: mark.h,
            sides: mark.sides,
          });
    return (
      <g>
        {gradDef}
        <polygon
          points={verts.map((p) => `${p.x},${p.y}`).join(" ")}
          {...strokeProps}
        />
      </g>
    );
  }
  if (mark.type === "ellipse") {
    return (
      <g>
        {gradDef}
        <ellipse
          cx={mark.x + mark.w / 2}
          cy={mark.y + mark.h / 2}
          rx={Math.max(mark.w / 2, 0.001)}
          ry={Math.max(mark.h / 2, 0.001)}
          {...strokeProps}
        />
      </g>
    );
  }
  if (mark.type === "arc") {
    return (
      <path
        d={ellipticalArcPathD(mark)}
        fill="none"
        stroke={ink}
        strokeWidth={sw}
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={opacity}
        {...hitProps}
      />
    );
  }
  if (mark.type === "measure" || mark._measure) {
    const d = dimensionMetrics({ ...mark, unit: "pct" });
    return (
      <g style={{ pointerEvents: "none" }}>
        <line
          x1={d.x1}
          y1={d.y1}
          x2={d.x2}
          y2={d.y2}
          stroke="#2563eb"
          strokeWidth={0.004}
          strokeDasharray="0.02 0.012"
          opacity={0.9}
        />
        <line
          x1={d.t1a.x}
          y1={d.t1a.y}
          x2={d.t1b.x}
          y2={d.t1b.y}
          stroke="#2563eb"
          strokeWidth={0.0035}
          opacity={0.9}
        />
        <line
          x1={d.t2a.x}
          y1={d.t2a.y}
          x2={d.t2b.x}
          y2={d.t2b.y}
          stroke="#2563eb"
          strokeWidth={0.0035}
          opacity={0.9}
        />
        <circle cx={d.x1} cy={d.y1} r={0.007} fill="#2563eb" opacity={0.9} />
        <circle cx={d.x2} cy={d.y2} r={0.007} fill="#2563eb" opacity={0.9} />
        <text
          x={d.labelX}
          y={d.labelY}
          fill="#1e40af"
          fontSize={0.026}
          fontWeight={700}
          textAnchor="middle"
          dominantBaseline="middle"
          style={{ userSelect: "none" }}
        >
          {`${d.distPct.toFixed(1)}% · ${d.distMm.toFixed(1)} mm`}
        </text>
      </g>
    );
  }
  if (mark.type === "dimension") {
    const d = dimensionMetrics(mark);
    return (
      <g {...hitProps}>
        <line
          x1={d.x1}
          y1={d.y1}
          x2={d.x2}
          y2={d.y2}
          fill="none"
          stroke={ink}
          strokeWidth={sw}
          strokeLinecap="round"
          opacity={opacity}
          style={{ filter: selGlow }}
        />
        <line
          x1={d.t1a.x}
          y1={d.t1a.y}
          x2={d.t1b.x}
          y2={d.t1b.y}
          stroke={ink}
          strokeWidth={sw * 0.9}
          opacity={opacity}
        />
        <line
          x1={d.t2a.x}
          y1={d.t2a.y}
          x2={d.t2b.x}
          y2={d.t2b.y}
          stroke={ink}
          strokeWidth={sw * 0.9}
          opacity={opacity}
        />
        <text
          x={d.labelX}
          y={d.labelY}
          fill={ink}
          fontSize={0.024}
          fontWeight={700}
          textAnchor="middle"
          dominantBaseline="middle"
          style={{ pointerEvents: "none", userSelect: "none" }}
        >
          {d.label}
        </text>
      </g>
    );
  }
  if (mark.type === "grid") {
    const cols = Math.max(2, Number(mark.cols) || 4);
    const rows = Math.max(2, Number(mark.rows) || 4);
    const lines = [];
    for (let c = 0; c <= cols; c += 1) {
      const gx = mark.x + (mark.w * c) / cols;
      lines.push(
        <line
          key={`gc${c}`}
          x1={gx}
          y1={mark.y}
          x2={gx}
          y2={mark.y + mark.h}
          stroke={ink}
          strokeWidth={sw}
          opacity={opacity}
        />,
      );
    }
    for (let r = 0; r <= rows; r += 1) {
      const gy = mark.y + (mark.h * r) / rows;
      lines.push(
        <line
          key={`gr${r}`}
          x1={mark.x}
          y1={gy}
          x2={mark.x + mark.w}
          y2={gy}
          stroke={ink}
          strokeWidth={sw}
          opacity={opacity}
        />,
      );
    }
    return <g {...hitProps}>{lines}</g>;
  }
  if (mark.type === "polarGrid") {
    const rings = Math.max(2, Number(mark.rings) || 4);
    const rays = Math.max(3, Number(mark.rays) || 8);
    const cx = mark.x + mark.w / 2;
    const cy = mark.y + mark.h / 2;
    const rx = Math.max(mark.w / 2, 0.001);
    const ry = Math.max(mark.h / 2, 0.001);
    const els = [];
    for (let r = 1; r <= rings; r += 1) {
      const t = r / rings;
      els.push(
        <ellipse
          key={`pr${r}`}
          cx={cx}
          cy={cy}
          rx={rx * t}
          ry={ry * t}
          fill="none"
          stroke={ink}
          strokeWidth={sw}
          opacity={opacity}
        />,
      );
    }
    for (let i = 0; i < rays; i += 1) {
      const a = (i / rays) * Math.PI * 2;
      els.push(
        <line
          key={`py${i}`}
          x1={cx}
          y1={cy}
          x2={cx + rx * Math.cos(a)}
          y2={cy + ry * Math.sin(a)}
          stroke={ink}
          strokeWidth={sw}
          opacity={opacity}
        />,
      );
    }
    return <g {...hitProps}>{els}</g>;
  }
  if (mark.type === "flare") {
    const g = flareGeometry(mark);
    const els = [
      <ellipse
        key="fc"
        cx={g.cx}
        cy={g.cy}
        rx={g.coreRx}
        ry={g.coreRy}
        fill={ink}
        stroke="none"
        opacity={opacity}
      />,
    ];
    g.rings.forEach((ring, i) => {
      els.push(
        <ellipse
          key={`fr${i}`}
          cx={g.cx}
          cy={g.cy}
          rx={ring.rx}
          ry={ring.ry}
          fill="none"
          stroke={ink}
          strokeWidth={sw * (i === g.rings.length - 1 ? 1.15 : 0.65)}
          opacity={opacity * (0.4 + (0.55 * (i + 1)) / g.rings.length)}
        />,
      );
    });
    g.rays.forEach((ray, i) => {
      els.push(
        <polygon
          key={`fy${i}`}
          points={`${ray.tip.x},${ray.tip.y} ${ray.b1.x},${ray.b1.y} ${ray.b2.x},${ray.b2.y}`}
          fill={ink}
          stroke="none"
          opacity={opacity * (ray.long ? 0.62 : 0.38)}
        />,
      );
    });
    return <g {...hitProps}>{els}</g>;
  }
  return null;
}

function textMarkApproxBounds(mark) {
  const tw = textMarkBoxWidth(mark);
  const lines = String(mark?.text || "").split(/\r?\n/);
  const lineCount = Math.max(1, lines.length);
  const charsPerLine = Math.max(4, Math.floor(tw / 0.012));
  const wrappedApprox = Math.max(
    lineCount,
    Math.ceil(
      lines.reduce(
        (sum, line) => sum + Math.max(1, Math.ceil(line.length / charsPerLine)),
        0,
      ),
    ),
  );
  const th = Math.max(
    0.03,
    (Number(mark?.fontSize) || 14) * 0.0028 * wrappedApprox,
  );
  return { x: Number(mark?.x) || 0, y: Number(mark?.y) || 0, w: tw, h: th };
}

/** Misma escala visual que el texto del tablero (1.85 cqw a 14 pt). */
function textMarkFontCqw(fontSize) {
  return ((Number(fontSize) || 14) / 14) * 1.85;
}

/** Annotator de degradado en lienzo: arrastrar extremo → ángulo. */
function GradientAnnotatorOverlay({ axis, onAngle }) {
  const dragRef = useRef(null);
  if (!axis) return null;
  const onDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const parent = e.currentTarget.parentElement;
    if (!parent) return;
    dragRef.current = { el: parent, pointerId: e.pointerId };
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch {
      /* ignore */
    }
  };
  const onMove = (e) => {
    if (!dragRef.current || dragRef.current.pointerId !== e.pointerId) return;
    const el = dragRef.current.el;
    const r = el.getBoundingClientRect();
    if (r.width < 1 || r.height < 1) return;
    const x = (e.clientX - r.left) / r.width;
    const y = (e.clientY - r.top) / r.height;
    const ang = (Math.atan2(y - axis.cy, x - axis.cx) * 180) / Math.PI;
    onAngle?.(Math.round(ang * 10) / 10);
  };
  const onUp = (e) => {
    if (dragRef.current?.pointerId === e.pointerId) dragRef.current = null;
  };
  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        zIndex: 10100,
      }}
    >
      <svg
        viewBox="0 0 1 1"
        preserveAspectRatio="none"
        width="100%"
        height="100%"
        style={{ position: "absolute", inset: 0, overflow: "visible" }}
      >
        <line
          x1={axis.x1}
          y1={axis.y1}
          x2={axis.x2}
          y2={axis.y2}
          stroke="#2563eb"
          strokeWidth={0.004}
          strokeDasharray="0.012 0.008"
        />
        <circle
          cx={axis.x1}
          cy={axis.y1}
          r={0.012}
          fill={axis.from}
          stroke="#fff"
          strokeWidth={0.003}
          style={{ pointerEvents: "auto", cursor: "grab" }}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
        />
        <circle
          cx={axis.x2}
          cy={axis.y2}
          r={0.012}
          fill={axis.to}
          stroke="#fff"
          strokeWidth={0.003}
          style={{ pointerEvents: "auto", cursor: "grab" }}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
        />
      </svg>
    </Box>
  );
}

/**
 * Herramientas que hacen hit-test en el SVG base (handlePointerDown).
 * Las capas por marca no deben capturar el pointer o el 2º clic en la misma
 * mesa no llega (solo cambiar de página “desatrancaba”).
 */
/**
 * Fracción de mesa que el overlay se extiende fuera.
 * Debe cubrir basura/lejos: si el paint sale del box, el clic pega en el pasteboard.
 */
const MARKS_BOARD_OUTSET = 3;

const MARK_PASS_THROUGH_TOOLS = new Set([
  "eraser",
  "pathEraser",
  "scissors",
  "knife",
  "lineCut",
  "rectCut",
  "mirrorCut",
  "width",
  "warp",
  "puppetWarp",
  "objectsOnPath",
  "symbolShifter",
  "symbolSprayer",
  "symbolScruncher",
  "symbolSizer",
  "symbolSpinner",
  "symbolStainer",
  "symbolScreener",
  "reshape",
  "twirl",
  "pucker",
  "bloat",
  "scallop",
  "crystallize",
  "wrinkle",
  "blob",
  "addAnchor",
  "deleteAnchor",
  "anchorPoint",
  "curvature",
  "eyedropper",
  "livePaintBucket",
  "livePaintSelection",
  "magicWand",
  "lasso",
  "pen",
  "brush",
  "vectorPen",
  "shaper",
  "shapeBuilder",
  "rotateDrag",
  "scaleDrag",
  "shearDrag",
]);

function textMarkFontPx(fontSize, canvasWidthPx) {
  const cw = Math.max(1, Number(canvasWidthPx) || 0);
  return Math.max(10, (textMarkFontCqw(fontSize) / 100) * cw);
}

function MarksOverlay({
  marks = [],
  tool,
  color,
  fillColor,
  strokeColor,
  stroke,
  draftMark,
  textDraft,
  markFontSize = 14,
  markFontFamily = "sans",
  markFontWeight = 700,
  markTextAlign = "left",
  gridOpts = { cols: 4, rows: 4, rings: 4, rays: 8 },
  spiralOpts = { turns: 3, kind: "archimedean" },
  shapeOpts = {
    sides: 6,
    tips: 5,
    sweep: 90,
    innerRatio: 0.45,
    rx: 0.04,
  },
  selectedMarkId,
  selectedMarkIds,
  multiSelectMode = false,
  onSelectMark,
  onMoveMark,
  onMoveMarkGroup,
  onPointerNorm,
  onCommitMark,
  onEraseAt,
  onEraseAlong,
  onEyedropColor,
  onLivePaintFill,
  onTouchCharOffset,
  onPuppetPinMove,
  onPuppetPinAdd,
  onObjectsOnPathAttach,
  onSymbolSprayAt,
  onSymbolShiftAt,
  onSymbolScrunchAt,
  onSymbolSizeAt,
  onSymbolSpinAt,
  onSymbolStainAt,
  onSymbolScreenAt,
  onSplitPath,
  onInsertAnchor,
  onDeleteAnchor,
  onMoveAnchor,
  onLassoSelect,
  onMagicWand,
  onStartAreaText,
  onMeasureDone,
  onKnifeCut,
  onRectCut,
  onMirrorCut,
  onDistortAt,
  onWidthAt,
  onLiveTransform,
  onShaperFinish,
  onShapeBuilderStroke,
  getDraftMark,
  onStartText,
  onEditText,
  onChangeTextDraft,
  onCommitText,
  onCancelText,
  onHistoryGestureStart,
  onHistoryGestureEnd,
  onTextFontSize,
  onPatchTextDraft,
  perspectiveGrid = null,
  boardPanActive = false,
  /** Soltar marca fuera de la mesa → pasteboard (modo tablero). */
  onDragOffBoard,
  /** Quitar path al continuar con la pluma desde un extremo */
  onAbsorbPath,
}) {
  const svgRef = useRef(null);
  const overlayRef = useRef(null);
  const drawingRef = useRef(false);
  const lastEraseRef = useRef(null);
  const dragRef = useRef(null);
  const touchCharDragRef = useRef(null);
  const puppetPinDragRef = useRef(null);
  const anchorDragRef = useRef(null);
  /** Evita commit del textarea al tocar handles de ancho en iPad (blur prematuro). */
  const suppressTextBlurRef = useRef(false);
  const textDraftAreaRef = useRef(null);

  /**
   * Coords normalizadas respecto a la mesa (0–1 = artboard).
   * El SVG se extiende MARKS_BOARD_OUTSET fuera; allowOutside deja x/y fuera de [0,1].
   */
  const toNorm = useCallback(
    (clientX, clientY, { allowOutside = false } = {}) => {
      const el = svgRef.current;
      if (!el) return null;
      const r = el.getBoundingClientRect();
      if (r.width < 1 || r.height < 1) return null;
      const span = 1 + 2 * MARKS_BOARD_OUTSET;
      const x = -MARKS_BOARD_OUTSET + ((clientX - r.left) / r.width) * span;
      const y = -MARKS_BOARD_OUTSET + ((clientY - r.top) / r.height) * span;
      if (allowOutside) {
        return {
          x: Number.isFinite(x) ? x : 0,
          y: Number.isFinite(y) ? y : 0,
        };
      }
      return {
        x: Math.min(1, Math.max(0, Number.isFinite(x) ? x : 0)),
        y: Math.min(1, Math.max(0, Number.isFinite(y) ? y : 0)),
      };
    },
    [],
  );

  const active = Boolean(tool);
  // V y A: vacío deja pasar a fotos; solo las marcas (stroke/fill) capturan el clic
  const idle = !tool || tool === "select" || tool === "directSelect";
  const passThroughEmpty = idle;
  const inkColor = strokeColor || color;
  const fillForClosed =
    fillColor === "none" ? "none" : fillColor || color || inkColor;
  const paintOpen = { color: inkColor, strokeColor: inkColor, fill: "none" };
  const paintClosed = {
    color: inkColor,
    strokeColor: inkColor,
    fill: fillForClosed,
  };

  const captureTarget = () => overlayRef.current || svgRef.current;

  const attachDragListeners = (e) => {
    // Capturar en la caja de texto (no el handle de 12px) ni en overlay con
    // pointer-events:none: en iPad Safari eso dispara pointercancel / scroll.
    const from = e.currentTarget;
    const textBox =
      typeof from?.closest === "function"
        ? from.closest("[data-mb-text]")
        : null;
    const target = textBox || captureTarget() || from;
    if (dragRef.current) dragRef.current.captureEl = target;
    try {
      target?.setPointerCapture?.(e.pointerId);
    } catch {
      /* ignore */
    }
    const onMove = (ev) => {
      ev.preventDefault?.();
      handlePointerMove(ev);
    };
    const onUp = (ev) => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      handlePointerUp(ev);
    };
    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  const markIsSelected = (id) => {
    if (selectedMarkIds?.size) return selectedMarkIds.has(String(id));
    return selectedMarkId != null && String(id) === String(selectedMarkId);
  };

  const nextMarkSelection = (id, additive) => {
    const sid = String(id);
    // Misma regla que handleSelectMark: V/grupo = compañeros; A = solo esa.
    const seedIds =
      tool === "directSelect"
        ? [sid]
        : [...expandSelectionByGroup(marks || [], sid)];
    const prev = selectedMarkIds?.size
      ? selectedMarkIds
      : selectedMarkId != null
        ? new Set([String(selectedMarkId)])
        : new Set();
    if (additive) {
      const next = new Set(prev);
      const allIn = seedIds.every((x) => next.has(String(x)));
      if (allIn) seedIds.forEach((x) => next.delete(String(x)));
      else seedIds.forEach((x) => next.add(String(x)));
      return next;
    }
    if (prev.has(sid) && prev.size > 1 && tool === "directSelect") {
      return new Set(prev);
    }
    return new Set(seedIds.map(String));
  };

  const beginDrag = (mark, p, e, selectionForDrag = null) => {
    onHistoryGestureStart?.();
    const sel = selectionForDrag || selectedMarkIds;
    const sid = String(mark.id);
    const group =
      sel?.has?.(sid) && sel.size > 1 && typeof onMoveMarkGroup === "function";
    const groupOrigins = group
      ? (marks || [])
          .filter((m) => sel.has(String(m.id)))
          .map((m) => ({ ...m }))
      : null;
    // Delta en px / unidad de mesa (SVG expandido → dividir por span)
    const r = svgRef.current?.getBoundingClientRect?.();
    const span = 1 + 2 * MARKS_BOARD_OUTSET;
    const cw = (r && r.width > 1 ? r.width : 1) / span || 1;
    const ch = (r && r.height > 1 ? r.height : 1) / span || 1;
    dragRef.current = {
      mode: "move",
      id: mark.id,
      startX: p?.x,
      startY: p?.y,
      startClientX: e.clientX,
      startClientY: e.clientY,
      cw,
      ch,
      orig: mark,
      groupOrigins,
      moved: false,
    };
    attachDragListeners(e);
  };

  const beginResizeMarkBox = (mark, edge, e) => {
    const p = toNorm(e.clientX, e.clientY);
    if (!p || !markHasBox(mark)) return;
    onHistoryGestureStart?.();
    onSelectMark?.(mark.id);
    dragRef.current = {
      mode: "resize-box",
      id: mark.id,
      edge,
      orig: { ...mark },
      moved: false,
    };
    attachDragListeners(e);
  };

  const beginResizeText = (mark, e, { axis = "both", edge = "se" } = {}) => {
    onHistoryGestureStart?.();
    onSelectMark?.(mark.id);
    dragRef.current = {
      mode: "resize-font",
      id: mark.id,
      startClientX: e.clientX,
      startClientY: e.clientY,
      orig: mark,
      origFs: Number(mark.fontSize) || 14,
      axis,
      edge,
      moved: false,
    };
    attachDragListeners(e);
  };

  const beginResizeTextWidth = (mark, edge, e) => {
    const p = toNorm(e.clientX, e.clientY);
    if (!p) return;
    onHistoryGestureStart?.();
    onSelectMark?.(mark.id);
    dragRef.current = {
      mode: "resize-width",
      edge: edge === "w" ? "w" : "e",
      id: mark.id,
      startX: p.x,
      orig: mark,
      origX: Number(mark.x) || 0,
      origW: textMarkBoxWidth(mark),
      moved: false,
    };
    attachDragListeners(e);
  };

  const beginRotateText = (mark, e) => {
    onHistoryGestureStart?.();
    onSelectMark?.(mark.id);
    const box = e.currentTarget?.closest?.("[data-mb-text]");
    const rect = box?.getBoundingClientRect?.();
    const cx = rect ? rect.left + rect.width / 2 : e.clientX;
    const cy = rect ? rect.top + rect.height / 2 : e.clientY;
    dragRef.current = {
      mode: "rotate",
      id: mark.id,
      centerX: cx,
      centerY: cy,
      startAngle: pointerAngleDeg(cx, cy, e.clientX, e.clientY),
      originRotation: normalizeRotation(mark.rotation),
      orig: mark,
      moved: false,
    };
    attachDragListeners(e);
  };

  const finishDrag = (e) => {
    const drag = dragRef.current;
    if (!drag) return false;
    if (
      !drag.moved &&
      drag.mode !== "resize-font" &&
      drag.mode !== "resize-width" &&
      drag.mode !== "rotate" &&
      drag.orig?.type === "text" &&
      (tool === "text" || drag.editOnTap)
    ) {
      onEditText?.(drag.orig);
    }
    if (drag.mode === "resize-font" && drag.moved) {
      const fs = Number(
        (marks || []).find((m) => String(m.id) === String(drag.id))?.fontSize,
      );
      if (Number.isFinite(fs)) onTextFontSize?.(fs);
    }
    // No eject automático al soltar: los textos/figuras se quedaban “desapareciendo”.
    // Sacar al fondo = Ctrl+Shift+B / herramienta explícita (como antes con fotos).
    dragRef.current = null;
    onHistoryGestureEnd?.();
    try {
      (drag.captureEl || captureTarget())?.releasePointerCapture?.(e.pointerId);
    } catch {
      /* ignore */
    }
    return true;
  };

  const handlePointerDown = (e) => {
    if (!tool) return;
    e.preventDefault();
    e.stopPropagation();
    // Dibujar / editar también fuera de la mesa (coords pueden ser <0 o >1).
    const p = toNorm(e.clientX, e.clientY, { allowOutside: true });
    if (!p) return;
    if (tool === "eyedropper") {
      const hit = findMarkAt(marks, p.x, p.y);
      if (hit?.color) onEyedropColor?.(hit.color);
      return;
    }
    // Symbol Sprayer / Shifter / …
    if (tool === "symbolShifter") {
      onHistoryGestureStart?.();
      drawingRef.current = true;
      lastEraseRef.current = p;
      onSymbolShiftAt?.(p.x, p.y, {
        alt: Boolean(e.altKey),
        prev: null,
      });
      try {
        svgRef.current?.setPointerCapture?.(e.pointerId);
      } catch {
        /* ignore */
      }
      return;
    }
    if (tool === "symbolSprayer") {
      onHistoryGestureStart?.();
      drawingRef.current = true;
      lastEraseRef.current = p;
      onSymbolSprayAt?.(p.x, p.y, { alt: Boolean(e.altKey) });
      try {
        svgRef.current?.setPointerCapture?.(e.pointerId);
      } catch {
        /* ignore */
      }
      return;
    }
    if (tool === "symbolScruncher") {
      onHistoryGestureStart?.();
      drawingRef.current = true;
      lastEraseRef.current = p;
      onSymbolScrunchAt?.(p.x, p.y, { alt: Boolean(e.altKey) });
      try {
        svgRef.current?.setPointerCapture?.(e.pointerId);
      } catch {
        /* ignore */
      }
      return;
    }
    if (tool === "symbolSizer") {
      onHistoryGestureStart?.();
      drawingRef.current = true;
      lastEraseRef.current = p;
      onSymbolSizeAt?.(p.x, p.y, { alt: Boolean(e.altKey) });
      try {
        svgRef.current?.setPointerCapture?.(e.pointerId);
      } catch {
        /* ignore */
      }
      return;
    }
    if (tool === "symbolSpinner") {
      onHistoryGestureStart?.();
      drawingRef.current = true;
      lastEraseRef.current = p;
      onSymbolSpinAt?.(p.x, p.y, { alt: Boolean(e.altKey) });
      try {
        svgRef.current?.setPointerCapture?.(e.pointerId);
      } catch {
        /* ignore */
      }
      return;
    }
    if (tool === "symbolStainer") {
      onHistoryGestureStart?.();
      drawingRef.current = true;
      lastEraseRef.current = p;
      onSymbolStainAt?.(p.x, p.y, { alt: Boolean(e.altKey) });
      try {
        svgRef.current?.setPointerCapture?.(e.pointerId);
      } catch {
        /* ignore */
      }
      return;
    }
    if (tool === "symbolScreener") {
      onHistoryGestureStart?.();
      drawingRef.current = true;
      lastEraseRef.current = p;
      onSymbolScreenAt?.(p.x, p.y, { alt: Boolean(e.altKey) });
      try {
        svgRef.current?.setPointerCapture?.(e.pointerId);
      } catch {
        /* ignore */
      }
      return;
    }
    // Objects on Path: clic en path → adjunta la selección (excl. el path)
    if (tool === "objectsOnPath") {
      const hit = findMarkAt(marks, p.x, p.y, 0.04);
      if (!hit) {
        onSelectMark?.(null);
        return;
      }
      const asPath = markAsPath(hit);
      if (!asPath?.points || asPath.points.length < 2) {
        onSelectMark?.(hit.id, { additive: Boolean(e.shiftKey) });
        return;
      }
      onObjectsOnPathAttach?.(hit.id);
      return;
    }
    // Puppet Warp: clic cerca de pin → arrastra; clic en forma → añade pin
    if (tool === "puppetWarp") {
      const hit = findMarkAt(marks, p.x, p.y, 0.04);
      if (!hit || hit.type === "text" || hit.type === "textpath") return;
      onSelectMark?.(hit.id, { additive: false });
      const pins = hit.puppet?.pins || [];
      let nearest = -1;
      let best = 0.028;
      pins.forEach((pin, i) => {
        const d = Math.hypot(
          (Number(pin.at?.x) || 0) - p.x,
          (Number(pin.at?.y) || 0) - p.y,
        );
        if (d < best) {
          best = d;
          nearest = i;
        }
      });
      if (nearest >= 0) {
        onHistoryGestureStart?.();
        puppetPinDragRef.current = {
          markId: hit.id,
          pinIndex: nearest,
          pointerId: e.pointerId,
        };
        try {
          svgRef.current?.setPointerCapture?.(e.pointerId);
        } catch {
          /* ignore */
        }
        return;
      }
      onPuppetPinAdd?.(hit.id, p.x, p.y);
      return;
    }
    // Live Paint Bucket (VC K): rellena la forma cerrada bajo el cursor
    if (tool === "livePaintBucket") {
      const hit = findLivePaintTarget(marks, p.x, p.y, 0.03);
      if (hit) onLivePaintFill?.(hit.id);
      return;
    }
    // Live Paint Selection (VC Shift+L): selecciona cara/forma bajo el cursor
    if (tool === "livePaintSelection") {
      const hit = findLivePaintTarget(marks, p.x, p.y, 0.03);
      if (hit) onSelectMark?.(hit.id, { additive: Boolean(e.shiftKey) });
      else onSelectMark?.(null);
      return;
    }
    if (tool === "magicWand") {
      const hit = findMarkAt(marks, p.x, p.y);
      if (hit?.color) onMagicWand?.(hit.color, { additive: e.shiftKey });
      else onSelectMark?.(null);
      return;
    }
    if (tool === "scissors") {
      const hit = findMarkAt(marks, p.x, p.y, 0.04);
      if (hit) onSplitPath?.(hit.id, p.x, p.y);
      else onSplitPath?.(null, p.x, p.y); // feedback "no hit"
      return;
    }
    if (tool === "addAnchor") {
      const hit = findMarkAt(marks, p.x, p.y);
      if (hit?.type === "path") onInsertAnchor?.(hit.id, p.x, p.y);
      return;
    }
    if (tool === "deleteAnchor") {
      const hit = findMarkAt(marks, p.x, p.y);
      if (hit?.type === "path") onDeleteAnchor?.(hit.id, p.x, p.y);
      return;
    }
    if (tool === "directSelect") {
      // Tol holgada: líneas finas / diagonales se dejan agarrar.
      const hit = findMarkAt(marks, p.x, p.y, 0.045);
      if (hit?.type === "path") {
        const idx = nearestAnchorIndex(hit.points || [], p.x, p.y, 0.045);
        onSelectMark?.(hit.id, { additive: Boolean(e.shiftKey) });
        onHistoryGestureStart?.();
        if (idx >= 0) {
          anchorDragRef.current = {
            markId: hit.id,
            index: idx,
            pointerId: e.pointerId,
          };
          try {
            svgRef.current?.setPointerCapture?.(e.pointerId);
          } catch {
            /* ignore */
          }
        } else {
          // Clic en el trazo (no en vértice) → mover el path entero
          beginDrag(hit, p, e);
        }
      } else if (
        hit &&
        (hit.type === "line" ||
          hit.type === "arrow" ||
          hit.type === "dimension")
      ) {
        onSelectMark?.(hit.id, { additive: Boolean(e.shiftKey) });
        const endPts = [
          { x: Number(hit.x1) || 0, y: Number(hit.y1) || 0 },
          { x: Number(hit.x2) || 0, y: Number(hit.y2) || 0 },
        ];
        const idx = nearestAnchorIndex(endPts, p.x, p.y, 0.045);
        onHistoryGestureStart?.();
        if (idx >= 0) {
          anchorDragRef.current = {
            markId: hit.id,
            index: idx,
            pointerId: e.pointerId,
            lineEnds: true,
          };
          try {
            svgRef.current?.setPointerCapture?.(e.pointerId);
          } catch {
            /* ignore */
          }
        } else {
          beginDrag(hit, p, e);
        }
      } else if (hit && markHasBox(hit) && hit.type !== "path") {
        onSelectMark?.(hit.id, { additive: Boolean(e.shiftKey) });
        const boxPts = boxMarkAnchorPoints(hit);
        const idx = nearestAnchorIndex(boxPts, p.x, p.y, 0.05);
        onHistoryGestureStart?.();
        if (idx >= 0) {
          beginAnchorDrag(hit, idx, e, { expandBox: true });
        } else {
          beginDrag(hit, p, e);
        }
      } else if (hit) {
        onSelectMark?.(hit.id, { additive: Boolean(e.shiftKey) });
        beginDrag(hit, p, e);
      } else {
        onSelectMark?.(null);
      }
      return;
    }
    if (tool === "vectorPen") {
      e.preventDefault();
      e.stopPropagation();
      const CLOSE_TOL = 0.02;
      const END_TOL = 0.028;
      const existing =
        draftMark?.type === "path" && (draftMark._pen || draftMark._continueId)
          ? draftMark
          : null;
      let anchors = existing?._anchors
        ? existing._anchors.map((a) => ({ ...a }))
        : existing?.points?.length
          ? existing.points.map((q) => ({ x: q.x, y: q.y }))
          : [];
      // Continuar path abierto seleccionado desde un extremo (Illustrator)
      if (!existing) {
        const sel =
          selectedMarkId != null
            ? (marks || []).find(
                (m) =>
                  String(m.id) === String(selectedMarkId) &&
                  m?.type === "path" &&
                  !m.closed,
              )
            : null;
        const selPts = sel?.points || [];
        if (selPts.length >= 2) {
          const first = selPts[0];
          const last = selPts[selPts.length - 1];
          const nearLast = Math.hypot(last.x - p.x, last.y - p.y) <= END_TOL;
          const nearFirst = Math.hypot(first.x - p.x, first.y - p.y) <= END_TOL;
          if (nearLast || nearFirst) {
            const base = nearFirst ? [...selPts].reverse() : [...selPts];
            anchors = base.map((q) => ({ x: q.x, y: q.y }));
            onAbsorbPath?.(sel.id);
            anchors.push({ x: p.x, y: p.y });
            drawingRef.current = true;
            try {
              svgRef.current?.setPointerCapture?.(e.pointerId);
            } catch {
              /* ignore */
            }
            onPointerNorm?.({
              type: "path",
              ...paintOpen,
              stroke: sel.stroke ?? stroke,
              color: sel.color || paintOpen.color,
              strokeColor: sel.strokeColor || paintOpen.strokeColor,
              fill: sel.fill || "none",
              points: penAnchorsToPolyline(anchors, 8),
              _anchors: anchors,
              _pen: true,
              _penDrag: true,
              _hover: p,
              _continueId: sel.id,
            });
            return;
          }
        }
      }
      if (anchors.length >= 3) {
        const first = anchors[0];
        if (Math.hypot(first.x - p.x, first.y - p.y) <= CLOSE_TOL) {
          const closedAnchors = [
            ...anchors,
            { x: first.x, y: first.y, ix: first.ix, iy: first.iy },
          ];
          onPointerNorm?.({
            type: "path",
            ...paintOpen,
            stroke,
            points: penAnchorsToPolyline(closedAnchors, 10),
            closed: true,
            _pen: true,
            _anchors: closedAnchors,
          });
          onCommitMark?.();
          return;
        }
      }
      if (e.detail >= 2 && anchors.length >= 2) {
        onPointerNorm?.({
          type: "path",
          ...paintOpen,
          stroke,
          points: penAnchorsToPolyline(anchors, 10),
          _pen: true,
          _anchors: anchors,
        });
        onCommitMark?.();
        return;
      }
      anchors.push({ x: p.x, y: p.y });
      drawingRef.current = true;
      try {
        svgRef.current?.setPointerCapture?.(e.pointerId);
      } catch {
        /* ignore */
      }
      onPointerNorm?.({
        type: "path",
        ...paintOpen,
        stroke,
        points: penAnchorsToPolyline(anchors, 8),
        _anchors: anchors,
        _pen: true,
        _penDrag: true,
        _hover: p,
        ...(existing?._continueId
          ? { _continueId: existing._continueId }
          : null),
      });
      return;
    }
    if (tool === "select" || !tool) {
      const hit = findMarkAt(marks, p.x, p.y);
      if (hit) {
        const additive = Boolean(e.shiftKey) || multiSelectMode;
        const nextSel = nextMarkSelection(hit.id, additive);
        const soleSelected =
          markIsSelected(hit.id) &&
          (selectedMarkIds?.size || (selectedMarkId != null ? 1 : 0)) === 1;
        if (additive || !soleSelected) {
          onSelectMark?.(hit.id, { additive });
        }
        beginDrag(hit, p, e, nextSel);
      } else {
        onSelectMark?.(null);
      }
      return;
    }
    if (tool === "eraser" || tool === "pathEraser") {
      onHistoryGestureStart?.();
      drawingRef.current = true;
      lastEraseRef.current = p;
      onEraseAt?.(p.x, p.y, { pathsOnly: tool === "pathEraser" });
      try {
        svgRef.current?.setPointerCapture?.(e.pointerId);
      } catch {
        /* ignore */
      }
      return;
    }
    if (tool === "curvature") {
      e.preventDefault();
      e.stopPropagation();
      if (
        e.detail >= 2 &&
        draftMark?.type === "path" &&
        (draftMark.points || []).length >= 2
      ) {
        onCommitMark?.();
        return;
      }
      let existing =
        draftMark?.type === "path" && draftMark._curvature ? draftMark : null;
      // Clic sobre path existente → absorber y seguir editando (VC Curvature)
      if (!existing) {
        const hit = findMarkAt(marks, p.x, p.y, 0.03);
        if (hit?.type === "path" && (hit.points || []).length >= 2) {
          onAbsorbPath?.(hit.id);
          const raw = (hit.points || []).map((q) => ({
            x: Number(q.x) || 0,
            y: Number(q.y) || 0,
          }));
          const nearest = nearestAnchorIndex(raw, p.x, p.y, 0.04);
          if (nearest >= 0) raw[nearest] = { x: p.x, y: p.y };
          else raw.push(p);
          onPointerNorm?.({
            type: "path",
            ...paintOpen,
            stroke: hit.stroke ?? stroke,
            color: hit.color || paintOpen.color,
            strokeColor: hit.strokeColor || paintOpen.strokeColor,
            fill: hit.fill || "none",
            closed: Boolean(hit.closed),
            points:
              raw.length >= 3
                ? smoothPathPoints(raw, 1)
                : raw.map((q) => ({ ...q })),
            _curvature: true,
            _raw: raw,
          });
          return;
        }
      }
      const raw = existing?._raw
        ? [...existing._raw]
        : existing?.points
          ? [...existing.points]
          : [];
      raw.push(p);
      const smoothed =
        raw.length >= 3 ? smoothPathPoints(raw, 1) : raw.map((q) => ({ ...q }));
      onPointerNorm?.({
        type: "path",
        ...paintOpen,
        stroke,
        points: smoothed,
        _curvature: true,
        _raw: raw,
      });
      return;
    }
    if (tool === "anchorPoint") {
      const hit = findMarkAt(marks, p.x, p.y);
      if (hit?.type === "path") {
        onSelectMark?.(hit.id, { additive: false });
        if (e.altKey) {
          onDeleteAnchor?.(hit.id, p.x, p.y);
          return;
        }
        const idx = nearestAnchorIndex(hit.points || [], p.x, p.y, 0.035);
        if (idx >= 0) {
          onHistoryGestureStart?.();
          anchorDragRef.current = {
            markId: hit.id,
            index: idx,
            pointerId: e.pointerId,
          };
          try {
            svgRef.current?.setPointerCapture?.(e.pointerId);
          } catch {
            /* ignore */
          }
        } else {
          // Clic en segmento → ancla + suavizado local
          onInsertAnchor?.(hit.id, p.x, p.y);
        }
      }
      return;
    }
    // Cualquier herramienta: clic fuera del textarea → guardar edición
    // (doble clic en select también abre draft sin tool==="text").
    if (textDraft) {
      const inDraft =
        typeof e.target?.closest === "function" &&
        e.target.closest("[data-mb-text-draft]");
      if (!inDraft) {
        onCommitText?.();
        return;
      }
    }
    if (tool === "text" || tool === "verticalType") {
      const hit = findMarkAt(marks, p.x, p.y);
      if (hit?.type === "text") {
        beginDrag(hit, p, e);
        return;
      }
      onStartText?.(p.x, p.y, {
        writingMode: tool === "verticalType" ? "vertical-rl" : "horizontal-tb",
      });
      return;
    }
    drawingRef.current = true;
    // Capture en el SVG (donde viven move/up). El overlay rompe el trazo.
    try {
      svgRef.current?.setPointerCapture?.(e.pointerId);
    } catch {
      /* ignore */
    }
    if (tool === "pen" || tool === "brush") {
      onPointerNorm?.({
        type: "path",
        ...paintOpen,
        stroke: tool === "brush" ? Math.max(Number(stroke) || 2, 4.5) : stroke,
        points: [p],
      });
      return;
    }
    if (tool === "line" || tool === "arrow") {
      onPointerNorm?.({
        type: tool,
        ...paintOpen,
        stroke,
        x1: p.x,
        y1: p.y,
        x2: p.x,
        y2: p.y,
      });
      return;
    }
    if (tool === "measure") {
      onPointerNorm?.({
        type: "measure",
        color: "#2563eb",
        stroke: 1.5,
        x1: p.x,
        y1: p.y,
        x2: p.x,
        y2: p.y,
        _measure: true,
      });
      return;
    }
    if (tool === "dimension") {
      onPointerNorm?.({
        type: "dimension",
        ...paintOpen,
        stroke,
        x1: p.x,
        y1: p.y,
        x2: p.x,
        y2: p.y,
      });
      return;
    }
    if (tool === "knife" || tool === "lineCut") {
      onPointerNorm?.({
        type: "path",
        color: "#7c3aed",
        strokeColor: "#7c3aed",
        fill: "none",
        stroke: 1.5,
        points: [p],
        _knife: true,
        _lineCut: tool === "lineCut",
      });
      return;
    }
    if (tool === "mirrorCut") {
      onPointerNorm?.({
        type: "line",
        color: "#0d9488",
        strokeColor: "#0d9488",
        fill: "none",
        stroke: 1.5,
        x1: p.x,
        y1: p.y,
        x2: p.x,
        y2: p.y,
        _mirrorCut: true,
      });
      return;
    }
    if (tool === "rectCut") {
      onPointerNorm?.({
        type: "rect",
        color: "#ea580c",
        strokeColor: "#ea580c",
        fill: "rgba(234,88,12,0.12)",
        stroke: 1.5,
        x: p.x,
        y: p.y,
        w: 0,
        h: 0,
        _ox: p.x,
        _oy: p.y,
        _rectCut: true,
      });
      return;
    }
    if (
      tool === "twirl" ||
      tool === "bloat" ||
      tool === "pucker" ||
      tool === "warp" ||
      tool === "reshape" ||
      tool === "scallop" ||
      tool === "crystallize" ||
      tool === "wrinkle"
    ) {
      onHistoryGestureStart?.();
      drawingRef.current = true;
      lastEraseRef.current = p;
      onDistortAt?.(p.x, p.y, tool === "reshape" ? "reshape" : tool);
      try {
        svgRef.current?.setPointerCapture?.(e.pointerId);
      } catch {
        /* ignore */
      }
      return;
    }
    if (tool === "width") {
      onHistoryGestureStart?.();
      drawingRef.current = true;
      lastEraseRef.current = p;
      onWidthAt?.(p.x, p.y, e.altKey ? -0.5 : 0.5);
      try {
        svgRef.current?.setPointerCapture?.(e.pointerId);
      } catch {
        /* ignore */
      }
      return;
    }
    if (tool === "rotateDrag" || tool === "scaleDrag" || tool === "shearDrag") {
      const hit = findMarkAt(marks, p.x, p.y);
      if (hit && !markIsSelected(hit.id)) {
        onSelectMark?.(hit.id, { additive: Boolean(e.shiftKey) });
      }
      const seedMarkIds =
        hit && !markIsSelected(hit.id)
          ? [String(hit.id)]
          : selectedMarkIds?.size
            ? [...selectedMarkIds]
            : selectedMarkId != null
              ? [String(selectedMarkId)]
              : hit
                ? [String(hit.id)]
                : [];
      const mode =
        tool === "rotateDrag"
          ? "rotate"
          : tool === "scaleDrag"
            ? "scale"
            : "shear";
      const started = onLiveTransform?.(mode, p, {
        phase: "start",
        shiftKey: e.shiftKey,
        seedMarkIds,
      });
      if (started === false) return;
      drawingRef.current = true;
      try {
        svgRef.current?.setPointerCapture?.(e.pointerId);
      } catch {
        /* ignore */
      }
      return;
    }
    if (tool === "spiral") {
      const turns = clampSpiralTurns(e.shiftKey ? 5 : spiralOpts?.turns, 3);
      const kind =
        String(spiralOpts?.kind || "").toLowerCase() === "log"
          ? "log"
          : "archimedean";
      onPointerNorm?.({
        type: "path",
        ...paintOpen,
        stroke,
        points: spiralPathPoints({
          cx: p.x,
          cy: p.y,
          rOuter: 0.001,
          turns,
          kind,
        }),
        _spiral: true,
        _spiralTurns: turns,
        _spiralKind: kind,
        _ox: p.x,
        _oy: p.y,
      });
      return;
    }
    if (tool === "blob") {
      drawingRef.current = true;
      try {
        svgRef.current?.setPointerCapture?.(e.pointerId);
      } catch {
        /* ignore */
      }
      const blobInk = paintClosed.color || inkColor;
      onPointerNorm?.({
        type: "path",
        color: blobInk,
        strokeColor: blobInk,
        fill: blobInk,
        stroke: Math.max(10, Number(stroke) * 4 || 12),
        points: [p],
        _blob: true,
      });
      return;
    }
    // Shaper (VC Shift+N): trazo libre → forma limpia / scribble borra
    if (tool === "shaper") {
      drawingRef.current = true;
      try {
        svgRef.current?.setPointerCapture?.(e.pointerId);
      } catch {
        /* ignore */
      }
      onPointerNorm?.({
        type: "path",
        ...paintOpen,
        stroke: Math.max(1.5, Number(stroke) || 2),
        points: [p],
        _shaper: true,
      });
      return;
    }
    // Shape Builder interactivo: arrastra sobre formas → Pathfinder
    if (tool === "shapeBuilder") {
      drawingRef.current = true;
      try {
        svgRef.current?.setPointerCapture?.(e.pointerId);
      } catch {
        /* ignore */
      }
      onPointerNorm?.({
        type: "path",
        color: "#7c3aed",
        strokeColor: "#7c3aed",
        stroke: 2,
        points: [p],
        _shapeBuilder: true,
      });
      return;
    }
    if (tool === "lasso") {
      drawingRef.current = true;
      try {
        svgRef.current?.setPointerCapture?.(e.pointerId);
      } catch {
        /* ignore */
      }
      onPointerNorm?.({
        type: "path",
        color: "#2563eb",
        stroke: 1.5,
        points: [p],
        _lasso: true,
      });
      return;
    }
    if (tool === "areaType" || tool === "verticalAreaType") {
      drawingRef.current = true;
      try {
        svgRef.current?.setPointerCapture?.(e.pointerId);
      } catch {
        /* ignore */
      }
      onPointerNorm?.({
        type: "rect",
        ...paintClosed,
        stroke,
        x: p.x,
        y: p.y,
        w: 0,
        h: 0,
        _ox: p.x,
        _oy: p.y,
        _areaType: true,
        _verticalArea: tool === "verticalAreaType",
      });
      return;
    }
    if (tool === "touchType") {
      // Hit en HTML de texto (capa superior); el SVG base no hace nada
      return;
    }
    if (
      tool === "ellipse" ||
      tool === "arc" ||
      tool === "rect" ||
      tool === "roundrect" ||
      tool === "polygon" ||
      tool === "star" ||
      tool === "grid" ||
      tool === "polarGrid" ||
      tool === "flare"
    ) {
      onPointerNorm?.({
        type: tool,
        ...(tool === "arc" ||
        tool === "grid" ||
        tool === "polarGrid" ||
        tool === "flare"
          ? paintOpen
          : paintClosed),
        stroke,
        x: p.x,
        y: p.y,
        w: 0,
        h: 0,
        ...(tool === "arc"
          ? {
              startAngle: -90,
              sweep: Math.max(
                -359,
                Math.min(359, Number(shapeOpts?.sweep) || 90),
              ),
            }
          : null),
        ...(tool === "grid"
          ? {
              cols: clampGridCols(gridOpts?.cols, 4),
              rows: clampGridRows(gridOpts?.rows, 4),
            }
          : null),
        ...(tool === "polarGrid"
          ? {
              rings: clampPolarRings(gridOpts?.rings, 4),
              rays: clampPolarRays(gridOpts?.rays, 8),
            }
          : null),
        ...(tool === "flare"
          ? {
              rings: Math.max(
                1,
                Math.min(12, clampPolarRings(gridOpts?.rings, 3)),
              ),
              rays: Math.max(
                4,
                Math.min(32, clampPolarRays(gridOpts?.rays, 8)),
              ),
            }
          : null),
        ...(tool === "roundrect"
          ? {
              rx: Math.min(
                0.25,
                Math.max(0.005, Number(shapeOpts?.rx) || 0.04),
              ),
            }
          : null),
        ...(tool === "polygon"
          ? {
              sides: Math.max(
                3,
                Math.min(24, Math.round(Number(shapeOpts?.sides) || 6)),
              ),
            }
          : null),
        ...(tool === "star"
          ? {
              tips: Math.max(
                3,
                Math.min(16, Math.round(Number(shapeOpts?.tips) || 5)),
              ),
              innerRatio: Math.min(
                0.9,
                Math.max(0.15, Number(shapeOpts?.innerRatio) || 0.45),
              ),
            }
          : null),
        _ox: p.x,
        _oy: p.y,
        _shift: e.shiftKey,
      });
    }
  };

  const handlePointerMove = (e) => {
    const tcd = touchCharDragRef.current;
    if (tcd && tcd.pointerId === e.pointerId) {
      const p = toNorm(e.clientX, e.clientY, { allowOutside: true });
      if (p) {
        onTouchCharOffset?.(
          tcd.markId,
          tcd.charIndex,
          tcd.baseDx + (p.x - tcd.startX),
          tcd.baseDy + (p.y - tcd.startY),
        );
      }
      return;
    }
    const ppd = puppetPinDragRef.current;
    if (ppd && ppd.pointerId === e.pointerId) {
      const p = toNorm(e.clientX, e.clientY, { allowOutside: true });
      if (p) onPuppetPinMove?.(ppd.markId, ppd.pinIndex, p.x, p.y);
      return;
    }
    const aDrag = anchorDragRef.current;
    if (aDrag) {
      const p = toNorm(e.clientX, e.clientY, { allowOutside: true });
      if (p) onMoveAnchor?.(aDrag.markId, aDrag.index, p.x, p.y, aDrag);
      return;
    }
    const drag = dragRef.current;
    if (drag) {
      if (drag.mode === "rotate") {
        const nextRot = rotationFromPointerDrag({
          centerX: drag.centerX,
          centerY: drag.centerY,
          clientX: e.clientX,
          clientY: e.clientY,
          startAngle: drag.startAngle,
          originRotation: drag.originRotation,
          shiftKey: e.shiftKey,
        });
        if (!drag.moved && Math.abs(nextRot - drag.originRotation) >= 0.5) {
          dragRef.current = { ...drag, moved: true };
        }
        onMoveMark?.(drag.id, clampMark({ ...drag.orig, rotation: nextRot }));
        return;
      }
      if (drag.mode === "resize-font") {
        const next = resizeTextMarkFontSize(drag.orig, {
          startClientX: drag.startClientX,
          startClientY: drag.startClientY,
          clientX: e.clientX,
          clientY: e.clientY,
          originFontSize: drag.origFs,
          axis: drag.axis || "both",
          edge: drag.edge || "se",
        });
        if (
          !drag.moved &&
          Math.abs((Number(next.fontSize) || 0) - drag.origFs) >= 1
        ) {
          dragRef.current = { ...drag, moved: true };
        }
        onMoveMark?.(drag.id, next);
        onTextFontSize?.(Number(next.fontSize) || drag.origFs);
        return;
      }
      if (drag.mode === "resize-width") {
        const p = toNorm(e.clientX, e.clientY);
        if (!p) return;
        const next = resizeTextMarkWidth(drag.orig, {
          edge: drag.edge,
          startX: drag.startX,
          currX: p.x,
          origX: drag.origX,
          origW: drag.origW,
        });
        if (
          !drag.moved &&
          Math.abs(textMarkBoxWidth(next) - drag.origW) > 0.004
        ) {
          dragRef.current = { ...drag, moved: true };
        }
        onMoveMark?.(drag.id, next);
        return;
      }
      if (drag.mode === "resize-box") {
        const p = toNorm(e.clientX, e.clientY, { allowOutside: true });
        if (!p) return;
        const next = resizeMarkBox(drag.orig, drag.edge, p.x, p.y, drag.orig, {
          allowOutside: true,
        });
        if (
          !drag.moved &&
          (Math.abs((Number(next.w) || 0) - (Number(drag.orig.w) || 0)) >
            0.004 ||
            Math.abs((Number(next.h) || 0) - (Number(drag.orig.h) || 0)) >
              0.004)
        ) {
          dragRef.current = { ...drag, moved: true };
        }
        onMoveMark?.(drag.id, next);
        return;
      }
      // Traslación rígida por delta px (permite salir por dcha/abajo sin aplastar)
      const cw = drag.cw > 1 ? drag.cw : 1;
      const ch = drag.ch > 1 ? drag.ch : 1;
      let dx = (e.clientX - (drag.startClientX ?? e.clientX)) / cw;
      let dy = (e.clientY - (drag.startClientY ?? e.clientY)) / ch;
      if (tool === "perspectiveSelect" && perspectiveGrid?.visible !== false) {
        const b0 = markBounds(drag.orig);
        const projected = projectDeltaOnPerspectivePlane(
          dx,
          dy,
          b0?.cx ?? 0.5,
          b0?.cy ?? 0.5,
          perspectiveGrid,
        );
        dx = projected.dx;
        dy = projected.dy;
      }
      if (!drag.moved && Math.hypot(dx, dy) > 0.004) {
        dragRef.current = { ...drag, moved: true };
      }
      if (drag.groupOrigins?.length) {
        onMoveMarkGroup?.(dx, dy, drag.groupOrigins);
      } else {
        onMoveMark?.(
          drag.id,
          translateMark(drag.orig, dx, dy, { allowOutside: true }),
        );
      }
      return;
    }
    if (tool === "vectorPen") {
      const p = toNorm(e.clientX, e.clientY, { allowOutside: true });
      if (!p) return;
      onPointerNorm?.((prev) => {
        if (prev?.type !== "path" || !prev._pen) return prev;
        if (
          prev._penDrag &&
          Array.isArray(prev._anchors) &&
          prev._anchors.length
        ) {
          const anchors = prev._anchors.map((a) => ({ ...a }));
          const last = anchors[anchors.length - 1];
          const dx = p.x - last.x;
          const dy = p.y - last.y;
          last.ox = dx;
          last.oy = dy;
          last.ix = -dx;
          last.iy = -dy;
          anchors[anchors.length - 1] = last;
          return {
            ...prev,
            _anchors: anchors,
            _hover: p,
            points: penAnchorsToPolyline(anchors, 8),
          };
        }
        return { ...prev, _hover: p };
      });
      return;
    }
    if (!tool || !drawingRef.current) return;
    const p = toNorm(e.clientX, e.clientY, { allowOutside: true });
    if (!p) return;
    if (tool === "eraser" || tool === "pathEraser") {
      onEraseAlong?.(lastEraseRef.current, p, {
        pathsOnly: tool === "pathEraser",
      });
      lastEraseRef.current = p;
      return;
    }
    if (
      tool === "twirl" ||
      tool === "bloat" ||
      tool === "pucker" ||
      tool === "warp" ||
      tool === "reshape" ||
      tool === "scallop" ||
      tool === "crystallize" ||
      tool === "wrinkle"
    ) {
      onDistortAt?.(p.x, p.y, tool === "reshape" ? "reshape" : tool);
      lastEraseRef.current = p;
      return;
    }
    if (tool === "width") {
      onWidthAt?.(p.x, p.y, e.altKey ? -0.35 : 0.35);
      lastEraseRef.current = p;
      return;
    }
    if (tool === "symbolShifter") {
      const prev = lastEraseRef.current;
      onSymbolShiftAt?.(p.x, p.y, {
        alt: Boolean(e.altKey),
        prev: prev ? { x: prev.x, y: prev.y } : null,
      });
      lastEraseRef.current = p;
      return;
    }
    if (tool === "symbolSprayer") {
      onSymbolSprayAt?.(p.x, p.y, { alt: Boolean(e.altKey) });
      lastEraseRef.current = p;
      return;
    }
    if (tool === "symbolScruncher") {
      onSymbolScrunchAt?.(p.x, p.y, { alt: Boolean(e.altKey) });
      lastEraseRef.current = p;
      return;
    }
    if (tool === "symbolSizer") {
      onSymbolSizeAt?.(p.x, p.y, { alt: Boolean(e.altKey) });
      lastEraseRef.current = p;
      return;
    }
    if (tool === "symbolSpinner") {
      onSymbolSpinAt?.(p.x, p.y, { alt: Boolean(e.altKey) });
      lastEraseRef.current = p;
      return;
    }
    if (tool === "symbolStainer") {
      onSymbolStainAt?.(p.x, p.y, { alt: Boolean(e.altKey) });
      lastEraseRef.current = p;
      return;
    }
    if (tool === "symbolScreener") {
      onSymbolScreenAt?.(p.x, p.y, { alt: Boolean(e.altKey) });
      lastEraseRef.current = p;
      return;
    }
    if (tool === "rotateDrag" || tool === "scaleDrag" || tool === "shearDrag") {
      const mode =
        tool === "rotateDrag"
          ? "rotate"
          : tool === "scaleDrag"
            ? "scale"
            : "shear";
      onLiveTransform?.(mode, p, {
        phase: "move",
        shiftKey: e.shiftKey,
      });
      return;
    }
    onPointerNorm?.((prev) => {
      if (!prev) return prev;
      if (prev.type === "path") {
        if (prev._pen) return prev;
        if (prev._curvature && Array.isArray(prev._raw) && prev._raw.length) {
          const raw = [...prev._raw];
          raw[raw.length - 1] = p;
          return {
            ...prev,
            _raw: raw,
            points:
              raw.length >= 3
                ? smoothPathPoints(raw, 1)
                : raw.map((q) => ({ ...q })),
          };
        }
        if (prev._spiral) {
          const ox = prev._ox ?? p.x;
          const oy = prev._oy ?? p.y;
          const rOuter = Math.max(0.001, Math.hypot(p.x - ox, p.y - oy));
          const turns = clampSpiralTurns(
            e.shiftKey ? 5 : (prev._spiralTurns ?? spiralOpts?.turns),
            3,
          );
          const kind =
            String(prev._spiralKind || spiralOpts?.kind || "").toLowerCase() ===
            "log"
              ? "log"
              : "archimedean";
          return {
            ...prev,
            points: spiralPathPoints({
              cx: ox,
              cy: oy,
              rOuter,
              turns,
              kind,
            }),
          };
        }
        const pts = prev.points || [];
        const minDist =
          prev._lasso || prev._knife || prev._shapeBuilder ? 0.008 : 0.004;
        const last = pts[pts.length - 1];
        if (last && Math.hypot(last.x - p.x, last.y - p.y) < minDist) {
          return prev;
        }
        return { ...prev, points: [...pts, p] };
      }
      if (
        prev.type === "line" ||
        prev.type === "arrow" ||
        prev.type === "measure" ||
        prev.type === "dimension"
      ) {
        return { ...prev, x2: p.x, y2: p.y };
      }
      if (
        prev.type === "ellipse" ||
        prev.type === "arc" ||
        prev.type === "rect" ||
        prev.type === "roundrect" ||
        prev.type === "polygon" ||
        prev.type === "star" ||
        prev.type === "grid" ||
        prev.type === "polarGrid" ||
        prev.type === "flare" ||
        prev._rectCut
      ) {
        const ox = prev._ox ?? prev.x;
        const oy = prev._oy ?? prev.y;
        let w = p.x - ox;
        let h = p.y - oy;
        if (e.shiftKey || (prev.type === "ellipse" && e.shiftKey)) {
          const side = Math.max(Math.abs(w), Math.abs(h));
          w = Math.sign(w || 1) * side;
          h = Math.sign(h || 1) * side;
        }
        if (e.shiftKey) {
          const side = Math.max(Math.abs(w), Math.abs(h));
          w = Math.sign(p.x - ox || 1) * side;
          h = Math.sign(p.y - oy || 1) * side;
        }
        const absW = Math.abs(w);
        const absH = Math.abs(h);
        const boxX = w < 0 ? ox + w : ox;
        const boxY = h < 0 ? oy + h : oy;
        const arcAngles =
          prev.type === "arc"
            ? arcAnglesFromPointer({
                ox,
                oy,
                w,
                h,
                px: p.x,
                py: p.y,
                startAngle: Number.isFinite(Number(prev.startAngle))
                  ? Number(prev.startAngle)
                  : -90,
                shiftKey: e.shiftKey,
              })
            : null;
        let rxPatch = null;
        if (prev.type === "roundrect" && e.altKey) {
          const maxR = Math.min(absW, absH) / 2;
          const t = Math.min(1, Math.hypot(absW, absH) / 0.35);
          rxPatch = {
            rx: Math.min(maxR, Math.max(0.005, 0.01 + t * 0.12)),
          };
        }
        return {
          ...prev,
          x: boxX,
          y: boxY,
          w: absW,
          h: absH,
          ...arcAngles,
          ...rxPatch,
          _ox: ox,
          _oy: oy,
        };
      }
      return prev;
    });
  };

  const handlePointerUp = (e) => {
    if (touchCharDragRef.current) {
      touchCharDragRef.current = null;
      onHistoryGestureEnd?.();
      return;
    }
    if (puppetPinDragRef.current) {
      puppetPinDragRef.current = null;
      try {
        svgRef.current?.releasePointerCapture?.(e.pointerId);
      } catch {
        /* ignore */
      }
      onHistoryGestureEnd?.();
      return;
    }
    if (anchorDragRef.current) {
      anchorDragRef.current = null;
      try {
        svgRef.current?.releasePointerCapture?.(e.pointerId);
      } catch {
        /* ignore */
      }
      onHistoryGestureEnd?.();
      return;
    }
    if (finishDrag(e)) return;
    if (!tool) return;
    if (
      tool === "eraser" ||
      tool === "pathEraser" ||
      tool === "twirl" ||
      tool === "bloat" ||
      tool === "pucker" ||
      tool === "warp" ||
      tool === "reshape" ||
      tool === "scallop" ||
      tool === "crystallize" ||
      tool === "wrinkle" ||
      tool === "width"
    ) {
      drawingRef.current = false;
      lastEraseRef.current = null;
      onHistoryGestureEnd?.();
      return;
    }
    if (tool === "rotateDrag" || tool === "scaleDrag" || tool === "shearDrag") {
      const pUp = toNorm(e.clientX, e.clientY);
      const mode =
        tool === "rotateDrag"
          ? "rotate"
          : tool === "scaleDrag"
            ? "scale"
            : "shear";
      onLiveTransform?.(mode, pUp, {
        phase: "end",
        shiftKey: e.shiftKey,
      });
      drawingRef.current = false;
      return;
    }
    if (tool === "curvature") {
      // Clics sucesivos; Enter / doble clic compromete (abajo). No cerrar al soltar.
      return;
    }
    if (tool === "vectorPen") {
      const draft =
        (typeof getDraftMark === "function" ? getDraftMark() : null) ||
        draftMark;
      if (
        draft?._penDrag &&
        Array.isArray(draft._anchors) &&
        draft._anchors.length
      ) {
        const anchors = draft._anchors.map((a) => ({ ...a }));
        const last = anchors[anchors.length - 1];
        const len = Math.hypot(Number(last.ox) || 0, Number(last.oy) || 0);
        if (len < PEN_HANDLE_DRAG_MIN) {
          delete last.ox;
          delete last.oy;
          delete last.ix;
          delete last.iy;
          anchors[anchors.length - 1] = last;
        }
        onPointerNorm?.({
          ...draft,
          _anchors: anchors,
          _penDrag: false,
          _hover: undefined,
          points: penAnchorsToPolyline(anchors, 10),
        });
      } else {
        onPointerNorm?.((prev) =>
          prev?._pen ? { ...prev, _penDrag: false, _hover: undefined } : prev,
        );
      }
      drawingRef.current = false;
      try {
        svgRef.current?.releasePointerCapture?.(e.pointerId);
      } catch {
        /* ignore */
      }
      return;
    }
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const draft =
      (typeof getDraftMark === "function" ? getDraftMark() : null) || draftMark;
    if (tool === "lasso" || draft?._lasso) {
      const pts = draft?.points || [];
      if (pts.length >= 3) onLassoSelect?.(pts, { additive: e.shiftKey });
      onPointerNorm?.(null);
    } else if (
      tool === "areaType" ||
      tool === "verticalAreaType" ||
      draft?._areaType
    ) {
      if (draft && draft.w >= 0.04 && draft.h >= 0.02) {
        onStartAreaText?.(
          draft.x,
          draft.y,
          Math.max(draft.w, 0.08),
          Math.max(draft.h, 0.04),
          {
            writingMode:
              tool === "verticalAreaType" || draft?._verticalArea
                ? "vertical-rl"
                : "horizontal-tb",
          },
        );
      }
      onPointerNorm?.(null);
    } else if (
      tool === "measure" ||
      draft?._measure ||
      draft?.type === "measure"
    ) {
      onMeasureDone?.(draft);
      onPointerNorm?.(null);
    } else if (tool === "knife" || draft?._knife) {
      onKnifeCut?.(draft?.points || [], { lineCut: Boolean(draft?._lineCut) });
      onPointerNorm?.(null);
    } else if (tool === "rectCut" || draft?._rectCut) {
      onRectCut?.(draft);
      onPointerNorm?.(null);
    } else if (tool === "mirrorCut" || draft?._mirrorCut) {
      onMirrorCut?.(draft);
      onPointerNorm?.(null);
    } else if (tool === "shaper" || draft?._shaper) {
      const pts = draft?.points || [];
      const recognized = recognizeShaperStroke(pts);
      onPointerNorm?.(null);
      onShaperFinish?.(recognized, {
        color: draft?.color || color,
        strokeColor: draft?.strokeColor || strokeColor || color,
        stroke: draft?.stroke || stroke,
        fill: draft?.fill || fillColor,
      });
    } else if (tool === "shapeBuilder" || draft?._shapeBuilder) {
      onShapeBuilderStroke?.(draft?.points || []);
      onPointerNorm?.(null);
    } else {
      onCommitMark?.();
    }
    try {
      svgRef.current?.releasePointerCapture?.(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const [draftFontPx, setDraftFontPx] = useState(16);
  const draftFsCqw = textMarkFontCqw(
    Number(textDraft?.fontSize) || markFontSize || 14,
  );

  // Medir cqw en un span dentro del mismo container (no getBoundingClientRect
  // del lienzo: el zoom del tablero achicaba el draft). El textarea a veces
  // ignora cqw → aplicamos los px equivalentes.
  useLayoutEffect(() => {
    if (!textDraft) return;
    const el = textDraftAreaRef.current;
    const host = overlayRef.current;
    if (!el || !host) return;
    const probe = document.createElement("span");
    probe.setAttribute("aria-hidden", "true");
    probe.textContent = "Hg";
    probe.style.cssText = [
      "position:absolute",
      "left:0",
      "top:0",
      "visibility:hidden",
      "pointer-events:none",
      "white-space:nowrap",
      `font-size:${draftFsCqw}cqw`,
      `font-family:${markFontCss(textDraft.fontFamily || markFontFamily || "sans")}`,
      `font-weight:${normalizeMarkFontWeight(textDraft.fontWeight ?? markFontWeight)}`,
      "line-height:1.2",
    ].join(";");
    host.appendChild(probe);
    const measured = parseFloat(window.getComputedStyle(probe).fontSize);
    host.removeChild(probe);
    const px =
      Number.isFinite(measured) && measured > 1
        ? measured
        : textMarkFontPx(
            Number(textDraft.fontSize) || markFontSize || 14,
            host.clientWidth || 800,
          );
    setDraftFontPx(px);
    el.style.fontSize = `${px}px`;
    el.style.height = "0px";
    el.style.height = `${Math.max(el.scrollHeight, px * 1.35)}px`;
  }, [
    textDraft,
    textDraft?.value,
    textDraft?.fontSize,
    textDraft?.w,
    textDraft?.fontFamily,
    textDraft?.fontWeight,
    markFontSize,
    markFontFamily,
    markFontWeight,
    draftFsCqw,
  ]);

  const perspSegs =
    perspectiveGrid?.visible !== false
      ? perspectiveGridSegments(perspectiveGrid)
      : [];

  const marksSpan = 1 + 2 * MARKS_BOARD_OUTSET;
  // % respecto al artboard padre (para el overlay expandido)
  const marksExpandPct = MARKS_BOARD_OUTSET * 100;
  // % respecto al overlay expandido: origen de la mesa (0,0) dentro del viewBox
  const marksArtboardOriginPct = (MARKS_BOARD_OUTSET / marksSpan) * 100;
  const marksArtboardSizePct = (1 / marksSpan) * 100;
  const marksViewBox = `${-MARKS_BOARD_OUTSET} ${-MARKS_BOARD_OUTSET} ${marksSpan} ${marksSpan}`;
  // Anclas en px de pantalla (Illustrator): el viewBox 0–1 no es cuadrado en pantalla.
  const [artboardPx, setArtboardPx] = useState({ w: 800, h: 560 });
  useEffect(() => {
    const el = overlayRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const measure = () => {
      const r = el.getBoundingClientRect();
      const w = r.width / marksSpan;
      const h = r.height / marksSpan;
      if (w > 8 && h > 8) setArtboardPx({ w, h });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [marksSpan]);
  const anchorBox = (nPx) => ({
    x: nPx / Math.max(1, artboardPx.w),
    y: nPx / Math.max(1, artboardPx.h),
  });

  const beginAnchorDrag = (
    mark,
    index,
    e,
    { lineEnds = false, expandBox = false } = {},
  ) => {
    onHistoryGestureStart?.();
    onSelectMark?.(mark.id, { additive: Boolean(e.shiftKey) });
    anchorDragRef.current = {
      markId: mark.id,
      index,
      pointerId: e.pointerId,
      ...(lineEnds ? { lineEnds: true } : null),
      ...(expandBox ? { expandBox: true } : null),
    };
    try {
      svgRef.current?.setPointerCapture?.(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  return (
    <Box
      ref={overlayRef}
      sx={{
        position: "absolute",
        // Más allá de la mesa: dibujar / ver / editar marcas fuera del artboard
        left: `${-marksExpandPct}%`,
        top: `${-marksExpandPct}%`,
        width: `${marksSpan * 100}%`,
        height: `${marksSpan * 100}%`,
        // Por encima de cualquier casilla (z hasta 9999): si no, al activar la mesa
        // las fotos tapaban cuadrados/espirales recién dibujados.
        zIndex: 10050,
        overflow: "visible",
        // El overlay mide 7 mesas. Sin este recorte el trazo se dibuja encima
        // de la mesa vecina y, al pinchar esa foto, parece que desaparece.
        clipPath: `inset(${marksArtboardOriginPct - marksArtboardSizePct * 0.12}%)`,
        // En select el vacío no bloquea casillas; marcas/texto llevan pointerEvents propios
        pointerEvents: boardPanActive
          ? "none"
          : passThroughEmpty
            ? "none"
            : active
              ? "auto"
              : "none",
        touchAction: "none",
        cursor: boardPanActive
          ? "grab"
          : tool === "select" || tool === "directSelect"
            ? "default"
            : tool === "eraser" || tool === "scissors"
              ? "cell"
              : tool === "text"
                ? "text"
                : tool
                  ? "crosshair"
                  : "default",
      }}
    >
      <svg
        ref={svgRef}
        viewBox={marksViewBox}
        preserveAspectRatio="none"
        width="100%"
        height="100%"
        overflow="visible"
        style={{
          position: "absolute",
          inset: 0,
          display: "block",
          overflow: "visible",
          zIndex: 1,
          pointerEvents: passThroughEmpty ? "none" : "auto",
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <defs>
          <filter
            id="markSelectedGlow"
            x="-20%"
            y="-20%"
            width="140%"
            height="140%"
          >
            <feDropShadow
              dx="0"
              dy="0"
              stdDeviation="0.012"
              floodColor="#2563eb"
              floodOpacity="0.85"
            />
          </filter>
        </defs>
        {/* Draft aquí; marcas committed van en capas por índice. Anclas arriba del todo. */}
        {draftMark && draftMark.type !== "text" ? (
          <MarkSvgShape mark={draftMark} draft />
        ) : null}
      </svg>
      {/* Capa = tamaño de la mesa (0–1). Origen = OUTSET/span del overlay expandido. */}
      <Box
        sx={{
          position: "absolute",
          left: `${marksArtboardOriginPct}%`,
          top: `${marksArtboardOriginPct}%`,
          width: `${marksArtboardSizePct}%`,
          height: `${marksArtboardSizePct}%`,
          overflow: "visible",
          zIndex: 2,
          pointerEvents: "none",
        }}
      >
        {/* Orden = índice en marks[] (Traer al frente / Enviar atrás).
          No subir z al seleccionar: al pinchar la mesa el hit elige la barra
          de cabecera y el +500 la tapaba encima de los textos. */}
        {(marks || []).map((m, markIdx) => {
          const layerZ = 30 + markIdx;
          if (m?.type === "text") {
            if (
              !String(m.text || "").trim() ||
              (textDraft?.id && String(textDraft.id) === String(m.id))
            ) {
              return null;
            }
            const fs = Number(m.fontSize) || 14;
            const boxW = textMarkBoxWidth(m);
            const rot = normalizeRotation(m.rotation);
            const canInteract =
              idle ||
              tool === "text" ||
              tool === "touchType" ||
              tool === "perspectiveSelect";
            const isSelected = markIsSelected(m.id);
            const showHandles =
              isSelected &&
              (!selectedMarkIds?.size || selectedMarkIds.size === 1) &&
              tool !== "touchType";
            const offsetMap = new Map(
              (Array.isArray(m.charOffsets) ? m.charOffsets : []).map((o) => [
                Number(o.i),
                o,
              ]),
            );
            const textChars = String(m.text || "");
            const renderTouchChars = offsetMap.size > 0 || tool === "touchType";
            return (
              <Box
                key={m.id}
                component="span"
                data-mb-text=""
                onPointerDown={(e) => {
                  if (!canInteract) return;
                  e.preventDefault();
                  e.stopPropagation();
                  const p = toNorm(e.clientX, e.clientY);
                  if (!p) return;
                  if (tool === "touchType") {
                    const idx = textCharIndexAt(m, p.x, p.y);
                    if (idx < 0) return;
                    onSelectMark?.(m.id, { additive: false });
                    const prev = offsetMap.get(idx);
                    onHistoryGestureStart?.();
                    touchCharDragRef.current = {
                      markId: m.id,
                      charIndex: idx,
                      startX: p.x,
                      startY: p.y,
                      baseDx: Number(prev?.dx) || 0,
                      baseDy: Number(prev?.dy) || 0,
                      pointerId: e.pointerId,
                    };
                    attachDragListeners(e);
                    return;
                  }
                  let nextSel = null;
                  let soleSelected = false;
                  if (idle || tool === "select") {
                    const additive = Boolean(e.shiftKey) || multiSelectMode;
                    nextSel = nextMarkSelection(m.id, additive);
                    soleSelected =
                      markIsSelected(m.id) &&
                      (selectedMarkIds?.size ||
                        (selectedMarkId != null ? 1 : 0)) === 1;
                    if (additive || !soleSelected) {
                      onSelectMark?.(m.id, { additive });
                    }
                  }
                  beginDrag(m, p, e, nextSel);
                  if (dragRef.current && soleSelected && !e.shiftKey) {
                    dragRef.current.editOnTap = true;
                  }
                }}
                onDoubleClick={(e) => {
                  if (!canInteract || tool === "touchType") return;
                  e.preventDefault();
                  e.stopPropagation();
                  dragRef.current = null;
                  onSelectMark?.(m.id, { additive: false });
                  onEditText?.(m);
                }}
                sx={{
                  position: "absolute",
                  left: `${m.x * 100}%`,
                  top: `${m.y * 100}%`,
                  width: `${boxW * 100}%`,
                  boxSizing: "border-box",
                  transform: rot ? `rotate(${rot}deg)` : undefined,
                  transformOrigin: "center center",
                  color: textMarkFillColor(m),
                  fontWeight: normalizeMarkFontWeight(m.fontWeight),
                  fontFamily: markFontCss(m.fontFamily),
                  fontSize: `${textMarkFontCqw(fs)}cqw`,
                  textAlign: normalizeMarkTextAlign(m.textAlign),
                  writingMode: normalizeMarkWritingMode(m.writingMode),
                  lineHeight: 1.2,
                  whiteSpace: "pre-wrap",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                  ...textMarkOutlineCss(m, Math.max(10, fs * 1.35)),
                  // Sin sombra automática (diseño: no parecer PowerPoint).
                  textShadow: "none",
                  pointerEvents: canInteract ? "auto" : "none",
                  touchAction: "none",
                  WebkitUserSelect: "none",
                  cursor: canInteract
                    ? tool === "touchType"
                      ? "grab"
                      : tool === "text"
                        ? "text"
                        : "move"
                    : "default",
                  zIndex: layerZ,
                  userSelect: "none",
                  outline: isSelected
                    ? "2px solid"
                    : canInteract
                      ? "1px dashed transparent"
                      : "none",
                  outlineColor: isSelected ? "primary.main" : undefined,
                  outlineOffset: 2,
                  "&:hover": canInteract
                    ? {
                        outline: isSelected ? "2px solid" : "1px dashed",
                        outlineColor: "primary.main",
                        bgcolor: "rgba(255,255,255,0.22)",
                      }
                    : undefined,
                }}
              >
                {renderTouchChars
                  ? textChars.split("").map((ch, i) => {
                      const off = offsetMap.get(i);
                      const dx = Number(off?.dx) || 0;
                      const dy = Number(off?.dy) || 0;
                      return (
                        <Box
                          key={`ch-${m.id}-${i}`}
                          component="span"
                          sx={{
                            display: "inline-block",
                            position: "relative",
                            transform:
                              dx || dy
                                ? `translate(${dx * 100}cqw, ${dy * 100}cqw)`
                                : undefined,
                            whiteSpace: "pre",
                          }}
                        >
                          {ch === "\n" ? <br /> : ch}
                        </Box>
                      );
                    })
                  : m.text}
                {showHandles && canInteract ? (
                  <>
                    <Box
                      component="span"
                      onTouchStart={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onPointerDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        beginRotateText(m, e);
                      }}
                      title={`${Math.round(rot)}°`}
                      sx={{
                        ...TEXT_RESIZE_HANDLE_SX,
                        left: "50%",
                        top: -28,
                        marginLeft: "-6px",
                        borderRadius: "50%",
                        zIndex: 25,
                        cursor: "grab",
                        "&:active": { cursor: "grabbing" },
                      }}
                    />
                    <Box
                      component="span"
                      onTouchStart={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onPointerDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        beginResizeText(m, e, { axis: "y", edge: "n" });
                      }}
                      title={`${fs}pt`}
                      sx={{
                        ...TEXT_FONT_HANDLE_SX,
                        left: "50%",
                        top: -6,
                        marginLeft: "-6px",
                        cursor: "ns-resize",
                      }}
                    />
                    <Box
                      component="span"
                      onTouchStart={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onPointerDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        beginResizeText(m, e, { axis: "y", edge: "s" });
                      }}
                      title={`${fs}pt`}
                      sx={{
                        ...TEXT_FONT_HANDLE_SX,
                        left: "50%",
                        bottom: -6,
                        marginLeft: "-6px",
                        cursor: "ns-resize",
                      }}
                    />
                    <Box
                      component="span"
                      onTouchStart={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onPointerDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        beginResizeTextWidth(m, "w", e);
                      }}
                      title="Ancho"
                      sx={{
                        ...TEXT_SIDE_HANDLE_SX,
                        left: -6,
                        top: "50%",
                        marginTop: "-6px",
                        cursor: "ew-resize",
                      }}
                    />
                    <Box
                      component="span"
                      onTouchStart={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onPointerDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        beginResizeTextWidth(m, "e", e);
                      }}
                      title="Ancho"
                      sx={{
                        ...TEXT_SIDE_HANDLE_SX,
                        right: -6,
                        top: "50%",
                        marginTop: "-6px",
                        cursor: "ew-resize",
                      }}
                    />
                    <Box
                      component="span"
                      onTouchStart={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onPointerDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        beginResizeText(m, e, { axis: "both", edge: "se" });
                      }}
                      title={`${fs}pt`}
                      sx={{
                        ...TEXT_FONT_HANDLE_SX,
                        right: -6,
                        bottom: -6,
                        cursor: "nwse-resize",
                      }}
                    />
                  </>
                ) : null}
              </Box>
            );
          }
          return (
            <svg
              key={m.id}
              viewBox="0 0 1 1"
              preserveAspectRatio="none"
              width="100%"
              height="100%"
              overflow="visible"
              style={{
                position: "absolute",
                inset: 0,
                display: "block",
                overflow: "visible",
                zIndex: layerZ,
                pointerEvents: "none",
              }}
            >
              <MarkSvgShape
                mark={m}
                hit={!MARK_PASS_THROUGH_TOOLS.has(tool)}
                selected={markIsSelected(m.id)}
                onPointerDown={(e) => {
                  if (MARK_PASS_THROUGH_TOOLS.has(tool)) return;
                  e.preventDefault();
                  e.stopPropagation();
                  const p = toNorm(e.clientX, e.clientY, {
                    allowOutside: true,
                  });
                  if (!p) return;
                  // A: primero vértice / extremo; si no, mueve el trazo
                  if (tool === "directSelect") {
                    if (m.type === "path") {
                      const idx = nearestAnchorIndex(
                        m.points || [],
                        p.x,
                        p.y,
                        0.05,
                      );
                      if (idx >= 0) {
                        beginAnchorDrag(m, idx, e);
                        return;
                      }
                    } else if (
                      m.type === "line" ||
                      m.type === "arrow" ||
                      m.type === "dimension"
                    ) {
                      const endPts = [
                        { x: Number(m.x1) || 0, y: Number(m.y1) || 0 },
                        { x: Number(m.x2) || 0, y: Number(m.y2) || 0 },
                      ];
                      const idx = nearestAnchorIndex(endPts, p.x, p.y, 0.05);
                      if (idx >= 0) {
                        beginAnchorDrag(m, idx, e, { lineEnds: true });
                        return;
                      }
                    } else if (markHasBox(m) && m.type !== "path") {
                      const boxPts = boxMarkAnchorPoints(m);
                      const idx = nearestAnchorIndex(boxPts, p.x, p.y, 0.05);
                      if (idx >= 0) {
                        // Expandir a path en el primer tirón de ancla
                        beginAnchorDrag(m, idx, e, { expandBox: true });
                        return;
                      }
                    }
                    onSelectMark?.(m.id, { additive: Boolean(e.shiftKey) });
                    beginDrag(m, p, e);
                    return;
                  }
                  const additive = Boolean(e.shiftKey) || multiSelectMode;
                  const nextSel = nextMarkSelection(m.id, additive);
                  const soleSelected =
                    markIsSelected(m.id) &&
                    (selectedMarkIds?.size ||
                      (selectedMarkId != null ? 1 : 0)) === 1;
                  if (additive || !soleSelected) {
                    onSelectMark?.(m.id, { additive });
                  }
                  beginDrag(m, p, e, nextSel);
                }}
              />
            </svg>
          );
        })}
        {/* Perspective Grid overlay */}
        {perspSegs.length ? (
          <svg
            viewBox="0 0 1 1"
            preserveAspectRatio="none"
            width="100%"
            height="100%"
            overflow="visible"
            style={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              zIndex: 20,
            }}
          >
            {perspSegs.map((s, i) => (
              <line
                key={`pg-${i}`}
                x1={s.x1}
                y1={s.y1}
                x2={s.x2}
                y2={s.y2}
                stroke={perspectivePlaneColor(s.plane)}
                strokeWidth={s.plane === "none" ? 0.004 : 0.0032}
                strokeOpacity={s.plane === "none" ? 0.75 : 0.6}
                strokeDasharray={s.plane === "none" ? "0.02 0.012" : undefined}
              />
            ))}
            {perspectiveGrid?.origin ? (
              <circle
                cx={Number(perspectiveGrid.origin.x) || 0.5}
                cy={Number(perspectiveGrid.origin.y) || 0.78}
                r={0.01}
                fill={perspectivePlaneColor(perspectiveGrid.plane || "ground")}
                opacity={0.85}
              />
            ) : null}
          </svg>
        ) : null}
        {/* Puppet Warp: pines sobre la forma seleccionada */}
        {tool === "puppetWarp"
          ? (marks || [])
              .filter(
                (m) =>
                  markIsSelected(m.id) &&
                  Array.isArray(m.puppet?.pins) &&
                  m.puppet.pins.length,
              )
              .map((m) => (
                <svg
                  key={`puppet-${m.id}`}
                  viewBox="0 0 1 1"
                  preserveAspectRatio="none"
                  width="100%"
                  height="100%"
                  overflow="visible"
                  style={{
                    position: "absolute",
                    inset: 0,
                    pointerEvents: "none",
                    zIndex: 10200,
                  }}
                >
                  {m.puppet.pins.map((pin, i) => (
                    <g key={`pp-${m.id}-${i}`}>
                      <circle
                        cx={Number(pin.at?.x) || 0}
                        cy={Number(pin.at?.y) || 0}
                        r={0.014}
                        fill="#fff"
                        stroke="#d9472f"
                        strokeWidth={0.004}
                      />
                      <circle
                        cx={Number(pin.at?.x) || 0}
                        cy={Number(pin.at?.y) || 0}
                        r={0.005}
                        fill="#d9472f"
                      />
                    </g>
                  ))}
                </svg>
              ))
          : null}
        {/* Width tool: diamantes de perfil sobre el trazo */}
        {tool === "width"
          ? (marks || [])
              .filter((m) => normalizeWidthPoints(m.widthPoints).length)
              .map((m) => {
                const pts = Array.isArray(m.points) ? m.points : null;
                if (!pts || pts.length < 2) return null;
                const wps = normalizeWidthPoints(m.widthPoints);
                return (
                  <svg
                    key={`width-${m.id}`}
                    viewBox="0 0 1 1"
                    preserveAspectRatio="none"
                    width="100%"
                    height="100%"
                    overflow="visible"
                    style={{
                      position: "absolute",
                      inset: 0,
                      pointerEvents: "none",
                      zIndex: 10200,
                    }}
                  >
                    {wps.map((wp, i) => {
                      const s = samplePolylineAtT(pts, wp.t, Boolean(m.closed));
                      if (!s) return null;
                      const half =
                        markStrokeSvg(m.stroke) * 0.5 * (Number(wp.scale) || 1);
                      const lx = s.x + s.nx * half;
                      const ly = s.y + s.ny * half;
                      const rx = s.x - s.nx * half;
                      const ry = s.y - s.ny * half;
                      const diamond = (cx, cy) =>
                        `${cx},${cy - 0.008} ${cx + 0.008},${cy} ${cx},${cy + 0.008} ${cx - 0.008},${cy}`;
                      return (
                        <g key={`wp-${m.id}-${i}`}>
                          <line
                            x1={lx}
                            y1={ly}
                            x2={rx}
                            y2={ry}
                            stroke="#2563eb"
                            strokeWidth={0.0025}
                            opacity={0.85}
                          />
                          <polygon
                            points={diamond(s.x, s.y)}
                            fill="#fff"
                            stroke="#2563eb"
                            strokeWidth={0.002}
                          />
                          <polygon
                            points={diamond(lx, ly)}
                            fill="#2563eb"
                            stroke="#fff"
                            strokeWidth={0.0015}
                          />
                          <polygon
                            points={diamond(rx, ry)}
                            fill="#2563eb"
                            stroke="#fff"
                            strokeWidth={0.0015}
                          />
                        </g>
                      );
                    })}
                  </svg>
                );
              })
          : null}
        {/* Bbox teal con V; con A solo anclas naranjas (también en rect/círculo). */}
        {(marks || [])
          .filter(
            (m) =>
              tool !== "directSelect" &&
              markHasBox(m) &&
              markIsSelected(m.id) &&
              (!selectedMarkIds?.size || selectedMarkIds.size <= 1),
          )
          .map((m) => {
            const b =
              m.type === "path" || m.type === "textpath" ? markBounds(m) : null;
            const x = b ? b.x : Number(m.x) || 0;
            const y = b ? b.y : Number(m.y) || 0;
            const w = Math.max(0.01, b ? b.w : Number(m.w) || 0);
            const h = Math.max(0.01, b ? b.h : Number(m.h) || 0);
            const markIdx = (marks || []).findIndex(
              (x) => String(x.id) === String(m.id),
            );
            const handles = [
              { edge: "nw", left: x, top: y, cursor: "nwse-resize" },
              { edge: "n", left: x + w / 2, top: y, cursor: "ns-resize" },
              { edge: "ne", left: x + w, top: y, cursor: "nesw-resize" },
              { edge: "e", left: x + w, top: y + h / 2, cursor: "ew-resize" },
              { edge: "se", left: x + w, top: y + h, cursor: "nwse-resize" },
              { edge: "s", left: x + w / 2, top: y + h, cursor: "ns-resize" },
              { edge: "sw", left: x, top: y + h, cursor: "nesw-resize" },
              { edge: "w", left: x, top: y + h / 2, cursor: "ew-resize" },
            ];
            return (
              <Box
                key={`box-h-${m.id}`}
                sx={{
                  position: "absolute",
                  inset: 0,
                  pointerEvents: "none",
                  // Asas por encima de todas las marcas; no altera el orden de pintura
                  zIndex: 10000 + Math.max(0, markIdx),
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    left: `${x * 100}%`,
                    top: `${y * 100}%`,
                    width: `${w * 100}%`,
                    height: `${h * 100}%`,
                    border: "1.5px solid #2563eb",
                    boxSizing: "border-box",
                    pointerEvents: "none",
                  }}
                />
                {handles.map((hnd) => (
                  <Box
                    key={hnd.edge}
                    component="span"
                    onPointerDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      beginResizeMarkBox(m, hnd.edge, e);
                    }}
                    sx={{
                      position: "absolute",
                      left: `${hnd.left * 100}%`,
                      top: `${hnd.top * 100}%`,
                      width: 10,
                      height: 10,
                      marginLeft: "-5px",
                      marginTop: "-5px",
                      bgcolor: "#fff",
                      border: "1.5px solid #2563eb",
                      borderRadius: "2px",
                      pointerEvents: "auto",
                      cursor: hnd.cursor,
                      touchAction: "none",
                      zIndex: 25,
                    }}
                  />
                ))}
              </Box>
            );
          })}
        {textDraft ? (
          <Box
            data-mb-text=""
            data-mb-text-draft="1"
            sx={{
              position: "absolute",
              left: `${textDraft.x * 100}%`,
              top: `${textDraft.y * 100}%`,
              width: `${textMarkBoxWidth(textDraft) * 100}%`,
              height:
                Number(textDraft.h) > 0.02
                  ? `${Number(textDraft.h) * 100}%`
                  : undefined,
              zIndex: 25,
              pointerEvents: "auto",
              touchAction: "none",
              transform: normalizeRotation(textDraft.rotation)
                ? `rotate(${normalizeRotation(textDraft.rotation)}deg)`
                : undefined,
              transformOrigin: "center center",
            }}
          >
            <Box
              component="textarea"
              ref={textDraftAreaRef}
              autoFocus
              rows={Number(textDraft.h) > 0.06 ? 4 : 1}
              value={textDraft.value}
              maxLength={TEXT_MARK_MAX_LEN}
              onChange={(e) => onChangeTextDraft?.(e.target.value)}
              onBlur={() => {
                if (suppressTextBlurRef.current) return;
                onCommitText?.();
              }}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  e.preventDefault();
                  onCancelText?.();
                  return;
                }
                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  onCommitText?.();
                }
              }}
              sx={{
                display: "block",
                width: "100%",
                height: Number(textDraft.h) > 0.02 ? "100%" : "auto",
                boxSizing: "border-box",
                minHeight: `${Math.max(draftFontPx * 1.35, 20)}px`,
                overflow: Number(textDraft.h) > 0.02 ? "auto" : "hidden",
                resize: "none",
                fontWeight: normalizeMarkFontWeight(
                  textDraft.fontWeight ?? markFontWeight,
                ),
                fontFamily: markFontCss(
                  textDraft.fontFamily || markFontFamily || "sans",
                ),
                textAlign: normalizeMarkTextAlign(
                  textDraft.textAlign || markTextAlign,
                ),
                writingMode: normalizeMarkWritingMode(textDraft.writingMode),
                // px medidos desde cqw en el mismo container (ver useLayoutEffect)
                fontSize: `${draftFontPx}px`,
                lineHeight: 1.2,
                color: textMarkFillColor({
                  type: "text",
                  color: textDraft.color || color || DEFAULT_MARK_COLOR,
                  fill: textDraft.fill,
                  strokeColor: textDraft.strokeColor || strokeColor,
                }),
                ...textMarkOutlineCss(
                  {
                    type: "text",
                    stroke:
                      textDraft.stroke != null ? textDraft.stroke : stroke,
                    strokeColor: textDraft.strokeColor || strokeColor,
                    color: textDraft.color || color,
                    fill: textDraft.fill,
                  },
                  draftFontPx,
                ),
                bgcolor: "rgba(255,255,255,0.35)",
                border: "1px solid",
                borderColor: "primary.main",
                borderRadius: 0.5,
                px: 0,
                py: 0,
                outline: "none",
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
                touchAction: "manipulation",
              }}
            />
            <Box
              component="span"
              onMouseDown={(e) => e.preventDefault()}
              onTouchStart={(e) => {
                // iPad: sin esto el textarea hace blur y se pierde el draft
                e.preventDefault();
                e.stopPropagation();
              }}
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                suppressTextBlurRef.current = true;
                const startX = e.clientX;
                const origW = textMarkBoxWidth(textDraft);
                const svgR = svgRef.current?.getBoundingClientRect?.();
                const span = 1 + 2 * MARKS_BOARD_OUTSET;
                const cw = (svgR?.width || 1) / span || 1;
                const captureEl =
                  (typeof e.currentTarget?.closest === "function"
                    ? e.currentTarget.closest("[data-mb-text]")
                    : null) ||
                  overlayRef.current ||
                  e.currentTarget;
                try {
                  captureEl?.setPointerCapture?.(e.pointerId);
                } catch {
                  /* ignore */
                }
                const onMove = (ev) => {
                  ev.preventDefault?.();
                  const dx = (ev.clientX - startX) / cw;
                  const nextW = Math.min(0.95, Math.max(0.05, origW + dx));
                  onPatchTextDraft?.({ w: nextW });
                };
                const onUp = (ev) => {
                  window.removeEventListener("pointermove", onMove);
                  window.removeEventListener("pointerup", onUp);
                  window.removeEventListener("pointercancel", onUp);
                  suppressTextBlurRef.current = false;
                  try {
                    captureEl?.releasePointerCapture?.(ev.pointerId);
                  } catch {
                    /* ignore */
                  }
                };
                window.addEventListener("pointermove", onMove, {
                  passive: false,
                });
                window.addEventListener("pointerup", onUp);
                window.addEventListener("pointercancel", onUp);
              }}
              title="Ancho"
              sx={{
                ...TEXT_RESIZE_HANDLE_SX,
                right: -6,
                top: "50%",
                marginTop: "-6px",
                cursor: "ew-resize",
                zIndex: 26,
              }}
            />
          </Box>
        ) : null}
      </Box>
      {/* Anclas encima de marcas/fotos: visibles con V y A; arrastre solo con A.
          Mismo viewBox 0–1 que el trazo (no el overlay ×7: ahí el cuadrado se estira). */}
      <svg
        viewBox="0 0 1 1"
        preserveAspectRatio="none"
        overflow="visible"
        style={{
          position: "absolute",
          left: `${marksArtboardOriginPct}%`,
          top: `${marksArtboardOriginPct}%`,
          width: `${marksArtboardSizePct}%`,
          height: `${marksArtboardSizePct}%`,
          zIndex: 10250,
          overflow: "visible",
          pointerEvents: "none",
        }}
      >
        {(marks || [])
          .filter((m) => {
            if (!m) return false;
            const isVector =
              m.type === "path" ||
              m.type === "line" ||
              m.type === "arrow" ||
              m.type === "dimension";
            const isBoxShape =
              markHasBox(m) && m.type !== "path" && m.type !== "textpath";
            if (!isVector && !isBoxShape) return false;
            // Formas bbox: anclas naranjas solo con A (con V usan marco teal)
            if (isBoxShape && !isVector) {
              return tool === "directSelect" && markIsSelected(m.id);
            }
            if (markIsSelected(m.id)) return true;
            return (
              tool === "addAnchor" ||
              tool === "deleteAnchor" ||
              tool === "anchorPoint"
            );
          })
          .flatMap((m) => {
            const isBoxShape =
              markHasBox(m) &&
              m.type !== "path" &&
              m.type !== "textpath" &&
              m.type !== "line" &&
              m.type !== "arrow" &&
              m.type !== "dimension";
            const pts =
              m.type === "path"
                ? m.points || []
                : isBoxShape
                  ? boxMarkAnchorPoints(m)
                  : [
                      { x: Number(m.x1) || 0, y: Number(m.y1) || 0 },
                      { x: Number(m.x2) || 0, y: Number(m.y2) || 0 },
                    ];
            const arm = anchorBox(tool === "directSelect" ? 3.25 : 2.75);
            const halfX = arm.x;
            const halfY = arm.y;
            const handleArm = anchorBox(2.25);
            const canDragAnchor =
              tool === "directSelect" ||
              tool === "anchorPoint" ||
              tool === "addAnchor" ||
              tool === "deleteAnchor";
            const lineEnds =
              m.type === "line" || m.type === "arrow" || m.type === "dimension";
            const anchorStroke =
              tool === "deleteAnchor"
                ? "#991b1b"
                : tool === "directSelect"
                  ? "#d9472f"
                  : "#1473e6";
            return pts.map((pt, idx) => {
              const cx = Number(pt.x) || 0;
              const cy = Number(pt.y) || 0;
              const handles = [];
              if (
                Number.isFinite(Number(pt.ox)) &&
                Number.isFinite(Number(pt.oy))
              ) {
                handles.push({
                  key: "o",
                  x: cx + Number(pt.ox),
                  y: cy + Number(pt.oy),
                });
              }
              if (
                Number.isFinite(Number(pt.ix)) &&
                Number.isFinite(Number(pt.iy))
              ) {
                handles.push({
                  key: "i",
                  x: cx + Number(pt.ix),
                  y: cy + Number(pt.iy),
                });
              }
              return (
                <g key={`${m.id}-a${idx}`}>
                  {handles.map((h) => (
                    <g key={h.key}>
                      <line
                        x1={cx}
                        y1={cy}
                        x2={h.x}
                        y2={h.y}
                        stroke={anchorStroke}
                        strokeWidth={1}
                        vectorEffect="non-scaling-stroke"
                        style={{ pointerEvents: "none" }}
                      />
                      <ellipse
                        cx={h.x}
                        cy={h.y}
                        rx={handleArm.x}
                        ry={handleArm.y}
                        fill={anchorStroke}
                        stroke="#fff"
                        strokeWidth={1}
                        vectorEffect="non-scaling-stroke"
                        style={{ pointerEvents: "none" }}
                      />
                    </g>
                  ))}
                  <rect
                    x={cx - halfX}
                    y={cy - halfY}
                    width={halfX * 2}
                    height={halfY * 2}
                    fill={tool === "deleteAnchor" ? "#ef4444" : "#fff"}
                    stroke={anchorStroke}
                    strokeWidth={1}
                    vectorEffect="non-scaling-stroke"
                    shapeRendering="crispEdges"
                    style={{
                      pointerEvents: canDragAnchor ? "auto" : "none",
                      cursor: canDragAnchor ? "default" : undefined,
                    }}
                    onPointerDown={
                      canDragAnchor
                        ? (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (tool === "deleteAnchor") {
                              onDeleteAnchor?.(m.id, cx, cy);
                              return;
                            }
                            beginAnchorDrag(m, idx, e, {
                              lineEnds,
                              expandBox: isBoxShape,
                            });
                          }
                        : undefined
                    }
                  />
                </g>
              );
            });
          })}
      </svg>
    </Box>
  );
}

function AssetThumb({
  assetId,
  onClick,
  selected,
  zoom = 1,
  panX = 0.5,
  panY = 0.5,
  rotation = 0,
  fillSlot = false,
  transparentBg = false,
  /** "cover" (default) o "contain" — contain encaja la foto entera en la casilla */
  fit = "cover",
}) {
  const boxRef = useRef(null);
  const fetchAssetBlob = useContext(MoodboardAssetBlobContext);
  const [src, setSrc] = useState(() => peekMoodboardAssetBlobUrl(assetId));
  const [nat, setNat] = useState(null);
  const [boxPx, setBoxPx] = useState({ w: 0, h: 0 });

  useEffect(() => {
    let cancelled = false;
    const peeked = peekMoodboardAssetBlobUrl(assetId);
    if (peeked) setSrc(peeked);
    else setNat(null);
    (async () => {
      try {
        const url = await fetchAssetBlob(assetId);
        if (!cancelled) setSrc(url);
      } catch {
        if (!cancelled) setSrc("");
      }
    })();
    return () => {
      cancelled = true;
      // No revoke: la URL vive en cache compartida (cambio de mesa sin parpadeo)
    };
  }, [assetId, fetchAssetBlob]);

  useEffect(() => {
    if (!fillSlot) return undefined;
    const el = boxRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const apply = () => {
      // clientWidth/Height = tamaño de layout (pre-transform).
      // getBoundingClientRect es tamaño en pantalla: con scale() del tablero
      // la foto quedaba pequeña al activar la mesa (preview → editor).
      setBoxPx({ w: el.clientWidth, h: el.clientHeight });
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fillSlot, src]);

  const fitted =
    fillSlot && nat && boxPx.w > 1 && boxPx.h > 1
      ? rotatedImageLayout(
          nat.w,
          nat.h,
          { x: 0, y: 0, w: boxPx.w, h: boxPx.h },
          {
            zoom,
            panX,
            panY,
          },
          rotation,
        )
      : null;

  const objectFit = fit === "contain" ? "contain" : "cover";

  return (
    <Box
      ref={boxRef}
      onClick={(e) => onClick?.(e)}
      sx={{
        width: "100%",
        height: "100%",
        bgcolor: transparentBg
          ? fit === "contain"
            ? "#f0f0f0"
            : "transparent"
          : fillSlot
            ? "#ebebeb"
            : "action.hover",
        cursor: onClick ? "pointer" : "default",
        outline: selected ? "2px solid" : "none",
        outlineColor: "primary.main",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {src ? (
        <Box
          component="img"
          src={src}
          alt=""
          draggable={false}
          data-slot-img="1"
          onLoad={(e) => {
            const img = e.currentTarget;
            if (img?.naturalWidth && img?.naturalHeight) {
              setNat({ w: img.naturalWidth, h: img.naturalHeight });
            }
          }}
          onError={() => {
            // URL revocada / rota en cache → re-fetch
            invalidateMoodboardAssetBlob(assetId);
            setSrc("");
            fetchAssetBlob(assetId)
              .then((url) => setSrc(url || ""))
              .catch(() => setSrc(""));
          }}
          sx={
            fillSlot && fitted
              ? {
                  position: "absolute",
                  left: fitted.x,
                  top: fitted.y,
                  width: fitted.w,
                  height: fitted.h,
                  objectFit: "fill",
                  display: "block",
                  pointerEvents: "none",
                  userSelect: "none",
                  transform: normalizeRotation(rotation)
                    ? `rotate(${normalizeRotation(rotation)}deg)`
                    : undefined,
                  transformOrigin: "center center",
                }
              : {
                  width: "100%",
                  height: "100%",
                  objectFit,
                  objectPosition: "center center",
                  display: "block",
                  pointerEvents: "none",
                  opacity: fillSlot ? 0 : 1,
                }
          }
        />
      ) : (
        <Box sx={{ display: "grid", placeItems: "center", height: "100%" }}>
          <CircularProgress size={14} />
        </Box>
      )}
    </Box>
  );
}

function clamp01Local(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0.5;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

function normalizeCropUi(rect) {
  let x = clamp01Local(rect.x ?? 0);
  let y = clamp01Local(rect.y ?? 0);
  let w = clamp01Local(rect.w ?? 0.8);
  let h = clamp01Local(rect.h ?? 0.8);
  if (w < 0.05) w = 0.05;
  if (h < 0.05) h = 0.05;
  if (x + w > 1) x = Math.max(0, 1 - w);
  if (y + h > 1) y = Math.max(0, 1 - h);
  return { x, y, w: Math.min(w, 1 - x), h: Math.min(h, 1 - y) };
}

/** Interactive crop dialog — rect in normalized 0–1 of the image. */
function MoodboardCropDialog({ open, assetId, busy, onClose, onConfirm }) {
  const { t } = useTranslation();
  const fetchAssetBlob = useContext(MoodboardAssetBlobContext);
  const boxRef = useRef(null);
  const dragRef = useRef(null);
  const [src, setSrc] = useState("");
  const [rect, setRect] = useState({ x: 0.1, y: 0.1, w: 0.8, h: 0.8 });

  useEffect(() => {
    if (!open || !assetId) return undefined;
    let cancelled = false;
    setRect({ x: 0.1, y: 0.1, w: 0.8, h: 0.8 });
    const peeked = peekMoodboardAssetBlobUrl(assetId);
    if (peeked) setSrc(peeked);
    (async () => {
      try {
        const url = await fetchAssetBlob(assetId);
        if (!cancelled) setSrc(url);
      } catch {
        if (!cancelled) setSrc("");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, assetId, fetchAssetBlob]);

  useEffect(() => {
    if (!open) return undefined;
    const onMove = (e) => {
      const d = dragRef.current;
      const box = boxRef.current;
      if (!d || !box) return;
      const r = box.getBoundingClientRect();
      if (r.width < 2 || r.height < 2) return;
      const mx = (e.clientX - r.left) / r.width;
      const my = (e.clientY - r.top) / r.height;
      const dx = mx - d.startMx;
      const dy = my - d.startMy;
      const o = d.orig;
      let next = { ...o };
      if (d.mode === "move") {
        next.x = o.x + dx;
        next.y = o.y + dy;
      } else if (d.mode === "se") {
        next.w = o.w + dx;
        next.h = o.h + dy;
      } else if (d.mode === "nw") {
        next.x = o.x + dx;
        next.y = o.y + dy;
        next.w = o.w - dx;
        next.h = o.h - dy;
      } else if (d.mode === "ne") {
        next.y = o.y + dy;
        next.w = o.w + dx;
        next.h = o.h - dy;
      } else if (d.mode === "sw") {
        next.x = o.x + dx;
        next.w = o.w - dx;
        next.h = o.h + dy;
      }
      setRect(normalizeCropUi(next));
    };
    const onUp = () => {
      dragRef.current = null;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [open]);

  const startDrag = (mode, e) => {
    e.preventDefault();
    e.stopPropagation();
    const box = boxRef.current;
    if (!box) return;
    const r = box.getBoundingClientRect();
    dragRef.current = {
      mode,
      orig: { ...rect },
      startMx: (e.clientX - r.left) / r.width,
      startMy: (e.clientY - r.top) / r.height,
    };
  };

  const handlePts = [
    {
      key: "nw",
      left: `${rect.x * 100}%`,
      top: `${rect.y * 100}%`,
      cursor: "nwse-resize",
    },
    {
      key: "ne",
      left: `${(rect.x + rect.w) * 100}%`,
      top: `${rect.y * 100}%`,
      cursor: "nesw-resize",
    },
    {
      key: "sw",
      left: `${rect.x * 100}%`,
      top: `${(rect.y + rect.h) * 100}%`,
      cursor: "nesw-resize",
    },
    {
      key: "se",
      left: `${(rect.x + rect.w) * 100}%`,
      top: `${(rect.y + rect.h) * 100}%`,
      cursor: "nwse-resize",
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={() => !busy && onClose?.()}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle fontWeight={800}>
        {t("skillAgent.moodboard.dialog.cropTitle")}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t("skillAgent.moodboard.dialog.cropBodyReplace")}
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            bgcolor: "#111",
            borderRadius: 2,
            overflow: "auto",
            maxHeight: "60vh",
            p: 1,
          }}
        >
          <Box
            ref={boxRef}
            sx={{
              position: "relative",
              display: "inline-block",
              maxWidth: "100%",
              userSelect: "none",
              touchAction: "none",
              lineHeight: 0,
            }}
          >
            {src ? (
              <Box
                component="img"
                src={src}
                alt=""
                draggable={false}
                sx={{
                  display: "block",
                  maxWidth: "100%",
                  maxHeight: "56vh",
                  width: "auto",
                  height: "auto",
                  pointerEvents: "none",
                }}
              />
            ) : (
              <Box
                sx={{ display: "grid", placeItems: "center", py: 8, px: 10 }}
              >
                <CircularProgress size={28} />
              </Box>
            )}
            {src ? (
              <>
                <Box
                  onPointerDown={(e) => startDrag("move", e)}
                  sx={{
                    position: "absolute",
                    left: `${rect.x * 100}%`,
                    top: `${rect.y * 100}%`,
                    width: `${rect.w * 100}%`,
                    height: `${rect.h * 100}%`,
                    border: "2px solid #fff",
                    boxShadow: "0 0 0 9999px rgba(0,0,0,0.45)",
                    cursor: "move",
                    zIndex: 2,
                  }}
                />
                {handlePts.map((h) => (
                  <Box
                    key={h.key}
                    onPointerDown={(e) => startDrag(h.key, e)}
                    sx={{
                      position: "absolute",
                      left: h.left,
                      top: h.top,
                      width: 14,
                      height: 14,
                      ml: "-7px",
                      mt: "-7px",
                      bgcolor: "#fff",
                      border: "1px solid #333",
                      borderRadius: 0.5,
                      cursor: h.cursor,
                      zIndex: 3,
                    }}
                  />
                ))}
              </>
            ) : null}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>
          {t("skillAgent.common.cancel")}
        </Button>
        <Button
          variant="contained"
          disabled={busy || !src}
          onClick={() => onConfirm?.(normalizeCropUi(rect))}
        >
          {busy
            ? t("skillAgent.moodboard.dialog.cropping")
            : t("skillAgent.moodboard.dialog.cropSubmit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PoolThumb({
  item,
  checked,
  onToggle,
  height,
  aspectRatio = "3 / 4",
  alreadyInBoard = false,
}) {
  const { t } = useTranslation();
  const [src, setSrc] = useState("");
  const source = String(item?.source || "search");

  useEffect(() => {
    let revoked = false;
    let url = "";
    let blobUrl = false;
    (async () => {
      try {
        if (source === "meeting") {
          url = meetingMediaUrl(item.SourceUrl) || "";
          blobUrl = false;
        } else if (source === "sketch") {
          url = await fetchSkillAgentMoodboardAssetBlobUrl(item.id);
          blobUrl = true;
        } else {
          url = await fetchSkillAgentSearchItemBlobUrl(item.id);
          blobUrl = true;
        }
        if (!revoked) setSrc(url);
      } catch {
        if (!revoked) setSrc("");
      }
    })();
    return () => {
      revoked = true;
      if (blobUrl && url) URL.revokeObjectURL(url);
    };
  }, [item.id, item.SourceUrl, source]);

  const fixedH = Number(height) > 0 ? Number(height) : null;

  return (
    <Box
      onClick={() => {
        if (alreadyInBoard) return;
        onToggle?.(!checked);
      }}
      title={
        alreadyInBoard
          ? t("skillAgent.moodboard.poolAlreadyInBoard")
          : checked
            ? t("skillAgent.moodboard.unmarkSelection")
            : t("skillAgent.moodboard.markToAdd")
      }
      sx={{
        position: "relative",
        borderRadius: 1.5,
        overflow: "hidden",
        border: "2px solid",
        borderColor: checked
          ? "primary.main"
          : alreadyInBoard
            ? "transparent"
            : "divider",
        cursor: alreadyInBoard ? "default" : "pointer",
        bgcolor: "#eee",
        width: "100%",
        ...(fixedH
          ? {
              height: fixedH,
              minHeight: fixedH,
              maxHeight: fixedH,
            }
          : { aspectRatio }),
        opacity: alreadyInBoard ? 0.45 : 1,
      }}
    >
      {!alreadyInBoard ? (
        <Checkbox
          size="small"
          checked={checked}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onToggle?.(e.target.checked)}
          sx={{
            position: "absolute",
            top: 4,
            left: 4,
            zIndex: 2,
            p: 0.25,
            bgcolor: alpha("#fff", 0.92),
          }}
        />
      ) : (
        <Chip
          size="small"
          label={t("skillAgent.moodboard.onBoardChip")}
          sx={{
            position: "absolute",
            top: 6,
            left: 6,
            zIndex: 2,
            height: 20,
            fontSize: "0.65rem",
            fontWeight: 700,
            bgcolor: alpha("#fff", 0.92),
          }}
        />
      )}
      {src ? (
        <Box
          component="img"
          src={src}
          alt={item.Title || item.BrandName || ""}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center top",
            display: "block",
          }}
        />
      ) : (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
          }}
        >
          <CircularProgress size={18} />
        </Box>
      )}
      <Typography
        variant="caption"
        noWrap
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1,
          px: 0.75,
          py: 0.4,
          fontSize: "0.68rem",
          fontWeight: 700,
          color: "#fff",
          bgcolor: "rgba(0,0,0,0.55)",
          lineHeight: 1.25,
        }}
      >
        {item.BrandName || item.Title || `#${item.id}`}
        {item.SearchTitulo ? ` · ${item.SearchTitulo}` : ""}
      </Typography>
    </Box>
  );
}

/** Dialog: marcar fotos de SEARCH del cliente/temporada y añadirlas al board. */
function MoodboardAddPhotosDialog({
  open,
  onClose,
  busy,
  pool,
  poolLoading,
  poolError,
  usedSearchItemIds,
  pick,
  setPick,
  onConfirm,
  seasonLabel,
  clientLabel,
  projectMode = false,
  sourceFilter = "sketch",
  onSourceFilterChange,
}) {
  const { t } = useTranslation();
  const [searchFilter, setSearchFilter] = useState("all");
  /** all | chosen (ya en board) | notChosen */
  const [placeFilter, setPlaceFilter] = useState("all");
  const [showCount, setShowCount] = useState(36);

  useEffect(() => {
    if (!open) return;
    setSearchFilter("all");
    setPlaceFilter("all");
    setShowCount(36);
  }, [open]);

  const searches = pool?.searches || [];

  const items = useMemo(() => {
    let list = pool?.items || [];
    if (projectMode && sourceFilter && sourceFilter !== "all") {
      list = list.filter((it) => String(it.source) === String(sourceFilter));
    }
    if (!projectMode && searchFilter !== "all") {
      list = list.filter((it) => Number(it.SearchId) === Number(searchFilter));
    }
    const used =
      usedSearchItemIds instanceof Set ? usedSearchItemIds : new Set();
    if (placeFilter === "chosen") {
      list = list.filter((it) =>
        projectMode
          ? Boolean(it.alreadyOnBoard) || used.has(Number(it.id))
          : used.has(Number(it.id)),
      );
    } else if (placeFilter === "notChosen") {
      list = list.filter((it) =>
        projectMode
          ? !it.alreadyOnBoard && !used.has(Number(it.id))
          : !used.has(Number(it.id)),
      );
    }
    return list;
  }, [
    pool?.items,
    searchFilter,
    placeFilter,
    usedSearchItemIds,
    projectMode,
    sourceFilter,
  ]);

  const pickCount = pick?.size || 0;
  const usedIds =
    usedSearchItemIds instanceof Set ? usedSearchItemIds : new Set();
  const pickKeyFor = (it) =>
    projectMode ? `${it.source}:${it.id}` : Number(it.id);

  return (
    <Dialog
      open={open}
      onClose={() => !busy && onClose?.()}
      maxWidth="lg"
      fullWidth
      slotProps={{
        backdrop: { sx: moodboardDialogBackdropSx },
      }}
      BackdropProps={{ sx: moodboardDialogBackdropSx }}
      PaperProps={{ sx: { borderRadius: 3, maxHeight: "92vh" } }}
    >
      <DialogTitle
        sx={{
          pb: 1.25,
          pt: 2,
          flexShrink: 0,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={2}
          useFlexGap
          flexWrap="wrap"
        >
          <Box sx={{ minWidth: 0, flex: "1 1 220px" }}>
            <Typography variant="h6" fontWeight={800} component="div">
              {projectMode
                ? t("skillAgent.moodboard.dialog.addProjectPhotosTitle", {
                    defaultValue: "Añadir del proyecto",
                  })
                : t("skillAgent.moodboard.dialog.addPhotosTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {projectMode ? (
                t("skillAgent.moodboard.dialog.addProjectPhotosLead", {
                  defaultValue: "Sketch, meeting y SEARCH (sin Recaps).",
                })
              ) : (
                <>
                  SEARCH de{" "}
                  <strong>
                    {clientLabel ||
                      t("skillAgent.moodboard.dialog.addPhotosClientFallback")}
                  </strong>
                  {seasonLabel ? (
                    <>
                      {" "}
                      · <strong>{seasonLabel}</strong>
                    </>
                  ) : null}
                </>
              )}
            </Typography>
          </Box>
          <Stack
            spacing={0.75}
            sx={{
              flex: "0 1 auto",
              alignItems: { xs: "flex-start", sm: "flex-end" },
              ml: { sm: "auto" },
            }}
          >
            {projectMode ? (
              <Stack
                direction="row"
                spacing={0.75}
                useFlexGap
                flexWrap="wrap"
                justifyContent={{ xs: "flex-start", sm: "flex-end" }}
              >
                {[
                  {
                    id: "sketch",
                    label: t("skillAgent.moodboard.dialog.filterSketch", {
                      defaultValue: "Sketch",
                    }),
                  },
                  {
                    id: "meeting",
                    label: t("skillAgent.moodboard.dialog.filterMeeting", {
                      defaultValue: "Meeting",
                    }),
                  },
                  {
                    id: "search",
                    label: t("skillAgent.moodboard.dialog.filterSearch", {
                      defaultValue: "SEARCH",
                    }),
                  },
                  {
                    id: "all",
                    label: t("skillAgent.moodboard.dialog.filterAll"),
                  },
                ].map((opt) => (
                  <Chip
                    key={opt.id}
                    size="small"
                    label={opt.label}
                    color={sourceFilter === opt.id ? "secondary" : "default"}
                    variant={sourceFilter === opt.id ? "filled" : "outlined"}
                    onClick={() => {
                      onSourceFilterChange?.(opt.id);
                      setShowCount(36);
                    }}
                    sx={{ fontWeight: 700 }}
                  />
                ))}
              </Stack>
            ) : null}
            <Stack
              direction="row"
              spacing={0.75}
              useFlexGap
              flexWrap="wrap"
              justifyContent={{ xs: "flex-start", sm: "flex-end" }}
            >
              {[
                {
                  id: "all",
                  label: t("skillAgent.moodboard.dialog.filterAll"),
                },
                {
                  id: "chosen",
                  label: t("skillAgent.moodboard.dialog.filterChosen"),
                },
                {
                  id: "notChosen",
                  label: t("skillAgent.moodboard.dialog.filterNotChosen"),
                },
              ].map((opt) => (
                <Chip
                  key={opt.id}
                  size="small"
                  label={opt.label}
                  color={placeFilter === opt.id ? "primary" : "default"}
                  variant={placeFilter === opt.id ? "filled" : "outlined"}
                  onClick={() => {
                    setPlaceFilter(opt.id);
                    setShowCount(36);
                  }}
                  sx={{ fontWeight: 700 }}
                />
              ))}
            </Stack>
            {!projectMode && searches.length > 1 ? (
              <Stack
                direction="row"
                spacing={0.75}
                useFlexGap
                flexWrap="wrap"
                justifyContent={{ xs: "flex-start", sm: "flex-end" }}
              >
                <Chip
                  size="small"
                  label={t("skillAgent.moodboard.dialog.filterAll")}
                  color={searchFilter === "all" ? "secondary" : "default"}
                  variant={searchFilter === "all" ? "filled" : "outlined"}
                  onClick={() => {
                    setSearchFilter("all");
                    setShowCount(36);
                  }}
                  sx={{ fontWeight: 700 }}
                />
                {searches.map((s) => (
                  <Chip
                    key={s.id}
                    size="small"
                    label={
                      s.Titulo ||
                      t("skillAgent.moodboard.dialog.searchChip", { id: s.id })
                    }
                    color={
                      Number(searchFilter) === Number(s.id)
                        ? "secondary"
                        : "default"
                    }
                    variant={
                      Number(searchFilter) === Number(s.id)
                        ? "filled"
                        : "outlined"
                    }
                    onClick={() => {
                      setSearchFilter(String(s.id));
                      setShowCount(36);
                    }}
                    sx={{ fontWeight: 700, maxWidth: 180 }}
                  />
                ))}
              </Stack>
            ) : null}
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ px: 2, py: 1.5 }}>
        {poolError ? (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            {poolError}
          </Alert>
        ) : null}

        {poolLoading ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
            <CircularProgress size={28} />
          </Box>
        ) : !items.length ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography fontWeight={700} color="text.secondary">
              {placeFilter === "chosen"
                ? t("skillAgent.moodboard.dialog.filterChosenEmpty")
                : placeFilter === "notChosen"
                  ? t("skillAgent.moodboard.dialog.filterNotChosenEmptyAdd")
                  : t("skillAgent.moodboard.dialog.addPhotosEmpty")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {placeFilter === "all"
                ? t("skillAgent.moodboard.dialog.addPhotosEmptyHint")
                : t("skillAgent.moodboard.dialog.filterEmptyHint")}
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, 1fr)",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                },
                gap: 1.25,
              }}
            >
              {items.slice(0, showCount).map((it) => {
                const already = projectMode
                  ? Boolean(it.alreadyOnBoard) || usedIds.has(Number(it.id))
                  : usedIds.has(Number(it.id));
                const key = pickKeyFor(it);
                return (
                  <PoolThumb
                    key={String(key)}
                    item={it}
                    alreadyInBoard={already}
                    checked={!already && pick.has(key)}
                    onToggle={(on) => {
                      setPick((prev) => {
                        const next = new Set(prev);
                        if (on) next.add(key);
                        else next.delete(key);
                        return next;
                      });
                    }}
                  />
                );
              })}
            </Box>
            {items.length > showCount ? (
              <Button
                size="small"
                sx={{ mt: 1.5, textTransform: "none" }}
                onClick={() => setShowCount((n) => n + 36)}
              >
                {t("skillAgent.moodboard.dialog.loadMore", {
                  n: items.length - showCount,
                })}
              </Button>
            ) : null}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.75, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={busy}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          {t("skillAgent.common.cancel")}
        </Button>
        <Button
          variant="contained"
          disabled={busy || pickCount === 0}
          onClick={onConfirm}
          sx={{ textTransform: "none", fontWeight: 800 }}
        >
          {busy
            ? t("skillAgent.moodboard.dialog.adding")
            : pickCount
              ? t("skillAgent.moodboard.dialog.addN", { n: pickCount })
              : t("skillAgent.moodboard.dialog.add")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Dialog: fotos de la reunión ligada al moodboard (mismo UX que SEARCH). */
function MoodboardAddMeetingPhotosDialog({
  open,
  onClose,
  busy,
  pool,
  poolLoading,
  poolError,
  pick,
  setPick,
  onConfirm,
}) {
  const { t } = useTranslation();
  /** all | chosen (ya en board) | notChosen */
  const [placeFilter, setPlaceFilter] = useState("all");
  const [showCount, setShowCount] = useState(36);

  useEffect(() => {
    if (!open) return;
    setPlaceFilter("all");
    setShowCount(36);
  }, [open]);

  const allItems = Array.isArray(pool?.items) ? pool.items : [];
  const items = useMemo(() => {
    if (placeFilter === "chosen") {
      return allItems.filter((it) => Boolean(it.alreadyOnBoard));
    }
    if (placeFilter === "notChosen") {
      return allItems.filter((it) => !it.alreadyOnBoard);
    }
    return allItems;
  }, [allItems, placeFilter]);

  const pickCount = pick?.size || 0;
  const sessionLabel =
    pool?.session?.title ||
    pool?.session?.customerName ||
    (pool?.MeetingSessionId
      ? t("skillAgent.moodboard.dialog.meetingChip", {
          id: pool.MeetingSessionId,
        })
      : "");

  return (
    <Dialog
      open={open}
      onClose={() => !busy && onClose?.()}
      maxWidth="lg"
      fullWidth
      slotProps={{
        backdrop: { sx: moodboardDialogBackdropSx },
      }}
      BackdropProps={{ sx: moodboardDialogBackdropSx }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: "92vh",
          minHeight: { md: "78vh" },
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1.25,
          pt: 2,
          flexShrink: 0,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={2}
          useFlexGap
          flexWrap="wrap"
        >
          <Box sx={{ minWidth: 0, flex: "1 1 220px" }}>
            <Typography variant="h6" fontWeight={800} component="div">
              {t("skillAgent.moodboard.dialog.addMeetingTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {sessionLabel
                ? t("skillAgent.moodboard.dialog.addMeetingSubtitle", {
                    meeting: sessionLabel,
                  })
                : t("skillAgent.moodboard.dialog.addMeetingSubtitleEmpty")}
            </Typography>
          </Box>
          <Stack
            direction="row"
            spacing={0.75}
            useFlexGap
            flexWrap="wrap"
            justifyContent={{ xs: "flex-start", sm: "flex-end" }}
            sx={{
              flex: "0 1 auto",
              ml: { sm: "auto" },
            }}
          >
            {[
              {
                id: "all",
                label: t("skillAgent.moodboard.dialog.filterAll"),
              },
              {
                id: "chosen",
                label: t("skillAgent.moodboard.dialog.filterChosen"),
              },
              {
                id: "notChosen",
                label: t("skillAgent.moodboard.dialog.filterNotChosen"),
              },
            ].map((opt) => (
              <Chip
                key={opt.id}
                size="small"
                label={opt.label}
                color={placeFilter === opt.id ? "primary" : "default"}
                variant={placeFilter === opt.id ? "filled" : "outlined"}
                onClick={() => {
                  setPlaceFilter(opt.id);
                  setShowCount(36);
                }}
                sx={{ fontWeight: 700 }}
              />
            ))}
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ px: 2, py: 1.5 }}>
        {poolError ? (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            {poolError}
          </Alert>
        ) : null}
        {poolLoading ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
            <CircularProgress size={28} />
          </Box>
        ) : !items.length ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography fontWeight={700} color="text.secondary">
              {placeFilter === "chosen"
                ? t("skillAgent.moodboard.dialog.filterChosenEmpty")
                : placeFilter === "notChosen"
                  ? t("skillAgent.moodboard.dialog.filterNotChosenEmptyAdd")
                  : t("skillAgent.moodboard.dialog.addMeetingEmpty")}
            </Typography>
            {placeFilter !== "all" ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {t("skillAgent.moodboard.dialog.filterEmptyHint")}
              </Typography>
            ) : null}
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, 1fr)",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                },
                gap: 1.25,
              }}
            >
              {items.slice(0, showCount).map((item) => {
                const id = Number(item.id);
                const already = Boolean(item.alreadyOnBoard);
                const selected = !already && Boolean(pick?.has(id));
                const src = meetingMediaUrl(item.SourceUrl);
                return (
                  <Box
                    key={id}
                    onClick={() => {
                      if (already || busy) return;
                      setPick((prev) => {
                        const next = new Set(prev);
                        if (next.has(id)) next.delete(id);
                        else next.add(id);
                        return next;
                      });
                    }}
                    title={
                      already
                        ? t("skillAgent.moodboard.poolAlreadyInBoard")
                        : selected
                          ? t("skillAgent.moodboard.unmarkSelection")
                          : t("skillAgent.moodboard.markToAdd")
                    }
                    sx={{
                      position: "relative",
                      borderRadius: 1.5,
                      overflow: "hidden",
                      border: "2px solid",
                      borderColor: selected
                        ? "primary.main"
                        : already
                          ? "transparent"
                          : "divider",
                      cursor: already || busy ? "default" : "pointer",
                      bgcolor: "#eee",
                      width: "100%",
                      aspectRatio: "3 / 4",
                      opacity: already ? 0.45 : 1,
                      boxShadow: selected
                        ? `0 0 0 1px ${alpha("#0ea5e9", 0.35)}`
                        : "none",
                    }}
                  >
                    {src ? (
                      <Box
                        component="img"
                        src={src}
                        alt={item.Title || ""}
                        sx={{
                          position: "absolute",
                          inset: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          objectPosition: "center top",
                          display: "block",
                          filter: selected ? "brightness(0.82)" : "none",
                        }}
                      />
                    ) : null}
                    {selected ? (
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          bgcolor: alpha("#0ea5e9", 0.18),
                          pointerEvents: "none",
                          zIndex: 1,
                        }}
                      />
                    ) : null}
                    {!already ? (
                      <Checkbox
                        size="small"
                        checked={selected}
                        disabled={busy}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          setPick((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(id);
                            else next.delete(id);
                            return next;
                          });
                        }}
                        sx={{
                          position: "absolute",
                          top: 4,
                          left: 4,
                          zIndex: 2,
                          p: 0.25,
                          bgcolor: alpha("#fff", 0.92),
                        }}
                      />
                    ) : (
                      <Chip
                        size="small"
                        label={t("skillAgent.moodboard.onBoardChip")}
                        sx={{
                          position: "absolute",
                          top: 6,
                          left: 6,
                          zIndex: 2,
                          height: 20,
                          fontSize: "0.65rem",
                          fontWeight: 700,
                          bgcolor: alpha("#fff", 0.92),
                        }}
                      />
                    )}
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 1,
                        px: 0.75,
                        py: 0.4,
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        color: "#fff",
                        bgcolor: "rgba(0,0,0,0.55)",
                        lineHeight: 1.25,
                      }}
                    >
                      {item.Title || `#${id}`}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
            {items.length > showCount ? (
              <Button
                size="small"
                sx={{ mt: 1.5, textTransform: "none" }}
                onClick={() => setShowCount((n) => n + 36)}
              >
                {t("skillAgent.moodboard.dialog.loadMore", {
                  n: items.length - showCount,
                })}
              </Button>
            ) : null}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.75, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={busy}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          {t("skillAgent.common.cancel")}
        </Button>
        <Button
          variant="contained"
          disabled={busy || pickCount === 0}
          onClick={onConfirm}
          sx={{ textTransform: "none", fontWeight: 800 }}
        >
          {busy
            ? t("skillAgent.moodboard.dialog.adding")
            : pickCount
              ? t("skillAgent.moodboard.dialog.addN", { n: pickCount })
              : t("skillAgent.moodboard.dialog.add")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Dialog: fotos ya en el board — marcar, colocar en página, quitar. */
function MoodboardBoardPhotosDialog({
  open,
  onClose,
  assets,
  selectedIds,
  usedOnPagesIds,
  onToggleSelect,
  onClearSelect,
  onPlaceOne,
  onPlaceSelected,
  onPlaceOnPasteboard,
  onStampNotes,
  onRemove,
  pageCount = 1,
  placePageIdx = 0,
  onPlacePageChange,
  busy,
}) {
  const { t } = useTranslation();
  const selected = selectedIds instanceof Set ? selectedIds : new Set();
  const usedOnPages =
    usedOnPagesIds instanceof Set ? usedOnPagesIds : new Set();
  const nSel = selected.size;
  const pagesTotal = Math.max(1, Number(pageCount) || 1);
  /** all | chosen (ya en página) | notChosen — bandeja = sin colocar */
  const [placeFilter, setPlaceFilter] = useState("notChosen");

  useEffect(() => {
    if (!open) return;
    setPlaceFilter("notChosen");
  }, [open]);

  const visibleAssets = useMemo(() => {
    const list = Array.isArray(assets) ? assets : [];
    if (placeFilter === "chosen") {
      return list.filter((a) => usedOnPages.has(Number(a.id)));
    }
    if (placeFilter === "notChosen") {
      return list.filter((a) => !usedOnPages.has(Number(a.id)));
    }
    return list;
  }, [assets, placeFilter, usedOnPagesIds]); // eslint-disable-line react-hooks/exhaustive-deps -- Set identity from parent

  return (
    <Dialog
      open={open}
      onClose={() => !busy && onClose?.()}
      maxWidth="lg"
      fullWidth
      slotProps={{
        backdrop: { sx: moodboardDialogBackdropSx },
      }}
      BackdropProps={{ sx: moodboardDialogBackdropSx }}
      PaperProps={{ sx: { borderRadius: 3, maxHeight: "92vh" } }}
    >
      <DialogTitle
        sx={{
          pb: 1.25,
          pt: 2,
          flexShrink: 0,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={2}
          flexWrap="wrap"
          useFlexGap
        >
          <Box sx={{ minWidth: 0, flex: "1 1 220px" }}>
            <Typography variant="h6" fontWeight={800} component="div">
              {t("skillAgent.moodboard.dialog.boardPhotosTitle", {
                n:
                  placeFilter === "notChosen"
                    ? visibleAssets.length
                    : assets.length,
              })}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t("skillAgent.moodboard.dialog.boardPhotosHintLong")}
            </Typography>
          </Box>
          <Stack
            spacing={0.75}
            sx={{
              flex: "0 1 auto",
              alignItems: { xs: "flex-start", sm: "flex-end" },
              ml: { sm: "auto" },
            }}
          >
            {nSel > 0 ? (
              <Chip
                size="small"
                color="primary"
                label={t("skillAgent.moodboard.dialog.selectedChip", {
                  n: nSel,
                })}
                onDelete={onClearSelect}
                sx={{ fontWeight: 700 }}
              />
            ) : null}
            {assets.length > 0 ? (
              <Stack
                direction="row"
                spacing={0.75}
                useFlexGap
                flexWrap="wrap"
                justifyContent={{ xs: "flex-start", sm: "flex-end" }}
              >
                {[
                  {
                    id: "all",
                    label: t("skillAgent.moodboard.dialog.filterAll"),
                  },
                  {
                    id: "chosen",
                    label: t("skillAgent.moodboard.dialog.filterChosen"),
                  },
                  {
                    id: "notChosen",
                    label: t("skillAgent.moodboard.dialog.filterNotChosen"),
                  },
                ].map((opt) => (
                  <Chip
                    key={opt.id}
                    size="small"
                    label={opt.label}
                    color={placeFilter === opt.id ? "primary" : "default"}
                    variant={placeFilter === opt.id ? "filled" : "outlined"}
                    onClick={() => setPlaceFilter(opt.id)}
                    sx={{ fontWeight: 700 }}
                  />
                ))}
              </Stack>
            ) : null}
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ px: 2, py: 1.5 }}>
        {!assets.length ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography fontWeight={700} color="text.secondary">
              {t("skillAgent.moodboard.dialog.boardPhotosEmpty")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t("skillAgent.moodboard.dialog.boardPhotosEmptyHint")}
            </Typography>
          </Box>
        ) : !visibleAssets.length ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography fontWeight={700} color="text.secondary">
              {placeFilter === "chosen"
                ? t("skillAgent.moodboard.dialog.filterChosenEmptyBoard")
                : t("skillAgent.moodboard.dialog.filterNotChosenEmptyBoard")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t("skillAgent.moodboard.dialog.filterEmptyHint")}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, 1fr)",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
              },
              gap: 1.5,
            }}
          >
            {visibleAssets.map((a) => {
              const id = Number(a.id);
              const isSelected = selected.has(id);
              const alreadyOnPage = usedOnPages.has(id);
              const note = assetTeamNote(a);
              const isCutout =
                String(a.Kind || "").toLowerCase() === "cutout" ||
                /\(\s*sin fondo\s*\)/i.test(String(a.Title || ""));
              return (
                <Box
                  key={a.id}
                  title={
                    note
                      ? note
                      : alreadyOnPage
                        ? t("skillAgent.moodboard.alreadyOnPageTitle")
                        : undefined
                  }
                  sx={{
                    position: "relative",
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "2px solid",
                    borderColor: isSelected ? "primary.main" : "divider",
                    bgcolor: "#f3f3f3",
                    aspectRatio: "3 / 4",
                    opacity: alreadyOnPage ? 0.45 : 1,
                    boxShadow: isSelected
                      ? `0 0 0 1px ${alpha("#0ea5e9", 0.35)}`
                      : "none",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      cursor: "pointer",
                    }}
                    onClick={() => onPlaceOne?.(a.id)}
                  >
                    <AssetThumb
                      assetId={a.id}
                      selected={isSelected}
                      transparentBg
                      fit="contain"
                    />
                  </Box>
                  <IconButton
                    size="small"
                    aria-label={
                      isSelected
                        ? t("skillAgent.moodboard.unmarkSelection")
                        : t("skillAgent.moodboard.markPhoto")
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleSelect?.(a.id);
                    }}
                    sx={{
                      position: "absolute",
                      top: 6,
                      left: 6,
                      zIndex: 2,
                      width: 30,
                      height: 30,
                      bgcolor: alpha("#fff", 0.95),
                      boxShadow: 1,
                      color: isSelected ? "primary.main" : "text.secondary",
                      "&:hover": { bgcolor: "#fff" },
                    }}
                  >
                    {isSelected ? (
                      <CheckBoxOutlinedIcon sx={{ fontSize: 18 }} />
                    ) : (
                      <CheckBoxOutlineBlankOutlinedIcon sx={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label={t("skillAgent.moodboard.removePhotoAria")}
                    disabled={busy}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove?.(a.id);
                    }}
                    sx={{
                      position: "absolute",
                      top: 6,
                      right: 6,
                      zIndex: 2,
                      width: 30,
                      height: 30,
                      bgcolor: alpha("#fff", 0.95),
                      boxShadow: 1,
                      color: "error.main",
                      "&:hover": { bgcolor: "#fff" },
                    }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                  {isCutout ? (
                    <Chip
                      size="small"
                      label={t("skillAgent.moodboard.dialog.cutoutChip")}
                      sx={{
                        position: "absolute",
                        bottom: note ? 36 : 8,
                        left: 8,
                        zIndex: 2,
                        height: 22,
                        fontWeight: 700,
                        fontSize: "0.68rem",
                        bgcolor: alpha("#fff", 0.92),
                      }}
                    />
                  ) : null}
                  {note ? (
                    <Box
                      sx={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 2,
                        px: 0.75,
                        py: 0.5,
                        bgcolor: alpha("#0f172a", 0.72),
                        color: "#fff",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.65rem",
                          fontWeight: 700,
                          lineHeight: 1.25,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {note}
                      </Typography>
                    </Box>
                  ) : null}
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: 2.5,
          py: 1.75,
          gap: 1,
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
      >
        {typeof onStampNotes === "function" ? (
          <Button
            variant="outlined"
            color="info"
            disabled={busy}
            onClick={onStampNotes}
            sx={{ textTransform: "none", fontWeight: 800 }}
          >
            {t("skillAgent.moodboard.dialog.stampNotes")}
          </Button>
        ) : (
          <Box />
        )}
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            onClick={onClose}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {t("skillAgent.common.close")}
          </Button>
          {onPlaceOnPasteboard ? (
            <Button
              variant="outlined"
              disabled={!nSel || busy}
              startIcon={<CropLandscapeOutlinedIcon />}
              onClick={onPlaceOnPasteboard}
              sx={{ textTransform: "none", fontWeight: 800 }}
            >
              {nSel ? `Al tablero (${nSel})` : "Al tablero"}
            </Button>
          ) : null}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "stretch",
              borderRadius: 2,
              overflow: "hidden",
              bgcolor: "primary.main",
              boxShadow: 1,
              opacity: nSel ? 1 : 0.72,
            }}
          >
            <Button
              disabled={!nSel}
              startIcon={<DashboardCustomizeOutlinedIcon />}
              onClick={onPlaceSelected}
              sx={{
                color: "#fff",
                textTransform: "none",
                fontWeight: 800,
                borderRadius: 0,
                px: 2,
                py: 0.85,
                boxShadow: "none",
                "&:hover": { bgcolor: "primary.dark", boxShadow: "none" },
                "&.Mui-disabled": {
                  color: "rgba(255,255,255,0.75)",
                },
              }}
            >
              {nSel
                ? t("skillAgent.moodboard.dialog.placeN", { n: nSel })
                : t("skillAgent.moodboard.dialog.placeOne")}
            </Button>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ borderColor: "rgba(255,255,255,0.35)" }}
            />
            <Select
              value={Math.min(
                pagesTotal - 1,
                Math.max(0, Number(placePageIdx) || 0),
              )}
              onChange={(e) => onPlacePageChange?.(Number(e.target.value))}
              variant="standard"
              disableUnderline
              aria-label={t("skillAgent.moodboard.dialog.placePageLabel")}
              sx={{
                color: "#fff",
                fontWeight: 800,
                alignSelf: "stretch",
                px: 1.25,
                "& .MuiSelect-select": {
                  py: 1.15,
                  pr: "28px !important",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiSelect-icon": { color: "#fff", right: 4 },
              }}
              MenuProps={{
                PaperProps: { sx: { borderRadius: 2, mt: 0.5 } },
              }}
            >
              {Array.from({ length: pagesTotal }, (_, i) => (
                <MenuItem key={i} value={i}>
                  {t("skillAgent.moodboard.dialog.placePageOption", {
                    n: i + 1,
                  })}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

function slotsBBox(slots) {
  const list = Array.isArray(slots) ? slots : [];
  if (!list.length) return null;
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const s of list) {
    x0 = Math.min(x0, Number(s.x) || 0);
    y0 = Math.min(y0, Number(s.y) || 0);
    x1 = Math.max(x1, (Number(s.x) || 0) + (Number(s.w) || 0));
    y1 = Math.max(y1, (Number(s.y) || 0) + (Number(s.h) || 0));
  }
  return { x: x0, y: y0, w: Math.max(0, x1 - x0), h: Math.max(0, y1 - y0) };
}

function scaleSlotsWithBBox(origins, bbox0, bbox1) {
  const sx = bbox0.w > 1e-6 ? bbox1.w / bbox0.w : 1;
  const sy = bbox0.h > 1e-6 ? bbox1.h / bbox0.h : 1;
  return origins.map((o) =>
    snapSlot(
      clampSlot({
        ...o,
        x: bbox1.x + (o.x - bbox0.x) * sx,
        y: bbox1.y + (o.y - bbox0.y) * sy,
        w: Math.max(MIN_SLOT, o.w * sx),
        h: Math.max(MIN_SLOT, o.h * sy),
      }),
    ),
  );
}

/** Rota casillas alrededor del centro del bbox (Free Transform). */
function rotateSlotsAroundBBox(origins, bbox0, deltaDeg) {
  const cx = bbox0.x + bbox0.w / 2;
  const cy = bbox0.y + bbox0.h / 2;
  const rad = ((Number(deltaDeg) || 0) * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return origins.map((o) => {
    const ocx = (Number(o.x) || 0) + (Number(o.w) || 0) / 2;
    const ocy = (Number(o.y) || 0) + (Number(o.h) || 0) / 2;
    const dx = ocx - cx;
    const dy = ocy - cy;
    const nx = cx + dx * cos - dy * sin;
    const ny = cy + dx * sin + dy * cos;
    const w = Number(o.w) || 0;
    const h = Number(o.h) || 0;
    return clampSlotAllowOutside({
      ...o,
      x: nx - w / 2,
      y: ny - h / 2,
      rotation: normalizeRotation(
        (Number(o.rotation) || 0) + (Number(deltaDeg) || 0),
      ),
    });
  });
}

/**
 * Shear por handle de lado (VC Free Transform + Alt/Cmd en lados).
 * e/w → dx visual según (y-cy); n/s → dy según (x-cx).
 */
function shearSlotsInBBox(origins, bbox0, handle, dx, dy) {
  const cx = bbox0.x + bbox0.w / 2;
  const cy = bbox0.y + bbox0.h / 2;
  const hw = Math.max(bbox0.w, 0.02);
  const hh = Math.max(bbox0.h, 0.02);
  return origins.map((o) => {
    const ocx = (Number(o.x) || 0) + (Number(o.w) || 0) / 2;
    const ocy = (Number(o.y) || 0) + (Number(o.h) || 0) / 2;
    let x = Number(o.x) || 0;
    let y = Number(o.y) || 0;
    if (handle === "e" || handle === "w") {
      const k = (dy / hh) * (handle === "e" ? 1 : -1);
      x += (ocy - cy) * k;
    } else if (handle === "n" || handle === "s") {
      const k = (dx / hw) * (handle === "s" ? 1 : -1);
      y += (ocx - cx) * k;
    }
    return clampSlotAllowOutside({ ...o, x, y });
  });
}

function rectsIntersect(a, b) {
  return !(
    a.x + a.w < b.x ||
    b.x + b.w < a.x ||
    a.y + a.h < b.y ||
    b.y + b.h < a.y
  );
}

function FreeformSlotBox({
  slot,
  selected,
  multiSelected = false,
  multiSelectMode = false,
  panInsideMode = false,
  /** Mano / Espacio: no capturar el gesto (pan del tablero) */
  boardPanActive = false,
  onTogglePanInside,
  onSelect,
  onChange,
  onGroupMoveStart,
  onGroupMove,
  onGroupMoveEnd,
  onHistoryGestureStart,
  onHistoryGestureEnd,
  /** ({ clientX, clientY, slotId, group }) — soltar fuera del artboard */
  onDragOffBoard,
  onRequestRemove,
  /** Doble clic (V): entrar a pan del contenido dentro del marco/máscara */
  onActivatePanInside,
  onContextMenu,
  canRemove,
  canvasRef,
  hideChrome = false,
}) {
  const { t } = useTranslation();
  const dragRef = useRef(null);
  const rootRef = useRef(null);
  const slotRef = useRef(slot);
  const selectedRef = useRef(selected);
  slotRef.current = slot;
  selectedRef.current = selected;
  /** Posición en vivo al arrastrar (sigue el cursor aunque el padre aún no haya re-renderizado). */
  const [dragPos, setDragPos] = useState(null);

  const emitSlot = (partial, { snap = true } = {}) => {
    const next = clampSlot({
      ...slot,
      ...partial,
      id: slot.id,
      assetId: slot.assetId,
    });
    onChange?.(snap ? snapSlot(next) : next);
  };

  /* Alt+rueda (o modo pan): zoom dentro de la casilla */
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const onWheelNative = (e) => {
      const s = slotRef.current;
      if (!selectedRef.current || !s?.assetId) return;
      if (!e.altKey && !panInsideMode) return;
      e.preventDefault();
      e.stopPropagation();
      const delta = e.deltaY > 0 ? -0.12 : 0.12;
      const zoom = Math.min(8, Math.max(0.25, (Number(s.zoom) || 1) + delta));
      onChange?.(
        clampSlot({
          ...s,
          id: s.id,
          assetId: s.assetId,
          zoom,
        }),
      );
    };
    el.addEventListener("wheel", onWheelNative, { passive: false });
    return () => el.removeEventListener("wheel", onWheelNative);
  }, [onChange, panInsideMode]);

  const startGesture = (mode, handle, e) => {
    if (boardPanActive) return;
    e.preventDefault();
    e.stopPropagation();
    // Shift o modo selección múltiple (tablet)
    const additive = Boolean(e.shiftKey) || multiSelectMode;
    if (additive) {
      onSelect?.(slot.id, { additive: true });
    } else if (!selected) {
      onSelect?.(slot.id, { additive: false });
    } else if (multiSelected) {
      // Clic en un miembro del grupo: mantener grupo y activar esta casilla
      onSelect?.(slot.id, { additive: false });
    }
    // Si ya es la única seleccionada, no re-disparar selección
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const imgEl = e.currentTarget.querySelector("img[data-slot-img]");
    const iw = Math.max(1, imgEl?.naturalWidth || 1);
    const ih = Math.max(1, imgEl?.naturalHeight || 1);
    // Si ya hay grupo seleccionado, mover todos; el Shift+clic solo cambia la selección
    const useGroupMove =
      mode === "move" && multiSelected && typeof onGroupMove === "function";
    onHistoryGestureStart?.();
    if (useGroupMove) onGroupMoveStart?.();
    dragRef.current = {
      mode,
      handle,
      startX: e.clientX,
      startY: e.clientY,
      orig: { ...slot },
      cw: rect.width,
      ch: rect.height,
      sw: Math.max(1, rect.width * (Number(slot.w) || 0)),
      sh: Math.max(1, rect.height * (Number(slot.h) || 0)),
      iw,
      ih,
      useGroupMove,
      moved: false,
    };
    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      const dist = Math.hypot(ev.clientX - d.startX, ev.clientY - d.startY);
      if (dist > 3) d.moved = true;
      let next = { ...d.orig };
      if (d.mode === "pan") {
        const dx = ev.clientX - d.startX;
        const dy = ev.clientY - d.startY;
        const box = { x: 0, y: 0, w: d.sw, h: d.sh };
        const panned = panAfterDrag(
          d.iw,
          d.ih,
          box,
          {
            zoom: d.orig.zoom,
            panX: d.orig.panX,
            panY: d.orig.panY,
          },
          d.orig.rotation,
          dx,
          dy,
        );
        next.panX = panned.panX;
        next.panY = panned.panY;
        onChange?.(
          clampSlot({
            ...next,
            id: slot.id,
            assetId: slot.assetId,
          }),
        );
        return;
      }
      if (d.mode === "rotate") {
        const nextRot = rotationFromPointerDrag({
          centerX: d.centerX,
          centerY: d.centerY,
          clientX: ev.clientX,
          clientY: ev.clientY,
          startAngle: d.startAngle,
          originRotation: d.originRotation,
          shiftKey: ev.shiftKey,
        });
        onChange?.(
          clampSlot({
            ...d.orig,
            id: slot.id,
            assetId: slot.assetId,
            rotation: nextRot,
          }),
        );
        return;
      }
      if (d.mode === "move") {
        const dx = (ev.clientX - d.startX) / d.cw;
        const dy = (ev.clientY - d.startY) / d.ch;
        if (d.useGroupMove) {
          onGroupMove?.(dx, dy);
          return;
        }
        next.x = d.orig.x + dx;
        next.y = d.orig.y + dy;
        // Cuerpo rígido fuera de la mesa (sin aplastar w/h al filo)
        const moved = clampSlotAllowOutside({
          ...next,
          id: slot.id,
          assetId: slot.assetId,
        });
        setDragPos({ x: moved.x, y: moved.y });
        slotRef.current = moved;
        // onChange en vivo para que el underlay no deje un “fantasma” debajo.
        // Tras eject, ejectedSlotIdsRef ignora updates tardíos que re-crearían la casilla.
        onChange?.(moved);
        return;
      }
      const dx = (ev.clientX - d.startX) / d.cw;
      const dy = (ev.clientY - d.startY) / d.ch;
      const h = d.handle;
      if (h.includes("e")) next.w = d.orig.w + dx;
      if (h.includes("s")) next.h = d.orig.h + dy;
      if (h.includes("w")) {
        next.x = d.orig.x + dx;
        next.w = d.orig.w - dx;
      }
      if (h.includes("n")) {
        next.y = d.orig.y + dy;
        next.h = d.orig.h - dy;
      }
      onChange?.(
        clampSlot({
          ...next,
          id: slot.id,
          assetId: slot.assetId,
        }),
      );
    };
    const onUp = (ev) => {
      const d = dragRef.current;
      dragRef.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      setDragPos(null);
      if (!d?.moved) {
        onHistoryGestureEnd?.();
        return;
      }
      // Arrastrar fuera de la mesa → pasteboard (foto) o borrar (casilla vacía)
      if (d.mode === "move" && ev) {
        const canvas = canvasRef.current;
        const rect = canvas?.getBoundingClientRect?.();
        const cur = slotRef.current || slot;
        const sx = Number(cur.x) || 0;
        const sy = Number(cur.y) || 0;
        const sw = Number(cur.w) || 0;
        const sh = Number(cur.h) || 0;
        const cx = sx + sw / 2;
        const cy = sy + sh / 2;
        const centerOutside = cx < 0 || cx > 1 || cy < 0 || cy > 1;
        const mostlyOutside =
          sx + sw < 0.08 || sx > 0.92 || sy + sh < 0.08 || sy > 0.92;
        let pointerOutside = false;
        if (rect && rect.width > 4 && rect.height > 4) {
          pointerOutside =
            ev.clientX < rect.left ||
            ev.clientX > rect.right ||
            ev.clientY < rect.top ||
            ev.clientY > rect.bottom;
        }
        const wantEject = centerOutside || mostlyOutside || pointerOutside;
        if (wantEject) {
          if (slot.assetId && typeof onDragOffBoard === "function") {
            d.ejected = Boolean(
              onDragOffBoard({
                clientX: ev.clientX,
                clientY: ev.clientY,
                slotId: slot.id,
                group: Boolean(d.useGroupMove),
                force: true,
              }),
            );
            if (d.ejected) {
              onHistoryGestureEnd?.();
              return;
            }
          } else if (!slot.assetId && typeof onRequestRemove === "function") {
            // Vacía fuera de la mesa → se elimina sin diálogo (página puede quedar sin casillas)
            onRequestRemove(slot.id, { force: true });
            onHistoryGestureEnd?.();
            return;
          }
        }
      }
      if (d.mode === "move" && d.useGroupMove) {
        onGroupMoveEnd?.();
        onHistoryGestureEnd?.();
        return;
      }
      if (d.ejected) {
        onHistoryGestureEnd?.();
        return;
      }
      // Reclavar sin aplastar w/h (traslación rígida al filo)
      if (d.mode === "move" || d.mode === "rotate" || d.handle) {
        const cur = slotRef.current || slot;
        onChange?.(
          snapSlot(
            d.mode === "move" || slotIsOffPage(cur)
              ? fitSlotInPage({
                  ...cur,
                  id: slot.id,
                  assetId: slot.assetId,
                })
              : clampSlot({
                  ...cur,
                  id: slot.id,
                  assetId: slot.assetId,
                }),
          ),
        );
      }
      onHistoryGestureEnd?.();
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const pinchDist = (t0, t1) => {
    const dx = t0.clientX - t1.clientX;
    const dy = t0.clientY - t1.clientY;
    return Math.hypot(dx, dy);
  };

  const onTouchStart = (e) => {
    if (!slot.assetId || e.touches.length !== 2) return;
    e.preventDefault();
    e.stopPropagation();
    onSelect?.(slot.id, { additive: false });
    dragRef.current = {
      mode: "pinch",
      orig: { ...slot },
      startDist: pinchDist(e.touches[0], e.touches[1]),
    };
  };

  const onTouchMove = (e) => {
    const d = dragRef.current;
    if (!d || d.mode !== "pinch" || e.touches.length !== 2) return;
    e.preventDefault();
    e.stopPropagation();
    const dist = pinchDist(e.touches[0], e.touches[1]);
    if (!d.startDist || d.startDist < 1) return;
    const ratio = dist / d.startDist;
    const zoom = Math.min(
      8,
      Math.max(0.25, (Number(d.orig.zoom) || 1) * ratio),
    );
    onChange?.(
      clampSlot({
        ...d.orig,
        id: slot.id,
        assetId: slot.assetId,
        zoom,
      }),
    );
  };

  const onTouchEnd = () => {
    if (dragRef.current?.mode === "pinch") dragRef.current = null;
  };

  const clipGeom = slotClipLocalGeometry(slot);
  // Legacy shape:circle solo si no hay clip de recorte real
  const isCircle = !clipGeom && String(slot.shape || "rect") === "circle";
  const hasAsset = Boolean(slot.assetId);
  const showChrome = selected && !hideChrome;
  const slotRotation = normalizeRotation(slot.rotation);
  const clipPathId = `slot-clip-${String(slot.id || "x").replace(/\W/g, "")}`;

  const startRotate = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!selected) onSelect?.(slot.id, { additive: false });
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const cx = rect.left + (slot.x + slot.w / 2) * rect.width;
    const cy = rect.top + (slot.y + slot.h / 2) * rect.height;
    onHistoryGestureStart?.();
    dragRef.current = {
      mode: "rotate",
      handle: null,
      startX: e.clientX,
      startY: e.clientY,
      centerX: cx,
      centerY: cy,
      startAngle: pointerAngleDeg(cx, cy, e.clientX, e.clientY),
      originRotation: slotRotation,
      orig: { ...slot },
      cw: rect.width,
      ch: rect.height,
      moved: false,
    };
    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d || d.mode !== "rotate") return;
      if (Math.hypot(ev.clientX - d.startX, ev.clientY - d.startY) > 3) {
        d.moved = true;
      }
      const nextRot = rotationFromPointerDrag({
        centerX: d.centerX,
        centerY: d.centerY,
        clientX: ev.clientX,
        clientY: ev.clientY,
        startAngle: d.startAngle,
        originRotation: d.originRotation,
        shiftKey: ev.shiftKey,
      });
      onChange?.(
        clampSlot({
          ...d.orig,
          id: slot.id,
          assetId: slot.assetId,
          rotation: nextRot,
        }),
      );
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      onHistoryGestureEnd?.();
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  return (
    <Box
      ref={rootRef}
      onPointerDown={(e) => {
        if (boardPanActive) return;
        if (e.pointerType === "touch" && dragRef.current?.mode === "pinch") {
          return;
        }
        if (slot.assetId && (e.altKey || e.metaKey || panInsideMode)) {
          // Alt (Win) / Option (Mac vía altKey) · Cmd+arrastrar también mueve interior
          startGesture("pan", null, e);
        } else {
          startGesture("move", null, e);
        }
      }}
      onDoubleClick={(e) => {
        if (boardPanActive || !slot.assetId) return;
        e.preventDefault();
        e.stopPropagation();
        onSelect?.(slot.id, { additive: false });
        onActivatePanInside?.(slot.id);
      }}
      onContextMenu={(e) => {
        if (boardPanActive) return;
        e.preventDefault();
        e.stopPropagation();
        if (!selected) onSelect?.(slot.id, { additive: false });
        onContextMenu?.(e, { kind: "slot", slotId: slot.id });
      }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
      data-slot-root={slot.id}
      sx={{
        position: "absolute",
        left: `${(dragPos?.x ?? slot.x) * 100}%`,
        top: `${(dragPos?.y ?? slot.y) * 100}%`,
        width: `${slot.w * 100}%`,
        height: `${slot.h * 100}%`,
        bgcolor: "transparent",
        overflow: "visible",
        // Gira el marco entero (Illustrator): no recortar la foto dentro de un AABB fijo
        transform: slotRotation ? `rotate(${slotRotation}deg)` : undefined,
        transformOrigin: "center center",
        outline: selected
          ? "2px solid"
          : hasAsset
            ? "1px solid transparent"
            : "1px dashed rgba(0,0,0,0.28)",
        outlineColor: selected ? "primary.main" : undefined,
        outlineOffset: -1,
        cursor: boardPanActive
          ? "grab"
          : panInsideMode && hasAsset
            ? "grab"
            : "move",
        // Mantener el z de capa: la selección NO salta al frente (pedido diseño).
        // Asas/papelera pueden quedar bajo marcas; mejor que reordenar visual.
        zIndex: Math.min(1 + (Number(slot.z) || 0), 9000),
        touchAction: "none",
        userSelect: "none",
        pointerEvents: boardPanActive ? "none" : "auto",
        "&:active": {
          cursor:
            panInsideMode && hasAsset
              ? "grabbing"
              : selected && slot.assetId
                ? "grabbing"
                : "move",
        },
        "&:hover .slot-remove": { opacity: 1 },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          // Marco = foto visible (bordes y foto a la vez). Máscara VC = clipPath.
          overflow: "hidden",
          borderRadius: isCircle ? "50%" : 0,
          pointerEvents: "none",
          ...(clipGeom
            ? {
                clipPath: `url(#${clipPathId})`,
                WebkitClipPath: `url(#${clipPathId})`,
              }
            : null),
        }}
      >
        {clipGeom ? (
          <svg
            width={0}
            height={0}
            aria-hidden
            style={{ position: "absolute" }}
          >
            <defs>
              <clipPath id={clipPathId} clipPathUnits="objectBoundingBox">
                {clipGeom.kind === "ellipse" ? (
                  <ellipse
                    cx={clipGeom.cx}
                    cy={clipGeom.cy}
                    rx={clipGeom.rx}
                    ry={clipGeom.ry}
                  />
                ) : clipGeom.kind === "path" ? (
                  <path d={clipGeom.d} />
                ) : (
                  <rect
                    x={clipGeom.x}
                    y={clipGeom.y}
                    width={clipGeom.w}
                    height={clipGeom.h}
                    rx={clipGeom.rx || 0}
                    ry={clipGeom.ry || clipGeom.rx || 0}
                  />
                )}
              </clipPath>
            </defs>
          </svg>
        ) : null}
        {slot.assetId ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              transform:
                slot.flipX || slot.flipY
                  ? `scale(${slot.flipX ? -1 : 1}, ${slot.flipY ? -1 : 1})`
                  : undefined,
            }}
          >
            <AssetThumb
              assetId={slot.assetId}
              fillSlot
              transparentBg
              zoom={slot.zoom}
              panX={slot.panX}
              panY={slot.panY}
              rotation={0}
            />
          </Box>
        ) : (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "grid",
              placeItems: "center",
              height: "100%",
              fontSize: "0.65rem",
              pointerEvents: "none",
            }}
          >
            vacía
          </Typography>
        )}
      </Box>
      {/* Máscara: solo trazo discontinuo (sin asas naranjas ni mano/%). */}
      {showChrome && clipGeom ? (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 5,
            pointerEvents: "none",
          }}
          title="Máscara de recorte · Ctrl+Alt+7 libera"
        >
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 1 1"
            preserveAspectRatio="none"
            aria-hidden
            style={{ position: "absolute", inset: 0, overflow: "visible" }}
          >
            {clipGeom.kind === "ellipse" ? (
              <ellipse
                cx={clipGeom.cx}
                cy={clipGeom.cy}
                rx={clipGeom.rx}
                ry={clipGeom.ry}
                fill="none"
                stroke="#64748b"
                strokeWidth={0.008}
                strokeDasharray="0.025 0.018"
                vectorEffect="non-scaling-stroke"
              />
            ) : clipGeom.kind === "path" ? (
              <path
                d={clipGeom.d}
                fill="none"
                stroke="#64748b"
                strokeWidth={0.008}
                strokeDasharray="0.025 0.018"
                vectorEffect="non-scaling-stroke"
              />
            ) : (
              <rect
                x={clipGeom.x}
                y={clipGeom.y}
                width={clipGeom.w}
                height={clipGeom.h}
                rx={clipGeom.rx || 0}
                ry={clipGeom.ry || clipGeom.rx || 0}
                fill="none"
                stroke="#64748b"
                strokeWidth={0.008}
                strokeDasharray="0.025 0.018"
                vectorEffect="non-scaling-stroke"
              />
            )}
          </svg>
        </Box>
      ) : null}
      {/* Sin barra mano/zoom/% sobre la foto (pedido diseño). Alt o doble clic = pan. */}
      {canRemove && showChrome ? (
        <Tooltip title={t("skillAgent.moodboard.removeSlot")}>
          <IconButton
            className="slot-remove"
            size="small"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onRequestRemove?.(slot.id);
            }}
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              zIndex: 7,
              opacity: 1,
              transition: "opacity 0.12s",
              bgcolor: alpha("#fff", 0.92),
              boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
              p: 0.35,
              "&:hover": { bgcolor: "#fff", color: "error.main" },
            }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      ) : null}
      {showChrome ? (
        <Box
          onPointerDown={startRotate}
          title={`${Math.round(slotRotation)}°`}
          sx={{
            position: "absolute",
            left: "50%",
            top: -20,
            marginLeft: "-6px",
            width: 12,
            height: 12,
            bgcolor: "primary.main",
            border: "1px solid #fff",
            borderRadius: "50%",
            zIndex: 8,
            cursor: "grab",
            boxShadow: "0 0 0 1px rgba(0,0,0,0.15)",
            "&:active": { cursor: "grabbing" },
          }}
        />
      ) : null}
      {showChrome
        ? HANDLES.map((h) => (
            <Box
              key={h}
              onPointerDown={(e) => startGesture("resize", h, e)}
              sx={{
                position: "absolute",
                width: 10,
                height: 10,
                bgcolor: "primary.main",
                border: "1px solid #fff",
                borderRadius: 0.25,
                zIndex: 6,
                ...(h.includes("n") ? { top: -5 } : null),
                ...(h.includes("s") ? { bottom: -5 } : null),
                ...(h.includes("w") ? { left: -5 } : null),
                ...(h.includes("e") ? { right: -5 } : null),
                ...(h === "n" || h === "s"
                  ? { left: "50%", marginLeft: "-5px" }
                  : null),
                ...(h === "e" || h === "w"
                  ? { top: "50%", marginTop: "-5px" }
                  : null),
                cursor: `${h}-resize`,
              }}
            />
          ))
        : null}
    </Box>
  );
}

/**
 * Marco Free Transform (VC E): escala, rotación, shear (Alt en lados).
 * Shift = proporcional · Alt en esquina = desde el centro.
 * `orientation` (grados): marco alineado a la selección (foto/marca única).
 */
function GroupTransformBox({
  slots,
  canvasRef,
  onScale,
  onRotate,
  onShear,
  freeTransform = false,
  onHistoryGestureStart,
  onHistoryGestureEnd,
  /** Override AABB (p.ej. caja local de marca/foto rotada). */
  bboxOverride = null,
  /** Rotación visual del marco (grados CSS). */
  orientation = 0,
  scaleWithBBox = scaleSlotsWithBBox,
  rotateAroundBBox = rotateSlotsAroundBBox,
  shearInBBox = shearSlotsInBBox,
}) {
  const aabb = useMemo(() => slotsBBox(slots), [slots]);
  const bbox = bboxOverride || aabb;
  const dragRef = useRef(null);
  const orient = Number(orientation) || 0;
  if (!bbox || bbox.w < 0.001 || bbox.h < 0.001) return null;

  const toLocalDelta = (dx, dy) => {
    if (!orient) return { dx, dy };
    const rad = (-orient * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    return { dx: dx * cos - dy * sin, dy: dx * sin + dy * cos };
  };

  const startResize = (handle, e) => {
    e.preventDefault();
    e.stopPropagation();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const origins = slots.map((s) => ({ ...s }));
    const bbox0 = { ...bbox };
    const sideOnly =
      handle === "n" || handle === "s" || handle === "e" || handle === "w";
    const shearMode = freeTransform && sideOnly && e.altKey;
    onHistoryGestureStart?.();
    dragRef.current = {
      mode: shearMode ? "shear" : "scale",
      handle,
      startX: e.clientX,
      startY: e.clientY,
      cw: rect.width,
      ch: rect.height,
      origins,
      bbox0,
      fromCenter: Boolean(e.altKey) && !shearMode,
      proportional: Boolean(e.shiftKey),
    };
    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      const rawDx = (ev.clientX - d.startX) / d.cw;
      const rawDy = (ev.clientY - d.startY) / d.ch;
      const { dx, dy } = toLocalDelta(rawDx, rawDy);
      if (d.mode === "shear") {
        onShear?.(shearInBBox(d.origins, d.bbox0, d.handle, dx, dy));
        return;
      }
      const h = d.handle;
      let x = d.bbox0.x;
      let y = d.bbox0.y;
      let w = d.bbox0.w;
      let hgt = d.bbox0.h;
      if (d.fromCenter) {
        if (h.includes("e") || h.includes("w")) {
          const dw = h.includes("e") ? dx : -dx;
          w = d.bbox0.w + dw * 2;
          x = d.bbox0.x + d.bbox0.w / 2 - w / 2;
        }
        if (h.includes("n") || h.includes("s")) {
          const dh = h.includes("s") ? dy : -dy;
          hgt = d.bbox0.h + dh * 2;
          y = d.bbox0.y + d.bbox0.h / 2 - hgt / 2;
        }
      } else {
        if (h.includes("e")) w = d.bbox0.w + dx;
        if (h.includes("s")) hgt = d.bbox0.h + dy;
        if (h.includes("w")) {
          x = d.bbox0.x + dx;
          w = d.bbox0.w - dx;
        }
        if (h.includes("n")) {
          y = d.bbox0.y + dy;
          hgt = d.bbox0.h - dy;
        }
      }
      if (ev.shiftKey || d.proportional) {
        const aspect = d.bbox0.w / Math.max(d.bbox0.h, 1e-6);
        const corner =
          (h.includes("n") || h.includes("s")) &&
          (h.includes("e") || h.includes("w"));
        if (corner) {
          const side = Math.max(Math.abs(w), Math.abs(hgt * aspect));
          w = Math.sign(w || 1) * side;
          hgt = Math.sign(hgt || 1) * (side / aspect);
          if (h.includes("w")) x = d.bbox0.x + d.bbox0.w - w;
          if (h.includes("n")) y = d.bbox0.y + d.bbox0.h - hgt;
          if (d.fromCenter) {
            x = d.bbox0.x + d.bbox0.w / 2 - w / 2;
            y = d.bbox0.y + d.bbox0.h / 2 - hgt / 2;
          }
        }
      }
      if (w < MIN_SLOT) {
        if (h.includes("w") && !d.fromCenter)
          x = d.bbox0.x + d.bbox0.w - MIN_SLOT;
        w = MIN_SLOT;
      }
      if (hgt < MIN_SLOT) {
        if (h.includes("n") && !d.fromCenter)
          y = d.bbox0.y + d.bbox0.h - MIN_SLOT;
        hgt = MIN_SLOT;
      }
      onScale?.(scaleWithBBox(d.origins, d.bbox0, { x, y, w, h: hgt }));
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      onHistoryGestureEnd?.();
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const startRotate = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const cx = rect.left + (bbox.x + bbox.w / 2) * rect.width;
    const cy = rect.top + (bbox.y + bbox.h / 2) * rect.height;
    const origins = slots.map((s) => ({ ...s }));
    const bbox0 = { ...bbox };
    const startAngle = pointerAngleDeg(cx, cy, e.clientX, e.clientY);
    onHistoryGestureStart?.();
    dragRef.current = {
      mode: "rotate",
      startAngle,
      origins,
      bbox0,
      cx,
      cy,
    };
    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d || d.mode !== "rotate") return;
      let ang =
        pointerAngleDeg(d.cx, d.cy, ev.clientX, ev.clientY) - d.startAngle;
      if (ev.shiftKey) {
        ang = Math.round(ang / 15) * 15;
      }
      onRotate?.(rotateAroundBBox(d.origins, d.bbox0, ang));
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      onHistoryGestureEnd?.();
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  return (
    <Box
      sx={{
        position: "absolute",
        left: `${bbox.x * 100}%`,
        top: `${bbox.y * 100}%`,
        width: `${bbox.w * 100}%`,
        height: `${bbox.h * 100}%`,
        outline: freeTransform ? "2px solid" : "2px dashed",
        outlineColor: "primary.main",
        outlineOffset: 2,
        pointerEvents: "none",
        zIndex: 1200,
        transform: orient ? `rotate(${orient}deg)` : undefined,
        transformOrigin: "center center",
      }}
    >
      {freeTransform ? (
        <Box
          onPointerDown={startRotate}
          title="Rotar (Shift = 15°)"
          sx={{
            position: "absolute",
            left: "50%",
            top: -28,
            marginLeft: "-7px",
            width: 14,
            height: 14,
            bgcolor: "primary.main",
            border: "2px solid #fff",
            borderRadius: "50%",
            pointerEvents: "auto",
            zIndex: 1202,
            cursor: "grab",
            boxShadow: "0 0 0 1px rgba(0,0,0,0.2)",
            "&:active": { cursor: "grabbing" },
            "&::after": {
              content: '""',
              position: "absolute",
              left: "50%",
              top: 14,
              width: 2,
              height: 10,
              marginLeft: "-1px",
              bgcolor: "primary.main",
            },
          }}
        />
      ) : null}
      {HANDLES.map((h) => (
        <Box
          key={h}
          onPointerDown={(e) => startResize(h, e)}
          title={
            freeTransform && (h === "n" || h === "s" || h === "e" || h === "w")
              ? "Escalar · Alt = shear"
              : "Escalar · Shift = proporcional · Alt = centro"
          }
          sx={{
            position: "absolute",
            width: 12,
            height: 12,
            bgcolor: "primary.main",
            border: "2px solid #fff",
            borderRadius: 0.25,
            pointerEvents: "auto",
            zIndex: 1201,
            ...(h.includes("n") ? { top: -6 } : null),
            ...(h.includes("s") ? { bottom: -6 } : null),
            ...(h.includes("w") ? { left: -6 } : null),
            ...(h.includes("e") ? { right: -6 } : null),
            ...(h === "n" || h === "s"
              ? { left: "50%", marginLeft: "-6px" }
              : null),
            ...(h === "e" || h === "w"
              ? { top: "50%", marginTop: "-6px" }
              : null),
            cursor: `${h}-resize`,
          }}
        />
      ))}
    </Box>
  );
}

async function loadPoolFromSearches(board) {
  const customerId = board?.CustomerId ? Number(board.CustomerId) : null;
  const temporada = String(board?.Temporada || "").trim();
  const projectId = board?.ProjectId ? Number(board.ProjectId) : null;
  const list = await listSkillAgentSearches({
    ...(customerId ? { customerId } : {}),
    ...(temporada ? { temporada } : {}),
    ...(Number.isFinite(projectId) && projectId > 0 ? { projectId } : {}),
  });
  const searches = (Array.isArray(list) ? list : []).filter((s) => {
    if (
      Number.isFinite(projectId) &&
      projectId > 0 &&
      Number(s.ProjectId) === projectId
    ) {
      return true;
    }
    const clientOk = !customerId || Number(s.CustomerId) === customerId;
    const seasonOk =
      !temporada || String(s.Temporada || "").trim() === temporada;
    return clientOk && seasonOk;
  });
  const items = [];
  for (const s of searches) {
    const detail = await getSkillAgentSearchById(s.id);
    const imgs = (Array.isArray(detail?.items) ? detail.items : []).filter(
      (it) => !it.Kind || String(it.Kind).toLowerCase() === "image",
    );
    for (const it of imgs) {
      items.push({
        id: it.id,
        SearchId: s.id,
        Title: it.Title,
        BrandName: it.BrandName,
        BrandId: it.BrandId,
        SourceUrl: it.SourceUrl,
        MimeType: it.MimeType,
        DriveFileUrl: it.DriveFileUrl,
        SearchTitulo: s.Titulo || null,
      });
    }
  }
  return {
    searches: searches.map((s) => ({
      id: s.id,
      Titulo: s.Titulo,
      Status: s.Status,
      Temporada: s.Temporada,
      CustomerId: s.CustomerId,
    })),
    items,
  };
}

export default function SkillAgentMoodboardDetail({
  moodboardId,
  onChanged,
  onDeleted,
  onOpenHelp,
  viewerName = "",
  viewerUserId = null,
  /** Si viene informado, el lienzo no toca las APIs de moodboards. */
  boardApi = null,
  /** Product Tree: avisa si cerrar sin PDF en Ficha Técnica. */
  onCloseGuardChange = null,
}) {
  const boardApiRef = useRef(boardApi);
  boardApiRef.current = boardApi;
  const onCloseGuardChangeRef = useRef(onCloseGuardChange);
  onCloseGuardChangeRef.current = onCloseGuardChange;
  /** false = hace falta exportar PDF a Ficha Técnica antes de cerrar sin aviso. */
  const pdfSyncedRef = useRef(true);

  const fetchAssetBlobCached = useMemo(
    () =>
      createMoodboardAssetBlobFetcher(
        boardApi?.fetchAssetBlobUrl || fetchSkillAgentMoodboardAssetBlobUrl,
      ),
    [boardApi?.fetchAssetBlobUrl],
  );

  useEffect(
    () => () => {
      clearMoodboardAssetBlobCache();
    },
    [],
  );

  const reportCloseGuard = useCallback((synced) => {
    if (!boardApiRef.current) return;
    pdfSyncedRef.current = synced;
    onCloseGuardChangeRef.current?.({ needsPdfExport: !synced });
  }, []);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const skipAutosaveRef = useRef(true);
  const pagesRef = useRef([emptyFreePage()]);
  /** Undo/redo de layout (páginas + slots + marks). No cubre rembg/crop. */
  const pagesHistoryRef = useRef(createPagesHistory());
  /** null | 'pending' (gesto sin mutar aún) | 'open' (ya se pusheó el before) */
  const historyGateRef = useRef(null);
  const historyCoalesceTimerRef = useRef(null);
  const bumpHistoryUiRef = useRef(() => {});
  const [historyUiRev, setHistoryUiRev] = useState(0);
  bumpHistoryUiRef.current = () => setHistoryUiRev((n) => n + 1);
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  /** Free Transform (VC E): marco con escala / rotar / shear. */
  const [freeTransformMode, setFreeTransformMode] = useState(false);
  const [panInsideMode, setPanInsideMode] = useState(false);
  const metaRef = useRef({
    titulo: "",
    categoryLabel: "",
    studioLine: "",
    chromeStyle: normalizeChromeStyle(null),
  });
  const expectedUpdatedAtRef = useRef(null);
  const saveStateRef = useRef("saved");
  const saveChainRef = useRef(Promise.resolve());
  const ignoreOwnSocketUntilRef = useRef(0);
  const pagesTimerRef = useRef(null);
  const metaTimerRef = useRef(null);
  const onChangedRef = useRef(onChanged);
  const tituloSavedRef = useRef("");
  const softRefreshBusyRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [fileDropActive, setFileDropActive] = useState(false);
  const fileDropDepthRef = useRef(0);
  const uploadInFlightRef = useRef(false);
  const [exporting, setExporting] = useState(false);
  const [saveState, setSaveState] = useState("saved"); // saved | saving | unsaved | error
  const [error, setError] = useState("");
  // Feedback corto desactivado (banners arriba); setOkMsg queda por si se reusa.
  const [, setOkMsg] = useState("");
  const [peerViewers, setPeerViewers] = useState([]);
  const [staleConflict, setStaleConflict] = useState(false);
  const [isOnline, setIsOnline] = useState(() => isBrowserOnline());
  const [offlineEnabled, setOfflineEnabled] = useState(false);
  const [offlinePending, setOfflinePending] = useState(false);
  const [connectivityNotice, setConnectivityNotice] = useState(""); // offline | syncing | ""
  const offlineEnabledRef = useRef(false);
  const isOnlineRef = useRef(isOnline);
  const wasOnlineRef = useRef(isOnline);
  offlineEnabledRef.current = offlineEnabled;
  isOnlineRef.current = isOnline;
  const [board, setBoard] = useState(null);
  const [assets, setAssets] = useState([]);
  const [pages, setPages] = useState(() =>
    assignDefaultBoards([emptyFreePage()]),
  );
  const [pasteboard, setPasteboard] = useState([]);
  const pasteboardRef = useRef([]);
  pasteboardRef.current = pasteboard;
  const [pageIdx, setPageIdx] = useState(0);
  const [viewZoom, setViewZoom] = useState(0.5);
  const viewScrollRef = useRef(null);
  const viewZoomUserRef = useRef(false);
  const viewFitRoRef = useRef(null);
  const [workspaceMode, setWorkspaceMode] = useState(readStoredWorkspaceMode);
  const [pasteboardBg, setPasteboardBg] = useState(readStoredPasteboardBg);
  const [pasteboardBgAnchor, setPasteboardBgAnchor] = useState(null);
  const boardViewportRef = useRef(null);
  const boardPanDragRef = useRef(null);
  const [boardCam, setBoardCam] = useState({
    panX: 48,
    panY: 48,
    zoom: 0.4,
    rotation: 0,
  });
  const boardCamRef = useRef(boardCam);
  boardCamRef.current = boardCam;
  /** true = el usuario hizo zoom/pan; no auto-encajar (evita pantallazos al 27%). */
  const boardCamUserRef = useRef(false);
  const boardViewportSizeRef = useRef({ w: 0, h: 0 });
  const workspaceModeRef = useRef(workspaceMode);
  workspaceModeRef.current = workspaceMode;
  /** Último punto del puntero en coords de tablero (para Ctrl+V / pegar fuera). */
  const lastBoardPointerRef = useRef(null);
  /** "page" | "pasteboard" — dónde estaba el puntero al pegar. */
  const lastBoardTargetKindRef = useRef("pasteboard");
  const [selectedPasteboardId, setSelectedPasteboardId] = useState(null);
  const [selectedSlotIds, setSelectedSlotIds] = useState(() => new Set());
  const [activeSlotId, setActiveSlotId] = useState(null);
  const groupMoveOriginsRef = useRef(null);
  /** Ids sacados al pasteboard: ignora setState tardíos que los re-crearían. */
  const ejectedSlotIdsRef = useRef(new Set());
  const selectedSlotIdsRef = useRef(selectedSlotIds);
  selectedSlotIdsRef.current = selectedSlotIds;
  const clipboardRef = useRef(null);
  /** Tras Ctrl+C/X en el moodboard: el pegado prioriza esto frente a imagen residual del SO. */
  const clipboardInternalAtRef = useRef(0);
  const [marquee, setMarquee] = useState(null);
  const [markTool, setMarkTool] = useState(null); // null | select|pen|brush|line|arrow|ellipse|rect|text|eraser
  /** Últimas divisiones de Grid / Polar / Flare (herramientas VC). */
  const [gridOpts, setGridOpts] = useState({
    cols: 4,
    rows: 4,
    rings: 4,
    rays: 8,
  });
  const gridOptsRef = useRef(gridOpts);
  gridOptsRef.current = gridOpts;
  /** Espiral: vueltas + arquímedes/log. */
  const [spiralOpts, setSpiralOpts] = useState({
    turns: 3,
    kind: "archimedean",
  });
  const spiralOptsRef = useRef(spiralOpts);
  spiralOptsRef.current = spiralOpts;
  /** Polígono / estrella / arco. */
  const [shapeOpts, setShapeOpts] = useState({
    sides: 6,
    tips: 5,
    sweep: 90,
    innerRatio: 0.45,
    rx: 0.04,
  });
  const shapeOptsRef = useRef(shapeOpts);
  shapeOptsRef.current = shapeOpts;
  /** Paso de la herramienta Anchura. */
  const [widthOpts, setWidthOpts] = useState({ step: 0.5 });
  const widthOptsRef = useRef(widthOpts);
  widthOptsRef.current = widthOpts;
  const [envelopeOpts, setEnvelopeOpts] = useState({
    style: "arc",
    bend: 50,
    horizontal: true,
    h: 0,
    v: 0,
    rows: 3,
    cols: 4,
  });
  const envelopeOptsRef = useRef(envelopeOpts);
  envelopeOptsRef.current = envelopeOpts;
  const [activeSymbolId, setActiveSymbolId] = useState(null);
  const activeSymbolIdRef = useRef(activeSymbolId);
  activeSymbolIdRef.current = activeSymbolId;
  const [symbolSprayOpts, setSymbolSprayOpts] = useState({
    radius: 0.05,
    density: 3,
  });
  const symbolSprayOptsRef = useRef(symbolSprayOpts);
  symbolSprayOptsRef.current = symbolSprayOpts;
  /** Suavizar paths: pasadas Chaikin. */
  const [smoothOpts, setSmoothOpts] = useState({ passes: 2 });
  const smoothOptsRef = useRef(smoothOpts);
  smoothOptsRef.current = smoothOpts;
  /** Varita: tolerancia de color 0–0.35. */
  const [wandOpts, setWandOpts] = useState({ tolerance: 0.14 });
  const wandOptsRef = useRef(wandOpts);
  wandOptsRef.current = wandOpts;
  /** Radio / fuerza de liquify (warp, twirl, …). */
  const [liquifyOpts, setLiquifyOpts] = useState({
    radius: 0.09,
    strength: 0.4,
    angleDeg: 28,
  });
  const liquifyOptsRef = useRef(liquifyOpts);
  liquifyOptsRef.current = liquifyOpts;
  /** Shape Builder interactivo: op pendiente del trazo (unite/minus/…). */
  const shapeBuilderOpRef = useRef("unite");
  /** Gesto live Rotar / Escalar (R / S). */
  const liveXfRef = useRef(null);
  /** Hand / Zoom mode (barra VectorCraft Navigate). */
  const [handMode, setHandMode] = useState(false);
  const [zoomMode, setZoomMode] = useState(false);
  /** Espacio mantenido = mano temporal (Illustrator). */
  const [spaceHeld, setSpaceHeld] = useState(false);
  const spaceHeldRef = useRef(false);
  /** true solo mientras H/Z son tecla mantenida (no sticky de barra). */
  const handKeyHeldRef = useRef(false);
  const zoomKeyHeldRef = useRef(false);
  const handModeRef = useRef(false);
  handModeRef.current = handMode;
  const zoomModeRef = useRef(false);
  zoomModeRef.current = zoomMode;
  const boardPanActive = handMode || spaceHeld;
  const boardPanActiveRef = useRef(false);
  boardPanActiveRef.current = boardPanActive;
  /** Mano o zoom: no marquee / no mover fotos. */
  const boardNavLock = boardPanActive || zoomMode;
  const boardNavLockRef = useRef(false);
  boardNavLockRef.current = boardNavLock;
  /** Drag de mesa (barra / herramienta Artboard). */
  const artboardMoveRef = useRef(null);
  const markToolRef = useRef(markTool);
  markToolRef.current = markTool;
  /** Congela el origen del mundo mientras se arrastra una mesa (si no, minX salta y no se ve el movimiento). */
  const [artboardDragBounds, setArtboardDragBounds] = useState(null);
  const [ctxMenu, setCtxMenu] = useState(null);
  const [eyedropperMode, setEyedropperMode] = useState(false);
  const [selectedMarkId, setSelectedMarkId] = useState(null);
  const [selectedMarkIds, setSelectedMarkIds] = useState(() => new Set());
  const selectedMarkIdsRef = useRef(selectedMarkIds);
  selectedMarkIdsRef.current = selectedMarkIds;
  const [markColor, setMarkColor] = useState(DEFAULT_MARK_COLOR);
  const [markFillColor, setMarkFillColor] = useState(DEFAULT_MARK_COLOR);
  const markFillColorRef = useRef(markFillColor);
  markFillColorRef.current = markFillColor;
  const [markStrokeColor, setMarkStrokeColor] = useState(DEFAULT_MARK_COLOR);
  const [colorTarget, setColorTarget] = useState("fill"); // fill | stroke
  const [markStroke, setMarkStroke] = useState(2);
  const [markFontSize, setMarkFontSize] = useState(14);
  const [markFontFamily, setMarkFontFamily] = useState("sans");
  const [markFontWeight, setMarkFontWeight] = useState(700);
  const [markTextAlign, setMarkTextAlign] = useState("left");
  const [draftMark, setDraftMark] = useState(null);
  const [textDraft, setTextDraft] = useState(null);
  const draftMarkRef = useRef(null);
  draftMarkRef.current = draftMark;
  const textDraftRef = useRef(null);
  textDraftRef.current = textDraft;
  const [pool, setPool] = useState({ searches: [], items: [] });
  const [poolPick, setPoolPick] = useState(() => new Set());
  const [poolError, setPoolError] = useState("");
  const [poolLoading, setPoolLoading] = useState(false);
  const [poolSourceFilter, setPoolSourceFilter] = useState("sketch");
  const [addPhotosOpen, setAddPhotosOpen] = useState(false);
  const [addMeetingPhotosOpen, setAddMeetingPhotosOpen] = useState(false);
  const [meetingPool, setMeetingPool] = useState({
    session: null,
    items: [],
    MeetingSessionId: null,
  });
  const [meetingPoolPick, setMeetingPoolPick] = useState(() => new Set());
  const [meetingPoolError, setMeetingPoolError] = useState("");
  const [meetingPoolLoading, setMeetingPoolLoading] = useState(false);
  const [boardPhotosOpen, setBoardPhotosOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [slotRemoveId, setSlotRemoveId] = useState(null);
  const [assetRemoveId, setAssetRemoveId] = useState(null);
  const [pageRemoveOpen, setPageRemoveOpen] = useState(false);
  const [aviatorConfirmOpen, setAviatorConfirmOpen] = useState(false);
  const [autoPagesConfirmOpen, setAutoPagesConfirmOpen] = useState(false);
  const [exportConfirmOpen, setExportConfirmOpen] = useState(false);
  const [exportPageIdxs, setExportPageIdxs] = useState([]);
  const [removeBgConfirmOpen, setRemoveBgConfirmOpen] = useState(false);
  const [gallerySelected, setGallerySelected] = useState(() => new Set());
  const [galleryPlacePageIdx, setGalleryPlacePageIdx] = useState(0);
  const [titulo, setTitulo] = useState("");
  const [categoryLabel, setCategoryLabel] = useState("");
  const [studioLine, setStudioLine] = useState("");
  const [chromeStyle, setChromeStyle] = useState(() =>
    normalizeChromeStyle(null),
  );
  const [layoutMenuEl, setLayoutMenuEl] = useState(null);
  const [strokeAnchor, setStrokeAnchor] = useState(null);
  const [strokeWidth, setStrokeWidth] = useState(6);
  const [strokeColor, setStrokeColor] = useState("#000000");
  const [cropOpen, setCropOpen] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [layersOpen, setLayersOpen] = useState(false);
  const [vcRightPanel, setVcRightPanel] = useState(null); // ICON_PANEL_GROUPS id
  /** VectorCraft Align To: selection | page | key */
  const [alignTo, setAlignTo] = useState("selection");
  /** Gap 0–1 para Distribute Spacing (con Key). */
  const [alignSpacing, setAlignSpacing] = useState(0.02);
  /** Panel Gradient (stops editables, como VectorCraft). */
  const [gradientDraft, setGradientDraft] = useState(() => ({
    from: DEFAULT_MARK_COLOR,
    to: "#ffffff",
    kind: "linear",
    angle: 0,
  }));
  const [inpaintOpen, setInpaintOpen] = useState(false);
  const [inpaintImageUrl, setInpaintImageUrl] = useState("");

  const load = useCallback(async () => {
    if (!moodboardId) return;
    skipAutosaveRef.current = true;
    setLoading(true);
    setError("");
    setPoolError("");
    setSaveState("saved");
    try {
      const data = await (
        boardApiRef.current?.getById || getSkillAgentMoodboardById
      )(moodboardId);
      setBoard(data);
      if (data?.updatedAt) expectedUpdatedAtRef.current = data.updatedAt;
      setStaleConflict(false);
      setPeerViewers([]);
      setAssets(Array.isArray(data?.assets) ? data.assets : []);
      {
        const seed = {
          categoryLabel: data?.CategoryLabel || "",
          studioLine: data?.StudioLine || "",
          chromeStyle: normalizeChromeStyle(data?.ChromeStyleJson),
        };
        // Plantillas carátula: no inyectar chrome Stage (barras diagonales).
        const skipChromeSeed =
          Boolean(boardApiRef.current) ||
          String(data?.Kind || "").toLowerCase() === "caratula";
        setPages(
          normalizePages(data?.PagesJson).map((p) =>
            skipChromeSeed
              ? { ...p, hf: 1 }
              : loadPageHeaderFooterMarks(p, seed),
          ),
        );
      }
      setPasteboard(normalizePasteboard(data?.Pasteboard ?? data?.pasteboard));
      pagesHistoryRef.current = clearPagesHistoryState(pagesHistoryRef.current);
      historyGateRef.current = null;
      if (historyCoalesceTimerRef.current) {
        clearTimeout(historyCoalesceTimerRef.current);
      }
      bumpHistoryUiRef.current();
      setTitulo(data?.Titulo || "");
      tituloSavedRef.current = String(data?.Titulo || "").trim();
      setCategoryLabel(data?.CategoryLabel || "");
      setStudioLine(data?.StudioLine || "");
      setChromeStyle(normalizeChromeStyle(data?.ChromeStyleJson));
      setActiveSlotId(null);
      setSelectedSlotIds(new Set());
      if (boardApiRef.current) {
        reportCloseGuard(String(data?.Status || "") === "ready");
      }

      setPoolLoading(true);
      let poolData = { searches: [], items: [] };
      const propuestaBoard = isPropuestaBoard(data);
      if (propuestaBoard && !boardApiRef.current) {
        try {
          poolData = await getSkillAgentMoodboardProjectPool(moodboardId);
          setPoolSourceFilter("sketch");
        } catch (poolErr) {
          setPoolError(
            poolErr?.response?.data?.error ||
              t("skillAgent.moodboard.error.projectPool", {
                defaultValue: "No se pudo cargar el pool del proyecto.",
              }),
          );
          poolData = { searches: [], items: [] };
        }
      } else if (boardApiRef.current?.getPool) {
        try {
          poolData = await boardApiRef.current.getPool(moodboardId);
        } catch (poolErr) {
          setPoolError(
            poolErr?.response?.data?.error ||
              t("skillAgent.moodboard.error.poolSearch"),
          );
          poolData = { searches: [], items: [] };
        }
      } else {
        try {
          poolData = await getSkillAgentMoodboardPool(moodboardId);
        } catch (poolErr) {
          try {
            poolData = await loadPoolFromSearches(data);
          } catch {
            setPoolError(
              poolErr?.response?.data?.error ||
                t("skillAgent.moodboard.error.poolSearch"),
            );
            poolData = { searches: [], items: [] };
          }
        }
        if (
          !Array.isArray(poolData?.items) ||
          (!poolData.items.length && data?.CustomerId)
        ) {
          try {
            const fallback = await loadPoolFromSearches(data);
            if (fallback.items?.length) poolData = fallback;
          } catch {
            /* keep primary */
          }
        }
      }
      setPool({
        searches: Array.isArray(poolData?.searches) ? poolData.searches : [],
        items: Array.isArray(poolData?.items) ? poolData.items : [],
        projectMode: Boolean(propuestaBoard),
      });
      try {
        const eligible = isOfflineEligibleBoardKind(data?.Kind || data?.kind);
        if (eligible) {
          // Auto-offline: al abrir el board se cachea sin botón; assets en background.
          setOfflineEnabled(true);
          offlineEnabledRef.current = true;
          setOfflinePending(await hasOfflinePending(moodboardId));
          await putOfflineBoardSnapshot(moodboardId, data, {
            offlineEnabled: true,
          });
          void prefetchBoardForOffline(moodboardId, {
            fetchBoard: async () => data,
            fetchAssetBlob:
              boardApiRef.current?.fetchAssetBlob ||
              fetchSkillAgentMoodboardAssetBlob,
          }).catch(() => {
            /* assets parciales siguen valiendo para layout */
          });
        } else {
          setOfflineEnabled(false);
          setOfflinePending(false);
        }
      } catch {
        /* ignore offline bookkeeping */
      }
    } catch (err) {
      let openedOffline = false;
      try {
        const cached = await getOfflineBoardSnapshot(moodboardId);
        if (cached) {
          openedOffline = true;
          setBoard(cached);
          if (cached?.updatedAt)
            expectedUpdatedAtRef.current = cached.updatedAt;
          setStaleConflict(false);
          setPeerViewers([]);
          setAssets(Array.isArray(cached?.assets) ? cached.assets : []);
          {
            const seed = {
              categoryLabel: cached?.CategoryLabel || "",
              studioLine: cached?.StudioLine || "",
              chromeStyle: normalizeChromeStyle(cached?.ChromeStyleJson),
            };
            const skipChromeSeed =
              Boolean(boardApiRef.current) ||
              String(cached?.Kind || "").toLowerCase() === "caratula";
            setPages(
              normalizePages(cached?.PagesJson).map((p) =>
                skipChromeSeed
                  ? { ...p, hf: 1 }
                  : loadPageHeaderFooterMarks(p, seed),
              ),
            );
          }
          setPasteboard(
            normalizePasteboard(cached?.Pasteboard ?? cached?.pasteboard),
          );
          pagesHistoryRef.current = clearPagesHistoryState(
            pagesHistoryRef.current,
          );
          historyGateRef.current = null;
          bumpHistoryUiRef.current();
          setTitulo(cached?.Titulo || "");
          tituloSavedRef.current = String(cached?.Titulo || "").trim();
          setCategoryLabel(cached?.CategoryLabel || "");
          setStudioLine(cached?.StudioLine || "");
          setChromeStyle(normalizeChromeStyle(cached?.ChromeStyleJson));
          setActiveSlotId(null);
          setSelectedSlotIds(new Set());
          setPool({ searches: [], items: [] });
          setOfflineEnabled(true);
          setOfflinePending(await hasOfflinePending(moodboardId));
          setOkMsg(t("skillAgent.moodboard.offline.loadedFromCache"));
          setError("");
        }
      } catch {
        /* fall through */
      }
      if (!openedOffline) {
        setError(
          err?.response?.data?.error || t("skillAgent.moodboard.error.load"),
        );
      }
    } finally {
      setPoolLoading(false);
      setLoading(false);
      // Enable autosave after React applies the loaded state.
      setTimeout(() => {
        skipAutosaveRef.current = false;
      }, 100);
    }
  }, [moodboardId, t, reportCloseGuard]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    onChangedRef.current = onChanged;
  }, [onChanged]);

  const boardKindRef = useRef(null);
  boardKindRef.current = board?.Kind || board?.kind;

  useEffect(() => {
    const onOnline = () => {
      const wasOffline = wasOnlineRef.current === false;
      wasOnlineRef.current = true;
      setIsOnline(true);
      if (wasOffline) {
        setConnectivityNotice("syncing");
      }
    };
    const onOffline = () => {
      wasOnlineRef.current = false;
      setIsOnline(false);
      setConnectivityNotice("offline");
      // Si estaban editando un board elegible, activar cola local al instante
      if (isOfflineEligibleBoardKind(boardKindRef.current)) {
        setOfflineEnabled(true);
        offlineEnabledRef.current = true;
      }
    };
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    const onlineNow = isBrowserOnline();
    wasOnlineRef.current = onlineNow;
    setIsOnline(onlineNow);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  const usedSearchItemIds = useMemo(() => {
    const s = new Set();
    for (const a of assets) {
      if (a.SearchItemId) s.add(Number(a.SearchItemId));
    }
    return s;
  }, [assets]);

  const meetingSessionId = useMemo(() => {
    const linked = Number(board?.MeetingSessionId);
    if (Number.isInteger(linked) && linked > 0) return linked;
    for (const a of assets) {
      const m = String(a?.SourceUrl || "").match(
        /\/meeting-sessions\/(\d+)\//i,
      );
      if (m) {
        const id = Number(m[1]);
        if (Number.isInteger(id) && id > 0) return id;
      }
    }
    return null;
  }, [board?.MeetingSessionId, assets]);

  const loadMeetingPool = useCallback(async () => {
    if (!moodboardId || boardApiRef.current) return;
    setMeetingPoolLoading(true);
    setMeetingPoolError("");
    try {
      const data = await getSkillAgentMoodboardMeetingPool(moodboardId);
      setMeetingPool({
        session: data?.session || null,
        items: Array.isArray(data?.items) ? data.items : [],
        MeetingSessionId: data?.MeetingSessionId || null,
      });
      if (data?.MeetingSessionId && board) {
        setBoard((prev) =>
          prev ? { ...prev, MeetingSessionId: data.MeetingSessionId } : prev,
        );
      }
    } catch (err) {
      setMeetingPoolError(
        err?.response?.data?.error ||
          t("skillAgent.moodboard.error.meetingPool"),
      );
      setMeetingPool({ session: null, items: [], MeetingSessionId: null });
    } finally {
      setMeetingPoolLoading(false);
    }
  }, [moodboardId, board, t]);

  const openAddMeetingPhotos = async () => {
    setMeetingPoolPick(new Set());
    setAddMeetingPhotosOpen(true);
    await loadMeetingPool();
  };

  const handleAddFromMeeting = async () => {
    const ids = [...meetingPoolPick]
      .map((id) => Number(id))
      .filter((id) => id > 0);
    if (!ids.length || !moodboardId) return;
    if (!isOnlineRef.current) {
      setError(t("skillAgent.moodboard.offline.networkBlocked"));
      return;
    }
    setBusy(true);
    setError("");
    try {
      const data = await (
        boardApiRef.current?.addAssets || addSkillAgentMoodboardAssets
      )(moodboardId, {
        meetingPhotoIds: ids,
        sessionId:
          meetingSessionId || meetingPool.MeetingSessionId || undefined,
      });
      const added = Array.isArray(data?.added) ? data.added : [];
      const serverPages = normalizePages(data?.PagesJson);
      const boardWasEmpty = !pagesRef.current.some((p) =>
        (p.slots || []).some((s) => s.assetId != null),
      );
      const placedOnServer = added.every((a) =>
        serverPages.some((p) =>
          p.slots.some((s) => Number(s.assetId) === Number(a.id)),
        ),
      );
      ignoreOwnSocketUntilRef.current = Date.now() + 5000;
      if (boardWasEmpty && placedOnServer) {
        applyBoard(data, { resetHistory: true });
      } else {
        applyBoard(data, { keepLocalPages: true });
      }
      setAddMeetingPhotosOpen(false);
      setMeetingPoolPick(new Set());
      onChanged?.();
      setOkMsg(
        t("skillAgent.moodboard.ok.assetsAddedPlace", {
          n: added.length || ids.length,
        }),
      );
      ignoreOwnSocketUntilRef.current = Date.now() + 2500;
      await loadMeetingPool();
    } catch (err) {
      setError(
        err?.response?.data?.error || t("skillAgent.moodboard.error.addAssets"),
      );
    } finally {
      setBusy(false);
    }
  };

  const usedOnPagesAssetIds = useMemo(() => {
    const s = new Set();
    for (const p of pages || []) {
      for (const slot of p?.slots || []) {
        if (slot?.assetId != null) s.add(Number(slot.assetId));
      }
    }
    return s;
  }, [pages]);

  // allowOutside: al arrastrar fuera de la mesa no reclavar x/y en cada render
  const currentPage = useMemo(
    () =>
      normalizeFreePage(pages[pageIdx] || emptyFreePage(), {
        allowOutside: true,
      }),
    [pages, pageIdx],
  );
  const selectedTextMark = useMemo(() => {
    if (selectedMarkId == null) return null;
    const m = (currentPage.marks || []).find(
      (x) => String(x.id) === String(selectedMarkId),
    );
    return m?.type === "text" ? m : null;
  }, [currentPage, selectedMarkId]);
  const showTextStyleControls =
    markTool === "text" ||
    Boolean(textDraft) ||
    Boolean(selectedTextMark) ||
    [...selectedMarkIds].some((id) =>
      (currentPage.marks || []).some(
        (m) => m?.type === "text" && String(m.id) === String(id),
      ),
    );
  const activeSlot = useMemo(
    () => (currentPage.slots || []).find((s) => s.id === activeSlotId) || null,
    [currentPage, activeSlotId],
  );
  const activeAsset = useMemo(() => {
    if (!activeSlot?.assetId) return null;
    return (
      assets.find((a) => Number(a.id) === Number(activeSlot.assetId)) || null
    );
  }, [assets, activeSlot]);

  const isCutoutAsset = useCallback((asset) => {
    if (!asset) return false;
    const kind = String(asset.Kind || "").toLowerCase();
    if (kind === "cutout") return true;
    return /\(\s*sin fondo\s*\)/i.test(String(asset.Title || ""));
  }, []);

  const activeAssetIsCutout = useMemo(
    () => isCutoutAsset(activeAsset),
    [activeAsset, isCutoutAsset],
  );

  /** AssetIds únicos de las casillas seleccionadas (o la activa) para quitar fondo en lote. */
  const removeBgAssetIds = useMemo(() => {
    const slots =
      selectedSlotIds.size > 0
        ? (currentPage.slots || []).filter((s) => selectedSlotIds.has(s.id))
        : activeSlot
          ? [activeSlot]
          : [];
    const ids = [];
    const seen = new Set();
    for (const s of slots) {
      if (s?.assetId == null) continue;
      const id = Number(s.assetId);
      if (!Number.isFinite(id) || seen.has(id)) continue;
      seen.add(id);
      ids.push(id);
    }
    return ids;
  }, [currentPage, selectedSlotIds, activeSlot]);

  const removeBgHasCutout = useMemo(() => {
    for (const id of removeBgAssetIds) {
      const a = assets.find((x) => Number(x.id) === id);
      if (isCutoutAsset(a)) return true;
    }
    return false;
  }, [removeBgAssetIds, assets, isCutoutAsset]);

  const layeredSlots = useMemo(
    () => sortSlotsByLayer(currentPage.slots || []),
    [currentPage],
  );

  const applyViewZoomFit = useCallback(() => {
    const el = viewScrollRef.current;
    if (!el) return null;
    const next = pageViewZoomFromScrollBox(el);
    if (next == null) return null;
    if (!viewZoomUserRef.current) setViewZoom(next);
    return next;
  }, []);

  const setViewZoomManual = useCallback((updater) => {
    viewZoomUserRef.current = true;
    setViewZoom((z) =>
      clampPageViewZoom(typeof updater === "function" ? updater(z) : updater),
    );
  }, []);

  const bindViewScrollRef = useCallback(
    (node) => {
      viewScrollRef.current = node;
      if (viewFitRoRef.current) {
        viewFitRoRef.current.disconnect();
        viewFitRoRef.current = null;
      }
      if (!node) return;
      applyViewZoomFit();
      requestAnimationFrame(() => {
        applyViewZoomFit();
        requestAnimationFrame(applyViewZoomFit);
      });
      if (typeof ResizeObserver === "undefined") return;
      const ro = new ResizeObserver(() => applyViewZoomFit());
      ro.observe(node);
      viewFitRoRef.current = ro;
    },
    [applyViewZoomFit],
  );

  // Encajar cuando ya hay board (el spinner no monta el lienzo).
  useEffect(() => {
    if (!board) return undefined;
    applyViewZoomFit();
    const t = window.setTimeout(applyViewZoomFit, 50);
    return () => window.clearTimeout(t);
  }, [board, applyViewZoomFit]);

  // Ctrl/Cmd + rueda: zoom de vista (listener no-passive para poder preventDefault)
  useEffect(() => {
    const el = viewScrollRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      const dir = e.deltaY > 0 ? -1 : 1;
      setViewZoomManual((z) => z + dir * PAGE_VIEW_ZOOM_STEP);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [board, setViewZoomManual]);

  const applyBoardCamFit = useCallback((opts = {}) => {
    const { force = false } = opts;
    const el = boardViewportRef.current;
    if (!el) return;
    if (!force && boardCamUserRef.current) return;
    const rect = el.getBoundingClientRect();
    if (!(rect.width > 8) || !(rect.height > 8)) return;
    boardViewportSizeRef.current = { w: rect.width, h: rect.height };
    const cam = fitBoardCamera(pagesRef.current, rect.width, rect.height, {
      pasteboard: pasteboardRef.current,
    });
    boardCamUserRef.current = false;
    setBoardCam((prev) => ({
      panX: cam.panX,
      panY: cam.panY,
      zoom: cam.zoom,
      rotation: Number(prev.rotation) || 0,
    }));
  }, []);

  const setWorkspaceModeSafe = useCallback(
    (mode) => {
      const next =
        mode === WORKSPACE_MODE_PAGE
          ? WORKSPACE_MODE_PAGE
          : WORKSPACE_MODE_BOARD;
      setWorkspaceMode(next);
      writeStoredWorkspaceMode(next);
      if (next === WORKSPACE_MODE_BOARD) {
        boardCamUserRef.current = false;
        requestAnimationFrame(() => {
          applyBoardCamFit({ force: true });
          requestAnimationFrame(() => applyBoardCamFit({ force: true }));
        });
      } else {
        requestAnimationFrame(() => applyViewZoomFit());
      }
    },
    [applyBoardCamFit, applyViewZoomFit],
  );

  // Encajar solo al abrir el moodboard / entrar en vista tablero / nueva página.
  useEffect(() => {
    if (!board || workspaceMode !== WORKSPACE_MODE_BOARD) return undefined;
    boardCamUserRef.current = false;
    applyBoardCamFit({ force: true });
    const t = window.setTimeout(() => applyBoardCamFit({ force: true }), 60);
    return () => window.clearTimeout(t);
  }, [board?.id, workspaceMode, applyBoardCamFit]);

  // Resize: solo re-encajar si el usuario no ha tocado la cámara y el tamaño cambió de verdad.
  useEffect(() => {
    if (workspaceMode !== WORKSPACE_MODE_BOARD) return undefined;
    const el = boardViewportRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    let timer = null;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      const w = entry?.contentRect?.width || el.getBoundingClientRect().width;
      const h = entry?.contentRect?.height || el.getBoundingClientRect().height;
      const prev = boardViewportSizeRef.current;
      const dw = Math.abs(w - (prev.w || 0));
      const dh = Math.abs(h - (prev.h || 0));
      if (dw < 24 && dh < 24) return;
      boardViewportSizeRef.current = { w, h };
      if (boardCamUserRef.current) return;
      clearTimeout(timer);
      timer = setTimeout(() => applyBoardCamFit(), 80);
    });
    ro.observe(el);
    return () => {
      clearTimeout(timer);
      ro.disconnect();
    };
  }, [workspaceMode, board?.id, applyBoardCamFit]);

  /**
   * VectorCraft: encuadrar un artboard es gesto explícito (botón / fit),
   * nunca al seleccionar. Marca la cámara como "del usuario" para que el RO
   * no vuelva a auto-encajar.
   */
  const focusBoardPage = useCallback((idx) => {
    const el = boardViewportRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const keepZoom = boardCamRef.current.zoom || 0.85;
    const cam = focusBoardCameraOnPage(
      pagesRef.current,
      idx,
      rect.width,
      rect.height,
      { targetZoom: keepZoom },
    );
    boardCamUserRef.current = true;
    setBoardCam({ panX: cam.panX, panY: cam.panY, zoom: cam.zoom });
  }, []);

  const onBoardViewportWheel = useCallback((e) => {
    if (workspaceModeRef.current !== WORKSPACE_MODE_BOARD) return;
    e.preventDefault();
    const el = boardViewportRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const dir = e.deltaY > 0 ? -1 : 1;
    boardCamUserRef.current = true;
    setBoardCam((prev) => {
      const nextZoom = clampBoardCamZoom(prev.zoom + dir * BOARD_CAM_ZOOM_STEP);
      if (nextZoom === prev.zoom) return prev;
      const wx = (mx - prev.panX) / prev.zoom;
      const wy = (my - prev.panY) / prev.zoom;
      return {
        zoom: nextZoom,
        panX: mx - wx * nextZoom,
        panY: my - wy * nextZoom,
      };
    });
  }, []);

  useEffect(() => {
    const el = boardViewportRef.current;
    if (!el || workspaceMode !== WORKSPACE_MODE_BOARD) return undefined;
    el.addEventListener("wheel", onBoardViewportWheel, { passive: false });
    return () => el.removeEventListener("wheel", onBoardViewportWheel);
  }, [workspaceMode, board?.id, onBoardViewportWheel]);

  const onBoardPanPointerDown = useCallback((e) => {
    if (workspaceModeRef.current !== WORKSPACE_MODE_BOARD) return;
    if (e.button !== 0 && e.button !== 1) return;
    // Zoom (Z): clic acerca/aleja — no iniciar pan del tablero.
    if (zoomModeRef.current) return;
    // Cualquier interacción en el tablero: no auto-encajar después.
    boardCamUserRef.current = true;
    const panTool = boardPanActiveRef.current;
    // Con Mano/Espacio: pan también sobre mesas (slots con pointer-events:none).
    // Sin herramienta: solo fondo gris / capa board-pan.
    if (!panTool) {
      if (
        e.target?.closest?.("[data-pasteboard-item]") ||
        e.target?.closest?.("[data-board-chrome]")
      ) {
        return;
      }
      if (e.target !== e.currentTarget && !e.target?.dataset?.boardPan) return;
    } else if (e.target?.closest?.("[data-board-chrome]")) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    const start = boardCamRef.current;
    boardPanDragRef.current = {
      pointerId: e.pointerId,
      x0: e.clientX,
      y0: e.clientY,
      panX0: start.panX,
      panY0: start.panY,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, []);

  const onBoardPanPointerMove = useCallback((e) => {
    const drag = boardPanDragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    boardCamUserRef.current = true;
    setBoardCam((prev) => ({
      ...prev,
      panX: drag.panX0 + (e.clientX - drag.x0),
      panY: drag.panY0 + (e.clientY - drag.y0),
    }));
  }, []);

  const onBoardPanPointerUp = useCallback((e) => {
    const drag = boardPanDragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    boardPanDragRef.current = null;
    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    } catch {
      /* ignore */
    }
  }, []);

  const applyBoard = (
    data,
    { fromServer = true, resetHistory = false, keepLocalPages = false } = {},
  ) => {
    if (!data) return;
    if (fromServer) skipAutosaveRef.current = true;
    setBoard(data);
    if (data.updatedAt) expectedUpdatedAtRef.current = data.updatedAt;
    if (Array.isArray(data.assets)) setAssets(data.assets);
    // Tras un autosave, no pisar el collage si el usuario siguió moviendo cosas.
    if (data.PagesJson && !keepLocalPages) {
      const seed = {
        categoryLabel:
          data.CategoryLabel != null ? data.CategoryLabel : categoryLabel || "",
        studioLine:
          data.StudioLine != null ? data.StudioLine : studioLine || "",
        chromeStyle: normalizeChromeStyle(
          data.ChromeStyleJson != null ? data.ChromeStyleJson : chromeStyle,
        ),
      };
      const skipChromeSeed =
        Boolean(boardApiRef.current) ||
        String(data?.Kind || "").toLowerCase() === "caratula";
      setPages(
        normalizePages(data.PagesJson).map((p) =>
          skipChromeSeed ? { ...p, hf: 1 } : loadPageHeaderFooterMarks(p, seed),
        ),
      );
      if (Array.isArray(data.Pasteboard) || Array.isArray(data.pasteboard)) {
        setPasteboard(normalizePasteboard(data.Pasteboard ?? data.pasteboard));
      } else if (!keepLocalPages) {
        setPasteboard([]);
      }
      if (resetHistory) {
        pagesHistoryRef.current = clearPagesHistoryState(
          pagesHistoryRef.current,
        );
        historyGateRef.current = null;
        bumpHistoryUiRef.current();
      }
    }
    if (data.Titulo != null) setTitulo(data.Titulo);
    if (data.CategoryLabel != null) setCategoryLabel(data.CategoryLabel);
    if (data.StudioLine != null) setStudioLine(data.StudioLine);
    if (data.ChromeStyleJson != null) {
      setChromeStyle(normalizeChromeStyle(data.ChromeStyleJson));
    }
    if (fromServer) {
      setTimeout(() => {
        skipAutosaveRef.current = false;
      }, 80);
    }
  };

  const beginPagesHistoryGesture = useCallback(() => {
    clearTimeout(historyCoalesceTimerRef.current);
    if (
      historyGateRef.current === "open" ||
      historyGateRef.current === "pending"
    ) {
      return;
    }
    historyGateRef.current = "pending";
  }, []);

  const endPagesHistoryGesture = useCallback(() => {
    clearTimeout(historyCoalesceTimerRef.current);
    historyGateRef.current = null;
  }, []);

  /** Guarda snapshot actual antes de mutar. coalesceMs agrupa rueda/zoom rápido. */
  const notePagesHistory = useCallback(({ coalesceMs = 0 } = {}) => {
    if (historyGateRef.current === "open") return;
    if (historyGateRef.current === "pending") {
      pagesHistoryRef.current = pushPagesHistory(
        pagesHistoryRef.current,
        pagesRef.current,
      );
      historyGateRef.current = "open";
      bumpHistoryUiRef.current();
      return;
    }
    pagesHistoryRef.current = pushPagesHistory(
      pagesHistoryRef.current,
      pagesRef.current,
    );
    bumpHistoryUiRef.current();
    if (coalesceMs > 0) {
      historyGateRef.current = "open";
      clearTimeout(historyCoalesceTimerRef.current);
      historyCoalesceTimerRef.current = setTimeout(() => {
        historyGateRef.current = null;
      }, coalesceMs);
    }
  }, []);

  const applyPagesUndo = useCallback(() => {
    endPagesHistoryGesture();
    const step = undoPagesHistory(pagesHistoryRef.current, pagesRef.current);
    if (!step.pages) return;
    pagesHistoryRef.current = step.hist;
    setPages(step.pages);
    bumpHistoryUiRef.current();
  }, [endPagesHistoryGesture]);

  const applyPagesRedo = useCallback(() => {
    endPagesHistoryGesture();
    const step = redoPagesHistory(pagesHistoryRef.current, pagesRef.current);
    if (!step.pages) return;
    pagesHistoryRef.current = step.hist;
    setPages(step.pages);
    bumpHistoryUiRef.current();
  }, [endPagesHistoryGesture]);

  const canUndo = canUndoPages(pagesHistoryRef.current);
  const canRedo = canRedoPages(pagesHistoryRef.current);
  void historyUiRev; // fuerza re-render de botones undo/redo

  // Tras Ctrl+Z/Y: re-sync Fill/Stroke UI con la marca restaurada.
  useEffect(() => {
    if (!selectedMarkId) return;
    const mark = (pages[pageIdx]?.marks || []).find(
      (m) => String(m.id) === String(selectedMarkId),
    );
    if (!mark) return;
    const paint = markPaintColors(mark);
    setMarkStrokeColor(paint.strokeColor);
    setMarkFillColor(paint.fill);
    setMarkStroke(
      mark.stroke != null
        ? Number(mark.stroke) || 0
        : mark.type === "text" || mark.type === "textpath"
          ? 0
          : 2,
    );
    if (mark.type === "text" || mark.type === "textpath") {
      setMarkColor(textMarkFillColor(mark));
    } else {
      setMarkColor(paint.strokeColor || mark.color || DEFAULT_MARK_COLOR);
    }
  }, [pages, pageIdx, selectedMarkId, historyUiRev]);

  const updateCurrentPageSlots = (
    updater,
    { history = true, coalesceMs = 0, allowOutside = false } = {},
  ) => {
    if (history) notePagesHistory({ coalesceMs });
    const idx = pageIdx;
    const clampFn = allowOutside ? clampSlotAllowOutside : clampSlot;
    const ejected = ejectedSlotIdsRef.current;
    setPages((prev) =>
      prev.map((p, i) => {
        if (i !== idx) return p;
        const base = normalizeFreePage(p);
        const prevById = new Map(
          (base.slots || []).map((s) => [String(s.id), s]),
        );
        const slots =
          typeof updater === "function" ? updater(base.slots) : updater;
        return {
          ...base,
          slots: (Array.isArray(slots) ? slots : [])
            .filter((s) => !ejected.has(String(s?.id)))
            .map((s) => {
              const clamped = clampFn(s);
              return syncSlotClipWithFrame(prevById.get(String(s.id)), clamped);
            }),
          // Copia marks para no compartir ref con otras páginas / history
          marks: (base.marks || []).map((m) => ({ ...m })),
        };
      }),
    );
  };

  const updateCurrentPageMarks = (
    updater,
    { history = true, coalesceMs = 0, allowOutside = false } = {},
  ) => {
    if (history) notePagesHistory({ coalesceMs });
    const idx = pageIdx;
    setPages((prev) =>
      prev.map((p, i) => {
        if (i !== idx) return p;
        // marks siempre allowOutside: si al crear un cuadrado se clampea con
        // allowOutside:false, las rejillas/textos ya sacados de la mesa vuelven.
        const base = normalizeFreePage(p, { allowOutside: true });
        const marks =
          typeof updater === "function" ? updater(base.marks || []) : updater;
        const clamped = (Array.isArray(marks) ? marks : []).map((m) =>
          clampMarkAllowOutside(m),
        );
        // allowOutside en el caller = gesto en vivo → no scrub (evita borrar al vuelo)
        const cleaned = allowOutside ? clamped : scrubChromeMarkDupes(clamped);
        const { chrome: _drop, ...rest } = base;
        return {
          ...rest,
          hf: 1,
          // Copia slots para no compartir ref (evita mutar zoom/pan de otra página)
          slots: (base.slots || []).map((s) => ({ ...s })),
          marks: cleaned,
        };
      }),
    );
  };

  const handleRotateSelection = useCallback(
    (deltaDeg = 90) => {
      const slotIds = [...selectedSlotIdsRef.current];
      const markIds = [...selectedMarkIdsRef.current];
      if (!slotIds.length && !markIds.length) return;
      notePagesHistory();
      if (slotIds.length) {
        updateCurrentPageSlots(
          (slots) => rotateSlotsBy(slots, slotIds, deltaDeg),
          { history: false },
        );
      }
      if (markIds.length) {
        updateCurrentPageMarks(
          (marks) => rotateMarksBy(marks, markIds, deltaDeg),
          { history: false },
        );
      }
    },
    [pageIdx, notePagesHistory],
  );

  const handleScaleSelection = useCallback(
    (factor = 1.1) => {
      const slotIds = [...selectedSlotIdsRef.current];
      const markIds = [...selectedMarkIdsRef.current];
      if (!slotIds.length && !markIds.length) return;
      const f = Math.min(8, Math.max(0.05, Number(factor) || 1.1));
      notePagesHistory();
      if (slotIds.length) {
        updateCurrentPageSlots((slots) => scaleSlotsBy(slots, slotIds, f), {
          history: false,
        });
      }
      if (markIds.length) {
        const page = pagesRef.current[pageIdx];
        const origins = (page?.marks || []).filter((m) =>
          markIds.includes(String(m.id)),
        );
        const bbox0 = marksBBox(origins);
        if (bbox0) {
          const cx = bbox0.x + bbox0.w / 2;
          const cy = bbox0.y + bbox0.h / 2;
          const bbox1 = {
            x: cx - (bbox0.w * f) / 2,
            y: cy - (bbox0.h * f) / 2,
            w: bbox0.w * f,
            h: bbox0.h * f,
          };
          const scaled = scaleMarksWithBBox(origins, bbox0, bbox1);
          const byId = new Map(scaled.map((m) => [String(m.id), m]));
          updateCurrentPageMarks(
            (marks) =>
              marks.map((m) => {
                const next = byId.get(String(m.id));
                return next ? clampMark({ ...next, id: m.id }) : m;
              }),
            { history: false },
          );
        }
      }
    },
    [pageIdx, notePagesHistory],
  );

  /** Rotar / Escalar interactivo (arrastra alrededor del centro de selección). */
  const handleLiveTransform = useCallback(
    (mode, p, meta = {}) => {
      const phase = meta.phase || "move";
      const shiftKey = Boolean(meta.shiftKey);
      if (phase === "start") {
        const seedMarks = (meta.seedMarkIds || []).map(String);
        const markIds = seedMarks.length
          ? seedMarks
          : [...selectedMarkIdsRef.current];
        const slotIds = [...selectedSlotIdsRef.current];
        if (!markIds.length && !slotIds.length) {
          setOkMsg(
            mode === "rotate"
              ? "Rotar: selecciona foto(s) o marca(s) y arrastra"
              : mode === "shear"
                ? "Inclinar: selecciona foto(s) o marca(s) y arrastra"
                : "Escalar: selecciona foto(s) o marca(s) y arrastra",
          );
          return false;
        }
        const page = pagesRef.current[pageIdx];
        const markOrigins = (page?.marks || [])
          .filter((m) => markIds.includes(String(m.id)))
          .map((m) => ({ ...m }));
        const slotOrigins = (page?.slots || [])
          .filter((s) => slotIds.includes(String(s.id)))
          .map((s) => ({ ...s }));
        const markBox = marksBBox(markOrigins);
        let x0 = Infinity;
        let y0 = Infinity;
        let x1 = -Infinity;
        let y1 = -Infinity;
        if (markBox) {
          x0 = markBox.x;
          y0 = markBox.y;
          x1 = markBox.x + markBox.w;
          y1 = markBox.y + markBox.h;
        }
        slotOrigins.forEach((s) => {
          const sx = Number(s.x) || 0;
          const sy = Number(s.y) || 0;
          const sw = Number(s.w) || 0;
          const sh = Number(s.h) || 0;
          x0 = Math.min(x0, sx);
          y0 = Math.min(y0, sy);
          x1 = Math.max(x1, sx + sw);
          y1 = Math.max(y1, sy + sh);
        });
        if (!Number.isFinite(x0)) return false;
        const bbox0 = {
          x: x0,
          y: y0,
          w: Math.max(0.001, x1 - x0),
          h: Math.max(0.001, y1 - y0),
        };
        const cx = bbox0.x + bbox0.w / 2;
        const cy = bbox0.y + bbox0.h / 2;
        beginPagesHistoryGesture();
        liveXfRef.current = {
          mode,
          cx,
          cy,
          bbox0,
          startX: p.x,
          startY: p.y,
          startAng: Math.atan2(p.y - cy, p.x - cx),
          startDist: Math.max(0.001, Math.hypot(p.x - cx, p.y - cy)),
          markOrigins,
          slotOrigins,
        };
        return true;
      }
      const st = liveXfRef.current;
      if (!st || !p) {
        if (phase === "end") {
          liveXfRef.current = null;
          endPagesHistoryGesture();
        }
        return;
      }
      if (st.mode === "rotate") {
        let delta =
          ((Math.atan2(p.y - st.cy, p.x - st.cx) - st.startAng) * 180) /
          Math.PI;
        if (shiftKey) delta = Math.round(delta / 45) * 45;
        if (st.markOrigins.length) {
          const rotated = rotateMarksAroundBBox(
            st.markOrigins,
            st.bbox0,
            delta,
          );
          const byId = new Map(rotated.map((m) => [String(m.id), m]));
          updateCurrentPageMarks(
            (marks) =>
              marks.map((m) => {
                const next = byId.get(String(m.id));
                return next ? clampMark({ ...next, id: m.id }) : m;
              }),
            { history: false, coalesceMs: 48 },
          );
        }
        if (st.slotOrigins.length) {
          const rotated = rotateSlotsAroundBBox(
            st.slotOrigins,
            st.bbox0,
            delta,
          );
          const byId = new Map(rotated.map((s) => [s.id, s]));
          updateCurrentPageSlots(
            (slots) => slots.map((s) => byId.get(s.id) || s),
            { history: false, allowOutside: true, coalesceMs: 48 },
          );
        }
      } else if (st.mode === "shear") {
        let dx = p.x - st.startX;
        let dy = p.y - st.startY;
        if (shiftKey) {
          if (Math.abs(dx) >= Math.abs(dy)) dy = 0;
          else dx = 0;
        }
        const handle = Math.abs(dx) >= Math.abs(dy) ? "s" : "e";
        if (st.markOrigins.length) {
          const sheared = shearMarksInBBox(
            st.markOrigins,
            st.bbox0,
            handle,
            dx,
            dy,
          );
          const byId = new Map(sheared.map((m) => [String(m.id), m]));
          updateCurrentPageMarks(
            (marks) =>
              marks.map((m) => {
                const next = byId.get(String(m.id));
                return next ? clampMark({ ...next, id: m.id }) : m;
              }),
            { history: false, coalesceMs: 48 },
          );
        }
        if (st.slotOrigins.length) {
          const sheared = shearSlotsInBBox(
            st.slotOrigins,
            st.bbox0,
            handle,
            dx,
            dy,
          );
          const byId = new Map(sheared.map((s) => [s.id, s]));
          updateCurrentPageSlots(
            (slots) => slots.map((s) => byId.get(s.id) || s),
            { history: false, allowOutside: true, coalesceMs: 48 },
          );
        }
      } else {
        const dist = Math.max(0.001, Math.hypot(p.x - st.cx, p.y - st.cy));
        let factor = dist / st.startDist;
        if (shiftKey) factor = Math.round(factor * 10) / 10;
        factor = Math.min(8, Math.max(0.05, factor));
        const bbox1 = {
          x: st.cx - (st.bbox0.w * factor) / 2,
          y: st.cy - (st.bbox0.h * factor) / 2,
          w: st.bbox0.w * factor,
          h: st.bbox0.h * factor,
        };
        if (st.markOrigins.length) {
          const scaled = scaleMarksWithBBox(st.markOrigins, st.bbox0, bbox1);
          const byId = new Map(scaled.map((m) => [String(m.id), m]));
          updateCurrentPageMarks(
            (marks) =>
              marks.map((m) => {
                const next = byId.get(String(m.id));
                return next ? clampMark({ ...next, id: m.id }) : m;
              }),
            { history: false, coalesceMs: 48 },
          );
        }
        if (st.slotOrigins.length) {
          const scaled = scaleSlotsWithBBox(st.slotOrigins, st.bbox0, bbox1);
          const byId = new Map(scaled.map((s) => [s.id, s]));
          updateCurrentPageSlots(
            (slots) => slots.map((s) => byId.get(s.id) || s),
            { history: false, allowOutside: true, coalesceMs: 48 },
          );
        }
      }
      if (phase === "end") {
        liveXfRef.current = null;
        endPagesHistoryGesture();
      }
    },
    [pageIdx, beginPagesHistoryGesture, endPagesHistoryGesture],
  );

  const handleReflectSelection = useCallback(() => {
    const slotIds = [...selectedSlotIdsRef.current];
    const markIds = [...selectedMarkIdsRef.current];
    if (!slotIds.length && !markIds.length) return;
    notePagesHistory();
    if (slotIds.length) {
      updateCurrentPageSlots((slots) => reflectSlotsH(slots, slotIds), {
        history: false,
      });
    }
    if (markIds.length) {
      updateCurrentPageMarks((marks) => reflectMarksH(marks, markIds), {
        history: false,
      });
    }
  }, [pageIdx, notePagesHistory]);

  const handleFlipVertical = useCallback(() => {
    const slotIds = [...selectedSlotIdsRef.current];
    const markIds = [...selectedMarkIdsRef.current];
    if (!slotIds.length && !markIds.length) return;
    notePagesHistory();
    if (slotIds.length) {
      updateCurrentPageSlots((slots) => reflectSlotsV(slots, slotIds), {
        history: false,
      });
    }
    if (markIds.length) {
      updateCurrentPageMarks((marks) => reflectMarksV(marks, markIds), {
        history: false,
      });
    }
  }, [pageIdx, notePagesHistory]);

  const handleMeasureDone = useCallback(
    (draft) => {
      if (!draft) return;
      const x1 = Number(draft.x1) || 0;
      const y1 = Number(draft.y1) || 0;
      const x2 = Number(draft.x2) || 0;
      const y2 = Number(draft.y2) || 0;
      const d = dimensionMetrics({ x1, y1, x2, y2, unit: "pct" });
      if (d.len < 0.004) return;
      notePagesHistory();
      updateCurrentPageMarks(
        (marks) => [
          ...marks,
          clampMark({
            id: newMarkId(),
            type: "dimension",
            x1,
            y1,
            x2,
            y2,
            color: draft.color || markColor || "#2563eb",
            stroke: Number(draft.stroke) || 1.5,
            unit: "pct",
            label: d.label,
          }),
        ],
        { history: false },
      );
      setOkMsg(
        `Cota: ${d.distPct.toFixed(1)}% · ${d.distMm.toFixed(1)} mm · ∠ ${d.angleDeg.toFixed(0)}° · Appearance = unidad`,
      );
    },
    [pageIdx, notePagesHistory, markColor],
  );

  const handleKnifeCut = useCallback(
    (knifePoints, { lineCut = false } = {}) => {
      const pts = Array.isArray(knifePoints) ? knifePoints : [];
      if (pts.length < 2) {
        setOkMsg(
          lineCut
            ? "Line Cut: traza una línea que cruce un path"
            : "Knife: dibuja un trazo sobre una foto o un path",
        );
        return;
      }
      notePagesHistory();
      let cutSlots = false;
      let cutMarks = false;
      if (!lineCut) {
        updateCurrentPageSlots(
          (slots) => {
            const next = knifeCutSlots(slots, pts);
            cutSlots = next.length > slots.length;
            return next;
          },
          { history: false },
        );
      }
      let knifeNewIds = [];
      updateCurrentPageMarks(
        (marks) => {
          const beforeIds = new Set(marks.map((m) => String(m.id)));
          const next = knifeCutMarks(marks, pts);
          cutMarks = next.length > marks.length;
          knifeNewIds = next
            .filter((m) => !beforeIds.has(String(m.id)))
            .map((m) => String(m.id));
          return next;
        },
        { history: false },
      );
      if (cutSlots) {
        setOkMsg("Foto partida en 2 casillas — muévelas o ajusta el encuadre");
        setMarkTool(null);
      } else if (cutMarks) {
        if (knifeNewIds.length) {
          const set = new Set(knifeNewIds);
          selectedMarkIdsRef.current = set;
          setSelectedMarkIds(set);
          setSelectedMarkId(knifeNewIds[knifeNewIds.length - 1]);
        }
        setOkMsg(
          lineCut
            ? `Line Cut · ${knifeNewIds.length || 2} piezas`
            : `Knife · ${knifeNewIds.length || 2} piezas seleccionadas`,
        );
        setMarkTool(null);
      } else {
        setOkMsg(
          lineCut
            ? "Line Cut: la línea debe cruzar un path"
            : "Knife: traza una línea que cruce una foto, un cuadrado/círculo o un path",
        );
      }
    },
    [pageIdx, notePagesHistory],
  );

  const handleRectCut = useCallback(
    (draft) => {
      if (!draft || !(Number(draft.w) > 0.02) || !(Number(draft.h) > 0.02)) {
        setOkMsg(
          "Rectangle Cut: arrastra un rectángulo sobre lo que quieras quitar",
        );
        return;
      }
      notePagesHistory();
      updateCurrentPageMarks(
        (marks) =>
          rectCutMarks(
            marks,
            { x: draft.x, y: draft.y, w: draft.w, h: draft.h },
            "delete-inside",
          ),
        { history: false },
      );
      setOkMsg("Rectangle Cut: eliminado lo del interior");
      setMarkTool(null);
    },
    [pageIdx, notePagesHistory],
  );

  const handleMirrorCut = useCallback(
    (draft) => {
      if (!draft) return;
      const dx = Math.abs((Number(draft.x2) || 0) - (Number(draft.x1) || 0));
      const dy = Math.abs((Number(draft.y2) || 0) - (Number(draft.y1) || 0));
      if (dx < 0.01 && dy < 0.01) {
        setOkMsg("Mirror & Cut: arrastra un eje (H o V)");
        return;
      }
      const axis = dx >= dy ? "v" : "h";
      const x = ((Number(draft.x1) || 0) + (Number(draft.x2) || 0)) / 2;
      const y = ((Number(draft.y1) || 0) + (Number(draft.y2) || 0)) / 2;
      notePagesHistory();
      updateCurrentPageMarks((marks) => mirrorCutMarks(marks, x, y, axis), {
        history: false,
      });
      setOkMsg(
        axis === "v"
          ? "Mirror & Cut: espejo vertical"
          : "Mirror & Cut: espejo horizontal",
      );
      setMarkTool(null);
    },
    [pageIdx, notePagesHistory],
  );

  const handleDistortAt = useCallback(
    (x, y, mode) => {
      const o = liquifyOptsRef.current;
      updateCurrentPageMarks(
        (marks) =>
          distortMarksNear(marks, x, y, {
            mode: mode || "twirl",
            radius: Number(o.radius) || 0.09,
            strength: Number(o.strength) || 0.4,
            angleDeg: mode === "twirl" ? Number(o.angleDeg) || 28 : 0,
          }),
        { history: false, coalesceMs: 80 },
      );
    },
    [pageIdx],
  );

  const handleWidthAt = useCallback(
    (x, y, delta) => {
      const step = Math.max(0.1, Number(widthOptsRef.current.step) || 0.5);
      const d = delta < 0 ? -step : step;
      updateCurrentPageMarks((marks) => widthMarksAt(marks, x, y, d, 0.06), {
        history: false,
        coalesceMs: 80,
      });
    },
    [pageIdx],
  );

  /** Shape Builder: trazo sobre formas → Pathfinder (op en shapeBuilderOpRef). */
  const handleShapeBuilderStroke = useCallback(
    (strokePts) => {
      const op = shapeBuilderOpRef.current || "unite";
      const page = pagesRef.current[pageIdx];
      const ids = markIdsHitByStroke(page?.marks || [], strokePts, 0.03);
      if (ids.length < 2) {
        setOkMsg(
          `Shape Builder · ${op}: pasa el trazo por 2+ formas (o Shift+clic y Pathfinder)`,
        );
        return;
      }
      notePagesHistory();
      let createdIds = [];
      let applied = false;
      updateCurrentPageMarks(
        (marks) => {
          const next = pathfinderMarks(marks, ids, op);
          if (!next) return marks;
          applied = true;
          const drop = new Set(ids.map(String));
          const restIds = new Set(
            marks
              .filter((m) => !drop.has(String(m.id)))
              .map((m) => String(m.id)),
          );
          createdIds = next
            .filter((m) => !restIds.has(String(m.id)))
            .map((m) => String(m.id));
          return next;
        },
        { history: false },
      );
      if (!applied || !createdIds.length) {
        setOkMsg(`Shape Builder · ${op}: no se pudo combinar`);
        return;
      }
      const set = new Set(createdIds);
      selectedMarkIdsRef.current = set;
      setSelectedMarkIds(set);
      setSelectedMarkId(createdIds[createdIds.length - 1]);
      setMarkTool(null);
      setOkMsg(
        `Shape Builder · ${op}: ${ids.length} → ${createdIds.length} forma(s)`,
      );
    },
    [pageIdx, notePagesHistory],
  );

  /** Shaper VC: forma limpia o scribble que borra marcas tocadas. */
  const handleShaperFinish = useCallback(
    (recognized, paint) => {
      if (!recognized) {
        setOkMsg("Shaper: trazo demasiado corto");
        return;
      }
      if (recognized.kind === "scribble") {
        notePagesHistory();
        const page = pagesRef.current[pageIdx];
        const hit = marksHitByShaperScribble(page?.marks || [], recognized);
        if (!hit.length) {
          setOkMsg("Shaper scribble: no hay figuras debajo");
          return;
        }
        const ids = new Set(hit.map((m) => String(m.id)));
        updateCurrentPageMarks(
          (marks) => marks.filter((m) => !ids.has(String(m.id))),
          { history: false },
        );
        selectedMarkIdsRef.current = new Set();
        setSelectedMarkIds(new Set());
        setSelectedMarkId(null);
        setOkMsg(
          hit.length === 1
            ? "Shaper: figura borrada"
            : `Shaper: ${hit.length} figuras borradas`,
        );
        return;
      }
      const draft = shaperResultToMarkDraft(recognized, paint || {});
      if (!draft) {
        setOkMsg("Shaper: no se reconoció la forma");
        return;
      }
      notePagesHistory();
      const createdId = newMarkId();
      updateCurrentPageMarks(
        (marks) => [...marks, clampMark({ ...draft, id: createdId })],
        { history: false },
      );
      const set = new Set([String(createdId)]);
      selectedMarkIdsRef.current = set;
      setSelectedMarkIds(set);
      setSelectedMarkId(createdId);
      setMarkTool(null);
      const label =
        recognized.kind === "line"
          ? "línea"
          : recognized.kind === "rect"
            ? "rectángulo"
            : recognized.kind === "ellipse"
              ? "elipse"
              : recognized.kind === "polygon"
                ? `polígono ${recognized.sides || ""}`.trim()
                : "forma";
      setOkMsg(`Shaper → ${label}`);
    },
    [pageIdx, notePagesHistory],
  );

  const handleCreateGradientMesh = useCallback(() => {
    const ids = [...selectedMarkIdsRef.current];
    if (!ids.length && selectedMarkId) ids.push(String(selectedMarkId));
    if (!ids.length) {
      setOkMsg("Mesh: selecciona una forma cerrada");
      return;
    }
    let rows = 3;
    let cols = 3;
    try {
      const raw = window.prompt("Malla de degradado · filas×cols (2–8)", "3x3");
      if (raw == null) return;
      const m = String(raw).match(/(\d+)\s*[x×,]\s*(\d+)/i);
      if (m) {
        rows = Math.min(8, Math.max(2, Number(m[1]) || 3));
        cols = Math.min(8, Math.max(2, Number(m[2]) || 3));
      }
    } catch {
      /* defaults */
    }
    const from =
      markFillColorRef.current && markFillColorRef.current !== "none"
        ? markFillColorRef.current
        : markColor;
    const to = markStrokeColor || "#ffffff";
    let ok = 0;
    updateCurrentPageMarks((marks) =>
      marks.map((m) => {
        if (!ids.includes(String(m.id))) return m;
        if (m.type === "text" || m.type === "textpath") return m;
        const next = createGradientMeshOnMark(m, { rows, cols, from, to });
        if (!next) return m;
        ok += 1;
        return next;
      }),
    );
    setOkMsg(
      ok
        ? `Mesh ${rows}×${cols} en ${ok} forma(s) · Fill/Stroke = extremos del degradado`
        : "Mesh: forma no válida",
    );
    setVcRightPanel("appearance");
  }, [pageIdx, selectedMarkId, markColor, markStrokeColor]);

  const handleBlendMarks = useCallback(() => {
    const ids = [...selectedMarkIdsRef.current];
    if (ids.length !== 2) {
      setOkMsg("Blend: selecciona exactamente 2 formas");
      return;
    }
    let steps = 4;
    try {
      const raw = window.prompt("Blend · pasos intermedios (1–20)", "4");
      if (raw == null) return; // cancelado
      const n = Math.round(Number(raw));
      if (Number.isFinite(n)) steps = Math.min(20, Math.max(1, n));
    } catch {
      steps = 4;
    }
    notePagesHistory();
    let ok = false;
    updateCurrentPageMarks(
      (marks) => {
        const next = blendTwoMarks(marks, ids[0], ids[1], steps);
        if (!next) return marks;
        ok = true;
        return next;
      },
      { history: false },
    );
    if (ok) setOkMsg(`Blend: ${steps} pasos intermedios añadidos`);
  }, [pageIdx, notePagesHistory]);

  const pageSymbols = pages[pageIdx]?.symbols || [];
  const pagePerspectiveGrid = normalizePerspectiveGrid(
    pages[pageIdx]?.perspectiveGrid,
  );

  const patchPerspectiveGrid = useCallback(
    (patch) => {
      notePagesHistory();
      setPages((prev) => {
        const list = Array.isArray(prev) ? [...prev] : [];
        const page = list[pageIdx] || emptyFreePage();
        const cur =
          normalizePerspectiveGrid(page.perspectiveGrid) ||
          defaultPerspectiveGrid({ visible: false });
        list[pageIdx] = {
          ...page,
          perspectiveGrid: normalizePerspectiveGrid({ ...cur, ...patch }),
        };
        return list;
      });
    },
    [pageIdx, notePagesHistory],
  );

  const handleTogglePerspectiveGrid = useCallback(() => {
    const cur =
      normalizePerspectiveGrid(pagesRef.current[pageIdx]?.perspectiveGrid) ||
      defaultPerspectiveGrid({ visible: false });
    if (!pagesRef.current[pageIdx]?.perspectiveGrid) {
      patchPerspectiveGrid({ ...defaultPerspectiveGrid(), visible: true });
      setOkMsg(
        "Perspective Grid: visible · plano Ground · Shift+V = mover en perspectiva",
      );
      return;
    }
    patchPerspectiveGrid({ visible: !cur.visible });
    setOkMsg(!cur.visible ? "Perspective Grid ON" : "Perspective Grid OFF");
  }, [pageIdx, patchPerspectiveGrid]);

  const handleDefineSymbol = useCallback(() => {
    const ids = [...selectedMarkIdsRef.current];
    if (!ids.length && selectedMarkId) ids.push(String(selectedMarkId));
    const mark = (pagesRef.current[pageIdx]?.marks || []).find((m) =>
      ids.includes(String(m.id)),
    );
    if (!mark) {
      setOkMsg("Define Symbol: selecciona una forma o texto");
      return;
    }
    notePagesHistory();
    let created = null;
    setPages((prev) => {
      const list = Array.isArray(prev) ? [...prev] : [];
      const page = list[pageIdx] || emptyFreePage();
      const result = defineSymbolFromMark(page.symbols || [], mark);
      if (!result) return prev;
      created = result.symbol;
      list[pageIdx] = {
        ...page,
        symbols: result.symbols,
      };
      return list;
    });
    if (created) {
      setActiveSymbolId(created.id);
      setVcRightPanel("symbols");
      setOkMsg(`Símbolo «${created.name}» · usa Symbol Sprayer para pintar`);
    } else {
      setOkMsg("Define Symbol: marca no válida");
    }
  }, [pageIdx, selectedMarkId, notePagesHistory]);

  const handleSymbolShiftAt = useCallback(
    (x, y, { alt = false, prev = null } = {}) => {
      const sid = activeSymbolIdRef.current;
      if (!sid) {
        setOkMsg("Symbol Shifter: elige un símbolo en Symbols");
        return;
      }
      if (!prev) return;
      const dx = x - Number(prev.x);
      const dy = y - Number(prev.y);
      if (Math.hypot(dx, dy) < 1e-5) return;
      const o = symbolSprayOptsRef.current || {};
      updateCurrentPageMarks(
        (marks) =>
          shiftSymbolsNear(marks, sid, x, y, {
            radius: Math.max(0.06, (Number(o.radius) || 0.05) * 2),
            dx,
            dy,
            alt: Boolean(alt),
          }),
        { history: false, coalesceMs: 80, allowOutside: true },
      );
    },
    [pageIdx],
  );

  const handleSymbolSprayAt = useCallback(
    (x, y, { alt = false } = {}) => {
      const page = pagesRef.current[pageIdx];
      const symbols = page?.symbols || [];
      const sid = activeSymbolIdRef.current;
      const symbol =
        symbols.find((s) => String(s.id) === String(sid)) || symbols[0];
      if (!symbol) {
        setOkMsg("Symbol Sprayer: Define Symbol primero (selección + Define)");
        return;
      }
      if (!activeSymbolIdRef.current) setActiveSymbolId(symbol.id);
      const o = symbolSprayOptsRef.current || {};
      updateCurrentPageMarks(
        (marks) =>
          spraySymbolAt(marks, symbol, x, y, {
            radius: Number(o.radius) || 0.05,
            density: Number(o.density) || 3,
            alt: Boolean(alt),
          }),
        { history: false, coalesceMs: 80, allowOutside: true },
      );
    },
    [pageIdx],
  );

  const handleSymbolScrunchAt = useCallback(
    (x, y, { alt = false } = {}) => {
      const sid = activeSymbolIdRef.current;
      if (!sid) {
        setOkMsg("Symbol Scruncher: elige un símbolo en Symbols");
        return;
      }
      const o = symbolSprayOptsRef.current || {};
      updateCurrentPageMarks(
        (marks) =>
          scrunchSymbolsNear(marks, sid, x, y, {
            radius: Math.max(0.06, (Number(o.radius) || 0.05) * 2),
            strength: 0.18,
            alt: Boolean(alt),
          }),
        { history: false, coalesceMs: 80, allowOutside: true },
      );
    },
    [pageIdx],
  );

  const handleSymbolSizeAt = useCallback(
    (x, y, { alt = false } = {}) => {
      const sid = activeSymbolIdRef.current;
      if (!sid) {
        setOkMsg("Symbol Sizer: elige un símbolo en Symbols");
        return;
      }
      const o = symbolSprayOptsRef.current || {};
      updateCurrentPageMarks(
        (marks) =>
          sizeSymbolsNear(marks, sid, x, y, {
            radius: Math.max(0.06, (Number(o.radius) || 0.05) * 2),
            strength: 0.14,
            alt: Boolean(alt),
          }),
        { history: false, coalesceMs: 80, allowOutside: true },
      );
    },
    [pageIdx],
  );

  const handleSymbolSpinAt = useCallback(
    (x, y, { alt = false } = {}) => {
      const sid = activeSymbolIdRef.current;
      if (!sid) {
        setOkMsg("Symbol Spinner: elige un símbolo en Symbols");
        return;
      }
      const o = symbolSprayOptsRef.current || {};
      updateCurrentPageMarks(
        (marks) =>
          spinSymbolsNear(marks, sid, x, y, {
            radius: Math.max(0.06, (Number(o.radius) || 0.05) * 2.2),
            strengthDeg: 16,
            alt: Boolean(alt),
          }),
        { history: false, coalesceMs: 80, allowOutside: true },
      );
    },
    [pageIdx],
  );

  const handleSymbolStainAt = useCallback(
    (x, y, { alt = false } = {}) => {
      const sid = activeSymbolIdRef.current;
      if (!sid) {
        setOkMsg("Symbol Stainer: elige un símbolo en Symbols");
        return;
      }
      const o = symbolSprayOptsRef.current || {};
      updateCurrentPageMarks(
        (marks) =>
          stainSymbolsNear(marks, sid, x, y, {
            radius: Math.max(0.06, (Number(o.radius) || 0.05) * 2),
            strength: 0.22,
            tint: markColor || DEFAULT_MARK_COLOR,
            alt: Boolean(alt),
          }),
        { history: false, coalesceMs: 80, allowOutside: true },
      );
    },
    [pageIdx, markColor],
  );

  const handleSymbolScreenAt = useCallback(
    (x, y, { alt = false } = {}) => {
      const sid = activeSymbolIdRef.current;
      if (!sid) {
        setOkMsg("Symbol Screener: elige un símbolo en Symbols");
        return;
      }
      const o = symbolSprayOptsRef.current || {};
      updateCurrentPageMarks(
        (marks) =>
          screenSymbolsNear(marks, sid, x, y, {
            radius: Math.max(0.06, (Number(o.radius) || 0.05) * 2),
            strength: 0.12,
            alt: Boolean(alt),
          }),
        { history: false, coalesceMs: 80, allowOutside: true },
      );
    },
    [pageIdx],
  );

  /** Object › Objects on Path › Attach (clic en path o selección con path). */
  const handleObjectsOnPathAttach = useCallback(
    (pathIdOverride = null) => {
      const ids = [...selectedMarkIdsRef.current];
      if (!ids.length && selectedMarkId) ids.push(String(selectedMarkId));
      let pathId = pathIdOverride != null ? String(pathIdOverride) : null;
      let memberIds = ids.filter((id) => id !== pathId);
      if (!pathId) {
        const resolved = resolveObjectsOnPathTargets(
          pagesRef.current[pageIdx]?.marks || [],
          ids,
        );
        if (!resolved) {
          setOkMsg(
            "Objects on Path: selecciona formas + un path, o activa la tool y clic en el path",
          );
          return;
        }
        pathId = resolved.pathId;
        memberIds = resolved.memberIds;
      } else if (!memberIds.length) {
        setOkMsg("Objects on Path: selecciona primero las formas a adjuntar");
        return;
      }
      notePagesHistory();
      let ok = false;
      let count = 0;
      updateCurrentPageMarks(
        (marks) => {
          const next = attachMarksToPath(marks, pathId, memberIds, {
            followPath: true,
            rotateExtraDeg: 0,
          });
          if (!next) return marks;
          ok = true;
          count = memberIds.length;
          return next;
        },
        { history: false, allowOutside: true },
      );
      setOkMsg(
        ok
          ? `Objects on Path: ${count} objeto(s) en el trazado`
          : "Objects on Path: path no válido",
      );
    },
    [pageIdx, selectedMarkId, notePagesHistory],
  );

  const handleExpandObjectsOnPath = useCallback(() => {
    const ids = [...selectedMarkIdsRef.current];
    if (!ids.length && selectedMarkId) ids.push(String(selectedMarkId));
    if (!ids.length) {
      setOkMsg("Expand Objects on Path: selecciona el grupo o el path");
      return;
    }
    notePagesHistory();
    let ok = false;
    updateCurrentPageMarks(
      (marks) => {
        const next = expandObjectsOnPath(marks, ids);
        if (!next) return marks;
        ok = true;
        return next;
      },
      { history: false, allowOutside: true },
    );
    setOkMsg(
      ok
        ? "Expand Objects on Path: vínculo liberado"
        : "Expand: no hay Objects on Path en la selección",
    );
  }, [pageIdx, selectedMarkId, notePagesHistory]);

  /** Object › Envelope Distort › Make with Warp (VC). */
  const handleEnvelopeWarp = useCallback(() => {
    const ids = [...selectedMarkIdsRef.current];
    if (!ids.length && selectedMarkId) ids.push(String(selectedMarkId));
    if (!ids.length) {
      setOkMsg("Envelope Warp: selecciona una forma o path");
      return;
    }
    const o = envelopeOptsRef.current || {};
    notePagesHistory();
    let ok = false;
    updateCurrentPageMarks(
      (marks) => {
        const next = envelopeWarpMarks(marks, ids, {
          style: o.style || "arc",
          bend: Number(o.bend) || 50,
          horizontal: o.horizontal !== false,
          h: Number(o.h) || 0,
          v: Number(o.v) || 0,
        });
        if (next === marks) return marks;
        ok = true;
        return next;
      },
      { history: false, allowOutside: true },
    );
    setVcRightPanel("appearance");
    setOkMsg(
      ok
        ? `Envelope · ${o.style || "arc"} (bend ${Math.round(Number(o.bend) || 50)}%)`
        : "Envelope: forma no válida",
    );
  }, [pageIdx, selectedMarkId, notePagesHistory]);

  /** Object › Envelope Distort › Make with Mesh (VC). */
  const handleEnvelopeMesh = useCallback(() => {
    const ids = [...selectedMarkIdsRef.current];
    if (!ids.length && selectedMarkId) ids.push(String(selectedMarkId));
    if (!ids.length) {
      setOkMsg("Envelope Mesh: selecciona una forma o path");
      return;
    }
    const o = envelopeOptsRef.current || {};
    const rows = Math.max(1, Math.min(12, Math.round(Number(o.rows) || 3)));
    const cols = Math.max(1, Math.min(12, Math.round(Number(o.cols) || 4)));
    notePagesHistory();
    let ok = false;
    updateCurrentPageMarks(
      (marks) => {
        const next = envelopeMeshMarks(marks, ids, { rows, cols });
        if (next === marks) return marks;
        ok = true;
        return next;
      },
      { history: false, allowOutside: true },
    );
    setVcRightPanel("appearance");
    setMarkTool("puppetWarp");
    setOkMsg(
      ok
        ? `Envelope Mesh ${rows}×${cols}: densificado · arrastra pines Puppet`
        : "Envelope Mesh: forma no válida",
    );
  }, [pageIdx, selectedMarkId, notePagesHistory]);

  const handleShearSelection = useCallback(
    (amount = 0.15) => {
      const slotIds = [...selectedSlotIdsRef.current];
      const markIds = [...selectedMarkIdsRef.current];
      if (!slotIds.length && !markIds.length) return;
      notePagesHistory();
      if (slotIds.length) {
        updateCurrentPageSlots(
          (slots) => shearSlotsBy(slots, slotIds, amount),
          {
            history: false,
          },
        );
      }
      if (markIds.length) {
        updateCurrentPageMarks(
          (marks) => shearMarksBy(marks, markIds, amount),
          {
            history: false,
          },
        );
      }
    },
    [pageIdx, notePagesHistory],
  );

  const handleSmoothSelection = useCallback(() => {
    const markIds = selectedMarkIdsRef.current.size
      ? selectedMarkIdsRef.current
      : selectedMarkId
        ? new Set([String(selectedMarkId)])
        : new Set();
    if (!markIds.size) {
      setOkMsg("Suavizar: selecciona un path (Pencil/Pen)");
      return;
    }
    const passes = Math.max(
      1,
      Math.min(6, Math.round(Number(smoothOptsRef.current.passes) || 2)),
    );
    let n = 0;
    updateCurrentPageMarks((marks) =>
      marks.map((m) => {
        if (!markIds.has(String(m.id)) || m.type !== "path") return m;
        const pts = m.points || [];
        if (pts.length < 3) return m;
        n += 1;
        return clampMark({
          ...m,
          points: smoothPathPoints(pts, passes),
        });
      }),
    );
    if (n) setOkMsg(`Suavizado · ${passes} pasada${passes > 1 ? "s" : ""}`);
  }, [pageIdx, selectedMarkId]);

  /** Object › Path › Offset Path (texto → contorno caja, como Create Outlines) */
  const handleOffsetPath = useCallback(() => {
    const markIds = selectedMarkIdsRef.current.size
      ? selectedMarkIdsRef.current
      : selectedMarkId
        ? new Set([String(selectedMarkId)])
        : new Set();
    if (!markIds.size) {
      setOkMsg("Offset Path: selecciona forma, path o texto");
      return;
    }
    const raw = window.prompt(
      "Offset Path · desplazamiento % artboard (−15…15, + = exterior)",
      "2",
    );
    if (raw == null) return;
    const pct = Number(String(raw).replace(",", "."));
    if (!Number.isFinite(pct)) {
      setOkMsg("Offset Path: número no válido");
      return;
    }
    const delta = Math.max(-0.15, Math.min(0.15, pct / 100));
    let n = 0;
    let fromText = 0;
    updateCurrentPageMarks((marks) =>
      marks.map((m) => {
        if (!markIds.has(String(m.id))) return m;
        if (m.type === "text" || m.type === "textpath") fromText += 1;
        const next = offsetMark(m, delta);
        if (!next) return m;
        n += 1;
        return next;
      }),
    );
    setOkMsg(
      n
        ? `Offset Path · ${delta >= 0 ? "+" : ""}${(delta * 100).toFixed(1)}% · ${n}${
            fromText ? ` (texto→path ${fromText})` : ""
          }`
        : "Offset Path: no aplicable a la selección",
    );
  }, [pageIdx, selectedMarkId]);

  /** Object › Path › Outline Stroke */
  const handleOutlineStroke = useCallback(() => {
    const markIds = selectedMarkIdsRef.current.size
      ? selectedMarkIdsRef.current
      : selectedMarkId
        ? new Set([String(selectedMarkId)])
        : new Set();
    if (!markIds.size) {
      setOkMsg("Outline Stroke: selecciona path, forma o texto");
      return;
    }
    let n = 0;
    updateCurrentPageMarks((marks) =>
      marks.map((m) => {
        if (!markIds.has(String(m.id))) return m;
        const src =
          m.type === "text" || m.type === "textpath"
            ? createOutlinesMark(m) || m
            : m;
        const next = outlineStrokeMark(src);
        if (!next) return m;
        n += 1;
        return next;
      }),
    );
    setOkMsg(
      n
        ? `Outline Stroke · ${n} marca(s) → relleno`
        : "Outline Stroke: sin trazo convertible",
    );
  }, [pageIdx, selectedMarkId]);

  /** Object › Path › Simplify */
  const handleSimplifyPath = useCallback(() => {
    const markIds = selectedMarkIdsRef.current.size
      ? selectedMarkIdsRef.current
      : selectedMarkId
        ? new Set([String(selectedMarkId)])
        : new Set();
    if (!markIds.size) {
      setOkMsg("Simplify: selecciona un path (o Create Outlines en texto)");
      return;
    }
    const raw = window.prompt("Simplify · tolerancia % (0.5–5)", "1.2");
    if (raw == null) return;
    const pct = Number(String(raw).replace(",", "."));
    if (!Number.isFinite(pct)) {
      setOkMsg("Simplify: número no válido");
      return;
    }
    const tol = Math.max(0.005, Math.min(0.05, pct / 100));
    let n = 0;
    let removed = 0;
    let skippedText = 0;
    updateCurrentPageMarks((marks) =>
      marks.map((m) => {
        if (!markIds.has(String(m.id))) return m;
        let cur = m;
        if (cur.type === "text" || cur.type === "textpath") {
          const outlined = createOutlinesMark(cur);
          if (!outlined) {
            skippedText += 1;
            return m;
          }
          cur = outlined;
        }
        if (cur.type !== "path") return m;
        const pts = cur.points || [];
        if (pts.length < 3) return m;
        const next = simplifyPathPoints(pts, tol);
        if (next.length < 2) return m;
        n += 1;
        removed += Math.max(0, pts.length - next.length);
        return clampMark({ ...cur, points: next });
      }),
    );
    setOkMsg(
      n
        ? `Simplify · −${removed} pts · ${n} path(s)`
        : skippedText
          ? "Simplify: convierte el texto con Create Outlines"
          : "Simplify: nada que reducir",
    );
  }, [pageIdx, selectedMarkId]);

  /** Type › Create Outlines */
  const handleCreateOutlines = useCallback(() => {
    const markIds = selectedMarkIdsRef.current.size
      ? selectedMarkIdsRef.current
      : selectedMarkId
        ? new Set([String(selectedMarkId)])
        : new Set();
    if (!markIds.size) {
      setOkMsg("Create Outlines: selecciona texto o texto en trazado");
      return;
    }
    let n = 0;
    updateCurrentPageMarks((marks) =>
      marks.map((m) => {
        if (!markIds.has(String(m.id))) return m;
        const next = createOutlinesMark(m);
        if (!next) return m;
        n += 1;
        return next;
      }),
    );
    setOkMsg(
      n
        ? `Create Outlines · ${n} texto(s) → path (caja tipográfica)`
        : "Create Outlines: solo texto / texto en trazado",
    );
  }, [pageIdx, selectedMarkId]);

  /** Object › Path › Average */
  const handleAverageAnchors = useCallback(() => {
    const ids = selectedMarkIdsRef.current.size
      ? [...selectedMarkIdsRef.current]
      : selectedMarkId
        ? [String(selectedMarkId)]
        : [];
    if (!ids.length) {
      setOkMsg("Average: selecciona 1+ paths (anclas a promediar)");
      return;
    }
    const raw = window.prompt("Average · eje (both / x / y)", "both");
    if (raw == null) return;
    const mode = String(raw).trim().toLowerCase() || "both";
    let ok = false;
    updateCurrentPageMarks((marks) => {
      const next = averagePathAnchors(marks, ids, mode);
      if (!next) {
        setOkMsg(
          "Average: hace falta path con anclas (Create Outlines si es texto)",
        );
        return marks;
      }
      ok = true;
      return next;
    });
    if (ok) setOkMsg(`Average · ${mode} · ${ids.length} path(s)`);
  }, [pageIdx, selectedMarkId]);

  const commitTextDraft = useCallback(() => {
    const draft = textDraftRef.current;
    if (!draft) return false;
    textDraftRef.current = null;
    setTextDraft(null);
    if (!String(draft.value || "").trim()) {
      if (draft.id) {
        updateCurrentPageMarks((marks) =>
          marks.filter((m) => String(m.id) !== String(draft.id)),
        );
      }
      return true;
    }
    const fillHex =
      draft.fill && draft.fill !== "none"
        ? draft.fill
        : draft.color || markFillColor || markColor;
    const strokeHex = draft.strokeColor || markStrokeColor || markColor;
    const strokeW =
      draft.stroke != null ? Number(draft.stroke) : Number(markStroke) || 0;
    const payload = {
      type: "text",
      x: draft.x,
      y: draft.y,
      w: textMarkBoxWidth(draft),
      text: String(draft.value).trimEnd(),
      color: fillHex,
      fill: fillHex,
      strokeColor: strokeHex,
      stroke: Number.isFinite(strokeW) ? strokeW : 0,
      fontSize: Number(draft.fontSize) || markFontSize || 14,
      fontFamily: normalizeMarkFontFamily(draft.fontFamily || markFontFamily),
      fontWeight: normalizeMarkFontWeight(draft.fontWeight ?? markFontWeight),
      textAlign: normalizeMarkTextAlign(draft.textAlign || markTextAlign),
      writingMode: normalizeMarkWritingMode(draft.writingMode),
      rotation: normalizeRotation(draft.rotation),
    };
    if (draft.id) {
      updateCurrentPageMarks((marks) =>
        marks.map((m) =>
          String(m.id) === String(draft.id)
            ? // allowOutside: texto sacado de la mesa no debe volver al pinchar fuera
              clampMarkAllowOutside({ ...m, ...payload, id: m.id })
            : m,
        ),
      );
    } else {
      updateCurrentPageMarks((marks) => [
        ...marks,
        clampMarkAllowOutside({ ...payload, id: newMarkId() }),
      ]);
    }
    return true;
  }, [
    markColor,
    markFillColor,
    markStrokeColor,
    markStroke,
    markFontSize,
    markFontFamily,
    markFontWeight,
    markTextAlign,
    pageIdx,
  ]);

  // Clic fuera (foto, vacío, otra marca, toolbar…): el blur a menudo no llega
  // porque preventDefault en pointerdown de marcas/casillas. Capture global.
  useEffect(() => {
    if (!textDraft) return undefined;
    const onPointerDownCapture = (e) => {
      const t = e.target;
      if (
        typeof t?.closest === "function" &&
        t.closest("[data-mb-text-draft]")
      ) {
        return;
      }
      commitTextDraft();
    };
    window.addEventListener("pointerdown", onPointerDownCapture, true);
    return () =>
      window.removeEventListener("pointerdown", onPointerDownCapture, true);
  }, [textDraft, commitTextDraft]);

  const handleDraftMark = useCallback((next) => {
    if (typeof next === "function") {
      setDraftMark((prev) => {
        const resolved = next(prev);
        draftMarkRef.current = resolved;
        return resolved;
      });
    } else {
      draftMarkRef.current = next;
      setDraftMark(next);
    }
  }, []);

  const handleCommitDraftMark = useCallback(() => {
    const draft = draftMarkRef.current;
    if (!draft) return;
    if (draft.type === "path" && (draft.points || []).length < 2) {
      setDraftMark(null);
      return;
    }
    if (draft._lasso || draft._areaType || draft._verticalArea) {
      setDraftMark(null);
      return;
    }
    if (draft.type === "measure" || draft._measure || draft._knife) {
      setDraftMark(null);
      return;
    }
    if (
      (draft.type === "line" ||
        draft.type === "arrow" ||
        draft.type === "dimension") &&
      Math.hypot(draft.x2 - draft.x1, draft.y2 - draft.y1) < 0.008
    ) {
      setDraftMark(null);
      return;
    }
    if (
      (draft.type === "ellipse" ||
        draft.type === "arc" ||
        draft.type === "rect" ||
        draft.type === "roundrect" ||
        draft.type === "polygon" ||
        draft.type === "star" ||
        draft.type === "grid" ||
        draft.type === "polarGrid" ||
        draft.type === "flare") &&
      (draft.w < 0.008 || draft.h < 0.008)
    ) {
      setDraftMark(null);
      return;
    }
    if (
      draft.type === "path" &&
      (draft._pen || draft._spiral || draft._blob) &&
      (draft.points || []).length < 2
    ) {
      setDraftMark(null);
      return;
    }
    const {
      _ox,
      _oy,
      _shift,
      _pen,
      _penDrag,
      _hover,
      _anchors,
      _lasso,
      _areaType,
      _measure,
      _knife,
      _spiral,
      _blob,
      _curvature,
      _raw,
      _lineCut,
      _rectCut,
      _mirrorCut,
      _shaper,
      _shapeBuilder,
      _continueId,
      ...clean
    } = draft;
    if (
      draft.type === "path" &&
      Array.isArray(_anchors) &&
      _anchors.length >= 2 &&
      (!(clean.points || []).length || _pen)
    ) {
      clean.points = penAnchorsToPolyline(_anchors, 12);
    }
    if (
      draft._blob &&
      Array.isArray(clean.points) &&
      clean.points.length >= 2
    ) {
      const outline = blobStrokeToOutline(
        clean.points,
        clean.stroke != null ? clean.stroke : 12,
      );
      if (outline && outline.length >= 4) {
        clean.points = outline;
        clean.closed = true;
        const ink = clean.fill || clean.color || DEFAULT_MARK_COLOR;
        clean.fill = ink;
        clean.color = ink;
        clean.strokeColor = ink;
        clean.stroke = Math.min(1.5, Math.max(0.5, Number(clean.stroke) || 1));
      }
    }
    if (
      draft._rectCut ||
      draft._mirrorCut ||
      draft._knife ||
      draft._shaper ||
      draft._shapeBuilder
    ) {
      setDraftMark(null);
      return;
    }
    const createdId = newMarkId();
    const created = clampMarkAllowOutside({ ...clean, id: createdId });
    const replacedId =
      draft._continueId != null ? String(draft._continueId) : "";
    updateCurrentPageMarks(
      (marks) => [
        ...marks.filter((m) => !replacedId || String(m.id) !== replacedId),
        created,
      ],
      {
        allowOutside: true,
      },
    );
    setDraftMark(null);
    // Seleccionar la marca nueva para poder mover/redimensionar al instante
    const set = new Set([String(createdId)]);
    selectedMarkIdsRef.current = set;
    setSelectedMarkIds(set);
    setSelectedMarkId(createdId);
    // Si es forma de máscara y solapa una foto, conservar esa foto (Ctrl+7 al tiro)
    if (CLIP_MASK_MARK_TYPES.has(String(created.type))) {
      const page = pagesRef.current[pageIdx];
      const pair = resolveClippingMaskTargets(
        {
          ...(page || {}),
          marks: [...(page?.marks || []), created],
        },
        { markIds: [createdId] },
      );
      if (pair?.slotId) {
        selectedSlotIdsRef.current = new Set([pair.slotId]);
        setSelectedSlotIds(new Set([pair.slotId]));
        setActiveSlotId(pair.slotId);
      } else {
        selectedSlotIdsRef.current = new Set();
        setSelectedSlotIds(new Set());
        setActiveSlotId(null);
      }
    } else {
      selectedSlotIdsRef.current = new Set();
      setSelectedSlotIds(new Set());
      setActiveSlotId(null);
    }
    // Volver a Selection tras formas (círculo, rect…) para poder modificar
    if (
      markHasBox(clean) ||
      clean.type === "line" ||
      clean.type === "arrow" ||
      clean.type === "dimension"
    ) {
      setMarkTool(null);
      setEyedropperMode(false);
    }
    const paint = markPaintColors({ ...clean, id: createdId });
    setMarkColor(paint.strokeColor);
    setMarkStrokeColor(paint.strokeColor);
    setMarkFillColor(paint.fill);
  }, [pageIdx]);

  const handleEraseAt = useCallback(
    (x, y, { pathsOnly = false } = {}) => {
      let erased = false;
      let trimmed = false;
      updateCurrentPageMarks((marks) => {
        const next = [];
        for (const m of marks) {
          if (pathsOnly && m.type !== "path" && m.type !== "textpath") {
            next.push(m);
            continue;
          }
          // Path eraser: corta el stroke (no solo vértices)
          if (
            pathsOnly &&
            m.type === "path" &&
            Array.isArray(m.points) &&
            m.points.length >= 2
          ) {
            const pieces = erasePathNearPoint(m.points, x, y, 0.038);
            if (pieces === null) {
              next.push(m);
              continue;
            }
            trimmed = true;
            pieces.forEach((pts) => {
              next.push(
                clampMark({
                  ...m,
                  id: newMarkId(),
                  points: pts,
                  closed: false,
                }),
              );
            });
            continue;
          }
          if (!markHitTest(m, x, y, 0.045, { fill: true })) {
            next.push(m);
            continue;
          }
          erased = true;
        }
        return next;
      });
      if (trimmed) setOkMsg("Path cortado / recortado");
      else if (erased) setOkMsg(pathsOnly ? "Path borrado" : "Borrado");
    },
    [pageIdx],
  );

  const handleEraseAlong = useCallback(
    (from, to, { pathsOnly = false } = {}) => {
      if (!to) return;
      handleEraseAt(to.x, to.y, { pathsOnly });
      if (from) handleEraseAt(from.x, from.y, { pathsOnly });
    },
    [handleEraseAt],
  );

  const clearMarkSelection = useCallback(() => {
    selectedMarkIdsRef.current = new Set();
    setSelectedMarkIds(new Set());
    setSelectedMarkId(null);
  }, []);

  const handleJoinPaths = useCallback(() => {
    const ids = [...selectedMarkIdsRef.current];
    if (ids.length < 2) {
      setOkMsg("Unir trazos: selecciona 2+ paths con extremos cercanos");
      return;
    }
    let ok = false;
    updateCurrentPageMarks((marks) => {
      const next =
        ids.length === 2
          ? joinTwoPaths(marks, ids[0], ids[1])
          : joinPathChain(marks, ids);
      if (!next) {
        setOkMsg("Los extremos no están lo bastante cerca");
        return marks;
      }
      ok = true;
      return next;
    });
    if (ok) {
      clearMarkSelection();
      setOkMsg(
        ids.length === 2
          ? "Trazos unidos"
          : `Cadena unida · ${ids.length} paths`,
      );
    }
  }, [pageIdx, clearMarkSelection]);

  const handleTypeOnPath = useCallback(
    ({ vertical = false } = {}) => {
      const ids = [...selectedMarkIdsRef.current];
      const sole =
        ids.length === 1
          ? ids[0]
          : selectedMarkId
            ? String(selectedMarkId)
            : null;
      if (!sole) {
        setOkMsg(
          vertical
            ? "Selecciona un path y pulsa Texto en trazado vertical"
            : "Selecciona un path (Pencil/Pen) y pulsa Type on Path",
        );
        return;
      }
      let label = vertical ? "Text" : "Text on path";
      try {
        const raw = window.prompt(
          vertical
            ? "Texto vertical sobre el trazado"
            : "Texto sobre el trazado",
          label,
        );
        if (raw == null) return;
        label = String(raw).trim() || label;
      } catch {
        /* keep default */
      }
      let createdId = null;
      updateCurrentPageMarks((marks) => {
        const src = marks.find((m) => String(m.id) === String(sole));
        if (!src || src.type !== "path") {
          setOkMsg("Type on Path necesita un path seleccionado");
          return marks;
        }
        const tp = pathToTextPath(src, label, { vertical });
        if (!tp) return marks;
        createdId = tp.id;
        return marks.filter((m) => String(m.id) !== String(sole)).concat([tp]);
      });
      if (createdId) {
        const set = new Set([String(createdId)]);
        selectedMarkIdsRef.current = set;
        setSelectedMarkIds(set);
        setSelectedMarkId(createdId);
        setVcRightPanel("character");
        setOkMsg(
          vertical
            ? "Texto vertical en path · Character para tipografía"
            : "Texto sobre path · Character para tipografía · doble clic edita",
        );
      }
    },
    [pageIdx, selectedMarkId],
  );

  const handleSplitPath = useCallback(
    (markId, x, y) => {
      if (markId == null) {
        setOkMsg("Scissors: haz clic sobre una forma, path o línea");
        return;
      }
      let ok = false;
      let newIds = [];
      updateCurrentPageMarks((marks) => {
        const beforeIds = new Set(marks.map((m) => String(m.id)));
        const next = scissorsCutMark(marks, markId, x, y);
        if (!next) {
          setOkMsg("Scissors: no se pudo cortar (¿punto lejos del trazo?)");
          return marks;
        }
        ok = true;
        newIds = next
          .filter((m) => !beforeIds.has(String(m.id)))
          .map((m) => String(m.id));
        // Si solo abrió el contorno, el mismo id sigue
        if (!newIds.length) {
          const kept = next.find((m) => String(m.id) === String(markId));
          if (kept) newIds = [String(markId)];
        }
        return next;
      });
      if (ok) {
        const set = new Set(newIds);
        selectedMarkIdsRef.current = set;
        setSelectedMarkIds(set);
        setSelectedMarkId(newIds[newIds.length - 1] || null);
        setOkMsg(
          newIds.length >= 2
            ? `Scissors · ${newIds.length} piezas seleccionadas — otro clic parte más`
            : "Scissors · contorno abierto — otro clic en el path lo parte en 2",
        );
      }
    },
    [pageIdx],
  );

  const handleInsertAnchor = useCallback(
    (markId, x, y) => {
      updateCurrentPageMarks((marks) =>
        marks.map((m) => {
          if (String(m.id) !== String(markId) || m.type !== "path") return m;
          const next = insertPathAnchor(m.points || [], x, y);
          return next ? clampMark({ ...m, points: next }) : m;
        }),
      );
    },
    [pageIdx],
  );

  const handleDeleteAnchor = useCallback(
    (markId, x, y) => {
      updateCurrentPageMarks((marks) =>
        marks.map((m) => {
          if (String(m.id) !== String(markId) || m.type !== "path") return m;
          const next = deletePathAnchor(m.points || [], x, y);
          return next ? clampMark({ ...m, points: next }) : m;
        }),
      );
    },
    [pageIdx],
  );

  const handleMoveAnchor = useCallback(
    (markId, index, x, y, meta = null) => {
      updateCurrentPageMarks(
        (marks) =>
          marks.map((m) => {
            if (String(m.id) !== String(markId)) return m;
            if (
              meta?.lineEnds &&
              (m.type === "line" ||
                m.type === "arrow" ||
                m.type === "dimension")
            ) {
              const patch =
                Number(index) === 0 ? { x1: x, y1: y } : { x2: x, y2: y };
              return clampMarkAllowOutside({ ...m, ...patch });
            }
            // Rect/círculo/… → path al primer arrastre de ancla (A)
            let cur = m;
            if (meta?.expandBox && cur.type !== "path") {
              const expanded = expandBoxMarkToPath(cur);
              if (expanded) cur = expanded;
            }
            if (cur.type !== "path") return m;
            return clampMarkAllowOutside({
              ...cur,
              points: movePathAnchor(cur.points || [], index, x, y, {
                allowOutside: true,
              }),
            });
          }),
        { coalesceMs: 120, allowOutside: true },
      );
    },
    [pageIdx],
  );

  /** Live Paint Bucket (VC K): aplica fill activo a la forma cerrada clicada. */
  const handleLivePaintFill = useCallback(
    (markId) => {
      if (!markId) return;
      const fill =
        markFillColorRef.current && markFillColorRef.current !== "none"
          ? markFillColorRef.current
          : markColor || DEFAULT_MARK_COLOR;
      updateCurrentPageMarks((marks) =>
        marks.map((m) => {
          if (String(m.id) !== String(markId)) return m;
          return clampMarkAllowOutside(applyLivePaintFillToMark(m, fill));
        }),
      );
      // No seleccionar: las asas de bbox bloqueaban el siguiente clic en la mesa
      setOkMsg("Live Paint: relleno aplicado");
    },
    [pageIdx, markColor],
  );

  const patchSelectedMark = useCallback(
    (patch) => {
      const ids = selectedMarkIdsRef.current;
      if (!ids.size && !selectedMarkId) return;
      const target = ids.size ? ids : new Set([String(selectedMarkId)]);
      updateCurrentPageMarks((marks) =>
        marks.map((m) =>
          target.has(String(m.id))
            ? // Character / apariencia no deben reclavar marcas fuera de la mesa
              clampMarkAllowOutside({ ...m, ...patch, id: m.id })
            : m,
        ),
      );
    },
    [pageIdx, selectedMarkId],
  );

  const normalizePickHex = (raw) => {
    const next = String(raw || "").trim();
    if (!/^#[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(next)) return null;
    return next.length === 4
      ? `#${next[1]}${next[1]}${next[2]}${next[2]}${next[3]}${next[3]}`.toLowerCase()
      : next.toLowerCase();
  };

  /** Aplica color al target activo (fill o stroke) + selección + draft. */
  const applyMarkColor = useCallback(
    (raw, forceTarget) => {
      const hex = normalizePickHex(raw);
      if (!hex) return;
      const target = forceTarget || colorTarget;
      setMarkColor(hex);
      if (target === "stroke") {
        setMarkStrokeColor(hex);
        // Solo strokeColor — NUNCA tocar fill/color (letra o relleno).
        // Si contorno estaba a 0, subir a 1 para que el stroke se vea.
        const bumpStroke = Number(markStroke) <= 0;
        if (bumpStroke) setMarkStroke(1);
        patchSelectedMark({
          strokeColor: hex,
          ...(bumpStroke ? { stroke: 1 } : null),
        });
        setDraftMark((prev) => {
          if (!prev) return prev;
          const updated = {
            ...prev,
            strokeColor: hex,
            ...(bumpStroke ? { stroke: 1 } : null),
          };
          draftMarkRef.current = updated;
          return updated;
        });
        setTextDraft((prev) => {
          if (!prev) return prev;
          const updated = {
            ...prev,
            strokeColor: hex,
            ...(bumpStroke ? { stroke: 1 } : null),
          };
          textDraftRef.current = updated;
          return updated;
        });
      } else {
        setMarkFillColor(hex);
        // Relleno sólido: quitar degradado (si no, el fill no se ve)
        // No tocar strokeColor.
        patchSelectedMark({ fill: hex, color: hex, gradient: null });
        setDraftMark((prev) => {
          if (!prev) return prev;
          const updated = {
            ...prev,
            fill: markSupportsFill(prev.type) ? hex : prev.fill || "none",
            color: hex,
            gradient: null,
          };
          draftMarkRef.current = updated;
          return updated;
        });
        setTextDraft((prev) => {
          if (!prev) return prev;
          const updated = { ...prev, fill: hex, color: hex };
          textDraftRef.current = updated;
          return updated;
        });
      }
    },
    [patchSelectedMark, colorTarget, markStroke],
  );

  const applyNoneFill = useCallback(() => {
    setMarkFillColor("none");
    setColorTarget("fill");
    patchSelectedMark({ fill: "none", gradient: null });
    setDraftMark((prev) => {
      if (!prev || !markSupportsFill(prev.type)) return prev;
      const updated = { ...prev, fill: "none", gradient: null };
      draftMarkRef.current = updated;
      return updated;
    });
  }, [patchSelectedMark]);

  const swapFillStrokeColors = useCallback(() => {
    const nextFill =
      markStrokeColor === "none"
        ? "none"
        : markStrokeColor || DEFAULT_MARK_COLOR;
    const nextStroke =
      markFillColor === "none"
        ? DEFAULT_MARK_COLOR
        : markFillColor || DEFAULT_MARK_COLOR;
    setMarkFillColor(nextFill);
    setMarkStrokeColor(nextStroke);
    setMarkColor(
      colorTarget === "stroke"
        ? nextStroke
        : nextFill === "none"
          ? nextStroke
          : nextFill,
    );
    patchSelectedMark({
      fill: nextFill,
      strokeColor: nextStroke,
      color: nextFill === "none" ? nextStroke : nextFill,
    });
    setDraftMark((prev) => {
      if (!prev) return prev;
      const updated = {
        ...prev,
        fill: markSupportsFill(prev.type) ? nextFill : "none",
        strokeColor: nextStroke,
        color: nextFill === "none" ? nextStroke : nextFill,
      };
      draftMarkRef.current = updated;
      return updated;
    });
    setTextDraft((prev) => {
      if (!prev) return prev;
      const updated = {
        ...prev,
        fill: nextFill === "none" ? nextStroke : nextFill,
        color: nextFill === "none" ? nextStroke : nextFill,
        strokeColor: nextStroke,
      };
      textDraftRef.current = updated;
      return updated;
    });
  }, [markFillColor, markStrokeColor, colorTarget, patchSelectedMark]);

  const resolveGradientDraft = useCallback(
    (patch = {}) => {
      const base = normalizeMarkGradient(gradientDraft, DEFAULT_MARK_COLOR) || {
        from: DEFAULT_MARK_COLOR,
        to: "#ffffff",
        kind: "linear",
        angle: 0,
      };
      return (
        normalizeMarkGradient({ ...base, ...patch }, base.from) || {
          ...base,
          ...patch,
        }
      );
    },
    [gradientDraft],
  );

  const applyMarkGradient = useCallback(
    (patch = {}) => {
      const next = resolveGradientDraft(patch);
      setGradientDraft(next);
      const ids = selectedMarkIdsRef.current.size
        ? selectedMarkIdsRef.current
        : selectedMarkId
          ? new Set([String(selectedMarkId)])
          : new Set();
      if (!ids.size) {
        setOkMsg("Selecciona una marca cerrada y Apply en Gradient");
        return;
      }
      let applied = 0;
      updateCurrentPageMarks((marks) =>
        marks.map((m) => {
          if (!ids.has(String(m.id))) return m;
          const closed =
            markSupportsFill(m.type) ||
            (m.type === "path" && Boolean(m.closed));
          if (!closed) return m;
          applied += 1;
          return clampMark({
            ...m,
            gradient: next,
            fill: next.from,
            color: next.from,
          });
        }),
      );
      setOkMsg(
        applied
          ? "Degradado aplicado"
          : "El degradado solo aplica a formas cerradas",
      );
    },
    [pageIdx, selectedMarkId, resolveGradientDraft],
  );

  const patchGradientDraft = useCallback(
    (patch) => {
      const next = resolveGradientDraft(patch);
      setGradientDraft(next);
      const ids = selectedMarkIdsRef.current.size
        ? selectedMarkIdsRef.current
        : selectedMarkId
          ? new Set([String(selectedMarkId)])
          : new Set();
      if (!ids.size) return;
      // Live como VectorCraft: al tocar stops/ángulo se aplica a la selección
      updateCurrentPageMarks(
        (marks) =>
          marks.map((m) => {
            if (!ids.has(String(m.id))) return m;
            const closed =
              markSupportsFill(m.type) ||
              (m.type === "path" && Boolean(m.closed));
            if (!closed) return m;
            return clampMark({
              ...m,
              gradient: next,
              fill: next.from,
              color: next.from,
            });
          }),
        { history: false },
      );
    },
    [pageIdx, selectedMarkId, resolveGradientDraft],
  );

  const reverseMarkGradient = useCallback(() => {
    const cur = resolveGradientDraft();
    applyMarkGradient({ from: cur.to, to: cur.from });
  }, [resolveGradientDraft, applyMarkGradient]);

  const handleSelectMark = useCallback(
    (id, { additive = false } = {}) => {
      if (id == null) {
        clearMarkSelection();
        return;
      }
      // Shift: mantener foto seleccionada (par para máscara de recorte)
      if (!additive) {
        setSelectedSlotIds(new Set());
        setActiveSlotId(null);
      }
      const sid = String(id);
      const marks = pagesRef.current[pageIdx]?.marks || [];
      // V / groupSelection: clic = grupo entero. A (directSelect): solo esa marca.
      const expandGroups = markTool !== "directSelect";
      const seedIds = expandGroups
        ? [...expandSelectionByGroup(marks, sid)]
        : [sid];
      setSelectedMarkIds((prev) => {
        let next;
        if (additive) {
          next = new Set(prev);
          const allIn = seedIds.every((x) => next.has(String(x)));
          if (allIn) {
            seedIds.forEach((x) => next.delete(String(x)));
            setSelectedMarkId((cur) => {
              if (cur != null && next.has(String(cur))) return cur;
              return next.size ? [...next][next.size - 1] : null;
            });
          } else {
            seedIds.forEach((x) => next.add(String(x)));
            setSelectedMarkId(id);
          }
        } else if (prev.has(sid) && prev.size > 1 && !expandGroups) {
          next = prev;
          setSelectedMarkId(id);
        } else {
          next = new Set(seedIds.map(String));
          setSelectedMarkId(id);
        }
        selectedMarkIdsRef.current = next;
        return next;
      });
      const mark = marks.find((m) => String(m.id) === sid);
      if (!mark) return;
      const paint = markPaintColors(mark);
      const isText = mark.type === "text" || mark.type === "textpath";
      setMarkStrokeColor(paint.strokeColor);
      setMarkFillColor(paint.fill);
      if (isText) {
        setMarkColor(textMarkFillColor(mark));
        setColorTarget("fill");
        setMarkStroke(Number(mark.stroke) || 0);
        setMarkFontSize(Number(mark.fontSize) || 14);
        setMarkFontFamily(normalizeMarkFontFamily(mark.fontFamily));
        setMarkFontWeight(normalizeMarkFontWeight(mark.fontWeight));
        if (mark.type === "text") {
          setMarkTextAlign(normalizeMarkTextAlign(mark.textAlign));
        }
      } else {
        setMarkColor(paint.strokeColor || mark.color || DEFAULT_MARK_COLOR);
        if (mark.stroke != null) setMarkStroke(Number(mark.stroke) || 2);
      }
      if (
        mark.type === "grid" ||
        mark.type === "polarGrid" ||
        mark.type === "flare"
      ) {
        setGridOpts((o) => ({
          ...o,
          ...(mark.type === "grid"
            ? {
                cols: clampGridCols(mark.cols, o.cols),
                rows: clampGridRows(mark.rows, o.rows),
              }
            : {
                rings: clampPolarRings(mark.rings, o.rings),
                rays: clampPolarRays(mark.rays, o.rays),
              }),
        }));
        setVcRightPanel("appearance");
      }
      if (mark.type === "dimension") {
        setVcRightPanel("appearance");
      }
      const g = normalizeMarkGradient(mark.gradient, paint.fill || paint.ink);
      if (g) setGradientDraft(g);
      else {
        setGradientDraft({
          from:
            paint.fill && paint.fill !== "none"
              ? paint.fill
              : DEFAULT_MARK_COLOR,
          to:
            paint.strokeColor && paint.strokeColor !== paint.fill
              ? paint.strokeColor
              : "#ffffff",
          kind: "linear",
          angle: 0,
        });
      }
    },
    [pageIdx, clearMarkSelection, markTool],
  );

  const handleGroupMarks = useCallback(() => {
    const ids = selectedMarkIdsRef.current.size
      ? [...selectedMarkIdsRef.current]
      : selectedMarkId
        ? [String(selectedMarkId)]
        : [];
    if (ids.length < 2) {
      setOkMsg("Agrupar: selecciona 2+ marcas (Shift+clic o marco)");
      return;
    }
    let applied = false;
    notePagesHistory();
    updateCurrentPageMarks(
      (marks) => {
        const next = groupMarks(marks, ids);
        if (!next) return marks;
        applied = true;
        return next;
      },
      { history: false },
    );
    setOkMsg(
      applied
        ? `Grupo · ${ids.length} marcas · Ctrl+Shift+G desagrupa · A = seleccionar una`
        : "Agrupar: sin cambios",
    );
  }, [pageIdx, selectedMarkId]);

  const handleUngroupMarks = useCallback(() => {
    const ids = selectedMarkIdsRef.current.size
      ? [...selectedMarkIdsRef.current]
      : selectedMarkId
        ? [String(selectedMarkId)]
        : [];
    if (!ids.length) {
      setOkMsg("Desagrupar: selecciona una marca del grupo");
      return;
    }
    let applied = false;
    notePagesHistory();
    updateCurrentPageMarks(
      (marks) => {
        const next = ungroupMarks(marks, ids);
        if (!next) return marks;
        applied = true;
        return next;
      },
      { history: false },
    );
    setOkMsg(
      applied
        ? "Grupo liberado"
        : "Desagrupar: la selección no está en un grupo",
    );
  }, [pageIdx, selectedMarkId]);

  const handleMoveMark = useCallback(
    (id, nextMark) => {
      if (!id || !nextMark) return;
      updateCurrentPageMarks(
        (marks) =>
          marks.map((m) =>
            String(m.id) === String(id)
              ? clampMarkAllowOutside({ ...m, ...nextMark, id: m.id })
              : m,
          ),
        { history: false, allowOutside: true, coalesceMs: 48 },
      );
    },
    [pageIdx],
  );

  const handleMoveMarkGroup = useCallback(
    (dx, dy, origins) => {
      if (!Array.isArray(origins) || !origins.length) return;
      const byId = new Map(
        origins.map((m) => [
          String(m.id),
          translateMark(m, dx, dy, { allowOutside: true }),
        ]),
      );
      updateCurrentPageMarks(
        (marks) =>
          marks.map((m) => {
            const next = byId.get(String(m.id));
            return next ? clampMarkAllowOutside({ ...next, id: m.id }) : m;
          }),
        { history: false, allowOutside: true, coalesceMs: 48 },
      );
    },
    [pageIdx],
  );

  const clearSlotSelection = useCallback(() => {
    setSelectedSlotIds(new Set());
    setActiveSlotId(null);
  }, []);

  const handleLassoSelect = useCallback(
    (polygon, { additive = false } = {}) => {
      const pageMarks = pagesRef.current[pageIdx]?.marks || [];
      const ids = markIdsInsidePolygon(pageMarks, polygon);
      if (!ids.length) {
        if (!additive) clearMarkSelection();
        return;
      }
      clearSlotSelection();
      setSelectedMarkIds((prev) => {
        const next = additive ? new Set(prev) : new Set();
        ids.forEach((id) => next.add(String(id)));
        selectedMarkIdsRef.current = next;
        setSelectedMarkId(ids[ids.length - 1]);
        return next;
      });
    },
    [pageIdx, clearMarkSelection, clearSlotSelection],
  );

  const handleMagicWand = useCallback(
    (color, { additive = false } = {}) => {
      const pageMarks = pagesRef.current[pageIdx]?.marks || [];
      const tol = Math.max(
        0,
        Math.min(0.35, Number(wandOptsRef.current.tolerance) || 0),
      );
      const ids = markIdsWithColor(pageMarks, color, tol);
      if (!ids.length) {
        setOkMsg("Varita: sin marcas de color similar");
        return;
      }
      clearSlotSelection();
      setSelectedMarkIds((prev) => {
        const next = additive ? new Set(prev) : new Set();
        ids.forEach((id) => next.add(String(id)));
        selectedMarkIdsRef.current = next;
        setSelectedMarkId(ids[ids.length - 1]);
        return next;
      });
      if (color) setMarkColor(color);
      setOkMsg(
        `Varita · ${ids.length} marca${ids.length > 1 ? "s" : ""} · tol ${(tol * 100).toFixed(0)}%`,
      );
    },
    [pageIdx, clearSlotSelection],
  );

  const clearChromeSelection = useCallback(() => {}, []);

  /**
   * Activa una mesa sin mover zoom/pan (VectorCraft).
   * Si el clic cayó sobre foto o chrome, lo selecciona en el mismo gesto (1 clic).
   */
  const activateBoardPage = useCallback(
    (idx, { slotId = null, markId = null } = {}) => {
      boardCamUserRef.current = true;
      setPageIdx(idx);
      setSelectedPasteboardId(null);
      if (markId != null) {
        clearSlotSelection();
        const id = String(markId);
        setSelectedMarkId(markId);
        setSelectedMarkIds(new Set([id]));
        selectedMarkIdsRef.current = new Set([id]);
        return;
      }
      clearMarkSelection();
      if (slotId != null) {
        setSelectedSlotIds(new Set([slotId]));
        setActiveSlotId(slotId);
      } else {
        clearSlotSelection();
      }
    },
    [clearSlotSelection, clearMarkSelection],
  );

  const activateArtboardTool = useCallback(() => {
    handKeyHeldRef.current = false;
    zoomKeyHeldRef.current = false;
    setHandMode(false);
    setZoomMode(false);
    setPanInsideMode(false);
    setEyedropperMode(false);
    setFreeTransformMode(false);
    setMultiSelectMode(false);
    setMarkTool("artboard");
    setWorkspaceModeSafe(WORKSPACE_MODE_BOARD);
    setVcRightPanel("artboards");
    setOkMsg(
      "Mesa de trabajo · arrastra una mesa para moverla · ↑↓ en Artboards = orden PDF · V = selección",
    );
  }, [setWorkspaceModeSafe]);

  const onArtboardMovePointerDown = useCallback(
    (pageIndex, e) => {
      // Mano / Espacio / Zoom = pan del tablero, no mover mesas.
      if (boardPanActiveRef.current || zoomModeRef.current) return;
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      const i = Math.max(
        0,
        Math.min(
          pagesRef.current.length - 1,
          Math.floor(Number(pageIndex) || 0),
        ),
      );
      setPageIdx(i);
      clearMarkSelection();
      clearSlotSelection();
      notePagesHistory({ coalesceMs: 450 });
      const b = normalizePageBoard(pagesRef.current[i]?.board, i);
      const zoom = Math.max(0.01, Number(boardCamRef.current.zoom) || 1);
      // Congelar AABB del mundo para que left/top CSS reflejen el drag.
      setArtboardDragBounds(
        boardWorldBounds(pagesRef.current, 0.35, pasteboardRef.current),
      );
      artboardMoveRef.current = {
        pointerId: e.pointerId,
        index: i,
        x0: e.clientX,
        y0: e.clientY,
        boardX0: b.x,
        boardY0: b.y,
        zoom,
      };
      e.currentTarget.setPointerCapture?.(e.pointerId);
    },
    [notePagesHistory, clearMarkSelection, clearSlotSelection],
  );

  const onArtboardMovePointerMove = useCallback((e) => {
    const d = artboardMoveRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    const dx = (e.clientX - d.x0) / (BOARD_PX_PER_UNIT * d.zoom);
    const dy = (e.clientY - d.y0) / (BOARD_PX_PER_UNIT * d.zoom);
    setPages((prev) =>
      movePageBoard(prev, d.index, d.boardX0 + dx, d.boardY0 + dy),
    );
  }, []);

  const onArtboardMovePointerUp = useCallback((e) => {
    const d = artboardMoveRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    artboardMoveRef.current = null;
    setArtboardDragBounds(null);
    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    } catch {
      /* ignore */
    }
  }, []);

  const addAssetsToPasteboard = useCallback(
    (assetIds, boardPoint, sizeByAssetId = null) => {
      const ids = (Array.isArray(assetIds) ? assetIds : [])
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id));
      if (!ids.length) return;
      const baseX = Number(boardPoint?.x);
      const baseY = Number(boardPoint?.y);
      let x0 = Number.isFinite(baseX) ? baseX : 0.1;
      let y0 = Number.isFinite(baseY) ? baseY : null;
      if (y0 == null) {
        let maxY = 0;
        (pagesRef.current || []).forEach((p, i) => {
          const b = normalizePageBoard(p?.board, i);
          maxY = Math.max(maxY, b.y + b.h);
        });
        y0 = maxY + ARTBOARD_GAP + 0.05;
      }
      setPasteboard((prev) => {
        const next = [...normalizePasteboard(prev)];
        ids.forEach((assetId, i) => {
          const sized =
            sizeByAssetId?.get?.(assetId) || sizeByAssetId?.[assetId] || null;
          const w = Number(sized?.w) || PASTEBOARD_ITEM_W;
          const h = Number(sized?.h) || PASTEBOARD_ITEM_H;
          next.push({
            id: newPasteboardId(),
            assetId,
            x: x0 + i * 0.05 - w / 2,
            y: y0 + i * 0.05 - h / 2,
            w,
            h,
          });
        });
        const normalized = normalizePasteboard(next);
        pasteboardRef.current = normalized;
        if (normalized.length) {
          setSelectedPasteboardId(normalized[normalized.length - 1].id);
        }
        return normalized;
      });
    },
    [],
  );

  const selectOnlySlot = useCallback(
    (id) => {
      clearMarkSelection();
      if (!id) {
        setSelectedSlotIds(new Set());
        setActiveSlotId(null);
        return;
      }
      setSelectedSlotIds(new Set([id]));
      setActiveSlotId(id);
    },
    [clearMarkSelection],
  );

  const handleSelectSlot = useCallback(
    (id, { additive = false } = {}) => {
      // Clic en foto: no tocar zoom/pan (VectorCraft — la cámara la mueve el usuario).
      boardCamUserRef.current = true;
      // Shift: mantener forma seleccionada (par para máscara de recorte)
      if (!additive) clearMarkSelection();
      setSelectedPasteboardId(null);
      if (!id) return;
      setSelectedSlotIds((prev) => {
        if (additive) {
          const next = new Set(prev);
          if (next.has(id)) {
            next.delete(id);
            setActiveSlotId((cur) => {
              if (cur === id)
                return next.size ? [...next][next.size - 1] : null;
              return cur && next.has(cur) ? cur : [...next][0] || null;
            });
          } else {
            next.add(id);
            setActiveSlotId(id);
          }
          return next;
        }
        if (prev.has(id) && prev.size > 1) {
          setActiveSlotId(id);
          return prev;
        }
        setActiveSlotId(id);
        return new Set([id]);
      });
    },
    [clearMarkSelection],
  );

  const selectedSlots = useMemo(
    () => (currentPage.slots || []).filter((s) => selectedSlotIds.has(s.id)),
    [currentPage, selectedSlotIds],
  );
  const selectedMarks = useMemo(
    () =>
      (currentPage.marks || []).filter((m) =>
        selectedMarkIds.has(String(m.id)),
      ),
    [currentPage, selectedMarkIds],
  );
  const multiSelected = selectedSlotIds.size > 1;
  const multiMarksSelected = selectedMarkIds.size > 1;
  const slotFtOrientation =
    selectedSlots.length === 1 ? Number(selectedSlots[0].rotation) || 0 : 0;
  const markFtOrientation =
    selectedMarks.length === 1 ? Number(selectedMarks[0].rotation) || 0 : 0;
  const markFtBBoxOverride = useMemo(() => {
    if (selectedMarks.length !== 1) return null;
    const m = selectedMarks[0];
    if (!markHasBox(m) && m.type !== "text") return null;
    // Caja local (sin AABB rotada) para marco orientado
    return {
      x: Number(m.x) || 0,
      y: Number(m.y) || 0,
      w: Math.max(
        0.02,
        Number(m.w) || (m.type === "text" ? textMarkBoxWidth(m) : 0.1),
      ),
      h: Math.max(0.02, Number(m.h) || 0.06),
    };
  }, [selectedMarks]);
  const slotFtBBoxOverride = useMemo(() => {
    if (selectedSlots.length !== 1 || !slotFtOrientation) return null;
    const s = selectedSlots[0];
    return {
      x: Number(s.x) || 0,
      y: Number(s.y) || 0,
      w: Math.max(0.02, Number(s.w) || 0.1),
      h: Math.max(0.02, Number(s.h) || 0.1),
    };
  }, [selectedSlots, slotFtOrientation]);

  const handleGroupMoveStart = useCallback(() => {
    groupMoveOriginsRef.current = (pagesRef.current[pageIdx]?.slots || [])
      .filter((s) => selectedSlotIdsRef.current.has(s.id))
      .map((s) => ({ ...s }));
  }, [pageIdx]);

  const handleGroupMove = useCallback(
    (dx, dy) => {
      const origins = groupMoveOriginsRef.current;
      if (!origins?.length) return;
      const byId = new Map(origins.map((o) => [o.id, o]));
      updateCurrentPageSlots(
        (slots) =>
          slots.map((s) => {
            const o = byId.get(s.id);
            if (!o) return s;
            // Sin snap; allowOutside para poder sacar el grupo al fondo
            return clampSlotAllowOutside({
              ...s,
              x: o.x + dx,
              y: o.y + dy,
            });
          }),
        { allowOutside: true },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pageIdx],
  );

  const handleGroupMoveEnd = useCallback(() => {
    const ids = selectedSlotIdsRef.current;
    if (!ids?.size) return;
    updateCurrentPageSlots((slots) =>
      slots.map((s) => (ids.has(s.id) ? snapSlot(clampSlot(s)) : s)),
    );
    groupMoveOriginsRef.current = null;
  }, [pageIdx]);

  const handleGroupScale = useCallback((scaledSlots) => {
    const byId = new Map(scaledSlots.map((s) => [s.id, s]));
    updateCurrentPageSlots((slots) => slots.map((s) => byId.get(s.id) || s), {
      allowOutside: true,
    });
  }, []);

  const handleGroupRotate = useCallback((rotatedSlots) => {
    const byId = new Map(rotatedSlots.map((s) => [s.id, s]));
    updateCurrentPageSlots((slots) => slots.map((s) => byId.get(s.id) || s), {
      allowOutside: true,
    });
  }, []);

  const handleGroupShear = useCallback((shearedSlots) => {
    const byId = new Map(shearedSlots.map((s) => [s.id, s]));
    updateCurrentPageSlots((slots) => slots.map((s) => byId.get(s.id) || s), {
      allowOutside: true,
    });
  }, []);

  const handleMarkGroupScale = useCallback((scaledMarks) => {
    const byId = new Map(scaledMarks.map((m) => [String(m.id), m]));
    updateCurrentPageMarks((marks) =>
      marks.map((m) => {
        const next = byId.get(String(m.id));
        return next ? clampMark({ ...next, id: m.id }) : m;
      }),
    );
  }, []);

  const handleMarkGroupRotate = useCallback((rotatedMarks) => {
    const byId = new Map(rotatedMarks.map((m) => [String(m.id), m]));
    updateCurrentPageMarks((marks) =>
      marks.map((m) => {
        const next = byId.get(String(m.id));
        return next ? clampMark({ ...next, id: m.id }) : m;
      }),
    );
  }, []);

  const handleMarkGroupShear = useCallback((shearedMarks) => {
    const byId = new Map(shearedMarks.map((m) => [String(m.id), m]));
    updateCurrentPageMarks((marks) =>
      marks.map((m) => {
        const next = byId.get(String(m.id));
        return next ? clampMark({ ...next, id: m.id }) : m;
      }),
    );
  }, []);

  const startCanvasMarquee = (e) => {
    if (boardNavLockRef.current) return;
    if (markTool && markTool !== "select") return;
    if (e.button != null && e.button !== 0) return;
    if (e.target !== e.currentTarget) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) return;
    // Illustrator: Shift mantiene/amplía; modo multi (tablet) igual
    const additive = Boolean(e.shiftKey) || multiSelectMode;
    const x0 = (e.clientX - rect.left) / rect.width;
    const y0 = (e.clientY - rect.top) / rect.height;
    setMarquee({ x0, y0, x1: x0, y1: y0, additive });
    if (!additive) {
      clearSlotSelection();
      clearMarkSelection();
    }
    const onMove = (ev) => {
      const x1 = (ev.clientX - rect.left) / rect.width;
      const y1 = (ev.clientY - rect.top) / rect.height;
      setMarquee((m) => (m ? { ...m, x1, y1 } : null));
    };
    const onUp = (ev) => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      const x1 = (ev.clientX - rect.left) / rect.width;
      const y1 = (ev.clientY - rect.top) / rect.height;
      const box = {
        x: Math.min(x0, x1),
        y: Math.min(y0, y1),
        w: Math.abs(x1 - x0),
        h: Math.abs(y1 - y0),
      };
      setMarquee(null);
      if (box.w < 0.01 && box.h < 0.01) {
        if (!additive) {
          clearSlotSelection();
          clearMarkSelection();
        }
        return;
      }
      const page = pagesRef.current[pageIdx] || {};
      const slotHits = (page.slots || [])
        .filter((s) =>
          rectsIntersect(box, {
            x: s.x,
            y: s.y,
            w: s.w,
            h: s.h,
          }),
        )
        .map((s) => s.id);
      const markHits = (page.marks || [])
        .filter((m) => {
          if (!m) return false;
          if (m.type === "text") {
            return (
              String(m.text || "").trim() &&
              rectsIntersect(box, textMarkApproxBounds(m))
            );
          }
          const b = markBounds(m);
          return b && rectsIntersect(box, b);
        })
        .map((m) => String(m.id));
      if (!slotHits.length && !markHits.length) {
        if (!additive) {
          clearSlotSelection();
          clearMarkSelection();
        }
        return;
      }
      if (slotHits.length) {
        setSelectedSlotIds((prev) => {
          const next = additive ? new Set(prev) : new Set();
          slotHits.forEach((id) => next.add(id));
          setActiveSlotId(slotHits[slotHits.length - 1]);
          return next;
        });
      } else if (!additive) {
        clearSlotSelection();
      }
      if (markHits.length) {
        setSelectedMarkIds((prev) => {
          const next = additive ? new Set(prev) : new Set();
          markHits.forEach((id) => next.add(id));
          selectedMarkIdsRef.current = next;
          setSelectedMarkId(markHits[markHits.length - 1]);
          return next;
        });
        const mark = (page.marks || []).find(
          (m) => String(m.id) === markHits[markHits.length - 1],
        );
        if (mark?.color) setMarkColor(mark.color);
        if (mark?.type === "text" || mark?.type === "textpath") {
          setMarkFontSize(Number(mark.fontSize) || 14);
          setMarkFontFamily(normalizeMarkFontFamily(mark.fontFamily));
          setMarkFontWeight(normalizeMarkFontWeight(mark.fontWeight));
          if (mark.type === "text") {
            setMarkTextAlign(normalizeMarkTextAlign(mark.textAlign));
          }
        }
      } else if (!additive) {
        clearMarkSelection();
      }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  useEffect(() => {
    pagesRef.current = pages;
  }, [pages]);

  const copyMoodboardSelection = useCallback(() => {
    const page = normalizeFreePage(
      pagesRef.current[pageIdx] || emptyFreePage(),
    );
    const pbSel = selectedPasteboardId ? [selectedPasteboardId] : [];
    const clip = buildMoodboardClipboard({
      slots: page.slots,
      marks: page.marks,
      selectedSlotIds: [...selectedSlotIdsRef.current],
      selectedMarkIds: [...selectedMarkIdsRef.current],
      pasteboard: pasteboardRef.current,
      selectedPasteboardIds: pbSel,
      fromPage: pageIdx,
    });
    if (!clip) return false;
    clipboardRef.current = clip;
    clipboardInternalAtRef.current = Date.now();
    // Sustituye la imagen residual del portapapeles del SO (si no, Ctrl+V pega eso).
    try {
      void navigator.clipboard?.writeText?.("\u200Bcrm-moodboard");
    } catch {
      /* ignore */
    }
    const parts = [];
    if (clip.slots.length) {
      parts.push(
        clip.slots.length === 1
          ? t("skillAgent.moodboard.ok.copiedPhoto")
          : t("skillAgent.moodboard.ok.copiedPhotos", { n: clip.slots.length }),
      );
    }
    if ((clip.pasteboard || []).length) {
      parts.push(
        clip.pasteboard.length === 1
          ? "1 foto del tablero"
          : `${clip.pasteboard.length} fotos del tablero`,
      );
    }
    if (clip.marks.length === 1) {
      parts.push(t("skillAgent.moodboard.ok.copiedText"));
    } else if (clip.marks.length > 1) {
      parts.push(
        t("skillAgent.moodboard.ok.copiedTexts", { n: clip.marks.length }),
      );
    }
    setOkMsg(
      t("skillAgent.moodboard.ok.copiedHint", { what: parts.join(" · ") }),
    );
    return true;
  }, [pageIdx, selectedPasteboardId, t]);

  const cutMoodboardSelection = useCallback(() => {
    const page = normalizeFreePage(
      pagesRef.current[pageIdx] || emptyFreePage(),
    );
    const pbSel = selectedPasteboardId ? [selectedPasteboardId] : [];
    const clip = buildMoodboardClipboard({
      slots: page.slots,
      marks: page.marks,
      selectedSlotIds: [...selectedSlotIdsRef.current],
      selectedMarkIds: [...selectedMarkIdsRef.current],
      pasteboard: pasteboardRef.current,
      selectedPasteboardIds: pbSel,
      fromPage: pageIdx,
    });
    if (!clip) return false;
    const stripped = pageAfterMoodboardCut(page, clip);
    const strippedPb = pasteboardAfterMoodboardCut(pasteboardRef.current, clip);
    const hasPageCut = Boolean(stripped);
    const hasPbCut = Boolean(strippedPb);
    if (!hasPageCut && !hasPbCut) return false;
    notePagesHistory();
    clipboardRef.current = clip;
    clipboardInternalAtRef.current = Date.now();
    try {
      void navigator.clipboard?.writeText?.("\u200Bcrm-moodboard");
    } catch {
      /* ignore */
    }
    if (hasPageCut) {
      setPages((prev) =>
        prev.map((p, i) => {
          if (i !== pageIdx) return p;
          const base = normalizeFreePage(p);
          return { ...base, slots: stripped.slots, marks: stripped.marks };
        }),
      );
      setSelectedSlotIds(new Set());
      setActiveSlotId(null);
      clearMarkSelection();
    }
    if (hasPbCut) {
      const normalized = normalizePasteboard(strippedPb);
      pasteboardRef.current = normalized;
      setPasteboard(normalized);
      setSelectedPasteboardId(null);
    }
    const parts = [];
    if (clip.slots.length) {
      parts.push(
        clip.slots.length === 1
          ? t("skillAgent.moodboard.ok.copiedPhoto")
          : t("skillAgent.moodboard.ok.copiedPhotos", { n: clip.slots.length }),
      );
    }
    if ((clip.pasteboard || []).length) {
      parts.push(
        clip.pasteboard.length === 1
          ? "1 foto del tablero"
          : `${clip.pasteboard.length} fotos del tablero`,
      );
    }
    if (clip.marks.length === 1) {
      parts.push(t("skillAgent.moodboard.ok.copiedText"));
    } else if (clip.marks.length > 1) {
      parts.push(
        t("skillAgent.moodboard.ok.copiedTexts", { n: clip.marks.length }),
      );
    }
    setOkMsg(t("skillAgent.moodboard.ok.cutHint", { what: parts.join(" · ") }));
    return true;
  }, [pageIdx, selectedPasteboardId, notePagesHistory, clearMarkSelection, t]);

  const defaultPasteboardPoint = useCallback(() => {
    const pagesNow = pagesRef.current || [];
    let maxY = 0;
    pagesNow.forEach((p, i) => {
      const b = normalizePageBoard(p?.board, i);
      maxY = Math.max(maxY, b.y + b.h);
    });
    return {
      x: 0.15 + (pasteboardRef.current.length % 5) * 0.06,
      y: maxY + ARTBOARD_GAP + 0.05,
    };
  }, []);

  /** Resuelve si un punto de pantalla cae en un artboard o en el pasteboard. */
  const resolveBoardPointerTarget = useCallback(
    (clientX, clientY) => {
      if (
        workspaceModeRef.current !== WORKSPACE_MODE_BOARD ||
        !boardViewportRef.current
      ) {
        return null;
      }
      const bounds = boardWorldBounds(
        pagesRef.current,
        0.35,
        pasteboardRef.current,
      );
      const boardPt = clientToBoardPoint(
        clientX,
        clientY,
        boardViewportRef.current,
        boardCamRef.current,
        bounds,
      );
      if (!boardPt) return null;
      const artIdx = artboardIndexAtPoint(
        pagesRef.current,
        boardPt.x,
        boardPt.y,
      );
      if (artIdx >= 0) {
        return {
          kind: "page",
          boardPt,
          pageIndex: artIdx,
          placeAt: pointToArtboardNorm(
            pagesRef.current,
            artIdx,
            boardPt.x,
            boardPt.y,
          ),
        };
      }
      return { kind: "pasteboard", boardPt, pageIndex: pageIdx, placeAt: null };
    },
    [pageIdx],
  );

  const rememberBoardPointer = useCallback(
    (clientX, clientY) => {
      const target = resolveBoardPointerTarget(clientX, clientY);
      if (!target?.boardPt) return null;
      lastBoardPointerRef.current = target.boardPt;
      lastBoardTargetKindRef.current = target.kind;
      return target;
    },
    [resolveBoardPointerTarget],
  );

  const pasteMoodboardSelection = useCallback(() => {
    const clip = clipboardRef.current;
    if (!clip) return false;
    const photoSlots = (clip.slots || []).filter(
      (s) => s?.assetId != null && Number.isFinite(Number(s.assetId)),
    );
    const floatItems = (clip.pasteboard || []).filter(
      (p) => p?.assetId != null && Number.isFinite(Number(p.assetId)),
    );
    const photoIds = [
      ...photoSlots.map((s) => Number(s.assetId)),
      ...floatItems.map((p) => Number(p.assetId)),
    ];

    // VectorCraft / Illustrator: fuera de mesa → foto flotante, nunca otra mesa.
    const tip = lastBoardPointerRef.current;
    const kindNow =
      lastBoardTargetKindRef.current ||
      (tip && artboardIndexAtPoint(pagesRef.current, tip.x, tip.y) >= 0
        ? "page"
        : "pasteboard");
    const toPasteboard =
      workspaceModeRef.current === WORKSPACE_MODE_BOARD &&
      kindNow !== "page" &&
      photoIds.length > 0;

    if (toPasteboard) {
      const sizeByAssetId = new Map();
      photoSlots.forEach((s) => {
        sizeByAssetId.set(Number(s.assetId), pasteboardSizeFromSlot(s));
      });
      floatItems.forEach((p) => {
        sizeByAssetId.set(Number(p.assetId), pasteboardSizeFromSlot(p));
      });
      const nudge = (Number(clip.pasteCount) || 0) * 0.04;
      const at = tip || defaultPasteboardPoint();
      addAssetsToPasteboard(
        photoIds,
        { x: at.x + nudge, y: at.y + nudge },
        sizeByAssetId,
      );
      clipboardRef.current = {
        ...clip,
        pasteCount: (Number(clip.pasteCount) || 0) + 1,
      };
      clearSlotSelection();
      clearMarkSelection();
      setOkMsg(
        photoIds.length === 1
          ? "Foto en el tablero (fuera de las mesas)"
          : `${photoIds.length} fotos en el tablero`,
      );
      return true;
    }

    // Solo floats en clip + puntero en mesa → meter en esa página
    if (
      floatItems.length &&
      !(clip.slots || []).length &&
      !(clip.marks || []).length &&
      kindNow === "page"
    ) {
      let targetIdx = pageIdx;
      let placeAt = { x: 0.5, y: 0.5 };
      if (tip) {
        const artIdx = artboardIndexAtPoint(pagesRef.current, tip.x, tip.y);
        if (artIdx >= 0) {
          targetIdx = artIdx;
          placeAt =
            pointToArtboardNorm(pagesRef.current, artIdx, tip.x, tip.y) ||
            placeAt;
        }
      }
      notePagesHistory();
      const notesMap = notesByAssetIdFromAssets(assets);
      const createdIds = [];
      setPages((prev) => {
        const nextPages = prev.map((p) => normalizeFreePage(p));
        const i = Math.min(
          Math.max(0, targetIdx),
          Math.max(0, nextPages.length - 1),
        );
        const pageNow = nextPages[i] || emptyFreePage();
        if (!nextPages[i]) nextPages.push(pageNow);
        let slots = [...(pageNow.slots || [])];
        let maxZ = Math.max(0, ...slots.map((s) => Number(s.z) || 0));
        const placed = [];
        floatItems.forEach((item, fi) => {
          const draft = pasteboardItemToSlotDraft(item, {
            x: placeAt.x + fi * 0.03,
            y: placeAt.y + fi * 0.03,
          });
          if (!draft) return;
          maxZ += 1;
          const created = clampSlot({
            id: newSlotId("pb"),
            ...draft,
            shape: "rect",
            z: maxZ,
          });
          slots.push(created);
          placed.push(created);
          createdIds.push(created.id);
        });
        nextPages[i] = {
          ...pageNow,
          slots,
          marks: mergeNoteMarksForSlots(pageNow, placed, notesMap),
        };
        setPageIdx(i);
        return nextPages;
      });
      if (createdIds.length) {
        setSelectedSlotIds(new Set(createdIds));
        setActiveSlotId(createdIds[createdIds.length - 1]);
      }
      clipboardRef.current = {
        ...clip,
        pasteCount: (Number(clip.pasteCount) || 0) + 1,
      };
      setSelectedPasteboardId(null);
      setOkMsg(t("skillAgent.moodboard.ok.pasted"));
      return true;
    }

    const page = normalizeFreePage(
      pagesRef.current[pageIdx] || emptyFreePage(),
    );
    const next = pasteMoodboardClipboard(page, clip, {
      pageIndex: pageIdx,
    });
    if (!next) {
      if (floatItems.length) {
        addAssetsToPasteboard(
          floatItems.map((p) => Number(p.assetId)),
          tip || defaultPasteboardPoint(),
        );
        return true;
      }
      return false;
    }
    notePagesHistory();
    clipboardRef.current = next.nextClipboard;
    setPages((prev) =>
      prev.map((p, i) => {
        if (i !== pageIdx) return p;
        const base = normalizeFreePage(p);
        return { ...base, slots: next.slots, marks: next.marks };
      }),
    );
    if (next.newSlotIds.length) {
      setSelectedSlotIds(new Set(next.newSlotIds));
      setActiveSlotId(next.newSlotIds[next.newSlotIds.length - 1]);
    } else {
      setSelectedSlotIds(new Set());
      setActiveSlotId(null);
    }
    if (next.newMarkIds.length) {
      const markSet = new Set(next.newMarkIds.map(String));
      selectedMarkIdsRef.current = markSet;
      setSelectedMarkIds(markSet);
      setSelectedMarkId(next.newMarkIds[next.newMarkIds.length - 1]);
    } else {
      clearMarkSelection();
    }
    setSelectedPasteboardId(null);
    setOkMsg(t("skillAgent.moodboard.ok.pasted"));
    return true;
  }, [
    pageIdx,
    notePagesHistory,
    clearMarkSelection,
    clearSlotSelection,
    t,
    addAssetsToPasteboard,
    defaultPasteboardPoint,
    assets,
  ]);

  /** Ctrl+Shift+Alt+V: pegar lo copiado en todas las mesas. */
  const pasteMoodboardSelectionOnAllPages = useCallback(() => {
    const clip = clipboardRef.current;
    if (!clip) {
      setOkMsg("Nada en el portapapeles interno");
      return false;
    }
    const result = pasteMoodboardClipboardOnAllPages(pagesRef.current, clip);
    if (!result) {
      setOkMsg("No hay fotos/marcas para pegar en todas las páginas");
      return false;
    }
    notePagesHistory();
    clipboardRef.current = result.nextClipboard;
    setPages(result.pages);
    clearSlotSelection();
    clearMarkSelection();
    setOkMsg(
      `Pegado en ${result.pages.length} mesa${result.pages.length === 1 ? "" : "s"}`,
    );
    return true;
  }, [notePagesHistory, clearSlotSelection, clearMarkSelection]);

  /**
   * Saca fotos de la página → pasteboard (move).
   * @param {string[]|null} slotIds — null = selección actual
   * @param {{x:number,y:number}|null} boardPoint — si viene del drag, centro bajo el puntero
   */
  const moveSlotsToPasteboard = useCallback(
    (slotIds = null, boardPoint = null) => {
      const ids = (slotIds || [...selectedSlotIdsRef.current]).map(String);
      if (!ids.length) {
        setOkMsg("Selecciona fotos en la mesa para sacarlas al fondo");
        return false;
      }
      const page = normalizeFreePage(
        pagesRef.current[pageIdx] || emptyFreePage(),
      );
      const board = normalizePageBoard(page?.board, pageIdx);
      const moving = (page.slots || []).filter(
        (s) => ids.includes(String(s.id)) && s?.assetId != null,
      );
      if (!moving.length) {
        setOkMsg("Nada que sacar al tablero");
        return false;
      }
      const at =
        boardPoint &&
        Number.isFinite(Number(boardPoint.x)) &&
        Number.isFinite(Number(boardPoint.y))
          ? { x: Number(boardPoint.x), y: Number(boardPoint.y) }
          : null;
      const floats = [];
      const removeIds = new Set();
      moving.forEach((s, i) => {
        const base = slotToPasteboardItem(s, board);
        if (!base) return;
        // Tamaño exacto del DOM (offset pre-transform) = mismo px que en la mesa
        try {
          const el = document.querySelector(
            `[data-slot-root="${CSS.escape(String(s.id))}"]`,
          );
          const ow = el?.offsetWidth;
          const oh = el?.offsetHeight;
          if (ow > 2 && oh > 2) {
            base.w = ow / BOARD_PX_PER_UNIT;
            base.h = oh / BOARD_PX_PER_UNIT;
          }
        } catch {
          /* ignore */
        }
        removeIds.add(String(s.id));
        floats.push(
          at
            ? {
                ...base,
                x: at.x - base.w / 2 + i * 0.04,
                y: at.y - base.h / 2 + i * 0.04,
              }
            : base,
        );
      });
      // No borrar casillas si no se pudo crear el float (evitar foto perdida)
      if (!floats.length) {
        setOkMsg("No se pudo sacar la foto al tablero");
        return false;
      }
      // Bloquea updates en cola (drag) que volverían a meter la casilla en la mesa
      removeIds.forEach((id) => ejectedSlotIdsRef.current.add(id));
      groupMoveOriginsRef.current = null;
      notePagesHistory();
      setPages((prev) =>
        prev.map((p, i) => {
          if (i !== pageIdx) return p;
          const base = normalizeFreePage(p);
          const slots = (base.slots || []).filter(
            (s) => !removeIds.has(String(s.id)),
          );
          return { ...base, slots };
        }),
      );
      window.setTimeout(() => {
        removeIds.forEach((id) => ejectedSlotIdsRef.current.delete(id));
      }, 500);
      setPasteboard((prev) => {
        const normalized = normalizePasteboard([
          ...normalizePasteboard(prev),
          ...floats,
        ]);
        pasteboardRef.current = normalized;
        if (floats.length) {
          setSelectedPasteboardId(floats[floats.length - 1].id);
        }
        return normalized;
      });
      setSelectedSlotIds(new Set());
      setActiveSlotId(null);
      clearMarkSelection();
      setOkMsg(
        floats.length === 1
          ? "Foto sacada al fondo del tablero"
          : `${floats.length} fotos sacadas al fondo`,
      );
      return true;
    },
    [pageIdx, notePagesHistory, clearMarkSelection],
  );

  const moveSelectedSlotsToPasteboard = useCallback(
    () => moveSlotsToPasteboard(null, null),
    [moveSlotsToPasteboard],
  );

  /**
   * Mueve fotos de la mesa activa a otra mesa (sin pasar por el fondo).
   * @param {string[]} slotIds
   * @param {number} destPageIndex
   * @param {{x:number,y:number}|null} placeAt — centro norm en la mesa destino
   */
  const moveSlotsToPage = useCallback(
    (slotIds, destPageIndex, placeAt = null) => {
      const ids = (slotIds || []).map(String).filter(Boolean);
      if (!ids.length) return false;
      const dest = Math.min(
        Math.max(0, Math.floor(Number(destPageIndex) || 0)),
        Math.max(0, (pagesRef.current || []).length - 1),
      );
      if (dest === pageIdx) return false;
      const result = reparentSlotsToPage(pagesRef.current, {
        sourcePageIndex: pageIdx,
        destPageIndex: dest,
        slotIds: ids,
        placeAt: placeAt || { x: 0.5, y: 0.5 },
      });
      if (!result?.movedIds?.length) {
        setOkMsg("No se pudo mover la foto a esa mesa");
        return false;
      }
      // Bloquea updates en cola del drag que re-crearían la casilla en origen
      result.movedIds.forEach((id) => ejectedSlotIdsRef.current.add(id));
      groupMoveOriginsRef.current = null;
      notePagesHistory();
      setPages(result.pages);
      window.setTimeout(() => {
        result.movedIds.forEach((id) => ejectedSlotIdsRef.current.delete(id));
      }, 500);
      setPageIdx(dest);
      setSelectedSlotIds(new Set(result.movedIds));
      setActiveSlotId(result.movedIds[result.movedIds.length - 1]);
      clearMarkSelection();
      setSelectedPasteboardId(null);
      setOkMsg(
        result.movedIds.length === 1
          ? "Foto movida a otra mesa"
          : `${result.movedIds.length} fotos movidas a otra mesa`,
      );
      return true;
    },
    [pageIdx, notePagesHistory, clearMarkSelection],
  );

  /**
   * Saca textos/figuras/cabecera-pie de la página → pasteboard (move).
   * @param {string[]|null} markIds
   * @param {{x:number,y:number}|null} boardPoint
   */
  const moveMarksToPasteboard = useCallback(
    (markIds = null, boardPoint = null) => {
      const ids = (markIds || [...selectedMarkIdsRef.current]).map(String);
      if (!ids.length) return false;
      const page = normalizeFreePage(
        pagesRef.current[pageIdx] || emptyFreePage(),
        { allowOutside: true },
      );
      const board = normalizePageBoard(page?.board, pageIdx);
      const moving = (page.marks || []).filter((m) =>
        ids.includes(String(m.id)),
      );
      if (!moving.length) return false;
      const at =
        boardPoint &&
        Number.isFinite(Number(boardPoint.x)) &&
        Number.isFinite(Number(boardPoint.y))
          ? { x: Number(boardPoint.x), y: Number(boardPoint.y) }
          : null;
      const floats = [];
      const removeIds = new Set();
      moving.forEach((m, i) => {
        const base = markToPasteboardItem(m, board, {
          boardPoint: at ? { x: at.x + i * 0.04, y: at.y + i * 0.04 } : null,
        });
        if (!base) return;
        removeIds.add(String(m.id));
        floats.push(base);
      });
      if (!floats.length) return false;
      notePagesHistory();
      setPages((prev) =>
        prev.map((p, i) => {
          if (i !== pageIdx) return p;
          const base = normalizeFreePage(p, { allowOutside: true });
          return {
            ...base,
            marks: (base.marks || []).filter(
              (m) => !removeIds.has(String(m.id)),
            ),
          };
        }),
      );
      setPasteboard((prev) => {
        const normalized = normalizePasteboard([
          ...normalizePasteboard(prev),
          ...floats,
        ]);
        pasteboardRef.current = normalized;
        if (floats.length) {
          setSelectedPasteboardId(floats[floats.length - 1].id);
        }
        return normalized;
      });
      clearMarkSelection();
      setOkMsg(
        floats.length === 1
          ? "Marca sacada al fondo del tablero"
          : `${floats.length} marcas sacadas al fondo`,
      );
      return true;
    },
    [pageIdx, notePagesHistory, clearMarkSelection],
  );

  /** Soltar marca fuera de la mesa (modo tablero) → pasteboard. */
  const handleMarkDragOffBoard = useCallback(
    ({ clientX, clientY, markId, group, force }) => {
      if (workspaceModeRef.current !== WORKSPACE_MODE_BOARD) return false;
      const target = resolveBoardPointerTarget(clientX, clientY);
      if (target?.kind === "page" && target.pageIndex !== pageIdx) {
        return false;
      }
      if (target?.kind === "page" && target.pageIndex === pageIdx && !force) {
        return false;
      }
      const ids = group
        ? [...selectedMarkIdsRef.current].map(String)
        : [String(markId)];
      const boardPt =
        target?.boardPt ||
        lastBoardPointerRef.current ||
        defaultPasteboardPoint();
      return Boolean(moveMarksToPasteboard(ids, boardPt));
    },
    [
      resolveBoardPointerTarget,
      moveMarksToPasteboard,
      defaultPasteboardPoint,
      pageIdx,
    ],
  );

  /** Soltar casilla fuera de la mesa (modo tablero) → otra mesa o pasteboard. */
  const handleSlotDragOffBoard = useCallback(
    ({ clientX, clientY, slotId, group, force }) => {
      if (workspaceModeRef.current !== WORKSPACE_MODE_BOARD) return false;
      const target = resolveBoardPointerTarget(clientX, clientY);
      const ids = group
        ? [...selectedSlotIdsRef.current].map(String)
        : [String(slotId)];
      // Soltó sobre otra mesa → reparent directo (sin pasar por el fondo)
      if (target?.kind === "page" && target.pageIndex !== pageIdx) {
        return Boolean(
          moveSlotsToPage(ids, target.pageIndex, target.placeAt || null),
        );
      }
      // Dentro de esta mesa y sin force → no sacar
      if (target?.kind === "page" && target.pageIndex === pageIdx && !force) {
        return false;
      }
      // Gris del tablero u outside forzado → float real (clicable, mismo recorte)
      const boardPt =
        target?.boardPt ||
        lastBoardPointerRef.current ||
        defaultPasteboardPoint();
      return Boolean(moveSlotsToPasteboard(ids, boardPt));
    },
    [
      resolveBoardPointerTarget,
      moveSlotsToPage,
      moveSlotsToPasteboard,
      defaultPasteboardPoint,
      pageIdx,
    ],
  );

  /** VC Object › Clipping Mask › Make: foto + forma (o infiere la que falta). */
  const handleMakeClippingMask = useCallback(() => {
    const slotIds = [...selectedSlotIdsRef.current];
    const markIds = [...selectedMarkIdsRef.current];
    const page = pagesRef.current[pageIdx];
    const pair = resolveClippingMaskTargets(page, { slotIds, markIds });
    if (!pair) {
      setOkMsg(
        "Máscara: selecciona la forma Y la foto (Shift+clic) · o forma encima de la foto y Ctrl+7",
      );
      return;
    }
    // Antes de aplicar: marcar foto + forma para que se vea el par (no solo el cuadro de arriba).
    selectedSlotIdsRef.current = new Set([pair.slotId]);
    setSelectedSlotIds(new Set([pair.slotId]));
    setActiveSlotId(pair.slotId);
    const markSet = new Set([String(pair.markId)]);
    selectedMarkIdsRef.current = markSet;
    setSelectedMarkIds(markSet);
    setSelectedMarkId(pair.markId);

    const next = makeClippingMask(page, pair);
    if (!next) {
      setOkMsg("No se pudo crear la máscara (hace falta foto + forma válida)");
      return;
    }
    notePagesHistory();
    setPages((prev) =>
      prev.map((p, i) =>
        i === pageIdx
          ? {
              ...normalizeFreePage(p),
              slots: next.slots,
              marks: next.marks,
            }
          : p,
      ),
    );
    selectedMarkIdsRef.current = new Set();
    setSelectedMarkIds(new Set());
    setSelectedMarkId(null);
    setSelectedSlotIds(new Set([pair.slotId]));
    setActiveSlotId(pair.slotId);
    setOkMsg("Máscara aplicada · Ctrl+Alt+7 libera");
  }, [pageIdx, notePagesHistory]);

  const handleReleaseClippingMask = useCallback(() => {
    const slotIds = [...selectedSlotIdsRef.current];
    const withClip = slotIds.filter((id) =>
      (pagesRef.current[pageIdx]?.slots || []).some(
        (s) => String(s.id) === String(id) && s.clip,
      ),
    );
    if (!withClip.length) {
      setOkMsg("Release: selecciona una foto con máscara de recorte");
      return;
    }
    notePagesHistory();
    setPages((prev) =>
      prev.map((p, i) => {
        if (i !== pageIdx) return p;
        let page = normalizeFreePage(p);
        for (const id of withClip) {
          const next = releaseClippingMask(page, id);
          if (next) page = { ...page, slots: next.slots, marks: next.marks };
        }
        return page;
      }),
    );
    setOkMsg("Máscara liberada · la forma vuelve a ser editable");
  }, [pageIdx, notePagesHistory]);

  const openContextMenu = useCallback((e, meta = {}) => {
    e.preventDefault?.();
    e.stopPropagation?.();
    setCtxMenu({
      x: e.clientX,
      y: e.clientY,
      ...meta,
    });
  }, []);

  const closeContextMenu = useCallback(() => setCtxMenu(null), []);

  useEffect(() => {
    const onKey = (e) => {
      const tag = String(e.target?.tagName || "").toLowerCase();
      if (
        tag === "input" ||
        tag === "textarea" ||
        e.target?.isContentEditable
      ) {
        return;
      }
      if (e.key === "Escape") {
        if (textDraft) {
          textDraftRef.current = null;
          setTextDraft(null);
          return;
        }
        if (handMode || zoomMode || spaceHeld) {
          setHandMode(false);
          setZoomMode(false);
          setSpaceHeld(false);
          spaceHeldRef.current = false;
          handKeyHeldRef.current = false;
          zoomKeyHeldRef.current = false;
          setPanInsideMode(false);
          return;
        }
        if (freeTransformMode) {
          setFreeTransformMode(false);
          return;
        }
        if (draftMark) {
          setDraftMark(null);
          return;
        }
        if (selectedMarkId || selectedMarkIdsRef.current.size) {
          clearMarkSelection();
          return;
        }
        if (panInsideMode) {
          setPanInsideMode(false);
          return;
        }
        if (eyedropperMode) {
          setEyedropperMode(false);
          return;
        }
        if (multiSelectMode) {
          setMultiSelectMode(false);
          return;
        }
        if (markTool) {
          setMarkTool(null);
          return;
        }
        clearSlotSelection();
        return;
      }
      // Espacio = mano temporal (Illustrator). No tipéar en inputs (ya filtrado arriba).
      if (e.code === "Space" || e.key === " ") {
        if (e.repeat) {
          e.preventDefault();
          return;
        }
        e.preventDefault();
        spaceHeldRef.current = true;
        setSpaceHeld(true);
        setZoomMode(false);
        setPanInsideMode(false);
        return;
      }
      // Atajos VectorCraft (sin Ctrl): V select, H hand, T type, N pencil, M rect, L ellipse…
      if (!e.ctrlKey && !e.metaKey && !e.altKey) {
        const k = String(e.key || "");
        const lower = k.toLowerCase();
        if (lower === "v") {
          e.preventDefault();
          handKeyHeldRef.current = false;
          zoomKeyHeldRef.current = false;
          setMarkTool(null);
          setHandMode(false);
          setZoomMode(false);
          setPanInsideMode(false);
          setEyedropperMode(false);
          setMultiSelectMode(false);
          setFreeTransformMode(false);
          setOkMsg(
            "Selección (V) · arrastra marco vacío = varias · Shift+clic = sumar · Alt+arrastrar foto = pan dentro",
          );
          return;
        }
        // VC: E = Free Transform
        if (lower === "e" && !e.shiftKey) {
          e.preventDefault();
          setMarkTool(null);
          setHandMode(false);
          setZoomMode(false);
          setPanInsideMode(false);
          setEyedropperMode(false);
          setFreeTransformMode(true);
          setMultiSelectMode(true);
          setOkMsg(
            selectedSlotIdsRef.current.size || selectedMarkIdsRef.current.size
              ? "Free Transform: esquinas=escala · asa=rotar · Alt+lado=shear · Shift=proporcional"
              : "Free Transform: selecciona foto(s) o marca(s) y usa el marco",
          );
          return;
        }
        if (lower === "h") {
          e.preventDefault();
          if (e.repeat) return;
          // H = mano temporal (como Espacio): al soltar deja de panear.
          handKeyHeldRef.current = true;
          zoomKeyHeldRef.current = false;
          setHandMode(true);
          setZoomMode(false);
          setMarkTool(null);
          setPanInsideMode(false);
          setEyedropperMode(false);
          setFreeTransformMode(false);
          setOkMsg(
            "Mano (H) · mantén pulsada y arrastra el tablero · suelta = selección",
          );
          return;
        }
        if (lower === "z") {
          e.preventDefault();
          if (e.repeat) return;
          // Z sticky (como barra): un toque queda · V/Esc sale.
          // Ctrl+clic / Alt+clic = zoom −
          zoomKeyHeldRef.current = false;
          handKeyHeldRef.current = false;
          setZoomMode(true);
          setHandMode(false);
          setMarkTool(null);
          setPanInsideMode(false);
          setEyedropperMode(false);
          setFreeTransformMode(false);
          setOkMsg(
            "Zoom (Z) · sticky · clic = + · Ctrl o Alt+clic = − · V/Esc = selección",
          );
          return;
        }
        if (lower === "t") {
          e.preventDefault();
          setMarkTool("text");
          setHandMode(false);
          setZoomMode(false);
          setEyedropperMode(false);
          setVcRightPanel("character");
          return;
        }
        if (lower === "n") {
          e.preventDefault();
          // VC: N = Pencil · Shift+N = Shaper
          setMarkTool(e.shiftKey ? "shaper" : "pen");
          setHandMode(false);
          setZoomMode(false);
          setEyedropperMode(false);
          if (e.shiftKey) {
            setOkMsg(
              "Shaper: dibuja rectángulo, elipse, triángulo… · zig-zag borra",
            );
          }
          return;
        }
        if (lower === "m") {
          e.preventDefault();
          if (e.shiftKey) {
            // VC Shift+M = Shape Builder / Pathfinder Unite
            const ids = [...selectedMarkIdsRef.current];
            if (ids.length < 2) {
              setMarkTool(null);
              setMultiSelectMode(true);
              setOkMsg("Pathfinder · Unir: selecciona 2+ formas y Shift+M");
              return;
            }
            notePagesHistory();
            let createdIds = [];
            let applied = false;
            updateCurrentPageMarks(
              (marks) => {
                const next = pathfinderMarks(marks, ids, "unite");
                if (!next) return marks;
                applied = true;
                const drop = new Set(ids.map(String));
                const restIds = new Set(
                  marks
                    .filter((m) => !drop.has(String(m.id)))
                    .map((m) => String(m.id)),
                );
                createdIds = next
                  .filter((m) => !restIds.has(String(m.id)))
                  .map((m) => String(m.id));
                return next;
              },
              { history: false },
            );
            if (!applied || !createdIds.length) {
              setOkMsg("Pathfinder · Unir: sin resultado (¿se solapan?)");
              return;
            }
            const set = new Set(createdIds);
            selectedMarkIdsRef.current = set;
            setSelectedMarkIds(set);
            setSelectedMarkId(createdIds[0]);
            setOkMsg("Pathfinder · Unir");
            return;
          }
          setMarkTool("rect");
          setHandMode(false);
          setZoomMode(false);
          setEyedropperMode(false);
          return;
        }
        if (lower === "l") {
          e.preventDefault();
          // VC: L = Ellipse · Shift+L = Live Paint Selection
          if (e.shiftKey) {
            setMarkTool("livePaintSelection");
            setHandMode(false);
            setZoomMode(false);
            setEyedropperMode(false);
            setOkMsg(
              "Live Paint Selection (Shift+L): clic selecciona cara · Shift = añadir",
            );
            return;
          }
          setMarkTool("ellipse");
          setHandMode(false);
          setZoomMode(false);
          setEyedropperMode(false);
          return;
        }
        if (lower === "k") {
          e.preventDefault();
          setMarkTool("livePaintBucket");
          setHandMode(false);
          setZoomMode(false);
          setEyedropperMode(false);
          setVcRightPanel("appearance");
          setOkMsg(
            "Live Paint (K): clic en letras = texto · en la caja = barra",
          );
          return;
        }
        if (lower === "b") {
          e.preventDefault();
          if (e.shiftKey) {
            setMarkTool("blob");
            setMarkStroke((s) => Math.max(Number(s) || 2, 10));
          } else {
            setMarkTool("brush");
            setMarkStroke((s) => Math.max(Number(s) || 2, 4.5));
          }
          setHandMode(false);
          setZoomMode(false);
          setEyedropperMode(false);
          return;
        }
        if (lower === "r") {
          e.preventDefault();
          if (e.shiftKey) {
            handleRotateSelection(90);
            return;
          }
          setMarkTool("rotateDrag");
          setHandMode(false);
          setZoomMode(false);
          setEyedropperMode(false);
          setFreeTransformMode(false);
          setOkMsg(
            "Rotar: arrastra alrededor del centro · Shift = 45° · Shift+R = +90°",
          );
          return;
        }
        if (lower === "s") {
          e.preventDefault();
          if (e.shiftKey) {
            handleScaleSelection(1.1);
            return;
          }
          setMarkTool("scaleDrag");
          setHandMode(false);
          setZoomMode(false);
          setEyedropperMode(false);
          setFreeTransformMode(false);
          setOkMsg(
            "Escalar: arrastra desde el centro · Shift = pasos 0.1 · Shift+S = +10%",
          );
          return;
        }
        if (lower === "o") {
          e.preventDefault();
          handleReflectSelection();
          return;
        }
        if (lower === "w") {
          e.preventDefault();
          if (e.shiftKey) {
            setMarkTool("width");
            setHandMode(false);
            setZoomMode(false);
            setEyedropperMode(false);
            setOkMsg("Width: arrastra sobre una forma (Alt = más fino)");
          } else {
            handleBlendMarks();
          }
          return;
        }
        if (lower === "i") {
          e.preventDefault();
          setEyedropperMode(true);
          setMarkTool(null);
          setHandMode(false);
          setZoomMode(false);
          return;
        }
        if (lower === "g") {
          e.preventDefault();
          setEyedropperMode(false);
          setMarkTool(null);
          setHandMode(false);
          setZoomMode(false);
          setVcRightPanel("gradient");
          setOkMsg("Gradient · stops en el dock derecho");
          return;
        }
        if (lower === "p") {
          e.preventDefault();
          setMarkTool("vectorPen");
          setHandMode(false);
          setZoomMode(false);
          setEyedropperMode(false);
          setOkMsg(
            "Pluma (P): clic = puntos · cerca del 1º cierra · doble clic/Enter fija · extremo de path seleccionado = continuar",
          );
          return;
        }
        if (lower === "a") {
          e.preventDefault();
          setMarkTool("directSelect");
          setHandMode(false);
          setZoomMode(false);
          setEyedropperMode(false);
          setPanInsideMode(false);
          setFreeTransformMode(false);
          // Sin banner: A = vectores (anclas). Fotos = usar V.
          return;
        }
        if (e.key === "+" || e.key === "=") {
          e.preventDefault();
          setMarkTool("addAnchor");
          setHandMode(false);
          setZoomMode(false);
          setEyedropperMode(false);
          setOkMsg("Añadir ancla (+): clic en un segmento del path");
          return;
        }
        if (e.key === "-" || e.key === "_") {
          e.preventDefault();
          setMarkTool("deleteAnchor");
          setHandMode(false);
          setZoomMode(false);
          setEyedropperMode(false);
          setOkMsg("Eliminar ancla (−): clic en un vértice");
          return;
        }
        if (lower === "c" && e.shiftKey) {
          e.preventDefault();
          setMarkTool("anchorPoint");
          setHandMode(false);
          setZoomMode(false);
          setEyedropperMode(false);
          setOkMsg(
            "Ancla (Shift+C): arrastra vértice · Alt/Option+clic borra · clic en segmento inserta",
          );
          return;
        }
        if (lower === "c" && !e.shiftKey) {
          e.preventDefault();
          setMarkTool("scissors");
          setHandMode(false);
          setZoomMode(false);
          setEyedropperMode(false);
          return;
        }
        if (lower === "q") {
          e.preventDefault();
          setMarkTool("lasso");
          setHandMode(false);
          setZoomMode(false);
          setEyedropperMode(false);
          return;
        }
        if (lower === "y") {
          e.preventDefault();
          setMarkTool("magicWand");
          setHandMode(false);
          setZoomMode(false);
          setEyedropperMode(false);
          return;
        }
        if (
          e.key === "Enter" &&
          (markTool === "vectorPen" || markTool === "curvature")
        ) {
          const draft = draftMarkRef.current;
          if (draft?.type === "path" && (draft.points || []).length >= 2) {
            e.preventDefault();
            handleCommitDraftMark();
          }
          return;
        }
      }
      const mod = e.ctrlKey || e.metaKey;
      if (mod && String(e.key || "").toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) applyPagesRedo();
        else applyPagesUndo();
        return;
      }
      if (mod && String(e.key || "").toLowerCase() === "y") {
        e.preventDefault();
        applyPagesRedo();
        return;
      }
      if (mod && String(e.key || "").toLowerCase() === "c") {
        if (copyMoodboardSelection()) e.preventDefault();
        return;
      }
      if (mod && String(e.key || "").toLowerCase() === "x") {
        if (cutMoodboardSelection()) e.preventDefault();
        return;
      }
      if (mod && String(e.key || "").toLowerCase() === "d") {
        e.preventDefault();
        if (copyMoodboardSelection()) pasteMoodboardSelection();
        return;
      }
      // VC / AI: Ctrl+7 Make Clipping Mask · Ctrl+Alt+7 Release
      if (mod && String(e.key || "") === "7") {
        e.preventDefault();
        if (e.altKey) handleReleaseClippingMask();
        else handleMakeClippingMask();
        return;
      }
      // Ctrl+G Agrupar · Ctrl+Shift+G Desagrupar
      if (mod && String(e.key || "").toLowerCase() === "g") {
        e.preventDefault();
        if (e.shiftKey) handleUngroupMarks();
        else handleGroupMarks();
        return;
      }
      // Ctrl+Shift+Alt+V: pegar en todas las páginas
      if (
        mod &&
        e.shiftKey &&
        e.altKey &&
        String(e.key || "").toLowerCase() === "v"
      ) {
        e.preventDefault();
        pasteMoodboardSelectionOnAllPages();
        return;
      }
      // Ctrl+Shift+B: sacar selección de la mesa → fondo (pasteboard), estilo Illustrator
      if (mod && e.shiftKey && String(e.key || "").toLowerCase() === "b") {
        e.preventDefault();
        if (selectedSlotIdsRef.current.size) moveSelectedSlotsToPasteboard();
        if (selectedMarkIdsRef.current.size) moveMarksToPasteboard(null, null);
        return;
      }
      // Ctrl+Shift+O: Type › Create Outlines (texto → path)
      if (mod && e.shiftKey && String(e.key || "").toLowerCase() === "o") {
        e.preventDefault();
        handleCreateOutlines();
        return;
      }
      // Shift+O: herramienta Mesa de trabajo (mover / reordenar artboards)
      if (
        !mod &&
        e.shiftKey &&
        String(e.key || "").toLowerCase() === "o" &&
        !e.altKey
      ) {
        e.preventDefault();
        activateArtboardTool();
        return;
      }
      // Ctrl+Alt+Backspace: borrar marcas fuera de la mesa (basura del offset)
      if (mod && e.altKey && (e.key === "Backspace" || e.key === "Delete")) {
        e.preventDefault();
        const page = pagesRef.current[pageIdx];
        const outsideIds = (page?.marks || [])
          .filter((m) => markIsMostlyOutsideArtboard(m))
          .map((m) => String(m.id));
        if (outsideIds.length) {
          const drop = new Set(outsideIds);
          notePagesHistory();
          updateCurrentPageMarks(
            (marks) => marks.filter((m) => !drop.has(String(m.id))),
            { history: false, allowOutside: true },
          );
          clearMarkSelection();
        }
        return;
      }
      // Ctrl+V: lo gestiona el listener 'paste' (SO images + selección interna).
      if (e.key === "Delete" || e.key === "Backspace") {
        if (selectedPasteboardId) {
          e.preventDefault();
          setPasteboard((prev) => {
            const normalized = prev.filter(
              (p) => String(p.id) !== String(selectedPasteboardId),
            );
            pasteboardRef.current = normalized;
            return normalized;
          });
          setSelectedPasteboardId(null);
          return;
        }
        if (
          (selectedMarkIdsRef.current.size || selectedMarkId) &&
          (!markTool || markTool === "select" || markTool === "directSelect")
        ) {
          e.preventDefault();
          const ids = selectedMarkIdsRef.current.size
            ? selectedMarkIdsRef.current
            : new Set([String(selectedMarkId)]);
          updateCurrentPageMarks((marks) =>
            marks.filter((m) => !ids.has(String(m.id))),
          );
          clearMarkSelection();
          return;
        }
        if (selectedSlotIdsRef.current.size > 0 && !markTool) {
          e.preventDefault();
          // Página puede quedar sin casillas (ficha / moodboard vacío)
          const ids = [...selectedSlotIdsRef.current];
          setSlotRemoveId(ids[0]);
        }
      }
    };
    const onKeyUp = (ev) => {
      if (ev.code === "Space" || ev.key === " ") {
        spaceHeldRef.current = false;
        setSpaceHeld(false);
      }
      const lower = String(ev.key || "").toLowerCase();
      if (lower === "h" && handKeyHeldRef.current) {
        handKeyHeldRef.current = false;
        setHandMode(false);
      }
      // Z ya no es temporal (sticky); solo H/Espacio se sueltan al keyup.
    };
    const onWinBlur = () => {
      spaceHeldRef.current = false;
      setSpaceHeld(false);
      if (handKeyHeldRef.current) {
        handKeyHeldRef.current = false;
        setHandMode(false);
      }
      zoomKeyHeldRef.current = false;
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onWinBlur);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onWinBlur);
    };
  }, [
    clearSlotSelection,
    clearMarkSelection,
    pageIdx,
    markTool,
    draftMark,
    textDraft,
    selectedMarkId,
    panInsideMode,
    handMode,
    zoomMode,
    spaceHeld,
    multiSelectMode,
    eyedropperMode,
    freeTransformMode,
    handleRotateSelection,
    handleScaleSelection,
    handleReflectSelection,
    handleCommitDraftMark,
    applyPagesUndo,
    applyPagesRedo,
    copyMoodboardSelection,
    cutMoodboardSelection,
    pasteMoodboardSelection,
    pasteMoodboardSelectionOnAllPages,
    moveSelectedSlotsToPasteboard,
    moveMarksToPasteboard,
    selectedPasteboardId,
    handleMakeClippingMask,
    handleReleaseClippingMask,
    handleGroupMarks,
    handleUngroupMarks,
    handleCreateOutlines,
    activateArtboardTool,
    notePagesHistory,
  ]);

  useEffect(() => {
    metaRef.current = { titulo, categoryLabel, studioLine, chromeStyle };
  }, [titulo, categoryLabel, studioLine, chromeStyle]);

  useEffect(() => {
    saveStateRef.current = saveState;
  }, [saveState]);

  const handleStaleConflict = useCallback(async () => {
    setStaleConflict(true);
    setSaveState("error");
    setError(t("skillAgent.moodboard.staleConflictReloadMsg"));
    try {
      const data = await (
        boardApiRef.current?.getById || getSkillAgentMoodboardById
      )(moodboardId);
      applyBoard(data, { resetHistory: true });
      setSaveState("saved");
      setStaleConflict(false);
      setError("");
      setOkMsg(t("skillAgent.moodboard.ok.reloadedNewer"));
    } catch {
      /* keep error */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moodboardId, t]);

  const persistPages = useCallback(async () => {
    if (!moodboardId || exporting || skipAutosaveRef.current) return false;
    const run = async () => {
      const payload = assignDefaultBoards(
        pagesRef.current.map((p) =>
          normalizeFreePage(p, { allowOutside: true }),
        ),
      );
      const pbPayload = normalizePasteboard(pasteboardRef.current);
      const sentFingerprint = JSON.stringify({ pages: payload, pb: pbPayload });
      const pendingLocal = buildPendingPagesPayload({
        pages: payload,
        pasteboard: pbPayload,
        expectedUpdatedAt: expectedUpdatedAtRef.current,
      });
      const queueLocal = async () => {
        await queueOfflinePages(moodboardId, pendingLocal);
        setOfflinePending(true);
        setStaleConflict(false);
        setError("");
        setSaveState("saved");
        return true;
      };
      if (
        shouldQueueOfflineSave({
          online: isOnlineRef.current,
          offlineEnabled: offlineEnabledRef.current,
        })
      ) {
        setSaveState("saving");
        try {
          return await queueLocal();
        } catch {
          setSaveState("error");
          setError(t("skillAgent.moodboard.error.saveLayout"));
          return false;
        }
      }
      setSaveState("saving");
      // Evita que el eco del socket de ESTE guardado dispare el banner.
      ignoreOwnSocketUntilRef.current = Date.now() + 4000;
      try {
        const data = await (
          boardApiRef.current?.savePages || saveSkillAgentMoodboardPages
        )(moodboardId, payload, {
          expectedUpdatedAt: expectedUpdatedAtRef.current,
          pasteboard: pbPayload,
        });
        // Misma tubería que payload: sin assignDefaultBoards el local sin board
        // divergía siempre tras placeAssetOnPage (Guardando infinito).
        const localFingerprint = JSON.stringify({
          pages: assignDefaultBoards(
            pagesRef.current.map((p) =>
              normalizeFreePage(p, { allowOutside: true }),
            ),
          ),
          pb: normalizePasteboard(pasteboardRef.current),
        });
        const diverged = localFingerprint !== sentFingerprint;
        applyBoard(data, { keepLocalPages: diverged });
        setStaleConflict(false);
        setError("");
        setSaveState(diverged ? "unsaved" : "saved");
        ignoreOwnSocketUntilRef.current = Date.now() + 2500;
        if (offlineEnabledRef.current) {
          try {
            await putOfflineBoardSnapshot(moodboardId, data, {
              offlineEnabled: true,
            });
            await clearOfflinePending(moodboardId, {
              pages: true,
              meta: false,
              expectedUpdatedAt: data?.updatedAt,
            });
            setOfflinePending(await hasOfflinePending(moodboardId));
          } catch {
            /* ignore */
          }
        }
        if (diverged) {
          // Había movimientos durante el guardado: encolar el layout actual
          // (después del skipAutosave corto de applyBoard).
          if (pagesTimerRef.current) clearTimeout(pagesTimerRef.current);
          pagesTimerRef.current = setTimeout(() => {
            persistPages();
          }, 120);
        }
        return true;
      } catch (err) {
        if (err?.response?.status === 409) {
          await handleStaleConflict();
          return false;
        }
        if (
          shouldQueueOfflineSave({
            online: isOnlineRef.current,
            offlineEnabled: offlineEnabledRef.current,
            networkError: true,
          })
        ) {
          try {
            return await queueLocal();
          } catch {
            /* fall through */
          }
        }
        setSaveState("error");
        setError(
          err?.response?.data?.error ||
            t("skillAgent.moodboard.error.saveLayout"),
        );
        return false;
      }
    };
    const p = saveChainRef.current.then(run, run);
    saveChainRef.current = p.then(
      () => undefined,
      () => undefined,
    );
    return p;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- applyBoard es local estable en práctica
  }, [moodboardId, exporting, handleStaleConflict, t]);

  const persistMeta = useCallback(async () => {
    if (!moodboardId || exporting || skipAutosaveRef.current) return false;
    const run = async () => {
      const m = metaRef.current;
      const pendingLocal = buildPendingMetaPayload({
        titulo: m.titulo,
        categoryLabel: m.categoryLabel,
        studioLine: m.studioLine,
        chromeStyle: m.chromeStyle,
        expectedUpdatedAt: expectedUpdatedAtRef.current,
      });
      const queueLocal = async () => {
        await queueOfflineMeta(moodboardId, pendingLocal);
        setOfflinePending(true);
        setStaleConflict(false);
        setError("");
        setSaveState("saved");
        return true;
      };
      if (
        shouldQueueOfflineSave({
          online: isOnlineRef.current,
          offlineEnabled: offlineEnabledRef.current,
        })
      ) {
        setSaveState("saving");
        try {
          return await queueLocal();
        } catch {
          setSaveState("error");
          setError(t("skillAgent.moodboard.error.saveData"));
          return false;
        }
      }
      setSaveState("saving");
      ignoreOwnSocketUntilRef.current = Date.now() + 4000;
      try {
        const data = await (
          boardApiRef.current?.update || updateSkillAgentMoodboard
        )(moodboardId, {
          Titulo: m.titulo,
          CategoryLabel: m.categoryLabel,
          StudioLine: m.studioLine,
          ChromeStyleJson: m.chromeStyle,
          ExpectedUpdatedAt: expectedUpdatedAtRef.current,
        });
        // El meta no debe reaplicar PagesJson: pisaría un arrastre en curso.
        applyBoard(data, { keepLocalPages: true });
        setStaleConflict(false);
        setError("");
        setSaveState("saved");
        ignoreOwnSocketUntilRef.current = Date.now() + 2500;
        if (offlineEnabledRef.current) {
          try {
            await putOfflineBoardSnapshot(moodboardId, data, {
              offlineEnabled: true,
            });
            await clearOfflinePending(moodboardId, {
              pages: false,
              meta: true,
              expectedUpdatedAt: data?.updatedAt,
            });
            setOfflinePending(await hasOfflinePending(moodboardId));
          } catch {
            /* ignore */
          }
        }
        const nextTitulo = String(m.titulo || "").trim();
        if (nextTitulo && nextTitulo !== tituloSavedRef.current) {
          tituloSavedRef.current = nextTitulo;
          onChangedRef.current?.();
        }
        return true;
      } catch (err) {
        if (err?.response?.status === 409) {
          await handleStaleConflict();
          return false;
        }
        if (
          shouldQueueOfflineSave({
            online: isOnlineRef.current,
            offlineEnabled: offlineEnabledRef.current,
            networkError: true,
          })
        ) {
          try {
            return await queueLocal();
          } catch {
            /* fall through */
          }
        }
        setSaveState("error");
        setError(
          err?.response?.data?.error ||
            t("skillAgent.moodboard.error.saveData"),
        );
        return false;
      }
    };
    const p = saveChainRef.current.then(run, run);
    saveChainRef.current = p.then(
      () => undefined,
      () => undefined,
    );
    return p;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moodboardId, exporting, handleStaleConflict, t]);

  const flushAutosave = useCallback(async () => {
    if (pagesTimerRef.current) {
      clearTimeout(pagesTimerRef.current);
      pagesTimerRef.current = null;
    }
    if (metaTimerRef.current) {
      clearTimeout(metaTimerRef.current);
      metaTimerRef.current = null;
    }
    const okPages = await persistPages();
    const okMeta = await persistMeta();
    return okPages && okMeta;
  }, [persistPages, persistMeta]);

  const syncOfflineQueue = useCallback(async () => {
    if (!moodboardId || !offlineEnabledRef.current || !isOnlineRef.current) {
      return;
    }
    if (!(await hasOfflinePending(moodboardId))) {
      setOfflinePending(false);
      return;
    }
    setSaveState("saving");
    try {
      const result = await flushOfflineBoard(moodboardId, {
        savePages:
          boardApiRef.current?.savePages || saveSkillAgentMoodboardPages,
        updateMeta: boardApiRef.current?.update || updateSkillAgentMoodboard,
        onConflict: async () => {
          setOkMsg("");
          setError(t("skillAgent.moodboard.offline.syncConflict"));
          await handleStaleConflict();
        },
      });
      if (result?.conflict) {
        setSaveState("error");
        return;
      }
      if (result?.flushed) {
        const data = await (
          boardApiRef.current?.getById || getSkillAgentMoodboardById
        )(moodboardId);
        applyBoard(data, { resetHistory: false, keepLocalPages: false });
        setOkMsg(t("skillAgent.moodboard.offline.syncOk"));
      }
      setOfflinePending(await hasOfflinePending(moodboardId));
      setConnectivityNotice("");
      setSaveState("saved");
      setError("");
    } catch (err) {
      setSaveState("error");
      setError(
        err?.response?.data?.error ||
          t("skillAgent.moodboard.error.saveLayout"),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moodboardId, handleStaleConflict, t]);

  useEffect(() => {
    if (!isOnline) return undefined;
    if (!offlinePending) {
      if (connectivityNotice === "syncing") {
        setConnectivityNotice("");
      }
      return undefined;
    }
    if (!offlineEnabled) return undefined;
    const tmr = setTimeout(() => {
      syncOfflineQueue();
    }, 400);
    return () => clearTimeout(tmr);
  }, [
    isOnline,
    offlineEnabled,
    offlinePending,
    connectivityNotice,
    syncOfflineQueue,
  ]);

  // Presencia + soft refresh si otro guarda
  useEffect(() => {
    if (boardApiRef.current) return undefined;
    if (!moodboardId) return undefined;
    const id = Number(moodboardId);
    if (!Number.isFinite(id) || id <= 0) return undefined;
    socket.emit("join-moodboard", {
      moodboardId: id,
      userId: viewerUserId,
      username: viewerName || t("skillAgent.moodboard.peerFallbackName"),
    });
    const onPresence = (payload) => {
      if (Number(payload?.moodboardId) !== id) return;
      const viewers = Array.isArray(payload?.viewers) ? payload.viewers : [];
      setPeerViewers(
        viewers.filter((v) => {
          if (viewerUserId == null) return true;
          return Number(v.userId) !== Number(viewerUserId);
        }),
      );
    };
    const onUpdated = async (payload) => {
      if (Number(payload?.moodboardId) !== id) return;
      // Nuestro propio autosave también emite: no es conflicto.
      if (
        viewerUserId != null &&
        payload?.userId != null &&
        Number(payload.userId) === Number(viewerUserId)
      ) {
        return;
      }
      if (Date.now() < ignoreOwnSocketUntilRef.current) return;
      if (softRefreshBusyRef.current) return;
      if (
        saveStateRef.current === "unsaved" ||
        saveStateRef.current === "saving"
      ) {
        setStaleConflict(true);
        setError(t("skillAgent.moodboard.staleConflictBanner"));
        return;
      }
      softRefreshBusyRef.current = true;
      try {
        const data = await (
          boardApiRef.current?.getById || getSkillAgentMoodboardById
        )(moodboardId);
        applyBoard(data, { resetHistory: true });
        setStaleConflict(false);
        setError("");
      } catch {
        /* ignore */
      } finally {
        softRefreshBusyRef.current = false;
      }
    };
    socket.on("moodboard-presence", onPresence);
    socket.on("moodboard-updated", onUpdated);
    return () => {
      socket.emit("leave-moodboard", { moodboardId: id });
      socket.off("moodboard-presence", onPresence);
      socket.off("moodboard-updated", onUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moodboardId, viewerName, viewerUserId, t]);

  // Autosave layout (debounce)
  useEffect(() => {
    if (loading || skipAutosaveRef.current || exporting) return undefined;
    setSaveState((s) => (s === "saved" ? "unsaved" : s));
    if (boardApiRef.current) reportCloseGuard(false);
    if (pagesTimerRef.current) clearTimeout(pagesTimerRef.current);
    pagesTimerRef.current = setTimeout(() => {
      persistPages();
    }, 750);
    return () => {
      if (pagesTimerRef.current) clearTimeout(pagesTimerRef.current);
    };
  }, [pages, pasteboard, loading, exporting, persistPages]);

  // Autosave título / barras PDF
  useEffect(() => {
    if (loading || skipAutosaveRef.current || exporting) return undefined;
    setSaveState((s) => (s === "saved" ? "unsaved" : s));
    if (boardApiRef.current) reportCloseGuard(false);
    if (metaTimerRef.current) clearTimeout(metaTimerRef.current);
    metaTimerRef.current = setTimeout(() => {
      persistMeta();
    }, 900);
    return () => {
      if (metaTimerRef.current) clearTimeout(metaTimerRef.current);
    };
  }, [
    titulo,
    categoryLabel,
    studioLine,
    chromeStyle,
    loading,
    exporting,
    persistMeta,
  ]);

  // Aviso si cierran la pestaña con cambios pendientes
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (saveState === "unsaved" || saveState === "saving") {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [saveState]);

  const handleSaveAll = async () => {
    setBusy(true);
    setError("");
    try {
      const ok = await flushAutosave();
      if (ok && boardApiRef.current?.publishTechnical) {
        try {
          const data = await boardApiRef.current.publishTechnical(moodboardId);
          const published = data?.moodboard || data;
          if (published?.PagesJson || published?.assets) applyBoard(published);
          reportCloseGuard(true);
          onChangedRef.current?.();
          setOkMsg(
            boardApiRef.current.publishMessage ||
              t("skillAgent.moodboard.ok.saved"),
          );
        } catch (err) {
          setSaveState("error");
          setError(
            err?.response?.data?.error ||
              t("skillAgent.moodboard.error.saveLayout"),
          );
        }
      } else if (ok) {
        onChangedRef.current?.();
        setOkMsg(t("skillAgent.moodboard.ok.saved"));
      }
    } finally {
      setBusy(false);
    }
  };

  const assignAssetToSlot = (slotId, assetId) => {
    const notesMap = notesByAssetIdFromAssets(assets);
    notePagesHistory();
    setPages((prev) => {
      const next = prev.map((p) => normalizeFreePage(p));
      const page = next[pageIdx] || emptyFreePage();
      if (!next[pageIdx]) next.push(page);
      const slots = (page.slots || []).map((s) =>
        s.id === slotId ? clampSlot({ ...s, assetId: Number(assetId) }) : s,
      );
      const placed = slots.filter((s) => s.id === slotId);
      next[pageIdx] = {
        ...page,
        slots,
        marks: mergeNoteMarksForSlots(page, placed, notesMap),
      };
      return next;
    });
  };

  /** Coloca una foto en la página indicada (hueco vacío o casilla nueva). */
  const placeAssetOnPage = (assetId, targetPageIdx = pageIdx) => {
    const notesMap = notesByAssetIdFromAssets(assets);
    notePagesHistory();
    setPages((prev) => {
      const next = prev.map((p) => normalizeFreePage(p));
      let i = Math.min(
        Math.max(0, Number(targetPageIdx) || 0),
        Math.max(0, next.length - 1),
      );
      if (!next.length) {
        const created = clampSlot({
          id: newSlotId("p"),
          x: 0.2,
          y: 0.2,
          w: 0.36,
          h: 0.46,
          shape: "rect",
          z: 1,
          assetId: Number(assetId),
        });
        // assignDefaultBoards: sin board el autosave diverge (Guardando infinito)
        const pages = assignDefaultBoards([
          {
            ...emptyFreePage(),
            slots: [created],
            marks: mergeNoteMarksForSlots(
              { slots: [created], marks: [] },
              [created],
              notesMap,
            ),
          },
        ]);
        setPageIdx(0);
        setSelectedSlotIds(new Set([created.id]));
        setActiveSlotId(created.id);
        return pages;
      }
      const page = next[i] || emptyFreePage();
      if (!next[i]) next.push(page);
      const empty = (page.slots || []).find((s) => s.assetId == null);
      if (empty) {
        const slots = page.slots.map((s) =>
          s.id === empty.id ? clampSlot({ ...s, assetId: Number(assetId) }) : s,
        );
        const placed = slots.filter((s) => s.id === empty.id);
        // Conservar board/hf: si no, fingerprint sent≠local → bucle Guardando
        next[i] = {
          ...page,
          slots,
          marks: mergeNoteMarksForSlots(page, placed, notesMap),
        };
        setPageIdx(i);
        setSelectedSlotIds(new Set([empty.id]));
        setActiveSlotId(empty.id);
        return next;
      }
      const maxZ = Math.max(
        0,
        ...(page.slots || []).map((s) => Number(s.z) || 0),
      );
      const n = (page.slots || []).length;
      const col = n % 3;
      const row = Math.floor(n / 3);
      const created = clampSlot({
        id: newSlotId("p"),
        x: 0.05 + col * 0.32,
        y: 0.08 + row * 0.3,
        w: 0.28,
        h: 0.26,
        shape: "rect",
        z: maxZ + 1,
        assetId: Number(assetId),
      });
      next[i] = {
        ...page,
        slots: [...(page.slots || []), created],
        marks: mergeNoteMarksForSlots(page, [created], notesMap),
      };
      setPageIdx(i);
      setSelectedSlotIds(new Set([created.id]));
      setActiveSlotId(created.id);
      return next;
    });
  };

  const toggleGallerySelect = (assetId) => {
    const id = Number(assetId);
    if (!Number.isFinite(id)) return;
    setGallerySelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /** Coloca la selección en la página elegida (huecos primero; si faltan, nuevas casillas). */
  const placeSelectedOnPage = (targetPageIdx = galleryPlacePageIdx) => {
    const ids = [...gallerySelected];
    if (!ids.length) return;
    const notesMap = notesByAssetIdFromAssets(assets);
    const destIdx = Math.max(0, Number(targetPageIdx) || 0);
    notePagesHistory();
    setPages((prev) => {
      const next = prev.map((p) => normalizeFreePage(p));
      const i = Math.min(destIdx, Math.max(0, next.length - 1));
      const page = next[i] || emptyFreePage();
      if (!next[i]) next.push(page);
      let slots = [...(page.slots || [])];
      let maxZ = Math.max(0, ...slots.map((s) => Number(s.z) || 0));
      const queue = [...ids];
      const placedSlots = [];
      slots = slots.map((s) => {
        if (s.assetId != null || !queue.length) return s;
        const filled = clampSlot({ ...s, assetId: Number(queue.shift()) });
        placedSlots.push(filled);
        return filled;
      });
      while (queue.length) {
        maxZ += 1;
        const n = slots.length;
        const col = n % 3;
        const row = Math.floor(n / 3);
        const created = clampSlot({
          id: newSlotId("p"),
          x: 0.05 + col * 0.32,
          y: 0.08 + row * 0.3,
          w: 0.28,
          h: 0.26,
          shape: "rect",
          z: maxZ,
          assetId: Number(queue.shift()),
        });
        slots.push(created);
        placedSlots.push(created);
      }
      // Conservar board/hf (placeSelectedOnPage no debe romper autosave)
      next[i] = {
        ...page,
        slots,
        marks: mergeNoteMarksForSlots(page, placedSlots, notesMap),
      };
      return next;
    });
    setPageIdx(Math.min(destIdx, Math.max(0, pagesRef.current.length - 1)));
    setActiveSlotId(null);
    setSelectedSlotIds(new Set());
    const n = gallerySelected.size;
    setGallerySelected(new Set());
    setOkMsg(
      t("skillAgent.moodboard.ok.placedOnPage", {
        n,
        p: destIdx + 1,
      }),
    );
  };

  const handleAddSlot = () => {
    const maxZ = Math.max(
      0,
      ...(currentPage.slots || []).map((s) => Number(s.z) || 0),
    );
    const slot = clampSlot({
      id: newSlotId(),
      x: 0.35,
      y: 0.35,
      w: 0.28,
      h: 0.3,
      shape: "rect",
      z: maxZ + 1,
      assetId: null,
    });
    updateCurrentPageSlots((slots) => [...slots, slot]);
    selectOnlySlot(slot.id);
  };

  const handleAddCircleSlot = () => {
    // Ya no creamos casillas-máscara circulares: foto = rect; círculo = clip explícito.
    handleAddSlot();
  };

  /**
   * Atajo legacy del botón ○/□: aplica/quita clip elipse = bounds de la foto
   * (no es shape del slot; es máscara de recorte real).
   */
  const handleToggleSlotShape = () => {
    if (!selectedSlotIds.size) return;
    notePagesHistory();
    setPages((prev) =>
      prev.map((p, i) => {
        if (i !== pageIdx) return p;
        const base = normalizeFreePage(p);
        const slots = (base.slots || []).map((s) => {
          if (!selectedSlotIds.has(s.id)) return s;
          if (s.clip || s.shape === "circle") {
            return clampSlot({ ...s, clip: null, shape: "rect" });
          }
          if (s.assetId == null) return s;
          return clampSlot({
            ...s,
            shape: "rect",
            clip: {
              type: "ellipse",
              x: 0,
              y: 0,
              w: 1,
              h: 1,
              space: "local",
            },
          });
        });
        return {
          ...base,
          slots,
          marks: (base.marks || []).map((m) => ({ ...m })),
        };
      }),
    );
    setOkMsg(
      "Clip elipse · para máscara libre: forma encima + Make Clipping Mask",
    );
  };

  const handleBringToFront = () => {
    const markIds = [...selectedMarkIdsRef.current];
    if (markIds.length) {
      updateCurrentPageMarks((marks) => bringMarksToFront(marks, markIds));
      return;
    }
    if (!selectedSlotIds.size) return;
    updateCurrentPageSlots((slots) => {
      const selected = slots
        .filter((s) => selectedSlotIds.has(s.id))
        .sort((a, b) => (Number(a.z) || 0) - (Number(b.z) || 0));
      let z = Math.max(0, ...slots.map((s) => Number(s.z) || 0));
      const zMap = new Map();
      for (const s of selected) {
        z += 1;
        zMap.set(s.id, z);
      }
      return slots.map((s) =>
        zMap.has(s.id) ? clampSlot({ ...s, z: zMap.get(s.id) }) : s,
      );
    });
  };

  const handleSendToBack = () => {
    const markIds = [...selectedMarkIdsRef.current];
    if (markIds.length) {
      updateCurrentPageMarks((marks) => sendMarksToBack(marks, markIds));
      return;
    }
    if (!selectedSlotIds.size) return;
    updateCurrentPageSlots((slots) => {
      const selected = slots
        .filter((s) => selectedSlotIds.has(s.id))
        .sort((a, b) => (Number(b.z) || 0) - (Number(a.z) || 0));
      let z = Math.min(...slots.map((s) => Number(s.z) || 0));
      const zMap = new Map();
      for (const s of selected) {
        z = Math.max(0, z - 1);
        zMap.set(s.id, z);
      }
      return slots.map((s) =>
        zMap.has(s.id) ? clampSlot({ ...s, z: zMap.get(s.id) }) : s,
      );
    });
  };

  const ctxMenuItems = useMemo(() => {
    if (!ctxMenu) return [];
    const slotIds = [...selectedSlotIdsRef.current];
    const markIds = [...selectedMarkIdsRef.current];
    const page = pagesRef.current[pageIdx];
    const hasSlots = slotIds.length > 0;
    const hasMarks = markIds.length > 0;
    const hasClip = (page?.slots || []).some(
      (s) => slotIds.includes(String(s.id)) && s.clip,
    );
    return [
      {
        id: "cut",
        label: "Cortar",
        shortcut: "Ctrl+X",
        disabled: !hasSlots && !hasMarks && !selectedPasteboardId,
        onClick: () => cutMoodboardSelection(),
      },
      {
        id: "copy",
        label: "Copiar",
        shortcut: "Ctrl+C",
        disabled: !hasSlots && !hasMarks && !selectedPasteboardId,
        onClick: () => copyMoodboardSelection(),
      },
      {
        id: "paste",
        label: "Pegar",
        shortcut: "Ctrl+V",
        onClick: () => pasteMoodboardSelection(),
      },
      "divider",
      {
        id: "bringFront",
        label: "Traer al frente",
        disabled: !hasSlots && !hasMarks,
        onClick: () => handleBringToFront(),
      },
      {
        id: "sendBack",
        label: "Enviar al fondo",
        disabled: !hasSlots && !hasMarks,
        onClick: () => handleSendToBack(),
      },
      "divider",
      {
        id: "makeClip",
        label: "Crear máscara de recorte",
        shortcut: "Ctrl+7",
        onClick: () => handleMakeClippingMask(),
      },
      {
        id: "releaseClip",
        label: "Liberar máscara de recorte",
        shortcut: "Ctrl+Alt+7",
        disabled: !hasClip,
        onClick: () => handleReleaseClippingMask(),
      },
      "divider",
      {
        id: "groupMarks",
        label: "Agrupar",
        shortcut: "Ctrl+G",
        disabled: markIds.length < 2,
        onClick: () => handleGroupMarks(),
      },
      {
        id: "ungroupMarks",
        label: "Desagrupar",
        shortcut: "Ctrl+Shift+G",
        disabled: !hasMarks,
        onClick: () => handleUngroupMarks(),
      },
      "divider",
      {
        id: "freeTransform",
        label: "Transformación libre",
        shortcut: "E",
        disabled: !hasSlots,
        onClick: () => {
          setMarkTool(null);
          setHandMode(false);
          setZoomMode(false);
          setPanInsideMode(false);
          setFreeTransformMode(true);
          setMultiSelectMode(true);
        },
      },
      {
        id: "panInside",
        label: "Mover contenido de la foto",
        disabled: !hasSlots,
        onClick: () => {
          setHandMode(false);
          setZoomMode(false);
          setMarkTool(null);
          setPanInsideMode(true);
          setOkMsg(
            "Contenido: arrastra dentro del marco · Esc o V para volver a selección",
          );
        },
      },
      {
        id: "toPasteboard",
        label: "Sacar al fondo (pasteboard)",
        shortcut: "Ctrl+Shift+B",
        disabled: !hasSlots && !hasMarks,
        onClick: () => {
          if (hasSlots) moveSelectedSlotsToPasteboard();
          if (hasMarks) moveMarksToPasteboard(null, null);
        },
      },
      {
        id: "purgeOutside",
        label: "Eliminar marcas fuera de la mesa",
        shortcut: "Ctrl+Alt+⌫",
        disabled: !(page?.marks || []).some((m) =>
          markIsMostlyOutsideArtboard(m),
        ),
        onClick: () => {
          const drop = new Set(
            (page?.marks || [])
              .filter((m) => markIsMostlyOutsideArtboard(m))
              .map((m) => String(m.id)),
          );
          if (!drop.size) return;
          notePagesHistory();
          updateCurrentPageMarks(
            (marks) => marks.filter((m) => !drop.has(String(m.id))),
            { history: false, allowOutside: true },
          );
          clearMarkSelection();
        },
      },
      "divider",
      {
        id: "hand",
        label: "Herramienta mano",
        shortcut: "H",
        onClick: () => {
          handKeyHeldRef.current = false;
          zoomKeyHeldRef.current = false;
          setHandMode(true);
          setZoomMode(false);
          setPanInsideMode(false);
          setMarkTool(null);
        },
      },
      {
        id: "zoom",
        label: "Herramienta zoom",
        shortcut: "Z",
        onClick: () => {
          zoomKeyHeldRef.current = false;
          handKeyHeldRef.current = false;
          setZoomMode(true);
          setHandMode(false);
          setPanInsideMode(false);
          setMarkTool(null);
        },
      },
      {
        id: "select",
        label: "Selección",
        shortcut: "V",
        onClick: () => {
          handKeyHeldRef.current = false;
          zoomKeyHeldRef.current = false;
          setMarkTool(null);
          setHandMode(false);
          setZoomMode(false);
          setPanInsideMode(false);
          setFreeTransformMode(false);
        },
      },
    ];
  }, [
    ctxMenu,
    pageIdx,
    selectedSlotIds,
    selectedMarkIds,
    selectedPasteboardId,
    cutMoodboardSelection,
    copyMoodboardSelection,
    pasteMoodboardSelection,
    handleMakeClippingMask,
    handleReleaseClippingMask,
    handleGroupMarks,
    handleUngroupMarks,
    moveSelectedSlotsToPasteboard,
    moveMarksToPasteboard,
    notePagesHistory,
  ]);

  const applyDerivedAssetResponse = (
    data,
    fromAssetId,
    { resetFraming = false } = {},
  ) => {
    if (Array.isArray(data?.assets)) setAssets(data.assets);
    // PagesJson puede venir como array o string (Sequelize)
    const pagesRaw = data?.PagesJson ?? data?.pages;
    let pagesFromServer = null;
    if (Array.isArray(pagesRaw)) {
      pagesFromServer = pagesRaw;
    } else if (typeof pagesRaw === "string" && pagesRaw.trim()) {
      try {
        const parsed = JSON.parse(pagesRaw);
        if (Array.isArray(parsed)) pagesFromServer = parsed;
      } catch {
        /* ignore */
      }
    }
    if (pagesFromServer) {
      notePagesHistory();
      // Si no hay que resetear, reinyecta zoom/pan local por slot id
      // (por si el servidor o un eco de socket devolviera framing por defecto).
      if (!resetFraming && fromAssetId != null) {
        const fromId = Number(fromAssetId);
        setPages((prev) => {
          const framingBySlotId = new Map();
          for (const p of prev) {
            for (const s of normalizeFreePage(p).slots) {
              if (Number(s.assetId) !== fromId) continue;
              framingBySlotId.set(String(s.id), {
                zoom: s.zoom,
                panX: s.panX,
                panY: s.panY,
              });
            }
          }
          return normalizePages(pagesFromServer).map((p) => ({
            ...p,
            slots: p.slots.map((s) => {
              const f = framingBySlotId.get(String(s.id));
              return f ? clampSlot({ ...s, ...f }) : s;
            }),
          }));
        });
      } else {
        setPages(normalizePages(pagesFromServer));
      }
    } else if (data?.asset?.id) {
      notePagesHistory();
      const nextId = Number(data.asset.id);
      const fromId = Number(fromAssetId);
      setPages((prev) =>
        prev.map((p) => ({
          ...normalizeFreePage(p),
          slots: normalizeFreePage(p).slots.map((s) => {
            if (Number(s.assetId) !== fromId) return s;
            const next = { ...s, assetId: nextId };
            // Solo crop (y similares) resetean zoom/pan; quitar fondo/borde los mantienen
            if (resetFraming) {
              next.zoom = 1;
              next.panX = 0.5;
              next.panY = 0.5;
            }
            return clampSlot(next);
          }),
        })),
      );
    }
    if (data?.Titulo != null || data?.id) {
      setBoard((p) => ({ ...p, ...data, assets: undefined }));
    }
  };

  const runRemoveBackground = async () => {
    const assetIds = removeBgAssetIds;
    if (!moodboardId || !assetIds.length || busy) return;
    if (!isOnlineRef.current) {
      setError(t("skillAgent.moodboard.offline.networkBlocked"));
      return;
    }
    setBusy(true);
    setError("");
    setOkMsg("");
    setRemoveBgConfirmOpen(false);
    let done = 0;
    try {
      for (const assetId of assetIds) {
        const data = await (
          boardApiRef.current?.removeBg || removeBgSkillAgentMoodboardAsset
        )(moodboardId, assetId);
        applyDerivedAssetResponse(data, assetId);
        done += 1;
      }
      setOkMsg(
        done > 1
          ? t("skillAgent.moodboard.ok.bgRemovedMany", { n: done })
          : t("skillAgent.moodboard.ok.bgRemovedDetail"),
      );
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          err.message ||
          t("skillAgent.moodboard.error.removeBg"),
      );
      if (done > 0) {
        setOkMsg(t("skillAgent.moodboard.ok.bgRemovedPartial", { n: done }));
      }
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveBackground = () => {
    if (!removeBgAssetIds.length || busy) return;
    if (removeBgHasCutout) {
      setRemoveBgConfirmOpen(true);
      return;
    }
    runRemoveBackground();
  };

  const handleApplyContourStroke = async () => {
    const assetId = activeSlot?.assetId;
    if (!moodboardId || !assetId || busy) return;
    setBusy(true);
    setError("");
    setOkMsg("");
    setStrokeAnchor(null);
    try {
      const data = await (
        boardApiRef.current?.contourStroke ||
        contourStrokeSkillAgentMoodboardAsset
      )(moodboardId, assetId, { width: strokeWidth, color: strokeColor });
      applyDerivedAssetResponse(data, assetId);
      setOkMsg(
        t("skillAgent.moodboard.ok.strokeAppliedContour", {
          n: strokeWidth,
        }),
      );
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          err.message ||
          t("skillAgent.moodboard.error.stroke"),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmCrop = async (rect) => {
    const assetId = activeSlot?.assetId;
    if (!moodboardId || !assetId || busy) return;
    setBusy(true);
    setError("");
    setOkMsg("");
    try {
      const data = await (
        boardApiRef.current?.crop || cropSkillAgentMoodboardAsset
      )(moodboardId, assetId, rect);
      applyDerivedAssetResponse(data, assetId, { resetFraming: true });
      setCropOpen(false);
      setOkMsg(t("skillAgent.moodboard.ok.croppedDetail"));
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          err.message ||
          t("skillAgent.moodboard.error.crop"),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleAlignSlots = (edge) => {
    const to = alignTo || "selection";
    const markIds = [...selectedMarkIdsRef.current];
    const slotIds = [...selectedSlotIdsRef.current];
    const minN = to === "page" ? 1 : 2;
    if (markIds.length >= minN) {
      const keyId =
        to === "key"
          ? String(selectedMarkId || markIds[markIds.length - 1])
          : null;
      updateCurrentPageMarks((marks) =>
        alignMarks(marks, markIds, edge, { to, keyId }),
      );
      return;
    }
    if (slotIds.length < minN) {
      setOkMsg(
        to === "page"
          ? "Selecciona 1+ casilla o marca para alinear a la página"
          : "Selecciona 2+ casillas o marcas (Shift) · Key = último activo",
      );
      return;
    }
    const keyId =
      to === "key" ? String(activeSlotId || slotIds[slotIds.length - 1]) : null;
    updateCurrentPageSlots((slots) =>
      alignSlots(slots, slotIds, edge, { to, keyId }),
    );
  };

  const handleDistributeSlots = (axis) => {
    const to = alignTo || "selection";
    const markIds = [...selectedMarkIdsRef.current];
    const slotIds = [...selectedSlotIdsRef.current];
    const useKeySpacing = to === "key";
    const spacing = useKeySpacing ? Number(alignSpacing) || 0 : null;
    const minN = useKeySpacing || to === "page" ? 2 : 3;
    if (markIds.length >= minN) {
      const keyId = useKeySpacing
        ? String(selectedMarkId || markIds[markIds.length - 1])
        : null;
      updateCurrentPageMarks((marks) =>
        distributeMarks(marks, markIds, axis, {
          to,
          keyId,
          spacing: useKeySpacing ? spacing : null,
        }),
      );
      return;
    }
    if (slotIds.length < minN) {
      setOkMsg(
        useKeySpacing
          ? "Key + spacing: selecciona 2+ casillas o marcas"
          : to === "page"
            ? "Page: selecciona 2+ para distribuir en la mesa"
            : "Selecciona 3+ casillas o marcas para distribuir",
      );
      return;
    }
    const keyId = useKeySpacing
      ? String(activeSlotId || slotIds[slotIds.length - 1])
      : null;
    updateCurrentPageSlots((slots) =>
      distributeSlots(slots, slotIds, axis, {
        to,
        keyId,
        spacing: useKeySpacing ? spacing : null,
      }),
    );
  };

  const handleDuplicatePage = () => {
    const from = pageIdx;
    notePagesHistory();
    setPages((prev) => {
      const next = duplicatePage(prev, from);
      setPageIdx(next.length - 1);
      return next;
    });
    clearSlotSelection();
    clearMarkSelection();
    setOkMsg(`Página ${from + 1} duplicada`);
  };

  const handleReorderPage = (fromIndex, toIndex) => {
    const from = Math.floor(Number(fromIndex));
    const to = Math.floor(Number(toIndex));
    if (!Number.isFinite(from) || !Number.isFinite(to) || from === to) return;
    notePagesHistory();
    setPages((prev) => {
      if (from < 0 || to < 0 || from >= prev.length || to >= prev.length) {
        return prev;
      }
      // Orden panel = PDF; también recoloca en fila para que el tablero coincida
      // (sin tener que pulsar Rearrange All).
      return rearrangeArtboards(reorderPages(prev, from, to), {
        layout: "row",
      });
    });
    setPageIdx((idx) => remapIndexAfterReorder(idx, from, to));
    setExportPageIdxs((prev) => {
      if (!Array.isArray(prev) || !prev.length) return prev;
      return [
        ...new Set(
          prev
            .map((n) => remapIndexAfterReorder(n, from, to))
            .filter((n) => n >= 0),
        ),
      ].sort((a, b) => a - b);
    });
    clearSlotSelection();
    clearMarkSelection();
  };

  const handleRearrangeArtboards = () => {
    if (pages.length < 2) return;
    notePagesHistory();
    setPages((prev) => rearrangeArtboards(prev, { layout: "row" }));
    setOkMsg("Artboards recolocados en fila (orden de impresión sin cambios)");
    requestAnimationFrame(() => {
      const el = boardViewportRef.current;
      if (!el || workspaceModeRef.current !== WORKSPACE_MODE_BOARD) return;
      const cam = fitBoardCamera(
        pagesRef.current,
        el.clientWidth || 800,
        el.clientHeight || 600,
        { pasteboard: pasteboardRef.current },
      );
      setBoardCam({ panX: cam.panX, panY: cam.panY, zoom: cam.zoom });
    });
  };

  const handleGenerateImage = async ({
    mode: modeRaw = "prompt",
    prompt,
    engine,
    place,
    sketchView = "front",
    refAssetIds,
  }) => {
    if (!moodboardId || busy) return;
    if (!isOnlineRef.current) {
      setError(t("skillAgent.moodboard.offline.networkBlocked"));
      return;
    }
    const mode = normalizeBoardAiMode(modeRaw);
    setBusy(true);
    setError("");
    setOkMsg("");
    try {
      // Prompt / Foto / Sketch → mismo /generate del board (refs en disco en BACK).
      // Evita el roundtrip frágil createNuevoDiseno + re-subida de blobs.
      if (mode === "sketch" && !(refAssetIds || []).length) {
        throw new Error(
          "Selecciona al menos una pieza del board como referencia para Sketch.",
        );
      }
      const effectivePrompt = resolveBoardAiPrompt({
        mode,
        prompt,
        sketchView,
      });
      if (!effectivePrompt) {
        throw new Error("Falta el prompt");
      }
      const studioEngine =
        mode === "sketch" && engine === "nano-banana" ? "gpt-image" : engine;
      const data = await generateSkillAgentMoodboardImage(moodboardId, {
        prompt: effectivePrompt,
        engine: studioEngine,
        mode: mode === "sketch" ? "sketch" : "photo",
        place,
        refAssetIds,
        pageIndex: pageIdx,
      });
      applyBoard(data, { resetHistory: false });
      setGenerateOpen(false);
      setOkMsg(
        place === "pool"
          ? mode === "sketch"
            ? "Sketch generado (en el pool)"
            : mode === "photo"
              ? "Foto generada (en el pool)"
              : "Imagen generada (en el pool)"
          : place === "page"
            ? mode === "sketch"
              ? "Sketch en la página"
              : mode === "photo"
                ? "Foto en la página"
                : "Imagen generada en la página"
            : mode === "sketch"
              ? "Sketch en el tablero"
              : mode === "photo"
                ? "Foto en el tablero"
                : "Imagen generada en el tablero",
      );
      onChanged?.();
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          err.message ||
          "Error al generar imagen",
      );
    } finally {
      setBusy(false);
    }
  };

  const openInpaint = async () => {
    const assetId = activeSlot?.assetId;
    if (!assetId || busy) return;
    setError("");
    try {
      const fetchBlob =
        boardApiRef.current?.fetchAssetBlobUrl ||
        fetchSkillAgentMoodboardAssetBlobUrl;
      const url = await fetchBlob(assetId);
      if (inpaintImageUrl) URL.revokeObjectURL(inpaintImageUrl);
      setInpaintImageUrl(url);
      setInpaintOpen(true);
    } catch (err) {
      setError(err?.message || "No se pudo cargar la imagen");
    }
  };

  const closeInpaint = () => {
    setInpaintOpen(false);
    if (inpaintImageUrl) {
      URL.revokeObjectURL(inpaintImageUrl);
      setInpaintImageUrl("");
    }
  };

  const handleInpaintSubmit = async ({ prompt, maskBase64 }) => {
    const assetId = activeSlot?.assetId;
    if (!moodboardId || !assetId || busy) return;
    setBusy(true);
    setError("");
    setOkMsg("");
    try {
      const data = await editSkillAgentMoodboardAsset(moodboardId, assetId, {
        prompt,
        maskBase64,
        replaceInPages: true,
      });
      applyDerivedAssetResponse(data, assetId, { resetFraming: true });
      closeInpaint();
      setOkMsg("Zona regenerada");
      onChanged?.();
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          err.message ||
          "Error al editar imagen",
      );
    } finally {
      setBusy(false);
    }
  };

  const generateRefAssetIds = useMemo(() => {
    const ids = [];
    for (const s of selectedSlots) {
      if (s.assetId != null) ids.push(Number(s.assetId));
    }
    return [...new Set(ids)].slice(0, 4);
  }, [selectedSlots]);

  const handleRemoveSlot = (slotId) => {
    const ids =
      selectedSlotIds.size > 1 && selectedSlotIds.has(slotId)
        ? [...selectedSlotIds]
        : slotId
          ? [slotId]
          : activeSlotId
            ? [activeSlotId]
            : [];
    if (!ids.length) return;
    const idSet = new Set(ids.map(String));
    // Con foto: vaciar casilla (plantilla ficha se conserva). Sin foto: quitar casilla.
    // Así se puede dejar la página sin fotos / sin casillas.
    updateCurrentPageSlots((slots) => {
      const next = [];
      for (const s of slots || []) {
        if (!idSet.has(String(s.id))) {
          next.push(s);
          continue;
        }
        if (s.assetId != null) {
          next.push(
            clampSlot({
              ...s,
              assetId: null,
              zoom: 1,
              panX: 0.5,
              panY: 0.5,
              clip: null,
            }),
          );
        }
        // vacía → no se re-añade (se elimina)
      }
      return next;
    });
    clearSlotSelection();
    setSlotRemoveId(null);
  };

  const handleConfirmRemovePage = () => {
    if (pages.length <= 1) {
      setPageRemoveOpen(false);
      return;
    }
    const removeAt = pageIdx;
    notePagesHistory();
    setPages((prev) =>
      prev.filter((_, i) => i !== removeAt).map(normalizeFreePage),
    );
    setPageIdx((idx) => {
      if (idx > removeAt) return idx - 1;
      if (idx === removeAt) return Math.max(0, Math.min(idx, pages.length - 2));
      return idx;
    });
    clearSlotSelection();
    setPageRemoveOpen(false);
    setOkMsg(t("skillAgent.moodboard.ok.pageDeleted", { n: removeAt + 1 }));
  };

  const handleAviatorPreset = () => {
    notePagesHistory();
    setPages((prev) =>
      prev.map((p, i) =>
        i === pageIdx ? applyAviatorPresetToPage(p) : normalizeFreePage(p),
      ),
    );
    clearSlotSelection();
    setAviatorConfirmOpen(false);
    setOkMsg(t("skillAgent.moodboard.ok.aviatorDone", { n: pageIdx + 1 }));
  };

  const handleAutoPages = async () => {
    setBusy(true);
    try {
      const data = await (
        boardApiRef.current?.autoPages || autoSkillAgentMoodboardPages
      )(moodboardId);
      applyBoard(data, { resetHistory: true });
      setPageIdx(0);
      clearSlotSelection();
      setAutoPagesConfirmOpen(false);
      onChanged?.();
      setOkMsg(
        boardApiRef.current
          ? "Fotos colocadas: muestra a la izquierda, técnicas a la derecha."
          : t("skillAgent.moodboard.ok.aviatorDistributed"),
      );
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          t("skillAgent.moodboard.error.distribute"),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleAddFromPool = async (idsOverride) => {
    const rawIds = Array.isArray(idsOverride) ? idsOverride : [...poolPick];
    const projectMode = Boolean(pool?.projectMode);
    let searchItemIds = [];
    let meetingPhotoIds = [];
    let sourceAssetIds = [];
    if (projectMode) {
      for (const key of rawIds) {
        const [source, idRaw] = String(key).split(":");
        const id = Number(idRaw);
        if (!Number.isInteger(id) || id <= 0) continue;
        if (source === "search") searchItemIds.push(id);
        else if (source === "meeting") meetingPhotoIds.push(id);
        else if (source === "sketch") sourceAssetIds.push(id);
      }
    } else {
      searchItemIds = rawIds
        .map((id) => Number(id))
        .filter((id) => id && !usedSearchItemIds.has(id));
    }
    if (
      !searchItemIds.length &&
      !meetingPhotoIds.length &&
      !sourceAssetIds.length
    ) {
      return;
    }
    if (!isOnlineRef.current) {
      setError(t("skillAgent.moodboard.offline.networkBlocked"));
      return;
    }
    setBusy(true);
    setError("");
    try {
      const data = await (
        boardApiRef.current?.addAssets || addSkillAgentMoodboardAssets
      )(moodboardId, {
        searchItemIds,
        meetingPhotoIds,
        sourceAssetIds,
      });
      const added = Array.isArray(data?.added) ? data.added : [];
      const serverPages = normalizePages(data?.PagesJson);
      // Solo sincronizamos layout si el servidor rellenó páginas vacías;
      // si no, las fotos quedan en el board sin colocar (siguiente paso).
      const boardWasEmpty = !pagesRef.current.some((p) =>
        (p.slots || []).some((s) => s.assetId != null),
      );
      const placedOnServer = added.every((a) =>
        serverPages.some((p) =>
          p.slots.some((s) => Number(s.assetId) === Number(a.id)),
        ),
      );
      ignoreOwnSocketUntilRef.current = Date.now() + 5000;
      if (boardWasEmpty && placedOnServer) {
        applyBoard(data, { resetHistory: true });
      } else {
        applyBoard(data, { keepLocalPages: true });
      }
      setPoolPick(new Set());
      setAddPhotosOpen(false);
      onChanged?.();
      setOkMsg(
        t("skillAgent.moodboard.ok.assetsAddedPlace", {
          n:
            added.length ||
            searchItemIds.length +
              meetingPhotoIds.length +
              sourceAssetIds.length,
        }),
      );
      ignoreOwnSocketUntilRef.current = Date.now() + 2500;
      if (projectMode) {
        try {
          const refreshed =
            await getSkillAgentMoodboardProjectPool(moodboardId);
          setPool({
            searches: Array.isArray(refreshed?.searches)
              ? refreshed.searches
              : [],
            items: Array.isArray(refreshed?.items) ? refreshed.items : [],
            projectMode: true,
          });
        } catch {
          /* keep */
        }
      }
    } catch (err) {
      setError(
        err?.response?.data?.error || t("skillAgent.moodboard.error.addAssets"),
      );
    } finally {
      setBusy(false);
    }
  };

  const collectImageFiles = (fileList) =>
    [...(fileList || [])].filter((f) => {
      if (!f) return false;
      const type = String(f.type || "").toLowerCase();
      if (type.startsWith("image/")) return true;
      return /\.(jpe?g|png|gif|webp|bmp|heic|heif|tif?f)$/i.test(
        String(f.name || ""),
      );
    });

  const isExternalFileDrag = (e) =>
    Array.from(e?.dataTransfer?.types || []).includes("Files");

  const placeAssetsOnCurrentPage = (
    assetIds,
    at = { x: 0.5, y: 0.5 },
    assetList,
  ) => {
    const ids = (assetIds || [])
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (!ids.length) return;
    const notesMap = notesByAssetIdFromAssets(assetList || assets);
    const dropX = Math.min(0.92, Math.max(0.08, Number(at.x) || 0.5));
    const dropY = Math.min(0.92, Math.max(0.08, Number(at.y) || 0.5));
    const slotW = 0.28;
    const slotH = 0.26;
    notePagesHistory();
    const createdIds = [];
    setPages((prev) => {
      const next = prev.map((p) => normalizeFreePage(p));
      const page = next[pageIdx] || emptyFreePage();
      if (!next[pageIdx]) next.push(page);
      let slots = [...(page.slots || [])];
      let maxZ = Math.max(0, ...slots.map((s) => Number(s.z) || 0));
      const placedSlots = [];
      ids.forEach((assetId, i) => {
        maxZ += 1;
        const col = i % 3;
        const row = Math.floor(i / 3);
        const x = Math.min(
          1 - slotW,
          Math.max(0, dropX - slotW / 2 + col * 0.04),
        );
        const y = Math.min(
          1 - slotH,
          Math.max(0, dropY - slotH / 2 + row * 0.04),
        );
        const created = clampSlot({
          id: newSlotId("drop"),
          x,
          y,
          w: slotW,
          h: slotH,
          shape: "rect",
          z: maxZ,
          assetId,
        });
        slots.push(created);
        placedSlots.push(created);
        createdIds.push(created.id);
      });
      next[pageIdx] = {
        ...page,
        slots,
        marks: mergeNoteMarksForSlots(page, placedSlots, notesMap),
      };
      return next;
    });
    if (createdIds.length) {
      setSelectedSlotIds(new Set(createdIds));
      setActiveSlotId(createdIds[createdIds.length - 1]);
    }
  };

  const placePasteboardItemOnPage = useCallback(
    (itemId, targetPageIdx = pageIdx, opts = {}) => {
      const item = pasteboardRef.current.find(
        (p) => String(p.id) === String(itemId),
      );
      if (!item) return;
      const placeAt = opts.placeAt || { x: 0.5, y: 0.5 };
      const iBoard = Math.min(
        Math.max(0, Number(targetPageIdx) || 0),
        Math.max(0, (pagesRef.current || []).length - 1),
      );

      // Marca (texto/figura) → reponer en la mesa
      if (String(item.kind || "").toLowerCase() === "mark" && item.mark) {
        const mb = markBounds(item.mark);
        const w = Math.max(0.02, mb?.w || Number(item.pageW) || 0.2);
        const h = Math.max(0.02, mb?.h || Number(item.pageH) || 0.12);
        const dx = Math.min(
          1 - w,
          Math.max(0, (Number(placeAt.x) || 0.5) - w / 2),
        );
        const dy = Math.min(
          1 - h,
          Math.max(0, (Number(placeAt.y) || 0.5) - h / 2),
        );
        const restored = clampMark(
          translateMark(
            { ...item.mark, id: item.mark.id || newMarkId() },
            dx,
            dy,
            { allowOutside: true },
          ),
        );
        notePagesHistory();
        setPasteboard((prev) => {
          const next = prev.filter((p) => String(p.id) !== String(itemId));
          pasteboardRef.current = next;
          return next;
        });
        setSelectedPasteboardId(null);
        const createdId = String(restored.id);
        setPages((prev) => {
          const next = prev.map((p) => normalizeFreePage(p));
          const page = next[iBoard] || emptyFreePage();
          if (!next[iBoard]) next.push(page);
          const maxZ = Math.max(
            0,
            ...(page.marks || []).map((m) => Number(m.z) || 0),
          );
          next[iBoard] = {
            ...page,
            marks: [
              ...(page.marks || []),
              clampMark({ ...restored, z: maxZ + 1 }),
            ],
          };
          return next;
        });
        setPageIdx(iBoard);
        clearSlotSelection();
        const set = new Set([createdId]);
        selectedMarkIdsRef.current = set;
        setSelectedMarkIds(set);
        setSelectedMarkId(createdId);
        setOkMsg("Marca colocada en la mesa");
        return;
      }

      if (!item?.assetId) return;
      const targetBoard = normalizePageBoard(
        pagesRef.current[iBoard]?.board,
        iBoard,
      );
      const draft = pasteboardItemToSlotDraft(item, placeAt, targetBoard);
      if (!draft) return;
      notePagesHistory();
      setPasteboard((prev) => {
        const next = prev.filter((p) => String(p.id) !== String(itemId));
        pasteboardRef.current = next;
        return next;
      });
      setSelectedPasteboardId(null);
      const notesMap = notesByAssetIdFromAssets(assets);
      const createdId = newSlotId("pb");
      setPages((prev) => {
        const next = prev.map((p) => normalizeFreePage(p));
        const page = next[iBoard] || emptyFreePage();
        if (!next[iBoard]) next.push(page);
        const maxZ = Math.max(
          0,
          ...(page.slots || []).map((s) => Number(s.z) || 0),
        );
        const created = clampSlot({
          id: createdId,
          ...draft,
          shape: "rect",
          z: maxZ + 1,
        });
        next[iBoard] = {
          ...page,
          slots: [...(page.slots || []), created],
          marks: mergeNoteMarksForSlots(page, [created], notesMap),
        };
        return next;
      });
      setPageIdx(iBoard);
      setSelectedSlotIds(new Set([createdId]));
      setActiveSlotId(createdId);
      setOkMsg("Foto colocada en la mesa");
    },
    [pageIdx, notePagesHistory, assets, clearSlotSelection],
  );

  const handleUpload = async (
    fileList,
    { placeAt = null, pasteboardAt = null } = {},
  ) => {
    const files = collectImageFiles(fileList);
    if (!files.length) {
      if ([...(fileList || [])].length) {
        setError(t("skillAgent.moodboard.error.dropNoImages"));
      }
      return;
    }
    if (!isOnlineRef.current) {
      setError(t("skillAgent.moodboard.offline.networkBlocked"));
      return;
    }
    if (uploadInFlightRef.current || busy) return;
    uploadInFlightRef.current = true;
    setBusy(true);
    setError("");
    // La subida actualiza UpdatedAt en servidor: sin sincronizar el ref, el
    // autosave de colocar casillas choca (409) y recarga el board.
    ignoreOwnSocketUntilRef.current = Date.now() + 5000;
    try {
      const data = await (
        boardApiRef.current?.addAssets || addSkillAgentMoodboardAssets
      )(moodboardId, { files });
      const added = Array.isArray(data?.added) ? data.added : [];
      const serverPages = normalizePages(data?.PagesJson);
      const boardWasEmpty = !pagesRef.current.some((p) =>
        (p.slots || []).some((s) => s.assetId != null),
      );
      const placedOnServer =
        added.length > 0 &&
        added.every((a) =>
          serverPages.some((p) =>
            p.slots.some((s) => Number(s.assetId) === Number(a.id)),
          ),
        );
      const nextAssets = Array.isArray(data?.assets) ? data.assets : null;
      if (pasteboardAt && added.length) {
        applyBoard(data, { keepLocalPages: true });
        addAssetsToPasteboard(
          added.map((a) => a.id),
          pasteboardAt,
        );
        setTimeout(() => {
          skipAutosaveRef.current = false;
          if (pagesTimerRef.current) clearTimeout(pagesTimerRef.current);
          pagesTimerRef.current = setTimeout(() => {
            persistPages();
          }, 120);
        }, 100);
        onChanged?.();
        setOkMsg(
          t("skillAgent.moodboard.ok.uploadedPasteboard", {
            n: added.length || files.length,
          }),
        );
      } else if (placeAt && added.length) {
        applyBoard(data, { keepLocalPages: true });
        placeAssetsOnCurrentPage(
          added.map((a) => a.id),
          placeAt,
          nextAssets || assets,
        );
        // applyBoard deja skipAutosave ~80ms: forzar guardado del layout nuevo
        // con el UpdatedAt ya sincronizado (si no, 409 y pantallazo).
        setTimeout(() => {
          skipAutosaveRef.current = false;
          if (pagesTimerRef.current) clearTimeout(pagesTimerRef.current);
          pagesTimerRef.current = setTimeout(() => {
            persistPages();
          }, 120);
        }, 100);
        onChanged?.();
        setOkMsg(
          t("skillAgent.moodboard.ok.uploadedPlaced", {
            n: added.length || files.length,
          }),
        );
      } else if (boardWasEmpty && placedOnServer) {
        applyBoard(data, { resetHistory: true });
        onChanged?.();
        setOkMsg(
          t("skillAgent.moodboard.ok.uploadedPlace", { n: files.length }),
        );
      } else {
        applyBoard(data, { keepLocalPages: true });
        onChanged?.();
        setOkMsg(
          t("skillAgent.moodboard.ok.uploadedPlace", { n: files.length }),
        );
      }
      ignoreOwnSocketUntilRef.current = Date.now() + 2500;
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          t("skillAgent.moodboard.error.uploadShort"),
      );
    } finally {
      uploadInFlightRef.current = false;
      setBusy(false);
    }
  };

  const resetFileDrop = () => {
    fileDropDepthRef.current = 0;
    setFileDropActive(false);
  };

  const handleCanvasDragEnter = (e) => {
    if (!isExternalFileDrag(e) || busy) return;
    e.preventDefault();
    e.stopPropagation();
    fileDropDepthRef.current += 1;
    setFileDropActive(true);
  };

  const handleCanvasDragLeave = (e) => {
    if (!isExternalFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    fileDropDepthRef.current = Math.max(0, fileDropDepthRef.current - 1);
    if (fileDropDepthRef.current === 0) setFileDropActive(false);
  };

  const handleCanvasDragOver = (e) => {
    if (!isExternalFileDrag(e) || busy) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
  };

  const handleCanvasDrop = (e) => {
    if (!isExternalFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    resetFileDrop();
    if (busy) return;
    if (workspaceModeRef.current === WORKSPACE_MODE_BOARD) {
      const target = resolveBoardPointerTarget(e.clientX, e.clientY) || {
        kind: "pasteboard",
        boardPt: lastBoardPointerRef.current || defaultPasteboardPoint(),
      };
      if (target.kind === "page") {
        setPageIdx(target.pageIndex);
        handleUpload(e.dataTransfer?.files, { placeAt: target.placeAt });
        return;
      }
      lastBoardPointerRef.current = target.boardPt;
      handleUpload(e.dataTransfer?.files, {
        pasteboardAt: target.boardPt || defaultPasteboardPoint(),
      });
      return;
    }
    const canvas = canvasRef.current;
    let placeAt = { x: 0.5, y: 0.5 };
    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      if (rect.width > 1 && rect.height > 1) {
        placeAt = {
          x: (e.clientX - rect.left) / rect.width,
          y: (e.clientY - rect.top) / rect.height,
        };
      }
    }
    handleUpload(e.dataTransfer?.files, { placeAt });
  };

  useEffect(() => {
    const onPaste = (e) => {
      const tag = String(e.target?.tagName || "").toLowerCase();
      if (
        tag === "input" ||
        tag === "textarea" ||
        e.target?.isContentEditable
      ) {
        return;
      }
      if (busy || uploadInFlightRef.current) return;
      const dt = e.clipboardData;
      const files = [];
      if (dt?.files?.length) {
        files.push(...collectImageFiles(dt.files));
      }
      if (!files.length && dt?.items?.length) {
        for (const item of dt.items) {
          if (item.kind === "file" && /^image\//i.test(item.type || "")) {
            const f = item.getAsFile();
            if (f) files.push(f);
          }
        }
      }
      const hasInternal = Boolean(clipboardRef.current);
      // Shift+Ctrl+V (o Shift+V en el evento paste): forzar pegado desde el SO.
      const forceSystem = Boolean(e.shiftKey);
      // Tras copiar en el moodboard, priorizar interno (evita imagen vieja del SO).
      const preferInternal =
        hasInternal &&
        !forceSystem &&
        Date.now() - (clipboardInternalAtRef.current || 0) < 60 * 60 * 1000;

      if (preferInternal && pasteMoodboardSelection()) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      if (files.length) {
        e.preventDefault();
        e.stopPropagation();
        // Pegado del SO: ya no preferir el clip interno antiguo.
        clipboardInternalAtRef.current = 0;
        if (workspaceModeRef.current === WORKSPACE_MODE_BOARD) {
          const tip = lastBoardPointerRef.current;
          const onArtboard =
            tip && artboardIndexAtPoint(pagesRef.current, tip.x, tip.y) >= 0;
          if (onArtboard) {
            const artIdx = artboardIndexAtPoint(pagesRef.current, tip.x, tip.y);
            const norm = pointToArtboardNorm(
              pagesRef.current,
              artIdx,
              tip.x,
              tip.y,
            );
            setPageIdx(artIdx);
            handleUpload(files, { placeAt: norm });
            return;
          }
          handleUpload(files, {
            pasteboardAt: tip || defaultPasteboardPoint(),
          });
          return;
        }
        handleUpload(files, { placeAt: { x: 0.5, y: 0.5 } });
        return;
      }
      if (pasteMoodboardSelection()) {
        e.preventDefault();
      }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy, defaultPasteboardPoint, moodboardId, pasteMoodboardSelection]);

  const handleRemoveAsset = async (assetId) => {
    if (!assetId) return;
    setBusy(true);
    try {
      const data = await (
        boardApiRef.current?.deleteAsset || deleteSkillAgentMoodboardAsset
      )(moodboardId, assetId);
      applyBoard(data);
      setAssetRemoveId(null);
      onChanged?.();
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          t("skillAgent.moodboard.error.removeShort"),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleExport = async () => {
    if (exporting || busy || !exportPageIdxs.length) return;
    if (!isOnlineRef.current) {
      setError(t("skillAgent.moodboard.offline.networkBlocked"));
      return;
    }
    setExportConfirmOpen(false);
    setExporting(true);
    setBusy(true);
    setError("");
    setOkMsg("");
    try {
      await flushAutosave();
      const data = await (
        boardApiRef.current?.exportPdf || exportSkillAgentMoodboardPdf
      )(moodboardId, {
        customerFolderName: board?.CustomerName || undefined,
        pageIndexes: exportPageIdxs,
      });
      applyBoard(data.moodboard || data.search || data);
      if (boardApiRef.current) reportCloseGuard(true);

      if (data.pdfBase64) {
        try {
          const bin = atob(data.pdfBase64);
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
          const blob = new Blob([bytes], { type: "application/pdf" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = data.pdfFileName || "moodboard.pdf";
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(url), 2000);
        } catch {
          /* download optional */
        }
      }

      onChanged?.();
      if (data.drivePdfFileUrl) {
        setOkMsg(t("skillAgent.moodboard.ok.pdfExportedDrive"));
      } else if (data.driveNote) {
        setOkMsg(
          t("skillAgent.moodboard.ok.pdfDriveNote", { note: data.driveNote }),
        );
      } else if (data.technicalFile && boardApiRef.current?.publishMessage) {
        setOkMsg(boardApiRef.current.publishMessage);
      } else {
        setOkMsg(t("skillAgent.moodboard.ok.pdfDownloaded"));
      }
    } catch (err) {
      setError(
        err?.response?.data?.error || t("skillAgent.moodboard.error.exportPdf"),
      );
    } finally {
      setExporting(false);
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (boardApiRef.current) return;
    setBusy(true);
    try {
      await deleteSkillAgentMoodboard(moodboardId);
      setDeleteOpen(false);
      onDeleted?.(moodboardId);
    } catch (err) {
      setError(
        err?.response?.data?.error || t("skillAgent.moodboard.error.delete"),
      );
      setBusy(false);
    }
  };

  if (loading && !board) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!board) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        Moodboard no encontrado.
      </Alert>
    );
  }

  return (
    <MoodboardAssetBlobContext.Provider value={fetchAssetBlobCached}>
      <Stack
        spacing={0}
        sx={{
          flex: 1,
          height: "100%",
          minHeight: 0,
          minWidth: 0,
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Backdrop
          open={exporting}
          sx={{
            zIndex: (theme) => theme.zIndex.modal + 1,
            bgcolor: alpha("#0f172a", 0.55),
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <CircularProgress size={48} sx={{ color: "#fff" }} />
          <Typography fontWeight={800} sx={{ color: "#fff", fontSize: 18 }}>
            {t("skillAgent.moodboard.exportBackdropTitle")}
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)" }}>
            {t("skillAgent.moodboard.exportBackdropBody")}
          </Typography>
        </Backdrop>

        {!isOnline ||
        connectivityNotice === "syncing" ||
        (isOnline && offlinePending) ? (
          <Alert
            severity={!isOnline ? "warning" : "info"}
            sx={{
              flexShrink: 0,
              borderRadius: 0,
              py: 0.25,
            }}
          >
            {!isOnline || connectivityNotice === "offline"
              ? t("skillAgent.moodboard.offline.bannerOffline")
              : connectivityNotice === "syncing" || offlinePending
                ? t("skillAgent.moodboard.offline.bannerSyncing")
                : t("skillAgent.moodboard.offline.bannerPending")}
          </Alert>
        ) : null}

        {peerViewers.length || staleConflict ? (
          <Alert
            severity={staleConflict ? "warning" : "info"}
            sx={{
              flexShrink: 0,
              borderRadius: 0,
              py: 0.25,
              "& .MuiAlert-message": { width: "100%" },
            }}
            action={
              staleConflict ? (
                <Button
                  color="inherit"
                  size="small"
                  sx={{ fontWeight: 800, textTransform: "none" }}
                  onClick={() => handleStaleConflict()}
                >
                  {t("skillAgent.common.reload")}
                </Button>
              ) : null
            }
          >
            {staleConflict ? t("skillAgent.moodboard.staleConflict") : null}
            {peerViewers.length
              ? `${staleConflict ? " · " : ""}${t(
                  peerViewers.length === 1
                    ? "skillAgent.moodboard.peerViewersOne"
                    : "skillAgent.moodboard.peerViewersMany",
                  {
                    names: peerViewers
                      .map(
                        (v) =>
                          v.username ||
                          t("skillAgent.moodboard.peerFallbackName"),
                      )
                      .join(", "),
                  },
                )}`
              : null}
          </Alert>
        ) : null}

        {/* Barras fijas: cabecera + páginas/casillas (fuera del scroll).
            z-index bajo para no tapar el overlay Compás (árbol). */}
        <Box
          sx={{
            flexShrink: 0,
            position: "relative",
            zIndex: 2,
            minWidth: 0,
            width: "100%",
            overflow: "hidden",
            bgcolor: (t) => alpha(t.palette.background.paper, 0.98),
            borderBottom: "1px solid",
            borderColor: "divider",
            boxShadow: "0 6px 16px rgba(15,23,42,0.06)",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={1}
            useFlexGap
            sx={{
              px: { xs: 1.5, md: 2 },
              pt: 0.65,
              pb: 0.25,
              minWidth: 0,
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            <Box
              sx={{
                flex: "0 1 auto",
                minWidth: 0,
                maxWidth: "100%",
              }}
            >
              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                sx={{ flexWrap: "nowrap", minWidth: 0 }}
              >
                <SkillAgentInlineTitle
                  entityKey={moodboardId}
                  value={titulo}
                  onChange={setTitulo}
                  onCommit={async (next) => {
                    metaRef.current = { ...metaRef.current, titulo: next };
                    if (metaTimerRef.current)
                      clearTimeout(metaTimerRef.current);
                    await persistMeta();
                  }}
                  placeholder={t("skillAgent.form.title")}
                  disabled={busy || exporting}
                  sx={{ flexShrink: 1, minWidth: 80 }}
                />
                {!boardApi && meetingSessionId ? (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<VideocamOutlinedIcon sx={{ fontSize: 16 }} />}
                    onClick={() =>
                      navigate(`/skills/tareas?meeting=${meetingSessionId}`)
                    }
                    sx={{
                      textTransform: "none",
                      fontWeight: 800,
                      borderRadius: 2,
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                      ml: 0.25,
                      px: 1.1,
                      color: "#0f766e",
                      borderColor: "rgba(15, 118, 110, 0.55)",
                      bgcolor: "rgba(15, 118, 110, 0.08)",
                      "&:hover": {
                        borderColor: "#0f766e",
                        bgcolor: "rgba(15, 118, 110, 0.16)",
                      },
                    }}
                  >
                    {t("skillAgent.moodboard.openMeeting")}
                  </Button>
                ) : null}
                {!boardApi && readSourceAiDesignId(board) ? (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
                    onClick={() => {
                      const sourceId = readSourceAiDesignId(board);
                      navigate(`/skills/tareas?aiDesign=${sourceId}`, {
                        state: {
                          fromCompass: true,
                          AiDesignId: sourceId,
                          MoodboardId: sourceId,
                          ProjectId: board.ProjectId || null,
                          MeetingSessionId:
                            board.MeetingSessionId || meetingSessionId || null,
                          CustomerId: board.CustomerId || null,
                          CustomerName: board.CustomerName || "",
                          Temporada: board.Temporada || "",
                          designKind: board.CategoryLabel || null,
                        },
                      });
                    }}
                    sx={{
                      textTransform: "none",
                      fontWeight: 800,
                      borderRadius: 2,
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                      ml: 0.25,
                      px: 1.1,
                      color: "#1473e6",
                      borderColor: "rgba(20, 115, 230, 0.55)",
                      bgcolor: "rgba(20, 115, 230, 0.08)",
                      "&:hover": {
                        borderColor: "#1473e6",
                        bgcolor: "rgba(20, 115, 230, 0.16)",
                      },
                    }}
                  >
                    {t("skillAgent.moodboard.backToAiDesign", {
                      defaultValue: "Volver a AI Designs",
                    })}
                  </Button>
                ) : null}
                <Tabs
                  value={pageIdx}
                  onChange={(_, v) => {
                    if (workspaceModeRef.current === WORKSPACE_MODE_BOARD) {
                      activateBoardPage(v);
                    } else {
                      setPageIdx(v);
                      clearSlotSelection();
                      clearMarkSelection();
                    }
                  }}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    minHeight: 28,
                    flex: "0 1 auto",
                    minWidth: 0,
                    maxWidth: { xs: 120, sm: 200, md: 280 },
                    ml: 0.5,
                    "& .MuiTabs-indicator": { display: "none" },
                    "& .MuiTabs-flexContainer": { gap: 0.25 },
                    "& .MuiTabScrollButton-root": {
                      width: 22,
                      "&.Mui-disabled": { opacity: 0.25 },
                    },
                    "& .MuiTab-root": {
                      minHeight: 26,
                      minWidth: 0,
                      py: 0.15,
                      px: 0.85,
                      my: 0,
                      borderRadius: 1,
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      textTransform: "none",
                      color: "text.secondary",
                      "&.Mui-selected": {
                        color: "primary.main",
                        bgcolor: (th) => alpha(th.palette.primary.main, 0.12),
                      },
                      "&:hover": {
                        bgcolor: (th) => alpha(th.palette.text.primary, 0.04),
                      },
                    },
                  }}
                >
                  {pages.map((_, i) => (
                    <Tab
                      key={i}
                      label={`${i + 1}`}
                      title={t("skillAgent.moodboard.pageTabTitle", {
                        n: i + 1,
                      })}
                    />
                  ))}
                </Tabs>
              </Stack>
              <Typography
                variant="body2"
                color="text.secondary"
                noWrap
                sx={{ flexShrink: 0, lineHeight: 1.2, mt: 0.15 }}
              >
                {[board.CustomerName, board.Temporada]
                  .filter(Boolean)
                  .join(" · ")}
              </Typography>
            </Box>
            <Stack
              direction="row"
              spacing={0.35}
              alignItems="center"
              useFlexGap
              sx={{
                flex: "0 1 auto",
                justifyContent: "flex-end",
                minWidth: 0,
                overflow: "auto",
                flexWrap: "nowrap",
              }}
            >
              <ToolGroup label={t("skillAgent.moodboard.toolGroup.board")}>
                {typeof onOpenHelp === "function" ? (
                  <Tooltip title={t("skillAgent.moodboard.help")}>
                    <IconButton
                      size="small"
                      onClick={onOpenHelp}
                      aria-label={t("skillAgent.moodboard.help")}
                    >
                      <HelpOutlineOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : null}
                <Tooltip
                  title={
                    exporting
                      ? t("skillAgent.moodboard.exporting")
                      : t("skillAgent.moodboard.exportPdf")
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      color="primary"
                      disabled={busy || exporting || !assets.length}
                      onClick={() => {
                        setExportPageIdxs(
                          (pages || []).map((_, index) => index),
                        );
                        setExportConfirmOpen(true);
                      }}
                      aria-label={t("skillAgent.moodboard.exportPdf")}
                    >
                      {exporting ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <PictureAsPdfOutlinedIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
                {board.DrivePdfFileUrl ? (
                  <Tooltip title={t("skillAgent.moodboard.openPdfDrive")}>
                    <IconButton
                      size="small"
                      href={board.DrivePdfFileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      component="a"
                    >
                      <OpenInNewOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : null}
                {!boardApi ? (
                  <Tooltip title={t("skillAgent.moodboard.moreOptions")}>
                    <IconButton
                      size="small"
                      onClick={(e) => setLayoutMenuEl(e.currentTarget)}
                      aria-label={t("skillAgent.moodboard.moreOptions")}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : null}
                <Tooltip
                  title={
                    workspaceMode === WORKSPACE_MODE_BOARD
                      ? t("skillAgent.moodboard.workspaceModePage")
                      : t("skillAgent.moodboard.workspaceModeBoard")
                  }
                >
                  <IconButton
                    size="small"
                    color={
                      workspaceMode === WORKSPACE_MODE_BOARD
                        ? "primary"
                        : "default"
                    }
                    onClick={() =>
                      setWorkspaceModeSafe(
                        workspaceMode === WORKSPACE_MODE_BOARD
                          ? WORKSPACE_MODE_PAGE
                          : WORKSPACE_MODE_BOARD,
                      )
                    }
                    aria-label={
                      workspaceMode === WORKSPACE_MODE_BOARD
                        ? t("skillAgent.moodboard.workspaceModeBoardShort")
                        : t("skillAgent.moodboard.workspaceModePageShort")
                    }
                  >
                    {workspaceMode === WORKSPACE_MODE_BOARD ? (
                      <DashboardOutlinedIcon fontSize="small" />
                    ) : (
                      <CropLandscapeOutlinedIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={
                    workspaceMode === WORKSPACE_MODE_BOARD
                      ? t("skillAgent.moodboard.pageZoomOut")
                      : t("skillAgent.moodboard.pageZoomOut")
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      disabled={
                        workspaceMode === WORKSPACE_MODE_BOARD
                          ? boardCam.zoom <= BOARD_CAM_ZOOM_MIN
                          : viewZoom <= PAGE_VIEW_ZOOM_MIN
                      }
                      onClick={() => {
                        if (workspaceMode === WORKSPACE_MODE_BOARD) {
                          boardCamUserRef.current = true;
                          setBoardCam((c) => ({
                            ...c,
                            zoom: clampBoardCamZoom(
                              c.zoom - BOARD_CAM_ZOOM_STEP,
                            ),
                          }));
                        } else {
                          setViewZoomManual((z) => z - PAGE_VIEW_ZOOM_STEP);
                        }
                      }}
                      aria-label={t("skillAgent.moodboard.pageZoomOut")}
                    >
                      <ZoomOutOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip
                  title={
                    workspaceMode === WORKSPACE_MODE_BOARD
                      ? t("skillAgent.moodboard.boardCamFit")
                      : t("skillAgent.moodboard.pageZoomReset")
                  }
                >
                  <Button
                    size="small"
                    color="inherit"
                    onClick={() => {
                      if (workspaceMode === WORKSPACE_MODE_BOARD) {
                        applyBoardCamFit({ force: true });
                      } else {
                        viewZoomUserRef.current = false;
                        applyViewZoomFit();
                      }
                    }}
                    sx={{
                      minWidth: 44,
                      px: 0.5,
                      fontWeight: 700,
                      fontSize: "0.7rem",
                      lineHeight: 1,
                    }}
                    aria-label={t("skillAgent.moodboard.pageZoomPct", {
                      pct: Math.round(
                        (workspaceMode === WORKSPACE_MODE_BOARD
                          ? boardCam.zoom
                          : viewZoom) * 100,
                      ),
                    })}
                  >
                    {Math.round(
                      (workspaceMode === WORKSPACE_MODE_BOARD
                        ? boardCam.zoom
                        : viewZoom) * 100,
                    )}
                    %
                  </Button>
                </Tooltip>
                <Tooltip title={t("skillAgent.moodboard.pageZoomIn")}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={
                        workspaceMode === WORKSPACE_MODE_BOARD
                          ? boardCam.zoom >= BOARD_CAM_ZOOM_MAX
                          : viewZoom >= PAGE_VIEW_ZOOM_MAX
                      }
                      onClick={() => {
                        if (workspaceMode === WORKSPACE_MODE_BOARD) {
                          boardCamUserRef.current = true;
                          setBoardCam((c) => ({
                            ...c,
                            zoom: clampBoardCamZoom(
                              c.zoom + BOARD_CAM_ZOOM_STEP,
                            ),
                          }));
                        } else {
                          setViewZoomManual((z) => z + PAGE_VIEW_ZOOM_STEP);
                        }
                      }}
                      aria-label={t("skillAgent.moodboard.pageZoomIn")}
                    >
                      <ZoomInOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Fondo del tablero">
                  <IconButton
                    size="small"
                    onClick={(e) => setPasteboardBgAnchor(e.currentTarget)}
                    aria-label="Fondo del tablero"
                    sx={{
                      color: "text.secondary",
                      "& .MuiSvgIcon-root": { fontSize: 18 },
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        width: 18,
                        height: 18,
                        display: "grid",
                        placeItems: "center",
                      }}
                    >
                      <PaletteOutlinedIcon />
                      <Box
                        sx={{
                          position: "absolute",
                          right: -1,
                          bottom: -1,
                          width: 8,
                          height: 8,
                          borderRadius: "2px",
                          border: "1px solid",
                          borderColor: "background.paper",
                          bgcolor: pasteboardBg || DEFAULT_PASTEBOARD_BG,
                        }}
                      />
                    </Box>
                  </IconButton>
                </Tooltip>
                <Popover
                  open={Boolean(pasteboardBgAnchor)}
                  anchorEl={pasteboardBgAnchor}
                  onClose={() => setPasteboardBgAnchor(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  <Stack spacing={1} sx={{ p: 1.25, minWidth: 180 }}>
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        color: "text.secondary",
                      }}
                    >
                      Fondo del tablero
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        component="input"
                        type="color"
                        value={pasteboardBg || DEFAULT_PASTEBOARD_BG}
                        onChange={(e) => {
                          const next = String(
                            e.target.value || DEFAULT_PASTEBOARD_BG,
                          );
                          setPasteboardBg(next);
                          writeStoredPasteboardBg(next);
                        }}
                        sx={{
                          width: 36,
                          height: 28,
                          p: 0,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          cursor: "pointer",
                          bgcolor: "transparent",
                        }}
                      />
                      <Typography
                        sx={{ fontSize: "0.75rem", fontFamily: "monospace" }}
                      >
                        {(pasteboardBg || DEFAULT_PASTEBOARD_BG).toUpperCase()}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5}>
                      {[
                        DEFAULT_PASTEBOARD_BG,
                        "#1e1e1e",
                        "#606060",
                        "#dcdcdc",
                        "#0f172a",
                      ].map((hex) => (
                        <Box
                          key={hex}
                          component="button"
                          type="button"
                          title={hex}
                          onClick={() => {
                            setPasteboardBg(hex);
                            writeStoredPasteboardBg(hex);
                          }}
                          sx={{
                            width: 22,
                            height: 22,
                            p: 0,
                            borderRadius: 0.5,
                            border:
                              (
                                pasteboardBg || DEFAULT_PASTEBOARD_BG
                              ).toLowerCase() === hex
                                ? "2px solid"
                                : "1px solid",
                            borderColor:
                              (
                                pasteboardBg || DEFAULT_PASTEBOARD_BG
                              ).toLowerCase() === hex
                                ? "primary.main"
                                : "divider",
                            bgcolor: hex,
                            cursor: "pointer",
                          }}
                        />
                      ))}
                    </Stack>
                    <Button
                      size="small"
                      onClick={() => {
                        setPasteboardBg(DEFAULT_PASTEBOARD_BG);
                        writeStoredPasteboardBg(DEFAULT_PASTEBOARD_BG);
                      }}
                    >
                      Restaurar gris
                    </Button>
                  </Stack>
                </Popover>
                {!boardApi ? (
                  <Menu
                    anchorEl={layoutMenuEl}
                    open={Boolean(layoutMenuEl)}
                    onClose={() => setLayoutMenuEl(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                  >
                    <MenuItem
                      disabled={busy}
                      onClick={() => {
                        setLayoutMenuEl(null);
                        setDeleteOpen(true);
                      }}
                      sx={{ color: "error.main" }}
                    >
                      {t("skillAgent.moodboard.deleteMenu")}
                    </MenuItem>
                  </Menu>
                ) : null}
              </ToolGroup>

              <ToolGroup>
                <Tooltip title={t("skillAgent.moodboard.undo")}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={!canUndo || busy || exporting}
                      onClick={applyPagesUndo}
                      aria-label={t("skillAgent.moodboard.undo")}
                    >
                      <UndoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={t("skillAgent.moodboard.redo")}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={!canRedo || busy || exporting}
                      onClick={applyPagesRedo}
                      aria-label={t("skillAgent.moodboard.redo")}
                    >
                      <RedoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </ToolGroup>

              <ToolGroup>
                {!boardApi ? (
                  <Tooltip
                    title={
                      isPropuestaBoard(board)
                        ? t("skillAgent.moodboard.addFromProject", {
                            defaultValue: "Añadir del proyecto",
                          })
                        : t("skillAgent.moodboard.addFromSearch")
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={busy}
                        onClick={() => {
                          setPoolPick(new Set());
                          if (isPropuestaBoard(board)) {
                            setPoolSourceFilter("sketch");
                          }
                          setAddPhotosOpen(true);
                        }}
                        aria-label={t("skillAgent.moodboard.addPhotosAria")}
                        sx={{ color: "warning.main" }}
                      >
                        <PhotoLibraryOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : null}
                {!boardApi && meetingSessionId && !isPropuestaBoard(board) ? (
                  <Tooltip title={t("skillAgent.moodboard.addFromMeeting")}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={busy}
                        onClick={openAddMeetingPhotos}
                        aria-label={t("skillAgent.moodboard.addFromMeeting")}
                        sx={{ color: "info.main" }}
                      >
                        <GroupsOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : null}
                {typeof boardApi?.openAddMuestras === "function" ? (
                  <Tooltip
                    title={
                      boardApi.addMuestrasLabel ||
                      t("skillAgent.moodboard.addFromMuestras", {
                        defaultValue: "Añadir fotos de muestras",
                      })
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={busy}
                        onClick={() => boardApi.openAddMuestras()}
                        aria-label={
                          boardApi.addMuestrasLabel ||
                          t("skillAgent.moodboard.addFromMuestras", {
                            defaultValue: "Añadir fotos de muestras",
                          })
                        }
                        sx={{ color: "warning.main" }}
                      >
                        <PhotoLibraryOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : null}
                <Tooltip
                  title={t("skillAgent.moodboard.boardPhotos", {
                    n: assets.length,
                  })}
                >
                  <span>
                    <IconButton
                      size="small"
                      color={assets.length ? "primary" : "default"}
                      disabled={busy}
                      onClick={() => {
                        setGalleryPlacePageIdx(pageIdx);
                        setBoardPhotosOpen(true);
                      }}
                      aria-label={t("skillAgent.moodboard.boardPhotosAria")}
                    >
                      <CollectionsOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={t("skillAgent.moodboard.uploadPc")}>
                  <span>
                    <IconButton
                      size="small"
                      component="label"
                      disabled={busy}
                      aria-label={t("skillAgent.moodboard.uploadPc")}
                    >
                      <CloudUploadOutlinedIcon fontSize="small" />
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        multiple
                        onChange={(e) => {
                          handleUpload(e.target.files);
                          e.target.value = "";
                        }}
                      />
                    </IconButton>
                  </span>
                </Tooltip>
              </ToolGroup>
            </Stack>
          </Stack>
          <Box
            sx={{
              px: { xs: 1.5, md: 2 },
              pb: 0.5,
              pt: 0,
              minWidth: 0,
              width: "100%",
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              spacing={0.35}
              sx={{
                gap: 0.35,
                flexWrap: "nowrap",
                overflow: "auto",
                minHeight: 32,
              }}
            >
              <ToolGroup label={t("skillAgent.moodboard.toolGroup.pages")}>
                <Tooltip title={t("skillAgent.moodboard.addPage")}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      notePagesHistory();
                      const seed = {
                        categoryLabel,
                        studioLine,
                        chromeStyle,
                      };
                      const from = pageIdx;
                      let newIdx = from;
                      setPages((p) => {
                        const list = assignDefaultBoards(
                          (Array.isArray(p) ? p : []).map((pg) =>
                            normalizeFreePage(pg),
                          ),
                        );
                        const template = list[0] || null;
                        const content = blankPageFromBoardTemplate(
                          template,
                          seed,
                        );
                        const next = [
                          ...list,
                          {
                            ...content,
                            board: nextBoardNearPages(list, from),
                          },
                        ];
                        newIdx = next.length - 1;
                        setPageIdx(newIdx);
                        return next;
                      });
                      clearSlotSelection();
                      clearMarkSelection();
                      clearChromeSelection();
                      requestAnimationFrame(() => {
                        if (workspaceModeRef.current === WORKSPACE_MODE_BOARD) {
                          focusBoardPage(newIdx);
                        }
                      });
                    }}
                  >
                    <AddOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Duplicar página">
                  <IconButton size="small" onClick={handleDuplicatePage}>
                    <ContentCopyOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={
                    pages.length <= 1
                      ? t("skillAgent.moodboard.cannotDeleteLastPage")
                      : t("skillAgent.moodboard.deletePageTooltip", {
                          n: pageIdx + 1,
                        })
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      disabled={pages.length <= 1}
                      onClick={() => setPageRemoveOpen(true)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={t("skillAgent.moodboard.aviatorTemplate")}>
                  <IconButton
                    size="small"
                    onClick={() => setAviatorConfirmOpen(true)}
                  >
                    <ViewQuiltOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t("skillAgent.moodboard.autoPagesTooltip")}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={busy || !assets.length}
                      onClick={() => setAutoPagesConfirmOpen(true)}
                    >
                      <GridViewOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </ToolGroup>

              {selectedSlotIds.size ? (
                <ToolGroup>
                  <Tooltip
                    title={
                      multiSelected
                        ? t("skillAgent.moodboard.toggleShapeGroup")
                        : activeSlot?.clip || activeSlot?.shape === "circle"
                          ? t("skillAgent.moodboard.toggleToSquare")
                          : t("skillAgent.moodboard.toggleToCircle")
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={!selectedSlotIds.size}
                        onClick={handleToggleSlotShape}
                      >
                        {activeSlot?.clip || activeSlot?.shape === "circle" ? (
                          <ShapeToSquareIcon />
                        ) : (
                          <ShapeToCircleIcon />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      multiSelected
                        ? t("skillAgent.moodboard.bringFrontGroup", {
                            n: selectedSlotIds.size,
                          })
                        : t("skillAgent.moodboard.bringFront")
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={!selectedSlotIds.size}
                        onClick={handleBringToFront}
                      >
                        <FlipToFrontOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      multiSelected
                        ? t("skillAgent.moodboard.sendBackGroup", {
                            n: selectedSlotIds.size,
                          })
                        : t("skillAgent.moodboard.sendBack")
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={!selectedSlotIds.size}
                        onClick={handleSendToBack}
                      >
                        <FlipToBackOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      removeBgAssetIds.length > 1
                        ? t("skillAgent.moodboard.removeBgMany", {
                            n: removeBgAssetIds.length,
                          })
                        : removeBgHasCutout || activeAssetIsCutout
                          ? t("skillAgent.moodboard.removeBgCutout")
                          : t("skillAgent.moodboard.removeBg")
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        color="primary"
                        disabled={busy || !removeBgAssetIds.length}
                        onClick={handleRemoveBackground}
                      >
                        <AutoFixHighOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={t("skillAgent.moodboard.stroke")}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={busy || !activeSlot?.assetId}
                        onClick={(e) => setStrokeAnchor(e.currentTarget)}
                      >
                        <BorderColorOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={t("skillAgent.moodboard.crop")}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={busy || !activeSlot?.assetId}
                        onClick={() => setCropOpen(true)}
                      >
                        <CropOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {!boardApi ? (
                    <Tooltip
                      title={t("skillAgent.moodboard.generateAiTooltip", {
                        defaultValue:
                          "Generar IA: Prompt, Foto o Sketch sobre el board",
                      })}
                    >
                      <span>
                        <IconButton
                          size="small"
                          color="primary"
                          disabled={busy || !moodboardId}
                          onClick={() => setGenerateOpen(true)}
                        >
                          <AutoAwesomeOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : null}
                  {!boardApi ? (
                    <Tooltip title="Editar zona (inpaint)">
                      <span>
                        <IconButton
                          size="small"
                          disabled={busy || !activeSlot?.assetId}
                          onClick={openInpaint}
                        >
                          <BrushOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : null}
                  <Tooltip title="Capas">
                    <IconButton
                      size="small"
                      color={layersOpen ? "primary" : "default"}
                      onClick={() => setLayersOpen(true)}
                    >
                      <LayersOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ToolGroup>
              ) : null}

              {selectedSlotIds.size >= 2 ? (
                <ToolGroup>
                  <Tooltip title="Alinear izquierda">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleAlignSlots("left")}
                      >
                        <AlignHorizontalLeftOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Alinear centro H">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleAlignSlots("hcenter")}
                      >
                        <AlignHorizontalCenterOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Alinear derecha">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleAlignSlots("right")}
                      >
                        <AlignHorizontalRightOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Alinear arriba">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleAlignSlots("top")}
                      >
                        <AlignVerticalTopOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Alinear centro V">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleAlignSlots("vcenter")}
                      >
                        <AlignVerticalCenterOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Alinear abajo">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleAlignSlots("bottom")}
                      >
                        <AlignVerticalBottomOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Distribuir horizontal">
                    <span>
                      <IconButton
                        size="small"
                        disabled={selectedSlotIds.size < 3}
                        onClick={() => handleDistributeSlots("x")}
                      >
                        <SwapHorizOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Distribuir vertical">
                    <span>
                      <IconButton
                        size="small"
                        disabled={selectedSlotIds.size < 3}
                        onClick={() => handleDistributeSlots("y")}
                      >
                        <SwapVertOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </ToolGroup>
              ) : null}

              {(markTool ||
                eyedropperMode ||
                selectedMarkIds.size ||
                textDraft ||
                showTextStyleControls) && (
                <ToolGroup>
                  <Tooltip title={t("skillAgent.moodboard.markColor")}>
                    <Box
                      component="input"
                      type="color"
                      value={
                        /^#[0-9a-fA-F]{6}$/i.test(String(markColor || ""))
                          ? String(markColor).toLowerCase()
                          : DEFAULT_MARK_COLOR
                      }
                      onChange={(e) => applyMarkColor(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        width: 26,
                        height: 26,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        p: 0,
                        bgcolor: "transparent",
                        cursor: "pointer",
                        flexShrink: 0,
                      }}
                    />
                  </Tooltip>
                  {showTextStyleControls ? (
                    <>
                      <Tooltip title={t("skillAgent.moodboard.markFontFamily")}>
                        <Box
                          component="select"
                          value={markFontFamily}
                          onChange={(e) => {
                            const next = normalizeMarkFontFamily(
                              e.target.value,
                            );
                            setMarkFontFamily(next);
                            patchSelectedMark({ fontFamily: next });
                            setTextDraft((prev) =>
                              prev ? { ...prev, fontFamily: next } : prev,
                            );
                          }}
                          aria-label={t("skillAgent.moodboard.markFontFamily")}
                          sx={{
                            width: 148,
                            minWidth: 148,
                            maxWidth: 168,
                            height: 26,
                            flexShrink: 0,
                            position: "relative",
                            zIndex: 2,
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                            px: 0.5,
                            bgcolor: "background.paper",
                            cursor: "pointer",
                            fontFamily: markFontCss(markFontFamily),
                          }}
                        >
                          {markFontGroups().map((group) => (
                            <optgroup
                              key={group.id}
                              label={t(
                                `skillAgent.moodboard.fontGroup.${group.id}`,
                              )}
                            >
                              {group.families.map((family) => (
                                <option
                                  key={family.id}
                                  value={family.id}
                                  style={{ fontFamily: family.css }}
                                >
                                  {family.label}
                                </option>
                              ))}
                            </optgroup>
                          ))}
                        </Box>
                      </Tooltip>
                      <Tooltip title={t("skillAgent.moodboard.markFontSize")}>
                        <Box
                          component="select"
                          value={markFontSize}
                          onChange={(e) => {
                            const next = clampMarkFontSize(e.target.value, 14);
                            setMarkFontSize(next);
                            patchSelectedMark({ fontSize: next });
                            setTextDraft((prev) =>
                              prev ? { ...prev, fontSize: next } : prev,
                            );
                          }}
                          aria-label={t("skillAgent.moodboard.markFontSize")}
                          sx={{
                            width: 58,
                            minWidth: 58,
                            height: 26,
                            flexShrink: 0,
                            position: "relative",
                            zIndex: 2,
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                            px: 0.5,
                            bgcolor: "background.paper",
                            cursor: "pointer",
                          }}
                        >
                          {markFontSizeOptions(markFontSize).map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </Box>
                      </Tooltip>
                    </>
                  ) : null}
                  <Tooltip
                    title={t("skillAgent.moodboard.markStroke", {
                      n: markStroke,
                    })}
                  >
                    <Box
                      sx={{
                        width: 72,
                        minWidth: 72,
                        ml: 1,
                        px: 1.25,
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        position: "relative",
                        zIndex: 1,
                      }}
                    >
                      <Slider
                        size="small"
                        min={0}
                        max={12}
                        step={0.5}
                        value={markStroke}
                        onChange={(_, v) => {
                          const next = Number(v);
                          setMarkStroke(next);
                          patchSelectedMark({ stroke: next });
                          setTextDraft((prev) => {
                            if (!prev) return prev;
                            const updated = { ...prev, stroke: next };
                            textDraftRef.current = updated;
                            return updated;
                          });
                        }}
                        aria-label={t("skillAgent.moodboard.markStrokeAria")}
                        sx={{ py: 0.75, mx: 0 }}
                      />
                    </Box>
                  </Tooltip>
                </ToolGroup>
              )}

              <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

              <Tooltip
                title={
                  saveState === "error"
                    ? t("skillAgent.moodboard.saveTooltip.error")
                    : saveState === "unsaved"
                      ? t("skillAgent.moodboard.saveTooltip.unsaved")
                      : saveState === "saving"
                        ? t("skillAgent.moodboard.saveTooltip.saving")
                        : t("skillAgent.moodboard.saveTooltip.saved")
                }
              >
                <Button
                  size="small"
                  variant="text"
                  disabled={busy || saveState === "saving"}
                  onClick={handleSaveAll}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    flexShrink: 0,
                    minWidth: 0,
                    px: 1,
                    color:
                      saveState === "error"
                        ? "error.main"
                        : saveState === "unsaved"
                          ? "warning.dark"
                          : "text.secondary",
                  }}
                >
                  {saveState === "saving"
                    ? t("skillAgent.moodboard.saveState.saving")
                    : saveState === "unsaved"
                      ? t("skillAgent.moodboard.saveState.unsaved")
                      : saveState === "error"
                        ? t("skillAgent.moodboard.saveState.error")
                        : t("skillAgent.moodboard.saveState.saved")}
                </Button>
              </Tooltip>
            </Stack>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "row",
            overflow: "hidden",
          }}
        >
          <MoodboardVcToolbar
            activeId={activeVcToolId({
              markTool,
              panInsideMode,
              handMode,
              zoomMode,
              multiSelectMode,
              eyedropperMode,
              freeTransformMode,
              spaceHeld,
            })}
            onSelectTool={(tool) => {
              if (!tool?.ready) return;
              if (tool.kind !== "nav") {
                handKeyHeldRef.current = false;
                zoomKeyHeldRef.current = false;
                setZoomMode(false);
                setHandMode(false);
              }
              if (tool.kind === "select") {
                commitTextDraft();
                setMarkTool(null);
                setDraftMark(null);
                setPanInsideMode(false);
                setEyedropperMode(false);
                setMultiSelectMode(false);
                setFreeTransformMode(false);
                setOkMsg(
                  "Selección (V) · arrastra el marco · Alt/Option+arrastrar o doble clic = mover foto dentro",
                );
                return;
              }
              if (tool.kind === "mark") {
                commitTextDraft();
                setEyedropperMode(false);
                setMultiSelectMode(false);
                if (tool.mark === "artboard") {
                  activateArtboardTool();
                  return;
                }
                if (tool.mark === "brush") {
                  setMarkStroke((s) => Math.max(Number(s) || 2, 4.5));
                }
                if (tool.mark === "blob") {
                  setMarkStroke((s) => Math.max(Number(s) || 2, 10));
                }
                setMarkTool(tool.mark || null);
                if (
                  tool.mark === "text" ||
                  tool.mark === "areaType" ||
                  tool.mark === "verticalAreaType" ||
                  tool.mark === "verticalType" ||
                  tool.mark === "touchType"
                ) {
                  setVcRightPanel("character");
                  if (tool.mark === "verticalAreaType") {
                    setOkMsg(
                      "Texto área vertical: arrastra un rectángulo · escribe en Character",
                    );
                  }
                  if (tool.mark === "touchType") {
                    setOkMsg(
                      "Touch Type: clic en una letra y arrastra para mover el glifo",
                    );
                  }
                }
                if (
                  [
                    "warp",
                    "twirl",
                    "pucker",
                    "bloat",
                    "scallop",
                    "crystallize",
                    "wrinkle",
                    "reshape",
                    "width",
                  ].includes(tool.mark)
                ) {
                  setVcRightPanel("appearance");
                  setOkMsg(
                    tool.mark === "width"
                      ? "Anchura: arrastra sobre el trazo = punto de perfil · Alt = más fino · interior = stroke uniforme"
                      : `Liquify · ${tool.label || tool.mark}: pinta sobre formas · radio/fuerza en Appearance`,
                  );
                }
                if (tool.mark === "puppetWarp") {
                  setVcRightPanel("appearance");
                  const ids = [...selectedMarkIdsRef.current];
                  if (!ids.length && selectedMarkId) {
                    ids.push(String(selectedMarkId));
                  }
                  if (ids.length) {
                    updateCurrentPageMarks((marks) =>
                      marks.map((m) => {
                        if (!ids.includes(String(m.id))) return m;
                        return ensurePuppetState(m) || m;
                      }),
                    );
                  }
                  setOkMsg(
                    ids.length
                      ? "Puppet Warp: arrastra pines · clic en la forma = añadir pin"
                      : "Puppet Warp: selecciona una forma y vuelve a pulsar (o clic en ella)",
                  );
                }
                if (tool.mark === "objectsOnPath") {
                  const ids = [...selectedMarkIdsRef.current];
                  if (!ids.length && selectedMarkId) {
                    ids.push(String(selectedMarkId));
                  }
                  const resolved = resolveObjectsOnPathTargets(
                    pagesRef.current[pageIdx]?.marks || [],
                    ids,
                  );
                  if (resolved) {
                    handleObjectsOnPathAttach(resolved.pathId);
                  } else {
                    setOkMsg(
                      "Objects on Path: selecciona formas y clic en el path destino",
                    );
                  }
                }
                if (tool.mark === "perspectiveSelect") {
                  if (!pagesRef.current[pageIdx]?.perspectiveGrid) {
                    patchPerspectiveGrid({
                      ...defaultPerspectiveGrid(),
                      visible: true,
                    });
                  } else {
                    patchPerspectiveGrid({ visible: true });
                  }
                  setVcRightPanel("artboards");
                  setOkMsg(
                    "Perspective Selection: arrastra en el plano activo (1/2/3 en Artboards)",
                  );
                }
                if (tool.mark === "symbolShifter") {
                  setVcRightPanel("symbols");
                  setOkMsg(
                    "Symbol Shifter: arrastra para mover instancias · Alt = invertir",
                  );
                }
                if (tool.mark === "symbolSprayer") {
                  setVcRightPanel("symbols");
                  const syms = pagesRef.current[pageIdx]?.symbols || [];
                  if (!syms.length) {
                    setOkMsg(
                      "Symbol Sprayer: selecciona una forma → Define Symbol, luego pinta",
                    );
                  } else {
                    if (!activeSymbolIdRef.current) {
                      setActiveSymbolId(syms[0].id);
                    }
                    setOkMsg(
                      "Symbol Sprayer: arrastra para pintar · Alt = borrar instancias",
                    );
                  }
                }
                if (tool.mark === "symbolScruncher") {
                  setVcRightPanel("symbols");
                  setOkMsg(
                    "Symbol Scruncher: arrastra para juntar · Alt = dispersar",
                  );
                }
                if (tool.mark === "symbolSizer") {
                  setVcRightPanel("symbols");
                  setOkMsg(
                    "Symbol Sizer: arrastra para agrandar · Alt = reducir",
                  );
                }
                if (tool.mark === "symbolSpinner") {
                  setVcRightPanel("symbols");
                  setOkMsg(
                    "Symbol Spinner: arrastra para rotar · Alt = sentido contrario",
                  );
                }
                if (tool.mark === "symbolStainer") {
                  setVcRightPanel("symbols");
                  setOkMsg(
                    "Symbol Stainer: arrastra = teñir con color activo · Alt = destiñir",
                  );
                }
                if (tool.mark === "symbolScreener") {
                  setVcRightPanel("symbols");
                  setOkMsg(
                    "Symbol Screener: arrastra = más transparente · Alt = más opaco",
                  );
                }
                if (tool.mark === "rotateDrag") {
                  setOkMsg(
                    "Rotar: arrastra alrededor del centro · Shift = 45° · Shift+R = +90°",
                  );
                }
                if (tool.mark === "scaleDrag") {
                  setOkMsg(
                    "Escalar: arrastra desde el centro · Shift = pasos 0.1 · Shift+S = +10%",
                  );
                }
                if (tool.mark === "shearDrag") {
                  setOkMsg(
                    "Inclinar: arrastra horizontal/vertical · Shift = un solo eje",
                  );
                }
                if (tool.mark === "polygon") {
                  try {
                    const cur = shapeOptsRef.current;
                    const raw = window.prompt(
                      "Polígono · lados (3–24)",
                      String(cur.sides),
                    );
                    if (raw != null) {
                      const sides = Math.max(
                        3,
                        Math.min(24, Math.round(Number(raw) || cur.sides)),
                      );
                      setShapeOpts((o) => ({ ...o, sides }));
                      setOkMsg(`Polígono ${sides} lados: arrastra en la mesa`);
                    }
                  } catch {
                    setOkMsg("Polígono: arrastra en la mesa");
                  }
                }
                if (tool.mark === "star") {
                  try {
                    const cur = shapeOptsRef.current;
                    const raw = window.prompt(
                      "Estrella · puntas (3–16)",
                      String(cur.tips),
                    );
                    if (raw != null) {
                      const tips = Math.max(
                        3,
                        Math.min(16, Math.round(Number(raw) || cur.tips)),
                      );
                      setShapeOpts((o) => ({ ...o, tips }));
                      setOkMsg(`Estrella ${tips} puntas: arrastra en la mesa`);
                    }
                  } catch {
                    setOkMsg("Estrella: arrastra en la mesa");
                  }
                }
                if (tool.mark === "arc") {
                  setOkMsg(
                    "Arco: arrastra — el barrido sigue al cursor · Shift = 45°",
                  );
                }
                if (tool.mark === "roundrect") {
                  try {
                    const cur = shapeOptsRef.current;
                    const raw = window.prompt(
                      "Rect redondeado · radio (0.01–0.2) · Alt+arrastrar ajusta",
                      String(cur.rx ?? 0.04),
                    );
                    if (raw != null) {
                      const rx = Math.min(
                        0.25,
                        Math.max(0.005, Number(raw) || cur.rx || 0.04),
                      );
                      setShapeOpts((o) => ({ ...o, rx }));
                      setOkMsg(
                        `Round rect rx=${rx.toFixed(2)} · Alt+arrastrar cambia radio`,
                      );
                    }
                  } catch {
                    setOkMsg("Round rect: arrastra · Alt = radio");
                  }
                }
                if (tool.mark === "magicWand") {
                  setVcRightPanel("appearance");
                  setOkMsg(
                    "Varita: clic en una marca · selecciona color similar · tol en Appearance",
                  );
                }
                if (tool.mark === "livePaintBucket") {
                  setVcRightPanel("appearance");
                  setOkMsg(
                    "Live Paint (K): clic en letras = texto · en la caja = barra · Shift+L = selección",
                  );
                }
                if (tool.mark === "livePaintSelection") {
                  setOkMsg(
                    "Live Paint Selection (Shift+L): clic selecciona cara · Shift = añadir",
                  );
                }
                if (tool.mark === "pathEraser") {
                  setOkMsg(
                    "Borrador de trazos: arrastra sobre el path — corta el stroke en dos",
                  );
                }
                const keepSelection = [
                  "directSelect",
                  "addAnchor",
                  "deleteAnchor",
                  "anchorPoint",
                  "scissors",
                  "lasso",
                  "magicWand",
                  "livePaintBucket",
                  "livePaintSelection",
                  "pathEraser",
                ].includes(tool.mark);
                if (tool.mark === "shaper") {
                  setOkMsg(
                    "Shaper: dibuja rectángulo, elipse, triángulo… · zig-zag borra",
                  );
                }
                if (tool.mark === "curvature") {
                  setOkMsg("Curvature: clic para puntos · Enter para fijar");
                }
                if (tool.mark === "grid") {
                  try {
                    const cur = gridOptsRef.current;
                    const raw = window.prompt(
                      "Cuadrícula · columnas × filas (ej. 6x4)",
                      `${cur.cols}x${cur.rows}`,
                    );
                    if (raw != null) {
                      const next = parseGridDivisions(raw, cur);
                      setGridOpts((o) => ({ ...o, ...next }));
                      setOkMsg(
                        `Grid ${next.cols}×${next.rows}: arrastra en la mesa · Appearance para editar`,
                      );
                    } else {
                      setOkMsg(
                        `Grid ${cur.cols}×${cur.rows}: arrastra en la mesa`,
                      );
                    }
                  } catch {
                    setOkMsg("Grid: arrastra en la mesa");
                  }
                }
                if (tool.mark === "polarGrid" || tool.mark === "flare") {
                  try {
                    const cur = gridOptsRef.current;
                    const label =
                      tool.mark === "polarGrid" ? "Polar Grid" : "Flare";
                    const raw = window.prompt(
                      `${label} · anillos × rayos (ej. 5x12)`,
                      `${cur.rings}x${cur.rays}`,
                    );
                    if (raw != null) {
                      const next = parsePolarDivisions(raw, cur);
                      setGridOpts((o) => ({ ...o, ...next }));
                      setOkMsg(
                        `${label} ${next.rings}×${next.rays}: arrastra · Appearance para editar`,
                      );
                    } else {
                      setOkMsg(
                        `${label} ${cur.rings}×${cur.rays}: arrastra en la mesa`,
                      );
                    }
                  } catch {
                    setOkMsg(
                      tool.mark === "polarGrid"
                        ? "Polar Grid: arrastra en la mesa"
                        : "Flare: arrastra en la mesa",
                    );
                  }
                }
                if (tool.mark === "spiral") {
                  try {
                    const cur = spiralOptsRef.current;
                    const raw = window.prompt(
                      "Espiral · vueltas (1–8) · añade 'log' para logarítmica (ej. 4 o 4 log)",
                      cur.kind === "log"
                        ? `${cur.turns} log`
                        : String(cur.turns),
                    );
                    if (raw != null) {
                      const turns = clampSpiralTurns(
                        String(raw).match(/(\d+)/)?.[1],
                        cur.turns,
                      );
                      const kind = /log/i.test(raw) ? "log" : "archimedean";
                      setSpiralOpts({ turns, kind });
                      setOkMsg(
                        `Espiral ${turns} vueltas${kind === "log" ? " · log" : ""} · Shift=5 · arrastra desde el centro`,
                      );
                    } else {
                      setOkMsg(
                        `Espiral ${cur.turns} vueltas: arrastra desde el centro`,
                      );
                    }
                  } catch {
                    setOkMsg("Espiral: arrastra desde el centro");
                  }
                }
                if (tool.mark === "dimension" || tool.mark === "measure") {
                  setVcRightPanel("appearance");
                  setOkMsg(
                    tool.mark === "measure"
                      ? "Medición: arrastra · se guarda como cota · Appearance = % / mm"
                      : "Cota: arrastra entre dos puntos · Appearance = unidad % / mm",
                  );
                }
                if (!keepSelection) {
                  clearMarkSelection();
                  clearSlotSelection();
                  clearChromeSelection();
                } else {
                  clearSlotSelection();
                  clearChromeSelection();
                }
                setPanInsideMode(false);
                return;
              }
              if (tool.kind === "slot" && tool.action === "addSlot") {
                handleAddSlot();
                setMarkTool(null);
                setEyedropperMode(false);
                return;
              }
              if (tool.kind === "nav") {
                if (tool.action === "hand") {
                  // Sticky desde barra (V / Esc para salir). H tecla = temporal.
                  handKeyHeldRef.current = false;
                  zoomKeyHeldRef.current = false;
                  setHandMode(true);
                  setZoomMode(false);
                  setMarkTool(null);
                  setPanInsideMode(false);
                  setEyedropperMode(false);
                  setFreeTransformMode(false);
                  setOkMsg(
                    "Mano · arrastra el tablero · H/Espacio = temporal · V/Esc = selección",
                  );
                  return;
                }
                if (tool.action === "zoom") {
                  zoomKeyHeldRef.current = false;
                  handKeyHeldRef.current = false;
                  setZoomMode(true);
                  setHandMode(false);
                  setMarkTool(null);
                  setPanInsideMode(false);
                  setEyedropperMode(false);
                  setOkMsg(
                    "Zoom · sticky · clic = + · Ctrl o Alt+clic = − · V/Esc = selección",
                  );
                  return;
                }
              }
              if (tool.kind === "meta") {
                if (tool.action === "toggleBoard") {
                  // Compat: el icono Mesa ahora es mark "artboard"; el toggle
                  // página/tablero sigue en la barra superior.
                  setWorkspaceModeSafe(
                    workspaceMode === WORKSPACE_MODE_BOARD
                      ? WORKSPACE_MODE_PAGE
                      : WORKSPACE_MODE_BOARD,
                  );
                  return;
                }
                if (tool.action === "groupSelection") {
                  setMarkTool(null);
                  setEyedropperMode(false);
                  setMultiSelectMode(true);
                  setPanInsideMode(false);
                  setFreeTransformMode(false);
                  setOkMsg(
                    "Selección de grupos · clic = grupo entero · Ctrl+G agrupa · Ctrl+Shift+G desagrupa · A = una marca",
                  );
                  return;
                }
                if (tool.action === "groupMarks") {
                  handleGroupMarks();
                  return;
                }
                if (tool.action === "ungroupMarks") {
                  handleUngroupMarks();
                  return;
                }
                if (
                  typeof tool.action === "string" &&
                  tool.action.startsWith("align:")
                ) {
                  const edge = tool.action.slice("align:".length);
                  setMarkTool(null);
                  setEyedropperMode(false);
                  setMultiSelectMode(true);
                  handleAlignSlots(edge);
                  return;
                }
                if (
                  typeof tool.action === "string" &&
                  tool.action.startsWith("distribute:")
                ) {
                  const axis = tool.action.slice("distribute:".length);
                  setMarkTool(null);
                  setEyedropperMode(false);
                  setMultiSelectMode(true);
                  handleDistributeSlots(axis);
                  return;
                }
                if (tool.action === "rotateSelection") {
                  // Compat: si llega meta antigua → modo interactivo
                  setMarkTool("rotateDrag");
                  setEyedropperMode(false);
                  setFreeTransformMode(false);
                  setOkMsg(
                    "Rotar: arrastra alrededor del centro · Shift = 45° · Shift+R = +90°",
                  );
                  return;
                }
                if (tool.action === "scaleSelection") {
                  setMarkTool("scaleDrag");
                  setEyedropperMode(false);
                  setFreeTransformMode(false);
                  setOkMsg(
                    "Escalar: arrastra desde el centro · Shift = pasos 0.1 · Shift+S = +10%",
                  );
                  return;
                }
                if (tool.action === "reflectSelection") {
                  handleReflectSelection();
                  setEyedropperMode(false);
                  return;
                }
                if (tool.action === "flipVertical") {
                  handleFlipVertical();
                  setEyedropperMode(false);
                  return;
                }
                if (tool.action === "bringToFront") {
                  handleBringToFront();
                  setEyedropperMode(false);
                  return;
                }
                if (tool.action === "sendToBack") {
                  handleSendToBack();
                  setEyedropperMode(false);
                  return;
                }
                if (tool.action === "eyedropper") {
                  commitTextDraft();
                  setMarkTool(null);
                  setEyedropperMode(true);
                  setMultiSelectMode(false);
                  setPanInsideMode(false);
                  return;
                }
                if (tool.action === "smoothSelection") {
                  setVcRightPanel("appearance");
                  handleSmoothSelection();
                  setEyedropperMode(false);
                  return;
                }
                if (tool.action === "offsetPath") {
                  handleOffsetPath();
                  setEyedropperMode(false);
                  return;
                }
                if (tool.action === "outlineStroke") {
                  handleOutlineStroke();
                  setEyedropperMode(false);
                  return;
                }
                if (tool.action === "simplifyPath") {
                  handleSimplifyPath();
                  setEyedropperMode(false);
                  return;
                }
                if (tool.action === "createOutlines") {
                  handleCreateOutlines();
                  setEyedropperMode(false);
                  return;
                }
                if (tool.action === "averageAnchors") {
                  handleAverageAnchors();
                  setEyedropperMode(false);
                  return;
                }
                if (
                  tool.action === "applyGradient" ||
                  tool.action === "openGradientPanel"
                ) {
                  setEyedropperMode(false);
                  setVcRightPanel("gradient");
                  return;
                }
                if (tool.action === "createGradientMesh") {
                  setEyedropperMode(false);
                  handleCreateGradientMesh();
                  return;
                }
                if (tool.action === "openCharacterPanel") {
                  setEyedropperMode(false);
                  setVcRightPanel("character");
                  return;
                }
                if (tool.action === "openParagraphPanel") {
                  setEyedropperMode(false);
                  setVcRightPanel("paragraph");
                  return;
                }
                if (tool.action === "shearSelection") {
                  setMarkTool("shearDrag");
                  setEyedropperMode(false);
                  setFreeTransformMode(false);
                  setOkMsg(
                    "Inclinar: arrastra H/V · Shift = un eje · (antes: meta fija)",
                  );
                  return;
                }
                if (tool.action === "duplicateSelection") {
                  if (copyMoodboardSelection()) {
                    pasteMoodboardSelection();
                  } else {
                    setOkMsg("Nada seleccionado para duplicar");
                  }
                  setEyedropperMode(false);
                  return;
                }
                if (tool.action === "joinPaths") {
                  handleJoinPaths();
                  setEyedropperMode(false);
                  return;
                }
                if (tool.action === "envelopeWarp") {
                  setVcRightPanel("appearance");
                  handleEnvelopeWarp();
                  setEyedropperMode(false);
                  return;
                }
                if (tool.action === "envelopeMesh") {
                  setVcRightPanel("appearance");
                  handleEnvelopeMesh();
                  setEyedropperMode(false);
                  return;
                }
                if (tool.action === "perspectiveGrid") {
                  handleTogglePerspectiveGrid();
                  setVcRightPanel("artboards");
                  setEyedropperMode(false);
                  return;
                }
                if (tool.action === "expandObjectsOnPath") {
                  handleExpandObjectsOnPath();
                  setEyedropperMode(false);
                  return;
                }
                if (tool.action === "defineSymbol") {
                  handleDefineSymbol();
                  setEyedropperMode(false);
                  return;
                }
                if (tool.action === "blendMarks") {
                  handleBlendMarks();
                  setEyedropperMode(false);
                  return;
                }
                if (tool.action === "typeOnPath") {
                  handleTypeOnPath({ vertical: false });
                  setEyedropperMode(false);
                  return;
                }
                if (tool.action === "verticalTypeOnPath") {
                  handleTypeOnPath({ vertical: true });
                  setEyedropperMode(false);
                  return;
                }
                if (tool.action === "freeTransform") {
                  commitTextDraft();
                  setMarkTool(null);
                  setEyedropperMode(false);
                  setPanInsideMode(false);
                  setHandMode(false);
                  setZoomMode(false);
                  setFreeTransformMode(true);
                  setMultiSelectMode(true);
                  setOkMsg(
                    selectedSlotIdsRef.current.size ||
                      selectedMarkIdsRef.current.size
                      ? "Free Transform: esquinas=escala · asa=rotar · Alt+lado=shear · Shift=proporcional · Esc sale"
                      : "Free Transform activo: selecciona foto(s) o marca(s) — Esc para salir",
                  );
                  return;
                }
                if (
                  tool.action === "shapeBuilder" ||
                  (typeof tool.action === "string" &&
                    tool.action.startsWith("shapeBuilder:"))
                ) {
                  const op =
                    tool.action === "shapeBuilder"
                      ? "unite"
                      : tool.action.slice("shapeBuilder:".length) || "unite";
                  const ids = [...selectedMarkIdsRef.current];
                  if (ids.length < 2) {
                    shapeBuilderOpRef.current = op;
                    setMarkTool("shapeBuilder");
                    setMultiSelectMode(true);
                    setEyedropperMode(false);
                    setFreeTransformMode(false);
                    setOkMsg(
                      `Shape Builder · ${op}: arrastra sobre 2+ formas (o Shift+clic y vuelve a pulsar)`,
                    );
                    return;
                  }
                  notePagesHistory();
                  let createdIds = [];
                  let applied = false;
                  updateCurrentPageMarks(
                    (marks) => {
                      const next = pathfinderMarks(marks, ids, op);
                      if (!next) return marks;
                      applied = true;
                      const drop = new Set(ids.map(String));
                      const restIds = new Set(
                        marks
                          .filter((m) => !drop.has(String(m.id)))
                          .map((m) => String(m.id)),
                      );
                      createdIds = next
                        .filter((m) => !restIds.has(String(m.id)))
                        .map((m) => String(m.id));
                      return next;
                    },
                    { history: false },
                  );
                  if (!applied || !createdIds.length) {
                    setOkMsg(
                      `Pathfinder · ${op}: sin resultado (¿se solapan? usa rect/elipse/poly)`,
                    );
                    setEyedropperMode(false);
                    return;
                  }
                  const set = new Set(createdIds);
                  selectedMarkIdsRef.current = set;
                  setSelectedMarkIds(set);
                  setSelectedMarkId(createdIds[createdIds.length - 1]);
                  const labels = {
                    unite: "Unir",
                    minus: "Restar",
                    intersect: "Intersectar",
                    exclude: "Excluir",
                  };
                  setOkMsg(
                    `Pathfinder · ${labels[op] || op}${
                      createdIds.length > 1
                        ? ` · ${createdIds.length} piezas`
                        : ""
                    }`,
                  );
                  setEyedropperMode(false);
                  return;
                }
                if (tool.action === "makeClippingMask") {
                  handleMakeClippingMask();
                  setEyedropperMode(false);
                  return;
                }
                if (tool.action === "releaseClippingMask") {
                  handleReleaseClippingMask();
                  setEyedropperMode(false);
                  return;
                }
                if (tool.action === "rotateView") {
                  boardCamUserRef.current = true;
                  setBoardCam((c) => ({
                    ...c,
                    rotation: ((Number(c.rotation) || 0) + 15) % 360,
                  }));
                  setOkMsg("Rotar vista +15° (clic de nuevo para seguir)");
                  setEyedropperMode(false);
                  return;
                }
                if (tool.action === "sendToPasteboard") {
                  const hadSlots = selectedSlotIdsRef.current.size > 0;
                  const hadMarks = selectedMarkIdsRef.current.size > 0;
                  if (hadSlots) moveSelectedSlotsToPasteboard();
                  if (hadMarks) moveMarksToPasteboard(null, null);
                  if (!hadSlots && !hadMarks) {
                    setOkMsg(
                      "Selecciona fotos o marcas en la mesa para sacarlas al fondo",
                    );
                  }
                  setEyedropperMode(false);
                  return;
                }
              }
            }}
          />
          <Box
            ref={bindViewScrollRef}
            onDragEnter={handleCanvasDragEnter}
            onDragLeave={handleCanvasDragLeave}
            onDragOver={handleCanvasDragOver}
            onDrop={handleCanvasDrop}
            onClick={(e) => {
              if (!zoomModeRef.current) return;
              // Click zoom: + = clic · − = Ctrl o Alt/Option+clic
              e.preventDefault();
              e.stopPropagation();
              boardCamUserRef.current = true;
              const zoomOut = Boolean(e.altKey || e.ctrlKey || e.metaKey);
              if (workspaceMode === WORKSPACE_MODE_BOARD) {
                const step = zoomOut
                  ? -BOARD_CAM_ZOOM_STEP
                  : BOARD_CAM_ZOOM_STEP;
                setBoardCam((c) => ({
                  ...c,
                  zoom: clampBoardCamZoom(c.zoom + step),
                }));
              } else {
                setViewZoomManual((z) =>
                  zoomOut ? z - PAGE_VIEW_ZOOM_STEP : z + PAGE_VIEW_ZOOM_STEP,
                );
              }
            }}
            onContextMenu={(e) => {
              if (boardPanActive || zoomMode) return;
              openContextMenu(e, { kind: "canvas" });
            }}
            onPointerDown={(e) => {
              // Mano / Espacio en modo página: arrastrar = scroll del lienzo
              if (zoomModeRef.current) return;
              if (
                workspaceMode !== WORKSPACE_MODE_PAGE ||
                !boardPanActiveRef.current
              ) {
                return;
              }
              if (e.button !== 0 && e.button !== 1) return;
              const el = e.currentTarget;
              e.preventDefault();
              const start = {
                pointerId: e.pointerId,
                x0: e.clientX,
                y0: e.clientY,
                sl: el.scrollLeft,
                st: el.scrollTop,
              };
              const onMove = (ev) => {
                if (ev.pointerId !== start.pointerId) return;
                el.scrollLeft = start.sl - (ev.clientX - start.x0);
                el.scrollTop = start.st - (ev.clientY - start.y0);
              };
              const onUp = (ev) => {
                if (ev.pointerId !== start.pointerId) return;
                window.removeEventListener("pointermove", onMove);
                window.removeEventListener("pointerup", onUp);
              };
              window.addEventListener("pointermove", onMove);
              window.addEventListener("pointerup", onUp);
            }}
            sx={{
              flex: 1,
              minHeight: 0,
              overflow:
                workspaceMode === WORKSPACE_MODE_BOARD ? "hidden" : "auto",
              p:
                workspaceMode === WORKSPACE_MODE_BOARD
                  ? 0
                  : { xs: 1.25, md: 1.75 },
              pt: workspaceMode === WORKSPACE_MODE_BOARD ? 0 : 1.25,
              position: "relative",
              bgcolor:
                workspaceMode === WORKSPACE_MODE_BOARD
                  ? pasteboardBg || DEFAULT_PASTEBOARD_BG
                  : "transparent",
              cursor: zoomMode
                ? "zoom-in"
                : boardPanActive
                  ? "grab"
                  : undefined,
              "&:active": boardPanActive ? { cursor: "grabbing" } : undefined,
            }}
          >
            {fileDropActive ? (
              <Box
                aria-hidden
                sx={{
                  pointerEvents: "none",
                  position: "sticky",
                  top: 8,
                  zIndex: 40,
                  mx: "auto",
                  mb: 1.5,
                  maxWidth: 420,
                  px: 2,
                  py: 1.25,
                  borderRadius: 2.5,
                  border: "1.5px dashed",
                  borderColor: "primary.main",
                  bgcolor: (th) => alpha(th.palette.primary.main, 0.12),
                  backdropFilter: "blur(6px)",
                  textAlign: "center",
                  boxShadow: (th) =>
                    `0 8px 24px ${alpha(th.palette.primary.main, 0.18)}`,
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={800}
                  color="primary.main"
                >
                  {t("skillAgent.moodboard.dropPhotosTitle")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("skillAgent.moodboard.dropPhotosBody")}
                </Typography>
              </Box>
            ) : null}
            {error ? (
              <Alert
                severity="error"
                onClose={() => setError("")}
                sx={{ mb: 1.5 }}
              >
                {error}
              </Alert>
            ) : null}
            <Box
              sx={
                workspaceMode === WORKSPACE_MODE_BOARD
                  ? {
                      height: "100%",
                      minHeight: 0,
                      display: "flex",
                      flexDirection: "column",
                    }
                  : undefined
              }
            >
              {(() => {
                const layers = (
                  <>
                    {layeredSlots.map((slot) => (
                      <FreeformSlotBox
                        key={slot.id}
                        slot={slot}
                        selected={selectedSlotIds.has(slot.id)}
                        multiSelected={multiSelected}
                        hideChrome={multiSelected || freeTransformMode}
                        canvasRef={canvasRef}
                        canRemove
                        onSelect={handleSelectSlot}
                        multiSelectMode={multiSelectMode || freeTransformMode}
                        panInsideMode={panInsideMode}
                        boardPanActive={boardNavLock}
                        onTogglePanInside={() => setPanInsideMode((v) => !v)}
                        onActivatePanInside={() => {
                          handKeyHeldRef.current = false;
                          zoomKeyHeldRef.current = false;
                          setHandMode(false);
                          setZoomMode(false);
                          setMarkTool(null);
                          setPanInsideMode(true);
                          setOkMsg(
                            "Contenido dentro del marco · Alt/Option+arrastrar también · Esc/V = selección",
                          );
                        }}
                        onContextMenu={openContextMenu}
                        onGroupMoveStart={handleGroupMoveStart}
                        onGroupMove={handleGroupMove}
                        onGroupMoveEnd={handleGroupMoveEnd}
                        onHistoryGestureStart={beginPagesHistoryGesture}
                        onHistoryGestureEnd={endPagesHistoryGesture}
                        onDragOffBoard={handleSlotDragOffBoard}
                        onRequestRemove={(id, opts) => {
                          if (opts?.force) handleRemoveSlot(id);
                          else setSlotRemoveId(id);
                        }}
                        onChange={(next) => {
                          updateCurrentPageSlots(
                            (slots) =>
                              slots.map((s) => (s.id === next.id ? next : s)),
                            { coalesceMs: 400, allowOutside: true },
                          );
                        }}
                      />
                    ))}
                    {(multiSelected ||
                      (freeTransformMode && selectedSlots.length >= 1)) &&
                    selectedSlots.length >= 1 ? (
                      <GroupTransformBox
                        slots={selectedSlots}
                        canvasRef={canvasRef}
                        freeTransform={freeTransformMode || multiSelected}
                        orientation={slotFtOrientation}
                        bboxOverride={slotFtBBoxOverride}
                        onScale={handleGroupScale}
                        onRotate={handleGroupRotate}
                        onShear={handleGroupShear}
                        onHistoryGestureStart={beginPagesHistoryGesture}
                        onHistoryGestureEnd={endPagesHistoryGesture}
                      />
                    ) : null}
                    {(multiMarksSelected ||
                      (freeTransformMode && selectedMarks.length >= 1)) &&
                    selectedMarks.length >= 1 ? (
                      <GroupTransformBox
                        slots={selectedMarks}
                        canvasRef={canvasRef}
                        freeTransform={freeTransformMode || multiMarksSelected}
                        orientation={markFtOrientation}
                        bboxOverride={
                          markFtBBoxOverride || marksBBox(selectedMarks)
                        }
                        scaleWithBBox={scaleMarksWithBBox}
                        rotateAroundBBox={rotateMarksAroundBBox}
                        shearInBBox={shearMarksInBBox}
                        onScale={handleMarkGroupScale}
                        onRotate={handleMarkGroupRotate}
                        onShear={handleMarkGroupShear}
                        onHistoryGestureStart={beginPagesHistoryGesture}
                        onHistoryGestureEnd={endPagesHistoryGesture}
                      />
                    ) : null}
                    {marquee ? (
                      <Box
                        sx={{
                          position: "absolute",
                          left: `${Math.min(marquee.x0, marquee.x1) * 100}%`,
                          top: `${Math.min(marquee.y0, marquee.y1) * 100}%`,
                          width: `${Math.abs(marquee.x1 - marquee.x0) * 100}%`,
                          height: `${Math.abs(marquee.y1 - marquee.y0) * 100}%`,
                          border: "1px solid",
                          borderColor: "primary.main",
                          bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                          pointerEvents: "none",
                          zIndex: 1300,
                        }}
                      />
                    ) : null}

                    <MarksOverlay
                      marks={currentPage.marks || []}
                      tool={eyedropperMode ? "eyedropper" : markTool}
                      boardPanActive={boardNavLock}
                      color={markColor}
                      fillColor={markFillColor}
                      strokeColor={markStrokeColor}
                      stroke={markStroke}
                      draftMark={draftMark}
                      textDraft={textDraft}
                      markFontSize={markFontSize}
                      markFontFamily={markFontFamily}
                      markFontWeight={markFontWeight}
                      markTextAlign={markTextAlign}
                      gridOpts={gridOpts}
                      spiralOpts={spiralOpts}
                      shapeOpts={shapeOpts}
                      selectedMarkId={selectedMarkId}
                      selectedMarkIds={selectedMarkIds}
                      multiSelectMode={multiSelectMode}
                      onSelectMark={handleSelectMark}
                      onEyedropColor={(c) => {
                        if (c) applyMarkColor(c);
                      }}
                      onLivePaintFill={handleLivePaintFill}
                      onTouchCharOffset={(markId, charIndex, dx, dy) => {
                        updateCurrentPageMarks(
                          (marks) =>
                            marks.map((m) => {
                              if (String(m.id) !== String(markId)) return m;
                              return applyTextCharOffset(m, charIndex, dx, dy);
                            }),
                          { coalesceMs: 80, allowOutside: true },
                        );
                      }}
                      onPuppetPinMove={(markId, pinIndex, x, y) => {
                        updateCurrentPageMarks(
                          (marks) =>
                            marks.map((m) => {
                              if (String(m.id) !== String(markId)) return m;
                              return movePuppetPin(m, pinIndex, x, y);
                            }),
                          { coalesceMs: 60, allowOutside: true },
                        );
                      }}
                      onPuppetPinAdd={(markId, x, y) => {
                        updateCurrentPageMarks((marks) =>
                          marks.map((m) => {
                            if (String(m.id) !== String(markId)) return m;
                            const base = m.puppet ? m : ensurePuppetState(m);
                            return addPuppetPin(base || m, x, y);
                          }),
                        );
                      }}
                      onObjectsOnPathAttach={handleObjectsOnPathAttach}
                      onSymbolSprayAt={handleSymbolSprayAt}
                      onSymbolShiftAt={handleSymbolShiftAt}
                      onSymbolScrunchAt={handleSymbolScrunchAt}
                      onSymbolSizeAt={handleSymbolSizeAt}
                      onSymbolSpinAt={handleSymbolSpinAt}
                      onSymbolStainAt={handleSymbolStainAt}
                      onSymbolScreenAt={handleSymbolScreenAt}
                      perspectiveGrid={pagePerspectiveGrid}
                      onSplitPath={handleSplitPath}
                      onInsertAnchor={handleInsertAnchor}
                      onDeleteAnchor={handleDeleteAnchor}
                      onMoveAnchor={handleMoveAnchor}
                      onLassoSelect={handleLassoSelect}
                      onMagicWand={handleMagicWand}
                      onMeasureDone={handleMeasureDone}
                      onKnifeCut={handleKnifeCut}
                      onRectCut={handleRectCut}
                      onMirrorCut={handleMirrorCut}
                      onDistortAt={handleDistortAt}
                      onWidthAt={handleWidthAt}
                      onLiveTransform={handleLiveTransform}
                      onShaperFinish={handleShaperFinish}
                      onShapeBuilderStroke={handleShapeBuilderStroke}
                      getDraftMark={() => draftMarkRef.current}
                      onStartAreaText={(x, y, w, h, opts = {}) => {
                        commitTextDraft();
                        const fill =
                          markFillColor && markFillColor !== "none"
                            ? markFillColor
                            : markColor;
                        const wm = normalizeMarkWritingMode(opts.writingMode);
                        const next = {
                          x,
                          y,
                          w: Math.min(
                            0.9,
                            Math.max(
                              wm === "vertical-rl" ? 0.06 : 0.08,
                              Number(w) || 0.22,
                            ),
                          ),
                          h: Math.min(0.9, Math.max(0.04, Number(h) || 0.08)),
                          value: "",
                          fontSize: markFontSize,
                          fontFamily: markFontFamily,
                          fontWeight: markFontWeight,
                          textAlign: markTextAlign,
                          writingMode: wm,
                          color: fill,
                          fill,
                          strokeColor: markStrokeColor,
                          stroke: markStroke,
                        };
                        textDraftRef.current = next;
                        setTextDraft(next);
                        setMarkTool("text");
                        setColorTarget("fill");
                        setVcRightPanel("character");
                      }}
                      onMoveMark={handleMoveMark}
                      onMoveMarkGroup={handleMoveMarkGroup}
                      onDragOffBoard={handleMarkDragOffBoard}
                      onPointerNorm={handleDraftMark}
                      onCommitMark={handleCommitDraftMark}
                      onAbsorbPath={(id) => {
                        // No borrar ya: un clic en la foto de la misma mesa
                        // absorbía el trazo y, si el draft no llegaba a commit,
                        // el vector desaparecía. Se quita al confirmar el trazo.
                        if (id == null) return;
                        clearMarkSelection();
                      }}
                      onEraseAt={handleEraseAt}
                      onEraseAlong={handleEraseAlong}
                      onHistoryGestureStart={beginPagesHistoryGesture}
                      onHistoryGestureEnd={endPagesHistoryGesture}
                      onTextFontSize={(fs) => {
                        const next = clampMarkFontSize(fs, 14);
                        setMarkFontSize(next);
                        setTextDraft((prev) =>
                          prev ? { ...prev, fontSize: next } : prev,
                        );
                      }}
                      onPatchTextDraft={(patch) => {
                        setTextDraft((prev) => {
                          if (!prev) return prev;
                          const next = { ...prev, ...patch };
                          textDraftRef.current = next;
                          return next;
                        });
                      }}
                      onStartText={(x, y, opts = {}) => {
                        commitTextDraft();
                        const fill =
                          markFillColor && markFillColor !== "none"
                            ? markFillColor
                            : markColor;
                        const next = {
                          x,
                          y,
                          w: opts.writingMode === "vertical-rl" ? 0.08 : 0.22,
                          value: "",
                          fontSize: markFontSize,
                          fontFamily: markFontFamily,
                          fontWeight: markFontWeight,
                          textAlign: markTextAlign,
                          writingMode: normalizeMarkWritingMode(
                            opts.writingMode,
                          ),
                          color: fill,
                          fill,
                          strokeColor: markStrokeColor,
                          stroke: markStroke,
                        };
                        textDraftRef.current = next;
                        setTextDraft(next);
                        setColorTarget("fill");
                        setVcRightPanel("character");
                      }}
                      onEditText={(mark) => {
                        if (!mark || mark.type !== "text") return;
                        commitTextDraft();
                        const fs = Number(mark.fontSize) || 14;
                        const ff = normalizeMarkFontFamily(mark.fontFamily);
                        const fw = normalizeMarkFontWeight(mark.fontWeight);
                        const ta = normalizeMarkTextAlign(mark.textAlign);
                        const paint = markPaintColors(mark);
                        const fill = textMarkFillColor(mark);
                        setMarkFontSize(fs);
                        setMarkFontFamily(ff);
                        setMarkFontWeight(fw);
                        setMarkTextAlign(ta);
                        setMarkFillColor(fill);
                        setMarkStrokeColor(paint.strokeColor);
                        setMarkStroke(Number(mark.stroke) || 0);
                        setMarkColor(fill);
                        setColorTarget("fill");
                        const next = {
                          id: mark.id,
                          x: mark.x,
                          y: mark.y,
                          w: textMarkBoxWidth(mark),
                          value: String(mark.text || ""),
                          fontSize: fs,
                          fontFamily: ff,
                          fontWeight: fw,
                          textAlign: ta,
                          rotation: normalizeRotation(mark.rotation),
                          color: fill,
                          fill,
                          strokeColor: paint.strokeColor,
                          stroke: Number(mark.stroke) || 0,
                        };
                        textDraftRef.current = next;
                        setTextDraft(next);
                        setVcRightPanel("character");
                      }}
                      onChangeTextDraft={(value) =>
                        setTextDraft((prev) => {
                          if (!prev) return prev;
                          const next = { ...prev, value };
                          textDraftRef.current = next;
                          return next;
                        })
                      }
                      onCommitText={() => {
                        commitTextDraft();
                      }}
                      onCancelText={() => {
                        textDraftRef.current = null;
                        setTextDraft(null);
                      }}
                    />
                    {(() => {
                      const mid =
                        selectedMarkId != null
                          ? (currentPage.marks || []).find(
                              (m) => String(m.id) === String(selectedMarkId),
                            )
                          : null;
                      if (!mid || mid.mesh) return null;
                      const axis = gradientAnnotatorAxis(mid);
                      if (!axis) return null;
                      return (
                        <GradientAnnotatorOverlay
                          axis={axis}
                          onAngle={(angle) => {
                            const base = normalizeMarkGradient(
                              mid.gradient,
                              mid.fill || mid.color,
                            ) || {
                              from: axis.from,
                              to: axis.to,
                              kind: axis.kind,
                              angle: 0,
                            };
                            const next = { ...base, angle };
                            patchSelectedMark({ gradient: next });
                            setGradientDraft(next);
                          }}
                        />
                      );
                    })()}
                  </>
                );
                const liveBounds = boardWorldBounds(pages, 0.35, pasteboard);
                const bounds = artboardDragBounds || liveBounds;
                const worldW = Math.max(1, bounds.width) * BOARD_PX_PER_UNIT;
                const worldH = Math.max(1, bounds.height) * BOARD_PX_PER_UNIT;
                if (workspaceMode === WORKSPACE_MODE_BOARD) {
                  return (
                    <Box
                      ref={boardViewportRef}
                      data-board-pan="1"
                      onPointerDown={(e) => {
                        rememberBoardPointer(e.clientX, e.clientY);
                        if (
                          e.target === e.currentTarget ||
                          e.target?.dataset?.boardPan
                        ) {
                          setSelectedPasteboardId(null);
                          clearChromeSelection();
                        }
                        onBoardPanPointerDown(e);
                      }}
                      onPointerMove={(e) => {
                        rememberBoardPointer(e.clientX, e.clientY);
                        onBoardPanPointerMove(e);
                      }}
                      onPointerUp={onBoardPanPointerUp}
                      onPointerCancel={onBoardPanPointerUp}
                      title={t("skillAgent.moodboard.boardPanHint")}
                      sx={{
                        position: "relative",
                        width: "100%",
                        height: "100%",
                        minHeight: 0,
                        overflow: "hidden",
                        borderRadius: 0,
                        bgcolor: pasteboardBg || DEFAULT_PASTEBOARD_BG,
                        border: fileDropActive ? "2px solid" : "none",
                        borderColor: fileDropActive
                          ? "primary.main"
                          : "transparent",
                        cursor: zoomMode
                          ? "zoom-in"
                          : boardPanActive
                            ? "grab"
                            : "default",
                        userSelect: "none",
                        touchAction: "none",
                        "&:active": boardPanActive
                          ? { cursor: "grabbing" }
                          : undefined,
                      }}
                    >
                      <Box
                        data-board-pan="1"
                        data-board-world="1"
                        sx={{
                          position: "absolute",
                          left: 0,
                          top: 0,
                          width: worldW,
                          height: worldH,
                          transform: `translate(${boardCam.panX}px, ${boardCam.panY}px) scale(${boardCam.zoom}) rotate(${Number(boardCam.rotation) || 0}deg)`,
                          transformOrigin: "0 0",
                          willChange: "transform",
                          // Floats pueden quedar con left/top negativos (origen = mesas)
                          overflow: "visible",
                        }}
                      >
                        {pages.map((p, i) => {
                          const b = normalizePageBoard(p?.board, i);
                          const left = (b.x - bounds.minX) * BOARD_PX_PER_UNIT;
                          const top = (b.y - bounds.minY) * BOARD_PX_PER_UNIT;
                          const w = b.w * BOARD_PX_PER_UNIT;
                          const h = b.h * BOARD_PX_PER_UNIT;
                          const active = i === pageIdx;
                          const artboardToolOn = markTool === "artboard";
                          return (
                            <Box
                              key={`artboard-${i}`}
                              sx={{
                                position: "absolute",
                                left,
                                top,
                                width: w,
                                height: h,
                                borderRadius: 1,
                                // visible: al arrastrar la foto puede asomarse fuera de la mesa
                                overflow: "visible",
                                zIndex: active ? 6 : 1,
                                boxShadow: active
                                  ? (th) =>
                                      `0 0 0 3px ${th.palette.primary.main}, 0 12px 28px rgba(0,0,0,0.35)`
                                  : "0 8px 20px rgba(0,0,0,0.28)",
                                outline: artboardToolOn
                                  ? (th) =>
                                      `2px dashed ${th.palette.warning.main}`
                                  : active
                                    ? (th) =>
                                        `2px solid ${th.palette.primary.light}`
                                    : "1px solid rgba(255,255,255,0.12)",
                              }}
                            >
                              {/* Barra siempre: arrastrar mesa sin Rearrange ni tool especial */}
                              <Box
                                data-board-chrome="1"
                                onPointerDown={(e) =>
                                  onArtboardMovePointerDown(i, e)
                                }
                                onPointerMove={onArtboardMovePointerMove}
                                onPointerUp={onArtboardMovePointerUp}
                                onPointerCancel={onArtboardMovePointerUp}
                                title={`Artboard ${i + 1} · arrastra para mover`}
                                sx={{
                                  position: "absolute",
                                  left: 0,
                                  top: -26,
                                  width: "100%",
                                  height: 24,
                                  zIndex: 30,
                                  display: "flex",
                                  alignItems: "center",
                                  px: 0.75,
                                  borderRadius: 0.75,
                                  bgcolor: artboardToolOn
                                    ? (th) => th.palette.warning.main
                                    : "rgba(15,23,42,0.88)",
                                  color: artboardToolOn ? "#111" : "#fff",
                                  fontSize: "0.7rem",
                                  fontWeight: 800,
                                  cursor: boardPanActive ? "grab" : "grab",
                                  userSelect: "none",
                                  touchAction: "none",
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.35)",
                                  "&:active": { cursor: "grabbing" },
                                }}
                              >
                                Artboard {i + 1} · arrastra
                              </Box>
                              {artboardToolOn ? (
                                <Box
                                  data-board-chrome="1"
                                  onPointerDown={(e) =>
                                    onArtboardMovePointerDown(i, e)
                                  }
                                  onPointerMove={onArtboardMovePointerMove}
                                  onPointerUp={onArtboardMovePointerUp}
                                  onPointerCancel={onArtboardMovePointerUp}
                                  sx={{
                                    position: "absolute",
                                    inset: 0,
                                    zIndex: 28,
                                    cursor: "move",
                                    bgcolor: "rgba(255, 193, 7, 0.08)",
                                    touchAction: "none",
                                  }}
                                />
                              ) : null}
                              {/* Preview siempre montado: al activar no parpadean las fotos.
                                  hideSlotIds: la selección solo en la capa interactiva (sin fantasma). */}
                              <MoodboardArtboardPreview
                                page={p}
                                assets={assets}
                                pageLabel={String(i + 1)}
                                active={false}
                                underlay={active}
                                boardNavLock={boardNavLock}
                                hideSlotIds={active ? selectedSlotIds : null}
                                fetchBlob={fetchAssetBlobCached}
                                onSelect={
                                  active
                                    ? undefined
                                    : (opts) => activateBoardPage(i, opts || {})
                                }
                                chromeSeed={{
                                  categoryLabel,
                                  studioLine,
                                  chromeStyle,
                                }}
                              />
                              {active ? (
                                <Box
                                  ref={canvasRef}
                                  onPointerDown={startCanvasMarquee}
                                  onClick={(e) => {
                                    if (boardNavLockRef.current) return;
                                    if (e.target === e.currentTarget) {
                                      clearSlotSelection();
                                      clearChromeSelection();
                                    }
                                  }}
                                  sx={{
                                    position: "absolute",
                                    inset: 0,
                                    width: "100%",
                                    height: "100%",
                                    overflow: "visible",
                                    // transparente: se ve el underlay mientras cargan thumbs
                                    bgcolor: "transparent",
                                    containerType: "size",
                                    containerName: "moodboard",
                                    cursor: zoomMode
                                      ? "zoom-in"
                                      : boardPanActive
                                        ? "grab"
                                        : "default",
                                    zIndex: 2,
                                  }}
                                >
                                  {layers}
                                </Box>
                              ) : null}
                            </Box>
                          );
                        })}
                        {pasteboard.map((item) => (
                          <MoodboardPasteboardItem
                            key={item.id}
                            item={item}
                            bounds={bounds}
                            assets={assets}
                            boardPanActive={boardNavLock}
                            selected={
                              String(selectedPasteboardId) === String(item.id)
                            }
                            fetchBlob={fetchAssetBlobCached}
                            onSelect={(id) => {
                              setSelectedPasteboardId(id);
                              clearSlotSelection();
                              clearMarkSelection();
                              clearChromeSelection();
                            }}
                            onContextMenu={openContextMenu}
                            onMove={(next) => {
                              setPasteboard((prev) => {
                                const normalized = normalizePasteboard(
                                  prev.map((p) =>
                                    String(p.id) === String(item.id)
                                      ? { ...p, ...next }
                                      : p,
                                  ),
                                );
                                pasteboardRef.current = normalized;
                                return normalized;
                              });
                            }}
                            onPlaceOnPage={() =>
                              placePasteboardItemOnPage(item.id, pageIdx)
                            }
                            onDropAt={(clientX, clientY) => {
                              const target = resolveBoardPointerTarget(
                                clientX,
                                clientY,
                              );
                              if (target?.kind !== "page") return;
                              // Revert last pasteboard move to artboard drop point
                              placePasteboardItemOnPage(
                                item.id,
                                target.pageIndex,
                                {
                                  placeAt: target.placeAt || { x: 0.5, y: 0.5 },
                                },
                              );
                            }}
                            onRemove={() => {
                              setPasteboard((prev) => {
                                const normalized = prev.filter(
                                  (p) => String(p.id) !== String(item.id),
                                );
                                pasteboardRef.current = normalized;
                                return normalized;
                              });
                              setSelectedPasteboardId(null);
                            }}
                          />
                        ))}
                      </Box>
                      <Tooltip title="Fondo del tablero">
                        <IconButton
                          size="small"
                          data-board-chrome="1"
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.stopPropagation();
                            setPasteboardBgAnchor(e.currentTarget);
                          }}
                          aria-label="Fondo del tablero"
                          sx={{
                            position: "absolute",
                            left: 12,
                            bottom: 12,
                            zIndex: 6,
                            width: 36,
                            height: 36,
                            bgcolor: "rgba(255,255,255,0.92)",
                            border: "1px solid",
                            borderColor: "rgba(0,0,0,0.18)",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
                            color: "#333",
                            "&:hover": { bgcolor: "#fff" },
                          }}
                        >
                          <Box
                            sx={{ position: "relative", width: 20, height: 20 }}
                          >
                            <PaletteOutlinedIcon sx={{ fontSize: 20 }} />
                            <Box
                              sx={{
                                position: "absolute",
                                right: -2,
                                bottom: -2,
                                width: 9,
                                height: 9,
                                borderRadius: "2px",
                                border: "1px solid #fff",
                                bgcolor: pasteboardBg || DEFAULT_PASTEBOARD_BG,
                              }}
                            />
                          </Box>
                        </IconButton>
                      </Tooltip>
                    </Box>
                  );
                }
                return (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "flex-start",
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        width: `${Math.round(viewZoom * 100)}%`,
                        maxWidth: "100%",
                        flexShrink: 0,
                        alignSelf: "flex-start",
                        bgcolor: "#f3f3f3",
                        border: "1.5px solid",
                        borderColor: fileDropActive
                          ? "primary.main"
                          : "divider",
                        borderRadius: 2,
                        // visible: figuras/textos pueden salir de la mesa sin recorte
                        overflow: "visible",
                        boxShadow: fileDropActive
                          ? (th) =>
                              `0 0 0 3px ${alpha(th.palette.primary.main, 0.25)}`
                          : "0 1px 2px rgba(15,23,42,0.04)",
                        transition: "border-color 0.15s, box-shadow 0.15s",
                      }}
                    >
                      <Box
                        aria-hidden
                        sx={{
                          width: "100%",
                          paddingBottom: `${(210 / 297) * 100}%`,
                          pointerEvents: "none",
                        }}
                      />
                      <Box
                        ref={canvasRef}
                        onPointerDown={startCanvasMarquee}
                        onClick={(e) => {
                          if (e.target === e.currentTarget) {
                            clearSlotSelection();
                            clearChromeSelection();
                          }
                        }}
                        sx={{
                          position: "absolute",
                          inset: 0,
                          width: "100%",
                          height: "100%",
                          overflow: "visible",
                          bgcolor: "#f3f3f3",
                          containerType: "size",
                          containerName: "moodboard",
                        }}
                      >
                        {layers}
                      </Box>
                    </Box>
                  </Box>
                );
              })()}
            </Box>
          </Box>
          <MoodboardVcRightDock
            panel={vcRightPanel}
            onChangePanel={(p) => {
              setVcRightPanel(p);
              if (p === "appearance") setLayersOpen(false);
            }}
            selectionLabel={(() => {
              const mid = selectedMarkId;
              if (mid) {
                const m = (pages[pageIdx]?.marks || []).find(
                  (x) => String(x.id) === String(mid),
                );
                return m ? `Mark · ${m.type}` : "Mark";
              }
              const sid = activeSlotId || [...selectedSlotIds][0];
              if (sid) return `Photo · ${sid}`;
              return "";
            })()}
            selectedMark={(() => {
              const mid = selectedMarkId;
              if (!mid) return null;
              return (
                (pages[pageIdx]?.marks || []).find(
                  (x) => String(x.id) === String(mid),
                ) || null
              );
            })()}
            onPatchSelectedMark={(patch) => {
              if (!patch || !selectedMarkId) return;
              patchSelectedMark(patch);
              if (
                patch.cols != null ||
                patch.rows != null ||
                patch.rings != null ||
                patch.rays != null
              ) {
                setGridOpts((o) => ({
                  ...o,
                  ...(patch.cols != null
                    ? { cols: clampGridCols(patch.cols, o.cols) }
                    : null),
                  ...(patch.rows != null
                    ? { rows: clampGridRows(patch.rows, o.rows) }
                    : null),
                  ...(patch.rings != null
                    ? { rings: clampPolarRings(patch.rings, o.rings) }
                    : null),
                  ...(patch.rays != null
                    ? { rays: clampPolarRays(patch.rays, o.rays) }
                    : null),
                }));
              }
            }}
            liquifyOpts={liquifyOpts}
            onChangeLiquifyOpts={setLiquifyOpts}
            widthOpts={widthOpts}
            onChangeWidthOpts={setWidthOpts}
            envelopeOpts={envelopeOpts}
            onChangeEnvelopeOpts={setEnvelopeOpts}
            onApplyEnvelopeWarp={handleEnvelopeWarp}
            onApplyEnvelopeMesh={handleEnvelopeMesh}
            perspectiveGrid={pagePerspectiveGrid}
            onChangePerspectiveGrid={patchPerspectiveGrid}
            onTogglePerspectiveGrid={handleTogglePerspectiveGrid}
            symbols={pageSymbols}
            activeSymbolId={activeSymbolId}
            onSelectSymbol={setActiveSymbolId}
            onDefineSymbol={handleDefineSymbol}
            symbolSprayOpts={symbolSprayOpts}
            onChangeSymbolSprayOpts={setSymbolSprayOpts}
            smoothOpts={smoothOpts}
            onChangeSmoothOpts={setSmoothOpts}
            wandOpts={wandOpts}
            onChangeWandOpts={setWandOpts}
            activeMarkTool={markTool}
            transform={(() => {
              const mid = selectedMarkId;
              if (mid) {
                const m = (pages[pageIdx]?.marks || []).find(
                  (x) => String(x.id) === String(mid),
                );
                if (!m) return null;
                const b = markBounds(m);
                if (!b) return null;
                return {
                  x: b.x,
                  y: b.y,
                  w: b.w,
                  h: b.h,
                  rotation: Number(m.rotation) || 0,
                };
              }
              const sid = activeSlotId || [...selectedSlotIds][0];
              const s = (pages[pageIdx]?.slots || []).find(
                (x) => String(x.id) === String(sid),
              );
              if (!s) return null;
              return {
                x: Number(s.x) || 0,
                y: Number(s.y) || 0,
                w: Number(s.w) || 0,
                h: Number(s.h) || 0,
                rotation: Number(s.rotation) || 0,
              };
            })()}
            onPatchTransform={(patch) => {
              if (selectedMarkId) {
                const m = (pagesRef.current[pageIdx]?.marks || []).find(
                  (x) => String(x.id) === String(selectedMarkId),
                );
                if (!m) return;
                if (markHasBox(m)) {
                  patchSelectedMark({
                    ...(patch.x != null ? { x: patch.x } : null),
                    ...(patch.y != null ? { y: patch.y } : null),
                    ...(patch.w != null ? { w: patch.w } : null),
                    ...(patch.h != null ? { h: patch.h } : null),
                    ...(patch.rotation != null
                      ? { rotation: normalizeRotation(patch.rotation) }
                      : null),
                  });
                } else if (patch.rotation != null) {
                  patchSelectedMark({
                    rotation: normalizeRotation(patch.rotation),
                  });
                }
                return;
              }
              const sid = activeSlotId || [...selectedSlotIds][0];
              if (!sid) return;
              updateCurrentPageSlots((slots) =>
                slots.map((s) =>
                  String(s.id) === String(sid)
                    ? clampSlot({
                        ...s,
                        ...(patch.x != null ? { x: patch.x } : null),
                        ...(patch.y != null ? { y: patch.y } : null),
                        ...(patch.w != null ? { w: patch.w } : null),
                        ...(patch.h != null ? { h: patch.h } : null),
                        ...(patch.rotation != null
                          ? { rotation: normalizeRotation(patch.rotation) }
                          : null),
                      })
                    : s,
                ),
              );
            }}
            stroke={markStroke}
            onChangeStroke={(v) => {
              const next = Math.min(12, Math.max(0, Number(v) || 0));
              setMarkStroke(next);
              // Solo grosor — no tocar fill. History coalesced para Ctrl+Z.
              updateCurrentPageMarks(
                (marks) =>
                  marks.map((m) => {
                    const ids = selectedMarkIdsRef.current;
                    const hit =
                      (ids.size && ids.has(String(m.id))) ||
                      String(m.id) === String(selectedMarkId);
                    return hit ? { ...m, stroke: next } : m;
                  }),
                { coalesceMs: 400 },
              );
              setTextDraft((prev) => {
                if (!prev) return prev;
                const updated = { ...prev, stroke: next };
                textDraftRef.current = updated;
                return updated;
              });
            }}
            fillColor={markFillColor}
            strokeColor={markStrokeColor}
            colorTarget={colorTarget}
            onChangeColorTarget={setColorTarget}
            onSwapFillStroke={swapFillStrokeColors}
            opacity={(() => {
              const mid = selectedMarkId;
              if (!mid) return 1;
              const m = (pages[pageIdx]?.marks || []).find(
                (x) => String(x.id) === String(mid),
              );
              return Number.isFinite(Number(m?.opacity))
                ? Number(m.opacity)
                : 1;
            })()}
            onChangeFill={(hex) => applyMarkColor(hex, "fill")}
            onChangeStrokeColor={(hex) => applyMarkColor(hex, "stroke")}
            onChangeOpacity={(v) => {
              const next = Math.min(1, Math.max(0.05, Number(v) || 1));
              patchSelectedMark({ opacity: next });
            }}
            onApplyGradient={() => applyMarkGradient()}
            gradient={gradientDraft}
            onChangeGradient={(patch) => patchGradientDraft(patch)}
            onReverseGradient={() => reverseMarkGradient()}
            onNoneFill={applyNoneFill}
            layerItems={(() => {
              const page = pages[pageIdx] || {};
              const slots = [...(page.slots || [])]
                .sort((a, b) => (Number(b.z) || 0) - (Number(a.z) || 0))
                .map((s) => ({
                  id: s.id,
                  kind: "slot",
                  label: `Photo ${String(s.id).slice(0, 8)}`,
                  selected: selectedSlotIds.has(s.id) || activeSlotId === s.id,
                  hidden: Boolean(s.hidden),
                }));
              const marks = [...(page.marks || [])]
                .sort((a, b) => (Number(b.z) || 0) - (Number(a.z) || 0))
                .map((m) => ({
                  id: m.id,
                  kind: "mark",
                  label: `${m.type} · ${String(m.id).slice(0, 6)}`,
                  selected:
                    String(selectedMarkId) === String(m.id) ||
                    selectedMarkIds.has(String(m.id)),
                  hidden: Boolean(m.hidden),
                }));
              return [...marks, ...slots];
            })()}
            onSelectLayer={(id, kind) => {
              if (kind === "mark") {
                clearSlotSelection();
                const set = new Set([String(id)]);
                selectedMarkIdsRef.current = set;
                setSelectedMarkIds(set);
                setSelectedMarkId(id);
              } else {
                clearMarkSelection();
                handleSelectSlot(id, { additive: false });
              }
            }}
            pageCount={pages.length}
            pageIdx={pageIdx}
            onSelectPage={(i) => {
              const idx = Math.max(
                0,
                Math.min(pages.length - 1, Number(i) || 0),
              );
              setPageIdx(idx);
              clearMarkSelection();
              clearSlotSelection();
            }}
            onReorderPage={handleReorderPage}
            onRearrangeArtboards={handleRearrangeArtboards}
            onAlign={(edge) => handleAlignSlots(edge)}
            onDistribute={(axis) => handleDistributeSlots(axis)}
            alignTo={alignTo}
            onChangeAlignTo={setAlignTo}
            alignSpacing={alignSpacing}
            onChangeAlignSpacing={setAlignSpacing}
            onPathfinder={(op) => {
              const ids = [...selectedMarkIdsRef.current];
              if (ids.length < 2) {
                setMarkTool(null);
                setMultiSelectMode(true);
                setOkMsg(
                  `Pathfinder · ${op}: selecciona 2+ formas (Shift+clic o marco)`,
                );
                return;
              }
              notePagesHistory();
              let createdIds = [];
              let applied = false;
              updateCurrentPageMarks(
                (marks) => {
                  const next = pathfinderMarks(marks, ids, op);
                  if (!next) return marks;
                  applied = true;
                  const drop = new Set(ids.map(String));
                  const restIds = new Set(
                    marks
                      .filter((m) => !drop.has(String(m.id)))
                      .map((m) => String(m.id)),
                  );
                  createdIds = next
                    .filter((m) => !restIds.has(String(m.id)))
                    .map((m) => String(m.id));
                  return next;
                },
                { history: false },
              );
              if (!applied || !createdIds.length) {
                setOkMsg(`Pathfinder · ${op}: sin resultado (¿se solapan?)`);
                return;
              }
              const set = new Set(createdIds);
              selectedMarkIdsRef.current = set;
              setSelectedMarkIds(set);
              setSelectedMarkId(createdIds[createdIds.length - 1]);
              setOkMsg(
                `Pathfinder · ${op}${
                  createdIds.length > 1 ? ` · ${createdIds.length} piezas` : ""
                }`,
              );
            }}
            fontSize={markFontSize}
            fontFamily={markFontFamily}
            fontWeight={markFontWeight}
            textAlign={markTextAlign}
            onChangeFontSize={(v) => {
              const next = clampMarkFontSize(v, 14);
              setMarkFontSize(next);
              patchSelectedMark({ fontSize: next });
              setTextDraft((prev) => {
                if (!prev) return prev;
                const nextDraft = { ...prev, fontSize: next };
                textDraftRef.current = nextDraft;
                return nextDraft;
              });
            }}
            onChangeFontFamily={(v) => {
              const next = normalizeMarkFontFamily(v);
              setMarkFontFamily(next);
              patchSelectedMark({ fontFamily: next });
              setTextDraft((prev) => {
                if (!prev) return prev;
                const nextDraft = { ...prev, fontFamily: next };
                textDraftRef.current = nextDraft;
                return nextDraft;
              });
            }}
            onChangeFontWeight={(v) => {
              const next = normalizeMarkFontWeight(v);
              setMarkFontWeight(next);
              patchSelectedMark({ fontWeight: next });
              setTextDraft((prev) => {
                if (!prev) return prev;
                const nextDraft = { ...prev, fontWeight: next };
                textDraftRef.current = nextDraft;
                return nextDraft;
              });
            }}
            onChangeTextAlign={(v) => {
              const next = normalizeMarkTextAlign(v);
              setMarkTextAlign(next);
              patchSelectedMark({ textAlign: next });
              setTextDraft((prev) => {
                if (!prev) return prev;
                const nextDraft = { ...prev, textAlign: next };
                textDraftRef.current = nextDraft;
                return nextDraft;
              });
            }}
            onUndo={applyPagesUndo}
            onRedo={applyPagesRedo}
            canUndo={canUndo}
            canRedo={canRedo}
            infoLines={[
              `Page ${pageIdx + 1} / ${pages.length}`,
              selectedMarkId
                ? `Mark ${selectedMarkId}`
                : activeSlotId
                  ? `Slot ${activeSlotId}`
                  : "No selection",
              `Zoom ${Math.round((Number(viewZoom) || 1) * 100)}%`,
            ]}
          />
        </Box>

        <MoodboardAddPhotosDialog
          open={addPhotosOpen}
          onClose={() => {
            if (!busy) {
              setAddPhotosOpen(false);
              setPoolPick(new Set());
            }
          }}
          busy={busy}
          pool={pool}
          poolLoading={poolLoading}
          poolError={poolError}
          usedSearchItemIds={usedSearchItemIds}
          pick={poolPick}
          setPick={setPoolPick}
          onConfirm={() => handleAddFromPool()}
          seasonLabel={board?.Temporada || ""}
          clientLabel={board?.CustomerName || board?.Titulo || ""}
          projectMode={Boolean(pool?.projectMode)}
          sourceFilter={poolSourceFilter}
          onSourceFilterChange={setPoolSourceFilter}
        />

        <MoodboardAddMeetingPhotosDialog
          open={addMeetingPhotosOpen}
          onClose={() => {
            if (!busy) {
              setAddMeetingPhotosOpen(false);
              setMeetingPoolPick(new Set());
            }
          }}
          busy={busy}
          pool={meetingPool}
          poolLoading={meetingPoolLoading}
          poolError={meetingPoolError}
          pick={meetingPoolPick}
          setPick={setMeetingPoolPick}
          onConfirm={handleAddFromMeeting}
        />

        <MoodboardBoardPhotosDialog
          open={boardPhotosOpen}
          onClose={() => {
            if (!busy) setBoardPhotosOpen(false);
          }}
          assets={assets}
          selectedIds={gallerySelected}
          usedOnPagesIds={usedOnPagesAssetIds}
          onToggleSelect={toggleGallerySelect}
          onClearSelect={() => setGallerySelected(new Set())}
          onPlaceOne={(id) => {
            if (activeSlotId) assignAssetToSlot(activeSlotId, id);
            else placeAssetOnPage(id, galleryPlacePageIdx);
          }}
          onPlaceSelected={() => {
            placeSelectedOnPage(galleryPlacePageIdx);
          }}
          onPlaceOnPasteboard={() => {
            const ids = [...gallerySelected];
            if (!ids.length) return;
            addAssetsToPasteboard(ids, defaultPasteboardPoint());
            setGallerySelected(new Set());
            setBoardPhotosOpen(false);
            setWorkspaceMode(WORKSPACE_MODE_BOARD);
            writeStoredWorkspaceMode(WORKSPACE_MODE_BOARD);
            setOkMsg(
              ids.length === 1
                ? "Foto en el tablero (fuera de las mesas)"
                : `${ids.length} fotos en el tablero (fuera)`,
            );
          }}
          onStampNotes={
            assets.some((a) => assetTeamNote(a))
              ? () => {
                  const result = stampMeetingNotesOnPages(
                    pagesRef.current,
                    notesByAssetIdFromAssets(assets),
                  );
                  if (!result.added) {
                    setOkMsg(t("skillAgent.moodboard.ok.notesNone"));
                    return;
                  }
                  notePagesHistory();
                  setPages(result.pages);
                  setOkMsg(
                    t("skillAgent.moodboard.ok.notesStamped", {
                      n: result.added,
                    }),
                  );
                }
              : undefined
          }
          onRemove={(id) => setAssetRemoveId(id)}
          pageCount={pages.length}
          placePageIdx={galleryPlacePageIdx}
          onPlacePageChange={setGalleryPlacePageIdx}
          busy={busy}
        />

        <Dialog
          open={Boolean(slotRemoveId)}
          onClose={() => setSlotRemoveId(null)}
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle fontWeight={800}>
            {selectedSlotIds.size > 1 && selectedSlotIds.has(slotRemoveId)
              ? t("skillAgent.moodboard.dialog.removeSlotsTitle", {
                  n: selectedSlotIds.size,
                })
              : t("skillAgent.moodboard.dialog.removeSlotTitle")}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              {selectedSlotIds.size > 1 && selectedSlotIds.has(slotRemoveId)
                ? t("skillAgent.moodboard.dialog.removeSlotsBody")
                : t("skillAgent.moodboard.dialog.removeSlotBodyLong")}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setSlotRemoveId(null)}>
              {t("skillAgent.common.cancel")}
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={() => handleRemoveSlot(slotRemoveId)}
            >
              {t("skillAgent.common.remove")}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={Boolean(assetRemoveId)}
          onClose={() => !busy && setAssetRemoveId(null)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              overflow: "hidden",
            },
          }}
        >
          <Box
            sx={{
              px: 3,
              pt: 3,
              pb: 1.5,
              display: "flex",
              alignItems: "flex-start",
              gap: 1.75,
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: 2.5,
                flexShrink: 0,
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "#f3f3f3",
                boxShadow: `0 8px 20px ${alpha("#0f172a", 0.12)}`,
              }}
            >
              {assetRemoveId ? (
                <AssetThumb
                  assetId={assetRemoveId}
                  selected={false}
                  transparentBg
                  fit="contain"
                />
              ) : null}
            </Box>
            <Box sx={{ minWidth: 0, pt: 0.25, flex: 1 }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 0.5 }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1.5,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: (t) => alpha(t.palette.error.main, 0.12),
                    color: "error.main",
                  }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                </Box>
                <Typography
                  variant="h6"
                  fontWeight={800}
                  sx={{ lineHeight: 1.25 }}
                >
                  {t("skillAgent.moodboard.dialog.removeAssetTitle")}
                </Typography>
              </Stack>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {t("skillAgent.moodboard.dialog.removeAssetBodyExtended")}
                {(() => {
                  const used = pages.reduce(
                    (n, p) =>
                      n +
                      (p.slots || []).filter((s) => s.assetId === assetRemoveId)
                        .length,
                    0,
                  );
                  if (!used) return null;
                  return (
                    <>
                      {" "}
                      {t("skillAgent.moodboard.dialog.removeAssetInSlots", {
                        n: used,
                      })}
                    </>
                  );
                })()}
              </Typography>
            </Box>
          </Box>
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
            <Button
              onClick={() => setAssetRemoveId(null)}
              disabled={busy}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              {t("skillAgent.common.cancel")}
            </Button>
            <Button
              color="error"
              variant="contained"
              disabled={busy}
              onClick={() => handleRemoveAsset(assetRemoveId)}
              startIcon={
                busy ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <DeleteOutlineIcon />
                )
              }
              sx={{ textTransform: "none", fontWeight: 800, borderRadius: 2 }}
            >
              {t("skillAgent.moodboard.dialog.removeAssetSubmit")}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={removeBgConfirmOpen}
          onClose={() => !busy && setRemoveBgConfirmOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
        >
          <Box
            sx={{
              px: 3,
              pt: 3,
              pb: 1.5,
              display: "flex",
              alignItems: "flex-start",
              gap: 1.75,
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2.5,
                flexShrink: 0,
                display: "grid",
                placeItems: "center",
                bgcolor: (t) => alpha(t.palette.warning.main, 0.14),
                color: "warning.dark",
              }}
            >
              <AutoFixHighOutlinedIcon />
            </Box>
            <Box sx={{ minWidth: 0, pt: 0.25, flex: 1 }}>
              <Typography
                variant="h6"
                fontWeight={800}
                sx={{ lineHeight: 1.25 }}
              >
                {t("skillAgent.moodboard.dialog.cutoutTitle")}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.75 }}
              >
                {removeBgAssetIds.length > 1
                  ? t("skillAgent.moodboard.dialog.cutoutBodyMany", {
                      n: removeBgAssetIds.length,
                    })
                  : t("skillAgent.moodboard.dialog.cutoutBodyLong")}
              </Typography>
            </Box>
          </Box>
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
            <Button
              onClick={() => setRemoveBgConfirmOpen(false)}
              disabled={busy}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              {t("skillAgent.common.cancel")}
            </Button>
            <Button
              variant="contained"
              color="warning"
              disabled={busy}
              onClick={runRemoveBackground}
              startIcon={
                busy ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <AutoFixHighOutlinedIcon />
                )
              }
              sx={{ textTransform: "none", fontWeight: 800, borderRadius: 2 }}
            >
              {t("skillAgent.moodboard.dialog.cutoutSubmit")}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={aviatorConfirmOpen}
          onClose={() => setAviatorConfirmOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
        >
          <Box
            sx={{
              px: 3,
              pt: 3,
              pb: 1.5,
              display: "flex",
              alignItems: "flex-start",
              gap: 1.75,
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 56,
                borderRadius: 2,
                flexShrink: 0,
                p: 0.6,
                bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                border: "1px solid",
                borderColor: (t) => alpha(t.palette.primary.main, 0.22),
                display: "grid",
                gridTemplateColumns: "1.15fr 1fr 1fr 1fr",
                gridTemplateRows: "1fr 1fr",
                gap: 0.4,
              }}
              aria-hidden
            >
              <Box
                sx={{
                  gridRow: "1 / 3",
                  borderRadius: 0.75,
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.45),
                }}
              />
              {Array.from({ length: 6 }).map((_, i) => (
                <Box
                  key={i}
                  sx={{
                    borderRadius: 0.5,
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.28),
                  }}
                />
              ))}
            </Box>
            <Box sx={{ minWidth: 0, pt: 0.15, flex: 1 }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 0.5 }}
              >
                <ViewQuiltOutlinedIcon color="primary" fontSize="small" />
                <Typography
                  variant="h6"
                  fontWeight={800}
                  sx={{ lineHeight: 1.25 }}
                >
                  {t("skillAgent.moodboard.dialog.aviatorTitle")}
                </Typography>
              </Stack>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {t("skillAgent.moodboard.dialog.aviatorBody", {
                  n: pageIdx + 1,
                })}
                {(() => {
                  const n = (currentPage.slots || []).filter(
                    (s) => s.assetId,
                  ).length;
                  if (n <= 7) {
                    return n
                      ? ` ${t("skillAgent.moodboard.dialog.aviatorPageCount", {
                          n,
                        })}`
                      : null;
                  }
                  return ` ${t("skillAgent.moodboard.dialog.aviatorOverflow", {
                    n,
                  })}`;
                })()}
              </Typography>
            </Box>
          </Box>
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
            <Button
              onClick={() => setAviatorConfirmOpen(false)}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              {t("skillAgent.common.cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={handleAviatorPreset}
              startIcon={<ViewQuiltOutlinedIcon />}
              sx={{ textTransform: "none", fontWeight: 800, borderRadius: 2 }}
            >
              {t("skillAgent.moodboard.dialog.aviatorSubmit")}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={autoPagesConfirmOpen}
          onClose={() => !busy && setAutoPagesConfirmOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
        >
          <Box
            sx={{
              px: 3,
              pt: 3,
              pb: 1.5,
              display: "flex",
              alignItems: "flex-start",
              gap: 1.75,
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2.5,
                flexShrink: 0,
                display: "grid",
                placeItems: "center",
                bgcolor: (t) => alpha(t.palette.warning.main, 0.14),
                color: "warning.dark",
              }}
            >
              <GridViewOutlinedIcon />
            </Box>
            <Box sx={{ minWidth: 0, pt: 0.25, flex: 1 }}>
              <Typography
                variant="h6"
                fontWeight={800}
                sx={{ lineHeight: 1.25 }}
              >
                {boardApi
                  ? "Colocar fotos de la muestra"
                  : t("skillAgent.moodboard.dialog.autoPagesTitle")}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.75 }}
              >
                {boardApi
                  ? "Las fotos de la muestra vuelven a la izquierda y las técnicas a la derecha. El layout actual se sustituye."
                  : t("skillAgent.moodboard.dialog.autoPagesBodyDetail", {
                      n: assets.length,
                    })}
                {!boardApi && pages.length > 1
                  ? ` ${t("skillAgent.moodboard.dialog.autoPagesPagesNote", {
                      n: pages.length,
                    })}`
                  : null}
                {!boardApi
                  ? ` ${t("skillAgent.moodboard.dialog.autoPagesNoDelete")}`
                  : null}
              </Typography>
            </Box>
          </Box>
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
            <Button
              onClick={() => setAutoPagesConfirmOpen(false)}
              disabled={busy}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              {t("skillAgent.common.cancel")}
            </Button>
            <Button
              variant="contained"
              color="warning"
              disabled={busy || !assets.length}
              onClick={handleAutoPages}
              startIcon={
                busy ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <GridViewOutlinedIcon />
                )
              }
              sx={{ textTransform: "none", fontWeight: 800, borderRadius: 2 }}
            >
              {t("skillAgent.moodboard.dialog.autoPagesSubmit")}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={pageRemoveOpen}
          onClose={() => setPageRemoveOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              overflow: "hidden",
            },
          }}
        >
          <Box
            sx={{
              px: 3,
              pt: 3,
              pb: 1.5,
              display: "flex",
              alignItems: "flex-start",
              gap: 1.75,
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2.5,
                flexShrink: 0,
                display: "grid",
                placeItems: "center",
                bgcolor: (t) => alpha(t.palette.error.main, 0.12),
                color: "error.main",
              }}
            >
              <DeleteOutlineIcon />
            </Box>
            <Box sx={{ minWidth: 0, pt: 0.25 }}>
              <Typography
                variant="h6"
                fontWeight={800}
                sx={{ lineHeight: 1.25 }}
              >
                {t("skillAgent.moodboard.dialog.deletePageTitle", {
                  n: pageIdx + 1,
                })}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.75 }}
              >
                {t("skillAgent.moodboard.dialog.deletePageBodyDetail")}
                {(currentPage.slots || []).filter((s) => s.assetId).length > 0
                  ? ` ${t("skillAgent.moodboard.dialog.deletePagePhotosNow", {
                      n: (currentPage.slots || []).filter((s) => s.assetId)
                        .length,
                    })}`
                  : null}
              </Typography>
            </Box>
          </Box>
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
            <Button
              onClick={() => setPageRemoveOpen(false)}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              {t("skillAgent.common.cancel")}
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleConfirmRemovePage}
              startIcon={<DeleteOutlineIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 800,
                borderRadius: 2,
                boxShadow: "none",
              }}
            >
              {t("skillAgent.moodboard.dialog.deletePageSubmit")}
            </Button>
          </DialogActions>
        </Dialog>

        <Popover
          open={Boolean(strokeAnchor)}
          anchorEl={strokeAnchor}
          onClose={() => setStrokeAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{ sx: { p: 2, width: 280, borderRadius: 2 } }}
        >
          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
            {t("skillAgent.moodboard.dialog.strokeTitle")}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {t("skillAgent.moodboard.strokePopoverHint")}
          </Typography>
          <Stack spacing={1.5} sx={{ mt: 1.5 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t("skillAgent.moodboard.dialog.strokeWidth", {
                  n: strokeWidth,
                })}
              </Typography>
              <Slider
                size="small"
                min={1}
                max={40}
                step={1}
                value={strokeWidth}
                onChange={(_, v) => setStrokeWidth(Number(v))}
              />
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="text.secondary">
                {t("skillAgent.common.color")}
              </Typography>
              <Box
                component="input"
                type="color"
                value={strokeColor}
                onChange={(e) => setStrokeColor(e.target.value)}
                sx={{
                  width: 36,
                  height: 28,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 0,
                  bgcolor: "transparent",
                  cursor: "pointer",
                }}
              />
              <TextField
                size="small"
                value={strokeColor}
                onChange={(e) => setStrokeColor(e.target.value)}
                sx={{ flex: 1, "& input": { fontSize: 12, py: 0.75 } }}
              />
            </Stack>
            <Button
              variant="contained"
              size="small"
              disabled={busy || !activeSlot?.assetId}
              onClick={handleApplyContourStroke}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              {t("skillAgent.moodboard.dialog.strokeApply")}
            </Button>
          </Stack>
        </Popover>

        <MoodboardCropDialog
          open={cropOpen}
          assetId={activeSlot?.assetId || null}
          busy={busy}
          onClose={() => setCropOpen(false)}
          onConfirm={handleConfirmCrop}
        />

        <MoodboardGeneratePanel
          open={generateOpen}
          onClose={() => !busy && setGenerateOpen(false)}
          busy={busy}
          assets={assets}
          selectedAssetIds={generateRefAssetIds}
          fetchBlob={fetchAssetBlobCached}
          onGenerate={handleGenerateImage}
        />

        <MoodboardContextMenu
          open={Boolean(ctxMenu)}
          anchorPosition={ctxMenu}
          onClose={closeContextMenu}
          items={ctxMenuItems}
        />

        <MoodboardInpaintDialog
          open={inpaintOpen}
          onClose={() => !busy && closeInpaint()}
          busy={busy}
          imageUrl={inpaintImageUrl}
          onSubmit={handleInpaintSubmit}
        />

        <MoodboardLayersDrawer
          open={layersOpen}
          onClose={() => setLayersOpen(false)}
          page={currentPage}
          selectedSlotIds={selectedSlotIds}
          selectedMarkId={selectedMarkId}
          onSelectSlot={(id) => {
            selectOnlySlot(id);
            clearMarkSelection();
          }}
          onSelectMark={(id) => {
            clearSlotSelection();
            setSelectedMarkId(id);
            setSelectedMarkIds(new Set([String(id)]));
          }}
          onBringForward={(id) => {
            updateCurrentPageSlots((slots) => bringSlotForward(slots, id));
          }}
          onSendBackward={(id) => {
            updateCurrentPageSlots((slots) => sendSlotBackward(slots, id));
          }}
        />

        <Dialog
          open={exportConfirmOpen}
          onClose={() => !exporting && setExportConfirmOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
        >
          <Box
            sx={{
              px: 3,
              pt: 3,
              pb: 1.5,
              display: "flex",
              gap: 2,
              alignItems: "flex-start",
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: 2.5,
                flexShrink: 0,
                display: "grid",
                placeItems: "center",
                bgcolor: (th) => alpha(th.palette.error.main, 0.1),
                border: "1px solid",
                borderColor: (th) => alpha(th.palette.error.main, 0.22),
                color: "error.main",
              }}
              aria-hidden
            >
              <PictureAsPdfOutlinedIcon sx={{ fontSize: 36 }} />
            </Box>
            <Box sx={{ minWidth: 0, pt: 0.25, flex: 1 }}>
              <Typography
                variant="h6"
                fontWeight={800}
                sx={{ lineHeight: 1.25, mb: 0.75 }}
              >
                {t("skillAgent.moodboard.dialog.exportTitle")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {boardApi?.exportBody ||
                  t("skillAgent.moodboard.dialog.exportBody")}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ px: 3, pb: 1 }}>
            <Stack
              spacing={1}
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: (th) => alpha(th.palette.text.primary, 0.03),
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              {[
                t("skillAgent.moodboard.dialog.exportBulletSave"),
                t("skillAgent.moodboard.dialog.exportBulletPdf"),
                t("skillAgent.moodboard.dialog.exportBulletDrive"),
              ].map((line) => (
                <Stack
                  key={line}
                  direction="row"
                  spacing={1}
                  alignItems="flex-start"
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: "error.main",
                      mt: "7px",
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {line}
                  </Typography>
                </Stack>
              ))}
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1.25, fontWeight: 600 }}
            >
              {t("skillAgent.moodboard.dialog.exportPagesNote", {
                n: exportPageIdxs.length,
                photos: (assets || []).length,
              })}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.5 }}
            >
              El orden del PDF es el del panel Artboards (1…n). Reordena allí
              con ↑↓ o arrastrando.
            </Typography>
            <Typography
              variant="caption"
              fontWeight={800}
              sx={{ display: "block", mt: 1.5, mb: 0.5 }}
            >
              {t("skillAgent.moodboard.dialog.exportPickPages")}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 0.5 }}>
              <Button
                size="small"
                onClick={() =>
                  setExportPageIdxs((pages || []).map((_, index) => index))
                }
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  minWidth: 0,
                  px: 1,
                }}
              >
                {t("skillAgent.moodboard.dialog.exportSelectAll")}
              </Button>
              <Button
                size="small"
                onClick={() => setExportPageIdxs([])}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  minWidth: 0,
                  px: 1,
                }}
              >
                {t("skillAgent.moodboard.dialog.exportSelectNone")}
              </Button>
            </Stack>
            <Box
              sx={{
                maxHeight: 200,
                overflow: "auto",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              {(pages || []).map((page, index) => {
                const checked = exportPageIdxs.includes(index);
                const photoCount = (page?.slots || []).filter(
                  (slot) => slot?.assetId != null,
                ).length;
                return (
                  <Stack
                    key={`export-page-${index}`}
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    onClick={() =>
                      setExportPageIdxs((prev) => {
                        if (prev.includes(index)) {
                          return prev.filter((n) => n !== index);
                        }
                        return [...prev, index].sort((a, b) => a - b);
                      })
                    }
                    sx={{
                      px: 1,
                      py: 0.25,
                      cursor: "pointer",
                      "&:hover": {
                        bgcolor: (th) => alpha(th.palette.text.primary, 0.04),
                      },
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={checked}
                      tabIndex={-1}
                      disableRipple
                      inputProps={{
                        "aria-label": t(
                          "skillAgent.moodboard.dialog.exportPageLabel",
                          { n: index + 1 },
                        ),
                      }}
                    />
                    <Typography variant="body2" fontWeight={700}>
                      {t("skillAgent.moodboard.dialog.exportPageLabel", {
                        n: index + 1,
                      })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("skillAgent.moodboard.dialog.exportPagePhotos", {
                        n: photoCount,
                      })}
                    </Typography>
                  </Stack>
                );
              })}
            </Box>
            {!exportPageIdxs.length ? (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: "block", mt: 0.75, fontWeight: 700 }}
              >
                {t("skillAgent.moodboard.dialog.exportNeedPage")}
              </Typography>
            ) : null}
          </Box>
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
            <Button
              onClick={() => setExportConfirmOpen(false)}
              disabled={exporting}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              {t("skillAgent.common.cancel")}
            </Button>
            <Button
              variant="contained"
              color="error"
              disabled={
                exporting || busy || !assets.length || !exportPageIdxs.length
              }
              onClick={handleExport}
              startIcon={
                exporting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <PictureAsPdfOutlinedIcon />
                )
              }
              sx={{ textTransform: "none", fontWeight: 800, borderRadius: 2 }}
            >
              {exporting
                ? t("skillAgent.moodboard.exporting")
                : t("skillAgent.moodboard.dialog.exportSubmit")}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={deleteOpen}
          onClose={() => !busy && setDeleteOpen(false)}
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle fontWeight={800}>
            {t("skillAgent.moodboard.dialog.deleteBoardTitle")}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              {t("skillAgent.moodboard.dialog.deleteBoardBody")}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDeleteOpen(false)} disabled={busy}>
              {t("skillAgent.common.cancel")}
            </Button>
            <Button
              color="error"
              variant="contained"
              disabled={busy}
              onClick={handleDelete}
            >
              {t("skillAgent.common.delete")}
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </MoodboardAssetBlobContext.Provider>
  );
}
